import { initializeTechnicalOperations } from "./technical-operations.js";
import { renderAuctionListings, renderAuctions, type AuctionState } from "./auction-view.js";
import { createRaffleManager } from "./community-raffle-manager.js";
import { renderCommunityRaffleAnnouncement } from "./render.js";
import { initializeLeaderboardPage } from "./leaderboard-controller.js";
import { createGiveawayController } from "./giveaway-controller.js";
import { mountSlots } from "./slots.js";
import { animateRouletteWheel } from "./roulette-wheel.js";
import { renderSlotsConfiguration } from "./render.js";
import { createPortalApiClient, isPortalAuthenticationError, PortalApiError, type SupportTicketCapabilities } from "./api-client.js";
import {
  Activity,
  Baby,
  BadgeCheck,
  BadgeDollarSign,
  Bell,
  BriefcaseBusiness,
  Bug,
  Camera,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Check,
  Circle,
  CircleDotDashed,
  CircleHelp,
  Clock3,
  Coins,
  createIcons,
  Crown,
  Dices,
  Dna,
  ExternalLink,
  Egg,
  Focus,
  Flag,
  FlagTriangleRight,
  FlaskConical,
  Gamepad2,
  Gavel,
  HeartPulse,
  Drumstick,
  LockKeyhole,
  LogIn,
  Map,
  MapPinned,
  MessagesSquare,
  Link,
  LogOut,
  LineChart,
  Palette,
  PawPrint,
  Pause,
  Plus,
  Megaphone,
  Rotate3D,
  RefreshCw,
  RadioTower,
  Route,
  Salad,
  Satellite,
  Save,
  ScanSearch,
  Search,
  Trophy,
  ShieldAlert,
  ShieldCheck,
  Shuffle,
  Skull,
  ScrollText,
  ServerCog,
  Settings2,
  Store,
  Sprout,
  TentTree,
  Ticket,
  Trash2,
  TerminalSquare,
  Unlink,
  UserRound,
  UsersRound,
  UserSearch,
  X,
  Warehouse,
  Waves
} from "lucide";
import type { PortalActionContext, SystemControlAction } from "./actions.js";
import { initializePublicLanding, publicLandingAuthNotice, renderPublicLanding } from "./public-landing.js";
import {
  renderAuditEvents,
  renderGameplayLogResults,
  renderGameBridgeOperations,
  renderGameServerLogSamples,
  renderPlayerTicketDetail,
  renderPlayerTicketMessages,
  renderPlayerTicketList,
  renderPlayerModerationHistory,
  renderPortal,
  renderPortalAnnouncementBar,
  renderAnnouncementManager,
  renderScheduledGameAnnouncementManager,
  renderBlackjack,
  renderBlackjackConfiguration,
  renderRoulette,
  renderRouletteConfiguration,
  renderRaptorRace,
  renderRaptorRaceConfiguration,
  renderEventWheel,
  arrangeEventWheelPrizes,
  renderEventWheelConfiguration,
  renderEventWheelPrizeRow,
  renderEventJoinCampaign,
  renderEventRewardLogs,
  renderSpecimenHatchery,
  renderSpecimenIncubator,
  renderPortalPageContent,
  liveMapPayload,
  renderLiveMapRoster,
  renderPortalNotifications,
  renderRulebookManager,
  renderSkinReviewQueue,
  renderSupportTicketDetail,
  renderCaseConversationMessages,
  renderSupportTicketEvidenceLogReview,
  renderSupportTicketCombinedLogReview,
  renderSupportTicketLogReview,
  renderSupportTicketList,
  renderSupportTicketMetrics,
  renderSupportTicketTranscript,
  renderStaffTicketNotifications,
  renderStaffDutyStatus,
  renderStaffPerformance,
  renderStaffPlayerWorkspace,
  renderServerHealth,
  playerTicketMessageSignature,
  telemetryStatus
} from "./render.js";
import { renderReleaseCentre } from "./release-centre-view.js";
import { renderGrowthCentre } from "./growth-centre-view.js";
import { playerTicketFormConfig, type PlayerTicketType } from "./ticket-form.js";
import type { AccessControlPreviewSettings, AccessDecision, AmbientAiSettings, BlackjackConfigurationSummary, BlackjackOutcome, BlackjackState, CorpseCleanupSettings, CrossSpeciesChatSettings, DiscordLogRoutingSettings, DiscordPatreonRolePolicySettings, EcosystemZoneSettings, EventWheelConfigurationSummary, EventWheelPrizeSummary, GameBridgeOperationSummary, GarageCapacityPolicySettings, GameplayEventFilters, GameplayEventSummary, GrowthCentreReport, ModerationPolicySettings, PlayerModerationStrikeSummary, PointBalancePolicySettings, PortalAnnouncementSummary, PortalSnapshotResponse, RaptorRaceConfigurationSummary, RaptorRaceState, ReleaseCentreSettings, RouletteBetType, RouletteConfigurationSummary, RouletteState, RuleDefinitionSummary, RuntimeReadinessSettings, ScheduledGameAnnouncementSummary, ServerHealthSettings, SkinAnimation, SpecimenEggInventory, SpecimenEggSummary, SpecimenEggTier, StaffCredentialsSettings, SupportTicketMessageSummary, SupportTicketSummary, TelemetryHealthSettings, TelemetryRetentionSettings, WheelPrizeType } from "./types.js";
import { portalPageFromPath, type PortalPage } from "./navigation.js";
import { GATEWAY_MAP_SIZE, availableStoreMutationsForSpecies, formatPlayerBridgeProgress, skinAnimationShadeCount, skinStructureLimitsForSpecies, skinThemeCountForPattern, theNestPatreonBenefits } from "@isle/domain";
import { captureSkinPreviewCanvas, disposeLiveDinoModelPreview, disposeMarketplaceSkinPreviews, disposeSkinModelPreview, initializeLiveDinoModelPreview, initializeMarketplaceSkinPreviews, initializeSkinModelPreview, playSkinModelPreviewMotion, skinPreviewGrowthStage, updateMarketplaceSkinPreviewGrowth, updateSkinModelPreviewAnimation, updateSkinModelPreviewChannel, updateSkinModelPreviewEffects, updateSkinModelPreviewGrowth, updateSkinModelPreviewLighting, updateSkinModelPreviewPattern, type SkinPreviewEffectSettings, type SkinPreviewLighting, type SkinPreviewMotion } from "./skin-preview.js";
import { raceFinishOrder, raceRunnerProgress } from "./raptor-race-motion.js";
import { disposeRaptorRace3d, mountRaptorRace3d } from "./raptor-race-3d.js";
import { clearLiveMapCalibrationSamples, copyLiveMapCalibrationSamples, disposeLiveMap, initializeLiveMap, expireLiveWeatherEvidence, focusLiveWeather, recenterLiveMap, setLiveMapLayerVisibility, setLiveMapMonochrome, toggleLiveMapCalibration, updateLiveMap } from "./live-map.js";
import { disposeEcosystemZoneMap, initializeEcosystemZoneMap, refreshEcosystemZoneMap } from "./ecosystem-zone-map.js";
import { disposeCustomAiMap, fitCustomAiMap, markCustomAiMapUnavailable, updateCustomAiMap } from "./custom-ai-map.js";
import { canReuseLiveDinoCard, canReuseLiveDinoModel, initialLiveSnapshotStabilizationState, stabilizeLiveSnapshot } from "./live-snapshot.js";
import { isGarageContentUnchanged, isLiveSkinEditorSpeciesCurrent, isOverviewIncrementalRefresh, isSkinsContentUnchanged, shouldRefreshPortalLivePage } from "./snapshot-refresh.js";
import { parkingCountdownProgress, parkingSecondsRemaining } from "./parking-countdown.js";
import { ticketEventRefreshPlan } from "./case-desk-refresh.js";

const app = document.querySelector("#app");
const apiClient = createPortalApiClient();
const PORTAL_ROLE_REFRESH_INTERVAL_MS = 4 * 60_000;
const NOTIFICATION_RETRY_MAX_MS = 60_000;
let searchParams = new URLSearchParams(window.location.search);
const roleIds = searchParams.get("roles")?.split(",").filter(Boolean) ?? [];
let userId = searchParams.get("userId") ?? "";
const giveawayController = createGiveawayController(apiClient, () => ({ userId, roleIds }));
let currentSnapshot: PortalSnapshotResponse["snapshot"];
let currentAccess: AccessDecision;
let actionsWired = false;
let pendingPortalAction: HTMLElement | undefined;
let pendingSystemAction: HTMLElement | undefined;
let mapRefreshStarted = false;
let portalEventSource: EventSource | undefined;
let mapRefreshInFlight = false;
let mapRefreshQueued = false;
let mapEventRefreshTimer: number | undefined;
let snapshotEventRefreshTimer: number | undefined;
let liveDataRefreshStarted = false;
let liveDataRefreshInFlight = false;
let liveSnapshotStabilizationState = initialLiveSnapshotStabilizationState();
let portalActionInFlight = false;
let auctionRefreshTimer: ReturnType<typeof setInterval> | undefined;
let auctionState: AuctionState | undefined;
let auctionRefreshInFlight = false;
let pendingAuctionRequest: { fingerprint: string; requestId: string } | undefined;
let statusTimer: number | undefined;
let cooldownTimerStarted = false;
let rulebookCache: RuleDefinitionSummary[] = [];
let profileSearchTimer: number | undefined;
let stickTransferSearchTimer: number | undefined;
let playerPickerListSequence = 0;
const playerPickerTimers = new WeakMap<HTMLInputElement, number>();
let discordLogRoutingSettings: DiscordLogRoutingSettings | undefined;
let crossSpeciesChatSettings: CrossSpeciesChatSettings | undefined;
let ambientAiSettings: AmbientAiSettings | undefined;
let corpseCleanupSettings: CorpseCleanupSettings | undefined;
let ecosystemZoneSettings: EcosystemZoneSettings | undefined;
let selectedEcosystemZoneId: string | undefined;
let staffCredentialsSettings: StaffCredentialsSettings | undefined;
let moderationPolicySettings: ModerationPolicySettings | undefined;
let garageCapacityPolicySettings: GarageCapacityPolicySettings | undefined;
let pointBalancePolicySettings: PointBalancePolicySettings | undefined;
let discordPatreonRolePolicySettings: DiscordPatreonRolePolicySettings | undefined;
let ticketQueueCursor: string | null = null;
let ticketQueueItems: SupportTicketSummary[] = [];
let ticketQueueLifecycle: "active" | "closed" | "all" = "active";
let ticketQueuePreset = "unassigned";
let navigationSequence = 0;
let lastSnapshotLoadedAt = 0;
let notificationRefreshStarted = false;
let notificationRefreshInFlight = false;
let notificationRefreshFailures = 0;
let notificationRefreshRetryAt = 0;
let notificationRefreshSuspended = false;
let notificationAuthenticationNoticeShown = false;
let portalRoleSessionRefreshedAt = 0;
let portalRoleSessionRefreshInFlight: Promise<void> | undefined;
let announcementRefreshStarted = false;
let announcementRefreshInFlight = false;
let activeAnnouncements: PortalAnnouncementSummary[] = [];
let scheduledGameAnnouncements: ScheduledGameAnnouncementSummary[] = [];
let eventWheelConfiguration: EventWheelConfigurationSummary | undefined;
let eventWheelLoaded = false;
let specimenEggInventory: SpecimenEggInventory | undefined;
let activeSpecimenEggTier: SpecimenEggTier = "recovered";
let blackjackState: BlackjackState | undefined;
let blackjackConfiguration: BlackjackConfigurationSummary | undefined;
let pendingBlackjackDeal: { fingerprint: string; requestId: string } | undefined;
let pendingBlackjackAction: { fingerprint: string; requestId: string } | undefined;
let rouletteState: RouletteState | undefined;
let slotsLoaded = false;
let slotsConfiguration: RouletteConfigurationSummary | undefined;
let rouletteConfiguration: RouletteConfigurationSummary | undefined;
let selectedRouletteBets: Array<{ type: RouletteBetType; value?: string; wager: number }> = [];
let rouletteBetActions: Array<{ key: string; wager: number }> = [];
let selectedRouletteChip = 0;
let pendingRouletteSpin: { fingerprint: string; requestId: string } | undefined;
let rouletteSpinInFlight = false;
let raptorRaceState: RaptorRaceState | undefined;
let raptorRaceConfiguration: RaptorRaceConfigurationSummary | undefined;
let selectedRaptorEntrantId: string | undefined;
let pendingRaptorRaceBet: { fingerprint: string; requestId: string } | undefined;
let raptorRaceLoading = false;
let raptorRaceWriteVersion = 0;
let raptorRaceBetting = false;
let renderedRaptorRaceKey = "";
let raptorRaceRefreshTimer: number | undefined;
let raptorRaceAnimationFrame: number | undefined;
document.addEventListener("fullscreenchange", () => {
  document.querySelectorAll<HTMLButtonElement>("[data-portal-action='raptor-expand']").forEach(button => { button.textContent = document.fullscreenElement ? "Exit fullscreen ↙" : "Expand race ↗"; });
});
let pendingEventWheelSpin: { fingerprint: string; requestId: string } | undefined;
let eventWheelSpinInFlight = false;
let pendingEventCreditGrant: { fingerprint: string; requestId: string } | undefined;
let pendingStaffPointGrant: { fingerprint: string; requestId: string } | undefined;
let pendingModerationStrike: { fingerprint: string; requestId: string } | undefined;
let pendingTicketCompensation: { fingerprint: string; requestId: string } | undefined;
let pendingTicketGarageRestore: { fingerprint: string; requestId: string; completed: boolean } | undefined;
let pendingStorePurchase: { fingerprint: string; requestId: string } | undefined;
let pendingStoreDinoSelection: { catalogItemId: string; itemName: string; species: string; price: number } | undefined;
let pendingGarageMigration: { fingerprint: string; requestId: string } | undefined;
const pendingGarageRestores = new globalThis.Map<string, string>();
let currentPlayerTicketId: string | undefined;
let playerModerationStrikes: PlayerModerationStrikeSummary[] | undefined;
let playerTicketRefreshInFlight = false;
let currentStaffTicketId: string | undefined;
let staffTicketRefreshInFlight = false;
let modGameplayEvents: GameplayEventSummary[] = [];
let modGameplayNextCursor: string | null = null;
let modLogView: "gameplay" | "commands" | "audit" = "gameplay";
let modLogRefreshInFlight = false;
let modLogRefreshTimer: number | undefined;
let serverHealthRefreshInFlight = false;
let serverHealthRefreshTimer: number | undefined;
let releaseCentreRefreshInFlight = false;
let growthCentreRefreshInFlight = false;
let growthCentreDays: 7 | 30 | 90 = 30;
let customAiMapRefreshInFlight = false;
const disclosureOpenStates = new globalThis.Map<string, boolean>();
let ticketMetricsRefreshInFlight = false;
let ticketQueueRefreshCount = 0;
let ticketQueueLoadSequence = 0;
let ticketQueueEventRefreshTimer: number | undefined;
let caseRuleCatalogCache: RuleDefinitionSummary[] = [];
let caseRuleCatalogLoadedAt = 0;
let playerTicketCapabilities: SupportTicketCapabilities = {
  evidenceUploads: { enabled: false, maxBytes: 0, maxFilesPerSubmission: 5, extensions: [] }
};

interface PortalFocusLocator {
  selector: string;
  index: number;
}

interface PortalRefreshViewState {
  focus?: PortalFocusLocator;
  detailStates: boolean[];
  liveActionMode?: string;
  scrollTop: number;
  skinEditor?: SkinEditorViewState;
}

interface SkinEditorViewState {
  mode?: "live" | "offline";
  species?: string;
  growth?: string;
  motion?: string;
  lighting?: string;
  lightIntensity?: string;
  female?: string;
  pattern?: string;
  theme?: string;
  name?: string;
  animation?: SkinAnimation;
  colors: Record<string, string>;
}

const skinWorkshopDrafts: Partial<Record<"live" | "offline", SkinEditorViewState>> = {};

interface PortalReauthenticationDraft {
  path: string;
  expiresAt: number;
  controls: Array<{
    key: string;
    occurrence: number;
    value: string;
    checked?: boolean;
  }>;
}

void boot();

async function boot(): Promise<void> {
  if (isLocalLandingPreview()) {
    renderPublicLandingPage();
    return;
  }
  let response: PortalSnapshotResponse;
  try {
    response = withHeatmapDemo(await apiClient.loadSnapshot(userId, roleIds));
    response = { ...response, snapshot: withCachedSkinCapture(response.snapshot) };
  } catch (error) {
    if (isPortalAuthenticationError(error)) {
      renderPublicLandingPage();
    } else {
      renderPortalUnavailable();
    }
    return;
  }
  userId = response.snapshot.player.userId;
  currentSnapshot = response.snapshot;
  currentAccess = response.access;
  lastSnapshotLoadedAt = Date.now();
  portalRoleSessionRefreshedAt = Date.now();
  if (app) {
    const page = portalPageFromPath(window.location.pathname);
    renderPortalWorkspace(page);
    wirePortalActions();
    const steamResult = searchParams.get("steam");
    if (steamResult === "linked") setStatus("Steam account linked successfully.");
    if (steamResult === "logged_in") setStatus("Signed in with Steam.");
    if (steamResult === "reauthenticated") setStatus("Secure sign-in refreshed. Your unsaved entries have been restored.");
    if (steamResult === "failed") setStatus("Steam sign-in could not be verified. Please try again or use the in-game code from /verify.");
    if (steamResult === "conflict") setStatus("That Steam account is already linked to another Discord profile. Contact staff if this is unexpected.");
    if (steamResult === "relink") setStatus("A different Steam account is already linked. Open a support ticket for a reviewed relink.");
    const discordResult = searchParams.get("discord");
    if (discordResult === "linked") setStatus("Discord connected. Your server roles and bot access are now available.");
    if (discordResult === "logged_in") setStatus("Signed in with Discord. Connect Steam next to unlock your game data and player services.");
    if (discordResult === "linked_role_pending") setStatus("Discord connected, but the Verified role could not be applied yet. You can continue using the portal; staff can retry the role sync without relinking your accounts.");
    if (discordResult === "conflict") setStatus("That Discord account belongs to another player profile. Open a support ticket so staff can review the profiles safely.");
    if (discordResult === "relink") setStatus("A different Discord account is already connected. Open a support ticket for a reviewed relink.");
    if (["failed", "state_failed", "session_failed", "unavailable"].includes(discordResult ?? "")) setStatus("Discord could not be connected. Please sign in again and retry.");
    initializePortalPage(page);
    schedulePortalReauthenticationDraftRestore();
    startNotificationRefresh();
    void refreshPortalNotifications();
    startAnnouncementRefresh();
    giveawayController.start();
    void refreshPortalAnnouncements();
    openPortalEventStream();
  }
}

function renderPortalWorkspace(page: PortalPage): void {
  if (!app) return;
  disposeLiveDinoModelPreview();
  disposeMarketplaceSkinPreviews();
  disposeSkinModelPreview();
  disposeLiveMap();
  disposeEcosystemZoneMap();
  app.innerHTML = renderPortal(currentSnapshot, currentAccess, page);
  hydratePortalIcons();
  updateAnnouncementSurfaces();
  startCooldownTimers();
  preserveDevelopmentQuery();
  alignActiveNavigation();
  initializeDisclosureStates(app);
  if (page === "garage") collapseGarageSpeciesGroups(app);
}

function collapseGarageSpeciesGroups(root: ParentNode): void {
  root.querySelectorAll<HTMLDetailsElement>(".garage-species-group").forEach((group) => {
    group.open = false;
    const key = group.dataset.disclosureKey;
    if (key) disclosureOpenStates.set(key, false);
  });
}

function alignActiveNavigation(): void {
  if (!window.matchMedia("(max-width: 780px)").matches) return;
  document.querySelector<HTMLElement>(".side-nav .active")?.scrollIntoView({
    behavior: "auto",
    block: "nearest",
    inline: "center"
  });
}

function selectStaffWorkspace(button: HTMLButtonElement, updateHistory = true): void {
  const workspace = button.closest<HTMLElement>(".staff-workspace");
  const targetId = button.dataset.staffWorkspaceTarget;
  if (!workspace || !targetId) return;

  const targetIds = Array.from(workspace.querySelectorAll<HTMLButtonElement>("[data-staff-workspace-target]"))
    .map((candidate) => candidate.dataset.staffWorkspaceTarget)
    .filter((value): value is string => Boolean(value));

  workspace.querySelectorAll<HTMLButtonElement>("[data-staff-workspace-target]").forEach((candidate) => {
    candidate.setAttribute("aria-pressed", String(candidate === button));
  });
  targetIds.forEach((id) => {
    workspace.querySelectorAll<HTMLElement>(`#${id}, [data-staff-workspace-panel="${id}"]`).forEach((panel) => {
      panel.classList.add("staff-workspace-panel");
      panel.setAttribute("data-staff-workspace-panel", id);
      panel.hidden = id !== targetId;
      if (id === targetId && panel instanceof HTMLDetailsElement) panel.open = true;
    });
  });

  if (updateHistory) {
    const url = new URL(window.location.href);
    url.hash = targetId;
    window.history.replaceState(window.history.state, "", url);
  }

  if (targetId === "staff-health" || targetId === "technical-health") {
    void Promise.all([handleServerHealthLoad(), handleCustomAiMapLoad()]);
    startServerHealthRefresh();
  } else if (targetId === "staff-release" || targetId === "technical-release") {
    stopServerHealthRefresh();
    void handleReleaseCentreLoad();
  } else if (targetId === "owner-growth" || targetId === "community-growth") {
    stopServerHealthRefresh();
    void handleGrowthCentreLoad();
  } else {
    stopServerHealthRefresh();
  }
}

function initializeStaffWorkspace(): void {
  const workspace = document.querySelector<HTMLElement>(".staff-workspace");
  if (!workspace) return;
  const requestedTarget = decodeURIComponent(window.location.hash.slice(1));
  const requestedButton = requestedTarget
    ? workspace.querySelector<HTMLButtonElement>(`[data-staff-workspace-target="${requestedTarget}"]`)
    : null;
  const defaultButton = workspace.querySelector<HTMLButtonElement>("[data-staff-workspace-target][aria-pressed='true']")
    ?? workspace.querySelector<HTMLButtonElement>("[data-staff-workspace-target]");
  if (requestedButton ?? defaultButton) selectStaffWorkspace(requestedButton ?? defaultButton!, false);
}

function prepareStaffWorkspacePanels(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>(".staff-workspace").forEach((workspace) => {
    const selectedButton = workspace.querySelector<HTMLButtonElement>("[data-staff-workspace-target][aria-pressed='true']")
      ?? workspace.querySelector<HTMLButtonElement>("[data-staff-workspace-target]");
    const selectedId = selectedButton?.dataset.staffWorkspaceTarget;
    workspace.querySelectorAll<HTMLButtonElement>("[data-staff-workspace-target]").forEach((button) => {
      const targetId = button.dataset.staffWorkspaceTarget;
      if (!targetId) return;
      workspace.querySelectorAll<HTMLElement>(`#${targetId}, [data-staff-workspace-panel="${targetId}"]`).forEach((panel) => {
        panel.classList.add("staff-workspace-panel");
        panel.setAttribute("data-staff-workspace-panel", targetId);
        panel.hidden = targetId !== selectedId;
      });
    });
  });
}

const raffleManager = createRaffleManager(apiClient, () => ({ userId, serverId: currentSnapshot.server.id }));

let stopTechnicalOperations: (() => void) | undefined;

function initializePortalPage(page: PortalPage): void {
  stopTechnicalOperations?.();
  stopTechnicalOperations = page === "technical" ? initializeTechnicalOperations({ userId, roleIds, canManage: currentAccess.permissions.includes("technical.operations.manage"), investigate: (targetUserId) => {
    document.querySelector<HTMLButtonElement>('[data-staff-workspace-target="technical-players"]')?.click();
    if (targetUserId) {
      const input = document.querySelector<HTMLInputElement>("[data-profile-lookup]");
      if (input) input.value = targetUserId;
      void handleProfileLookup();
    }
  } }) : undefined;
  if (auctionRefreshTimer) clearInterval(auctionRefreshTimer);
  auctionRefreshTimer = undefined;
  startMapRefresh();
  startLiveDataRefresh();
  prepareStaffWorkspacePanels(document);
  if (page === "staff" || page === "senior" || page === "community" || page === "technical" || page === "owner" || page === "developer") initializeStaffWorkspace();
  if (page !== "staff" && page !== "senior" && page !== "community" && page !== "technical" && page !== "owner" && page !== "developer") stopServerHealthRefresh();
  if (page === "overview") void initializeLiveDinoModelPreview(currentSnapshot.skinCapture?.colors);
  if (page === "map") {
    restoreMapLayers();
    initializeLiveMap();
    openLiveMapStream();
    void refreshMapSnapshot();
  }
  if (page === "support") {
    const category = searchParams.get("category");
    const categorySelect = document.querySelector<HTMLSelectElement>("[data-player-ticket-type]");
    if (categorySelect && category && Array.from(categorySelect.options).some((option) => option.value === category)) {
      categorySelect.value = category;
    }
    updateSupportTicketForm();
    const ticketId = searchParams.get("ticket");
    void Promise.all([handlePlayerTicketCapabilities(), loadPlayerModerationStrikes(), ticketId ? handlePlayerTicketDetail(ticketId) : handlePlayerTicketList()]);
  }
  if (page === "cases") void initializeCaseDesk();
  if (page === "skins") {
    void initializeSkinModelPreview().then(syncSkinAnimationLab);
    if (currentAccess.permissions.includes("skins.approve")) void handleSkinReviewLoad();
  }
  if (page === "marketplace") {
    void refreshAuctions(true);
    auctionRefreshTimer = setInterval(() => { if (!document.hidden && !portalActionInFlight && !portalInteractionActive()) void refreshAuctions(); }, 15000);
    marketplaceMineOnly = false;
    applyMarketplaceFilters();
    updateMarketplaceCountdowns();
    initializeMarketplaceSkinPreviews();
  }
  if (page === "store") initializeMarketplaceSkinPreviews();
  if (page === "gambling") {
    eventWheelLoaded = false;
    blackjackState = undefined;
    rouletteState = undefined;
    raptorRaceState = undefined;
    selectedRouletteBets = [];
    rouletteBetActions = [];
    slotsLoaded = false;
    void handleSlotsLoad();
  }
  if (page === "events") { eventWheelLoaded = false; void handleEventWheelLoad(); }
  if (page === "leaderboards") void initializeLeaderboard();
  if (page === "staff" && currentAccess.permissions.includes("game.staff_duty")) void handleStaffDutyLoad();
  if (page === "staff" && (currentAccess.permissions.includes("moderation.create") || currentAccess.permissions.includes("events.manage"))) void refreshPortalAnnouncements();
  if (page === "staff" && currentAccess.permissions.includes("audit.view")) {
    setModLogView(modLogView);
    void handleModLogRefresh(false);
    startModLogRefresh();
  } else {
    stopModLogRefresh();
  }
  if (page === "senior" && currentAccess.featureFlags.showSeniorTools) {
    void handleStaffPerformance();
    void handleEventWheelConfigLoad();
    void handleEventCampaignLoad();
    void handleBlackjackConfigLoad();
    void handleRouletteConfigLoad();
    void handleSlotsConfigLoad();
    void handleRaptorRaceConfigLoad();
    void handleCorpseCleanupLoad();
  }
  if (page === "community" && currentAccess.featureFlags.showCommunityTools) {
    void handleStaffPerformance();
    void handleEventWheelConfigLoad();
    void handleEventCampaignLoad();
    void handleRaptorRaceConfigLoad();
  }
  if (page === "technical" && currentAccess.featureFlags.showTechnicalTools) {
    void handleEventWheelConfigLoad();
    void handleEventCampaignLoad();
    if (currentAccess.permissions.includes("gambling.manage")) {
      void handleBlackjackConfigLoad();
      void handleRouletteConfigLoad();
      void handleSlotsConfigLoad();
      void handleRaptorRaceConfigLoad();
    }
  }
}

async function navigatePortal(url: URL, historyMode: "push" | "pop" = "push"): Promise<void> {
  if (!currentSnapshot || !currentAccess) return;
  const destination = `${url.pathname}${url.search}`;
  const current = `${window.location.pathname}${window.location.search}`;
  if (historyMode === "push" && destination === current) return;
  if (historyMode === "push") window.history.pushState({}, "", destination);
  searchParams = new URLSearchParams(url.search);
  const page = portalPageFromPath(url.pathname);
  const sequence = ++navigationSequence;
  renderPortalWorkspace(page);
  const main = document.querySelector<HTMLElement>(".main-area");
  if (main) main.dataset.sweep = navigationSequence % 2 === 0 ? "a" : "b";
  initializePortalPage(page);
  window.scrollTo({ top: 0, behavior: "auto" });
  focusPortalMain();

  if (!pageUsesLiveSnapshot(page) || Date.now() - lastSnapshotLoadedAt < 10_000) return;
  document.documentElement.classList.add("portal-navigating");
  try {
    const response = withHeatmapDemo(await loadPortalSnapshot(userId, roleIds));
    if (!response || sequence !== navigationSequence || portalPageFromPath(window.location.pathname) !== page) return;
    const stabilized = stabilizeLiveSnapshot(
      currentSnapshot,
      withCachedSkinCapture(response.snapshot),
      liveSnapshotStabilizationState
    );
    currentSnapshot = stabilized.snapshot;
    liveSnapshotStabilizationState = stabilized.state;
    currentAccess = response.access;
    lastSnapshotLoadedAt = Date.now();
    portalRoleSessionRefreshedAt = Date.now();
    renderPortalWorkspace(page);
    initializePortalPage(page);
  } finally {
    if (sequence === navigationSequence) document.documentElement.classList.remove("portal-navigating");
  }
}

function pageUsesLiveSnapshot(page: PortalPage): boolean {
  return ["overview", "map", "skins", "garage", "store", "marketplace"].includes(page);
}

window.addEventListener("popstate", () => {
  searchParams = new URLSearchParams(window.location.search);
  void navigatePortal(new URL(window.location.href), "pop");
});

function startLiveDataRefresh(): void {
  if (liveDataRefreshStarted) return;
  liveDataRefreshStarted = true;
  window.setInterval(() => {
    const page = portalPageFromPath(window.location.pathname);
    if (shouldRefreshPortalLivePage(page, currentSnapshot) && !document.hidden && !portalActionInFlight && !liveDataRefreshInFlight && !portalInteractionActive()) {
      void refreshLiveDataSnapshot(page);
    }
  }, 60_000);
}

function initializeLeaderboard(): void {
  const root = document.querySelector<HTMLElement>(".records-workspace");
  if (root) initializeLeaderboardPage(root, currentSnapshot.server.id, (serverId, period) => apiClient.getLeaderboard(serverId, period), hydratePortalIcons);
}

function portalInteractionActive(): boolean {
  if (document.querySelector("dialog[open]")) return true;
  return document.activeElement instanceof HTMLInputElement
    || document.activeElement instanceof HTMLSelectElement
    || document.activeElement instanceof HTMLTextAreaElement;
}

async function refreshLiveDataSnapshot(page: "overview" | "garage" | "skins"): Promise<void> {
  liveDataRefreshInFlight = true;
  try {
    const response = await loadPortalSnapshot(userId, roleIds);
    if (!response || !app || portalActionInFlight || portalInteractionActive() || portalPageFromPath(window.location.pathname) !== page) return;
    const viewState = capturePortalRefreshViewState();
    const previousSnapshot = currentSnapshot;
    const previousAccess = currentAccess;
    const stabilized = stabilizeLiveSnapshot(
      currentSnapshot,
      withCachedSkinCapture(response.snapshot),
      liveSnapshotStabilizationState
    );
    currentSnapshot = stabilized.snapshot;
    liveSnapshotStabilizationState = stabilized.state;
    currentAccess = response.access;
    lastSnapshotLoadedAt = Date.now();
    portalRoleSessionRefreshedAt = Date.now();
    refreshPortalShell(currentSnapshot);
    const overviewIncremental = page === "overview"
      && isOverviewIncrementalRefresh(previousSnapshot, currentSnapshot, previousAccess, response.access);
    if (overviewIncremental) {
      refreshOverviewLiveTelemetry(currentSnapshot, response.access);
    } else if (
      !(page === "garage" && isGarageContentUnchanged(previousSnapshot, currentSnapshot))
      && !(page === "skins" && isSkinsContentUnchanged(previousSnapshot, currentSnapshot, previousAccess, response.access))
    ) {
      refreshPortalPageContent(page, currentSnapshot, response.access);
      if (page === "skins") void initializeSkinModelPreview().then(syncSkinAnimationLab);
    }
    restorePortalRefreshViewState(viewState);
  } finally {
    liveDataRefreshInFlight = false;
  }
}

function refreshOverviewLiveTelemetry(snapshot: PortalSnapshotResponse["snapshot"], access: AccessDecision): void {
  const content = document.querySelector<HTMLElement>("#portalPageContent");
  if (!content) {
    refreshPortalPageContent("overview", snapshot, access);
    return;
  }

  const template = document.createElement("template");
  template.innerHTML = renderPortalPageContent("overview", snapshot, access);
  const nextContent = template.content;
  const currentCard = content.querySelector<HTMLElement>('[data-overview-refresh-region="live-dino"]');
  const nextCard = nextContent.querySelector<HTMLElement>('[data-overview-refresh-region="live-dino"]');
  if (!currentCard || !nextCard) {
    refreshPortalPageContent("overview", snapshot, access);
    return;
  }

  patchLiveDinoCard(currentCard, nextCard);

  replaceOverviewRegionIfChanged(content, nextContent, "controls");
  replaceOverviewRegionIfChanged(content, nextContent, "specimen");
  replaceOverviewRegionIfChanged(content, nextContent, "prime");
  replaceOverviewRegionIfChanged(content, nextContent, "actions");
  replaceOverviewRegionIfChanged(content, nextContent, "wallet");
  hydratePortalIcons();
  startCooldownTimers();
  void initializeLiveDinoModelPreview(snapshot.skinCapture?.colors);
}

function syncText(currentRoot: ParentNode, nextRoot: ParentNode, selector: string): void {
  const current = currentRoot.querySelector<HTMLElement>(selector);
  const next = nextRoot.querySelector<HTMLElement>(selector);
  if (current && next) current.textContent = next.textContent;
}

function patchLiveDinoCard(currentCard: HTMLElement, nextCard: HTMLElement): void {
  currentCard.dataset.liveDinoSpecies = nextCard.dataset.liveDinoSpecies;
  currentCard.dataset.liveDinoStatus = nextCard.dataset.liveDinoStatus;

  syncTextAndClass(currentCard, nextCard, ".player-presence");
  syncText(currentCard, nextCard, ".console-server");
  syncText(currentCard, nextCard, ".console-uid");
  syncText(currentCard, nextCard, "[data-live-dino-updated]");
  syncMarkup(currentCard, nextCard, ".terminal-feed-label");
  syncMarkup(currentCard, nextCard, ".dino-name");
  syncText(currentCard, nextCard, "[data-live-dino-growth]");

  const currentEnvironment = currentCard.querySelector<HTMLImageElement>(".dino-environment");
  const nextEnvironment = nextCard.querySelector<HTMLImageElement>(".dino-environment");
  if (currentEnvironment && nextEnvironment) {
    if (currentEnvironment.getAttribute("src") !== nextEnvironment.getAttribute("src")) {
      currentEnvironment.src = nextEnvironment.src;
      currentEnvironment.hidden = false;
    }
    currentEnvironment.alt = nextEnvironment.alt;
  }

  const currentModel = currentCard.querySelector<HTMLElement>("[data-live-dino-model-preview]");
  const nextModel = nextCard.querySelector<HTMLElement>("[data-live-dino-model-preview]");
  if (currentModel && nextModel) {
    currentModel.dataset.liveDinoUid = nextModel.dataset.liveDinoUid;
    currentModel.dataset.liveDinoSpecies = nextModel.dataset.liveDinoSpecies;
  }

  for (const metric of ["health", "stamina", "hunger", "thirst"]) {
    const selector = `[data-live-dino-metric="${metric}"]`;
    const currentMetric = currentCard.querySelector<HTMLElement>(selector);
    const nextMetric = nextCard.querySelector<HTMLElement>(selector);
    if (!currentMetric || !nextMetric) continue;
    const currentValue = currentMetric.querySelector<HTMLElement>("strong");
    const nextValue = nextMetric.querySelector<HTMLElement>("strong");
    if (currentValue && nextValue && currentValue.textContent !== nextValue.textContent) {
      currentValue.textContent = nextValue.textContent;
    }
    const currentFill = currentMetric.querySelector<HTMLElement>(".metric-line > span");
    const nextFill = nextMetric.querySelector<HTMLElement>(".metric-line > span");
    if (currentFill && nextFill && currentFill.style.width !== nextFill.style.width) {
      currentFill.style.width = nextFill.style.width;
    }
  }

  syncOptionalElement(currentCard, nextCard, ".status-note", ".group-status");
  syncOptionalElement(currentCard, nextCard, ".group-status");
}

function syncTextAndClass(currentRoot: ParentNode, nextRoot: ParentNode, selector: string): void {
  const current = currentRoot.querySelector<HTMLElement>(selector);
  const next = nextRoot.querySelector<HTMLElement>(selector);
  if (!current || !next) return;
  if (current.textContent !== next.textContent) current.textContent = next.textContent;
  if (current.className !== next.className) current.className = next.className;
}

function syncMarkup(currentRoot: ParentNode, nextRoot: ParentNode, selector: string): void {
  const current = currentRoot.querySelector<HTMLElement>(selector);
  const next = nextRoot.querySelector<HTMLElement>(selector);
  if (current && next && current.innerHTML !== next.innerHTML) current.innerHTML = next.innerHTML;
}

function syncOptionalElement(
  currentRoot: HTMLElement,
  nextRoot: ParentNode,
  selector: string,
  beforeSelector?: string
): void {
  const current = currentRoot.querySelector<HTMLElement>(selector);
  const next = nextRoot.querySelector<HTMLElement>(selector);
  if (current && next) {
    if (current.outerHTML !== next.outerHTML) current.replaceWith(next.cloneNode(true));
    return;
  }
  if (current && !next) {
    current.remove();
    return;
  }
  if (!current && next) {
    const clone = next.cloneNode(true);
    const before = beforeSelector ? currentRoot.querySelector<HTMLElement>(beforeSelector) : null;
    if (before) before.before(clone);
    else currentRoot.append(clone);
  }
}

function replaceOverviewRegionIfChanged(
  currentRoot: ParentNode,
  nextRoot: ParentNode,
  region: string
): void {
  const selector = `[data-overview-refresh-region="${region}"]`;
  const current = currentRoot.querySelector<HTMLElement>(selector);
  const next = nextRoot.querySelector<HTMLElement>(selector);
  if (current && next && current.outerHTML !== next.outerHTML) current.replaceWith(next);
}

function startMapRefresh(): void {
  if (mapRefreshStarted) return;
  mapRefreshStarted = true;
  window.setInterval(() => expireLiveWeatherEvidence(), 1_000);
  window.setInterval(() => {
    if (!document.hidden && portalPageFromPath(window.location.pathname) === "map") void refreshMapSnapshot();
  }, 10_000);
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden && portalPageFromPath(window.location.pathname) === "map") void refreshMapSnapshot();
  });
  window.addEventListener("online", () => {
    if (portalPageFromPath(window.location.pathname) === "map") void refreshMapSnapshot();
  });
}

function openLiveMapStream(): void {
  openPortalEventStream();
}

function closeLiveMapStream(): void {
  if (mapEventRefreshTimer !== undefined) window.clearTimeout(mapEventRefreshTimer);
  mapEventRefreshTimer = undefined;
}

function openPortalEventStream(): void {
  if (portalEventSource || typeof EventSource === "undefined") return;
  portalEventSource = new EventSource("/api/portal/events");
  portalEventSource.addEventListener("update", (event) => handlePortalStreamUpdate(event));
}

function handlePortalStreamUpdate(event: Event): void {
  if (!(event instanceof MessageEvent) || document.hidden) return;
  let topics: string[] = [];
  try {
    const payload = JSON.parse(String(event.data)) as { topics?: unknown };
    topics = Array.isArray(payload.topics) ? payload.topics.filter((topic): topic is string => typeof topic === "string") : [];
  } catch {
    return;
  }
  const page = portalPageFromPath(window.location.pathname);
  if (topics.includes("map") && page === "map") {
    if (mapEventRefreshTimer !== undefined) window.clearTimeout(mapEventRefreshTimer);
    mapEventRefreshTimer = window.setTimeout(() => {
      mapEventRefreshTimer = undefined;
      void refreshMapSnapshot();
    }, 150);
  }
  if (topics.includes("snapshot") && shouldRefreshPortalLivePage(page, currentSnapshot)) {
    if (snapshotEventRefreshTimer !== undefined) window.clearTimeout(snapshotEventRefreshTimer);
    snapshotEventRefreshTimer = window.setTimeout(() => {
      snapshotEventRefreshTimer = undefined;
      if (!portalActionInFlight && !portalInteractionActive()) void refreshLiveDataSnapshot(page);
    }, 250);
  }
  if (topics.includes("notifications")) void refreshPortalNotifications();
  if (topics.includes("announcements")) void refreshPortalAnnouncements();
  if (topics.includes("tickets")) {
    void refreshCaseNotificationBadge();
    const plan = ticketEventRefreshPlan({
      page,
      currentStaffTicketId,
      currentPlayerTicketId,
      actionInFlight: portalActionInFlight,
      interactionActive: portalInteractionActive()
    });
    if (plan.refreshPlayerConversation && currentPlayerTicketId) void refreshPlayerTicketConversation(currentPlayerTicketId);
    if (plan.refreshStaffMetrics) void handleTicketMetrics(true);
    if (plan.refreshStaffConversation && currentStaffTicketId) void refreshStaffTicketConversation(currentStaffTicketId);
    if (plan.scheduleStaffQueue) scheduleTicketQueueEventRefresh();
  }
  if (topics.includes("release") && document.querySelector<HTMLElement>('[data-staff-workspace-panel="staff-release"]:not([hidden])')) {
    void handleReleaseCentreLoad();
  }
}

async function refreshMapSnapshot(): Promise<void> {
  if (portalPageFromPath(window.location.pathname) !== "map" || portalActionInFlight) return;
  if (mapRefreshInFlight) {
    mapRefreshQueued = true;
    return;
  }
  mapRefreshInFlight = true;
  try {
    const mapSnapshot = await apiClient.loadLiveMap();
    if (!app || portalActionInFlight || portalPageFromPath(window.location.pathname) !== "map") return;
    const effective = searchParams.get("heatmapDemo") === "1"
      ? { ...mapSnapshot, heatmap: withHeatmapDemo({ access: currentAccess, snapshot: currentSnapshot }).snapshot.heatmap }
      : mapSnapshot;
    currentSnapshot = {
      ...currentSnapshot,
      playerPosition: effective.playerPosition,
      group: effective.group,
      heatmap: effective.heatmap,
      weather: effective.weather
    };
    const payload = liveMapPayload(effective);
    const updated = updateLiveMap(payload);
    if (!updated) {
      refreshPortalPageContent("map", currentSnapshot, currentAccess);
      restoreMapLayers();
    } else {
      const roster = document.querySelector<HTMLElement>("[data-live-map-roster]");
      if (roster) roster.innerHTML = renderLiveMapRoster(effective);
      const payloadElement = document.querySelector<HTMLScriptElement>("#liveMapPayload");
      if (payloadElement) payloadElement.textContent = JSON.stringify(payload);
    }
    updateMapRefreshStatus();
  } catch {
    // EventSource reconnects automatically; the 15-second weather fallback will retry.
  } finally {
    mapRefreshInFlight = false;
    if (mapRefreshQueued) {
      mapRefreshQueued = false;
      void refreshMapSnapshot();
    }
  }
}

function refreshPortalPageContent(page: PortalPage, snapshot: PortalSnapshotResponse["snapshot"], access: AccessDecision): void {
  const content = document.querySelector<HTMLElement>("#portalPageContent");
  if (!content) {
    renderPortalWorkspace(page);
    return;
  }
  if (page === "map") disposeLiveMap();
  const currentLiveCard = page === "overview"
    ? content.querySelector<HTMLElement>('[data-overview-refresh-region="live-dino"]')
    : null;
  const canReuseLiveCard = Boolean(currentLiveCard
    && canReuseLiveDinoCard(
      currentLiveCard.dataset.liveDinoSpecies,
      currentLiveCard.dataset.liveDinoStatus,
      snapshot.liveDino.species,
      snapshot.liveDino.status
    ));
  const currentLiveModel = page === "overview" && !canReuseLiveCard
    ? content.querySelector<HTMLElement>("[data-live-dino-model-preview]")
    : null;
  const canReuseLiveModel = Boolean(currentLiveModel
    && snapshot.liveDino.uid !== "none"
    && canReuseLiveDinoModel(currentLiveModel.dataset.liveDinoSpecies, snapshot.liveDino.species));
  if (canReuseLiveCard) currentLiveCard?.remove();
  else if (canReuseLiveModel) currentLiveModel?.remove();
  else if (page === "overview") disposeLiveDinoModelPreview();
  content.innerHTML = renderPortalPageContent(page, snapshot, access);
  if (canReuseLiveCard && currentLiveCard) {
    const nextLiveCard = content.querySelector<HTMLElement>('[data-overview-refresh-region="live-dino"]');
    if (nextLiveCard) {
      patchLiveDinoCard(currentLiveCard, nextLiveCard);
      nextLiveCard.replaceWith(currentLiveCard);
    }
  } else if (canReuseLiveModel && currentLiveModel) {
    currentLiveModel.dataset.liveDinoUid = snapshot.liveDino.uid;
    currentLiveModel.dataset.liveDinoSpecies = snapshot.liveDino.species;
    content.querySelector<HTMLElement>("[data-live-dino-model-preview]")?.replaceWith(currentLiveModel);
  }
  initializeDisclosureStates(content);
  hydratePortalIcons();
  startCooldownTimers();
  preserveDevelopmentQuery();
  if (page === "map") {
    restoreMapLayers();
    initializeLiveMap();
  }
  if (page === "overview") void initializeLiveDinoModelPreview(snapshot.skinCapture?.colors);
}

function refreshPortalShell(snapshot: PortalSnapshotResponse["snapshot"]): void {
  const telemetry = telemetryStatus(snapshot.server.lastTelemetryAt, snapshot.server.telemetrySource, snapshot.liveDino.status);
  document.querySelectorAll<HTMLElement>("[data-online-player-count]").forEach((element) => {
    element.textContent = formatPortalInteger(snapshot.server.onlinePlayers);
  });
  document.querySelectorAll<HTMLElement>("[data-wallet-points]").forEach((element) => {
    element.textContent = formatPortalInteger(snapshot.wallet.points);
  });
  document.querySelectorAll<HTMLElement>("[data-wallet-premium]").forEach((element) => {
    element.textContent = formatPortalInteger(snapshot.wallet.premium);
  });
  document.querySelectorAll<HTMLElement>("[data-server-node-status]").forEach((element) => {
    element.classList.remove("server-online", "server-degraded", "server-offline", "server-unknown");
    element.classList.add(`server-${snapshot.server.status}`);
    element.dataset.serverNodeStatus = snapshot.server.status;
    element.setAttribute("aria-label", `${snapshot.server.name} is ${snapshot.server.status}; ${formatPortalInteger(snapshot.server.onlinePlayers)} players online`);
  });
  const serverStatus = document.querySelector<HTMLElement>("[data-server-status]");
  if (serverStatus) {
    const labels = {
      online: "Online",
      degraded: "Connection issues",
      offline: "Offline",
      unknown: "Checking status"
    } as const;
    serverStatus.classList.remove("online", "degraded", "offline", "unknown");
    serverStatus.classList.add(snapshot.server.status);
    serverStatus.dataset.state = snapshot.server.status;
    serverStatus.title = snapshot.server.statusCheckedAt ? `Last checked ${new Date(snapshot.server.statusCheckedAt).toLocaleTimeString("en-GB")}` : "";
    const serverStatusLabel = serverStatus.querySelector<HTMLElement>("[data-server-status-label]");
    if (serverStatusLabel) serverStatusLabel.textContent = labels[snapshot.server.status];
  }
  const state = document.querySelector<HTMLElement>("[data-telemetry-state]");
  state?.classList.remove("live", "stale", "unavailable");
  state?.classList.add(telemetry.state);
  const label = state?.querySelector<HTMLElement>("[data-telemetry-label]");
  if (label) label.textContent = telemetry.label;
}

function formatPortalInteger(value: number): string {
  return new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 }).format(value);
}

function withCachedSkinCapture(
  snapshot: PortalSnapshotResponse["snapshot"]
): PortalSnapshotResponse["snapshot"] {
  const key = `the-nest:skin-capture:${snapshot.player.userId}:${snapshot.server.id}`;
  try {
    if (snapshot.skinCapture && snapshot.skinCapture.stale !== true) {
      const capture = { ...snapshot.skinCapture };
      delete capture.targetToken;
      delete capture.stale;
      window.sessionStorage.setItem(key, JSON.stringify({
        uid: snapshot.liveDino.uid,
        species: snapshot.liveDino.species,
        capture
      }));
      return snapshot;
    }
    if (snapshot.liveDino.status.toLowerCase() !== "live") return snapshot;
    const cached = JSON.parse(window.sessionStorage.getItem(key) ?? "null") as {
      uid?: string;
      species?: string;
      capture?: NonNullable<PortalSnapshotResponse["snapshot"]["skinCapture"]>;
    } | null;
    if (!cached?.capture || cached.uid !== snapshot.liveDino.uid || cached.species !== snapshot.liveDino.species) {
      return snapshot;
    }
    return {
      ...snapshot,
      skinCapture: {
        ...cached.capture,
        targetToken: undefined,
        stale: true
      }
    };
  } catch {
    return snapshot;
  }
}

function needsRecentSteamAuthentication(error: unknown): boolean {
  return error instanceof PortalApiError
    && error.status === 401
    && /recent sign-in|sign in with steam again/i.test(error.message);
}

function persistPortalReauthenticationDraft(): void {
  const occurrenceByKey = new globalThis.Map<string, number>();
  const controls = Array.from(document.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
    "#portalPageContent input, #portalPageContent select, #portalPageContent textarea"
  )).flatMap((control) => {
    if (control instanceof HTMLInputElement && ["file", "password", "button", "submit", "hidden"].includes(control.type)) return [];
    const key = portalDraftControlKey(control);
    const occurrence = occurrenceByKey.get(key) ?? 0;
    occurrenceByKey.set(key, occurrence + 1);
    return [{
      key,
      occurrence,
      value: control.value,
      ...((control instanceof HTMLInputElement && ["checkbox", "radio"].includes(control.type))
        ? { checked: control.checked }
        : {})
    }];
  });
  if (!controls.length) return;
  try {
    window.sessionStorage.setItem("the-nest:portal-reauth-draft", JSON.stringify({
      path: window.location.pathname,
      expiresAt: Date.now() + 15 * 60_000,
      controls
    } satisfies PortalReauthenticationDraft));
  } catch {
    // Reauthentication still works in strict privacy modes; only draft recovery is unavailable.
  }
}

function schedulePortalReauthenticationDraftRestore(): void {
  for (const delay of [0, 500, 1_500, 3_000]) {
    window.setTimeout(() => restorePortalReauthenticationDraft(), delay);
  }
}

function restorePortalReauthenticationDraft(): void {
  let draft: PortalReauthenticationDraft | undefined;
  try {
    draft = JSON.parse(window.sessionStorage.getItem("the-nest:portal-reauth-draft") ?? "null") as PortalReauthenticationDraft | undefined;
  } catch {
    return;
  }
  if (!draft || draft.expiresAt < Date.now() || draft.path !== window.location.pathname) {
    try { window.sessionStorage.removeItem("the-nest:portal-reauth-draft"); } catch { /* ignore */ }
    return;
  }
  const controls = Array.from(document.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
    "#portalPageContent input, #portalPageContent select, #portalPageContent textarea"
  ));
  const byKey = new globalThis.Map<string, Array<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>>();
  for (const control of controls) {
    const key = portalDraftControlKey(control);
    byKey.set(key, [...(byKey.get(key) ?? []), control]);
  }
  const matched = draft.controls.filter((saved) => byKey.get(saved.key)?.[saved.occurrence]).length;
  if (matched < Math.max(1, Math.ceil(draft.controls.length * 0.6))) return;
  for (const saved of draft.controls) {
    const control = byKey.get(saved.key)?.[saved.occurrence];
    if (!control) continue;
    control.value = saved.value;
    if (control instanceof HTMLInputElement && saved.checked !== undefined) control.checked = saved.checked;
    control.dispatchEvent(new Event(control instanceof HTMLInputElement ? "input" : "change", { bubbles: true }));
  }
  try { window.sessionStorage.removeItem("the-nest:portal-reauth-draft"); } catch { /* ignore */ }
}

function portalDraftControlKey(control: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): string {
  const data = Array.from(control.attributes)
    .filter((attribute) => attribute.name.startsWith("data-"))
    .map((attribute) => `${attribute.name}=${attribute.value}`)
    .sort()
    .join("|");
  return `${control.tagName.toLowerCase()}:${control instanceof HTMLInputElement ? control.type : ""}:${control.id}:${control.getAttribute("name") ?? ""}:${data}`;
}

function capturePortalRefreshViewState(): PortalRefreshViewState {
  return {
    focus: capturePortalFocus(),
    detailStates: Array.from(document.querySelectorAll<HTMLDetailsElement>("details"), (detail) => detail.open),
    liveActionMode: document.querySelector<HTMLButtonElement>("[data-live-action-mode].selected")?.dataset.liveActionMode,
    scrollTop: window.scrollY,
    skinEditor: captureSkinEditorViewState()
  };
}

function restorePortalRefreshViewState(state: PortalRefreshViewState): void {
  document.querySelectorAll<HTMLDetailsElement>("details").forEach((detail, index) => {
    if (state.detailStates[index] !== undefined) {
      detail.open = state.detailStates[index];
      rememberDisclosureState(detail);
    }
  });
  if (state.liveActionMode) {
    const selector = document.querySelector<HTMLButtonElement>(`[data-live-action-mode="${cssEscape(state.liveActionMode)}"]`);
    if (selector) selectLiveActionMode(selector);
  }
  restoreSkinEditorViewState(state.skinEditor);
  window.scrollTo({ top: state.scrollTop, behavior: "auto" });
  restorePortalFocus(state.focus);
}

function captureSkinEditorViewState(): SkinEditorViewState | undefined {
  const creator = document.querySelector<HTMLElement>("[data-skin-creator]");
  if (!creator) return undefined;
  const colors: Record<string, string> = {};
  document.querySelectorAll<HTMLInputElement>("[data-skin-color]").forEach((input) => {
    if (input.dataset.skinColor) colors[input.dataset.skinColor] = input.value;
  });
  return {
    mode: creator.dataset.skinEditorMode === "offline" ? "offline" : "live",
    species: document.querySelector<HTMLSelectElement>("[data-skin-pattern]")?.dataset.skinSpecies,
    growth: document.querySelector<HTMLButtonElement>("button[data-skin-preview-growth][aria-pressed='true']")?.dataset.skinPreviewGrowth,
    motion: document.querySelector<HTMLButtonElement>("button[data-skin-preview-motion][aria-pressed='true']")?.dataset.skinPreviewMotion,
    lighting: document.querySelector<HTMLButtonElement>("button[data-skin-preview-lighting][aria-pressed='true']")?.dataset.skinPreviewLighting,
    lightIntensity: document.querySelector<HTMLInputElement>("[data-skin-preview-intensity]")?.value,
    female: document.querySelector<HTMLSelectElement>("[data-skin-female]")?.value,
    pattern: document.querySelector<HTMLSelectElement>("[data-skin-pattern]")?.value,
    theme: document.querySelector<HTMLSelectElement>("[data-skin-theme]")?.value,
    name: document.querySelector<HTMLInputElement>("[data-skin-name]")?.value,
    animation: readSkinAnimation(),
    colors
  };
}

function restoreSkinEditorViewState(state: SkinEditorViewState | undefined): void {
  if (!state || !document.querySelector("[data-skin-creator]")) return;
  const mode = state.mode === "offline" ? "offline" : "live";
  syncSkinWorkshopModeUi(mode);
  if (mode === "live" && !isLiveSkinEditorSpeciesCurrent(state.species, currentSnapshot.liveDino.species)) {
    // A live refresh has rendered the newly detected dinosaur. Never overwrite
    // its structure with a draft captured from the previous species: pattern
    // and theme indices have species-specific limits even when both selects
    // happen to contain the same numeric values.
    skinWorkshopDrafts.live = captureSkinEditorViewState();
    return;
  }
  const previewHost = document.querySelector<HTMLElement>("[data-skin-model-preview]");
  const speciesChanged = Boolean(state.species && previewHost?.dataset.skinPreviewSpecies !== state.species);
  if (state.species) setSkinWorkshopSpecies(state.species, false);
  const female = document.querySelector<HTMLSelectElement>("[data-skin-female]");
  const pattern = document.querySelector<HTMLSelectElement>("[data-skin-pattern]");
  const name = document.querySelector<HTMLInputElement>("[data-skin-name]");
  if (female && (state.female === "true" || state.female === "false")) female.value = state.female;
  if (pattern && state.pattern && Array.from(pattern.options).some((option) => option.value === state.pattern)) {
    pattern.value = state.pattern;
    syncSkinThemeOptions(pattern, Number(state.theme));
    updateSkinModelPreviewPattern(Number(state.pattern));
  }
  if (name && state.name !== undefined) name.value = state.name;
  for (const [field, value] of Object.entries(state.colors)) {
    const input = document.querySelector<HTMLInputElement>(`[data-skin-color="${cssEscape(field)}"]`);
    if (input && !input.disabled) setSkinColourInput(input, value);
  }
  if (state.growth) selectSkinPreviewGrowth(state.growth);
  if (state.lighting === "containment" || state.lighting === "daylight" || state.lighting === "night") {
    selectSkinPreviewLighting(state.lighting, Number(state.lightIntensity ?? "100"));
  }
  if (state.motion) selectSkinPreviewMotion(state.motion as SkinPreviewMotion, false);
  loadSkinAnimationIntoLab(state.animation ? encodeURIComponent(JSON.stringify(state.animation)) : undefined);
  skinWorkshopDrafts[mode] = captureSkinEditorViewState() ?? state;
  if (speciesChanged) void initializeSkinModelPreview().then(syncSkinAnimationLab);
}

function switchSkinWorkshopMode(mode: "live" | "offline"): void {
  const creator = document.querySelector<HTMLElement>("[data-skin-creator]");
  if (!creator || (mode === "live" && creator.dataset.skinLiveAvailable !== "true")) return;
  const current = captureSkinEditorViewState();
  if (current?.mode) skinWorkshopDrafts[current.mode] = current;
  const nextSpecies = mode === "live"
    ? currentSnapshot.liveDino.species
    : skinWorkshopDrafts.offline?.species ?? current?.species ?? currentSnapshot.liveDino.species;
  const next = skinWorkshopDrafts[mode] ?? (current ? { ...current, mode, species: nextSpecies } : undefined);
  syncSkinWorkshopModeUi(mode);
  if (next) restoreSkinEditorViewState({ ...next, mode, species: nextSpecies });
  else setSkinWorkshopSpecies(nextSpecies, true);
  void initializeSkinModelPreview().then(syncSkinAnimationLab);
}

function syncSkinWorkshopModeUi(mode: "live" | "offline"): void {
  const creator = document.querySelector<HTMLElement>("[data-skin-creator]");
  if (!creator) return;
  if (mode === "live" && creator.dataset.skinLiveAvailable !== "true") mode = "offline";
  creator.dataset.skinEditorMode = mode;
  creator.querySelectorAll<HTMLButtonElement>("[data-skin-workshop-mode]").forEach((button) => {
    button.setAttribute("aria-pressed", String(button.dataset.skinWorkshopMode === mode));
  });
  creator.querySelectorAll<HTMLElement>("[data-skin-live-controls]").forEach((element) => { element.hidden = mode !== "live"; });
  creator.querySelectorAll<HTMLElement>("[data-skin-offline-controls]").forEach((element) => { element.hidden = mode !== "offline"; });
  const species = document.querySelector<HTMLSelectElement>("[data-skin-pattern]")?.dataset.skinSpecies ?? currentSnapshot.liveDino.species;
  const modeLabel = creator.querySelector<HTMLElement>("[data-skin-workshop-mode-label]");
  const targetLabel = creator.querySelector<HTMLElement>("[data-skin-workshop-target-label]");
  const targetCopy = creator.querySelector<HTMLElement>("[data-skin-workshop-target-copy]");
  const targetMetricLabel = creator.querySelector<HTMLElement>("[data-skin-target-metric-label]");
  const targetMetric = creator.querySelector<HTMLElement>("[data-skin-target-metric]");
  const saveLabel = creator.querySelector<HTMLElement>("[data-skin-save-label]");
  if (modeLabel) modeLabel.textContent = mode === "live" ? "Live specimen linked" : "Offline draft active";
  if (targetLabel) targetLabel.textContent = mode === "live" ? currentSnapshot.liveDino.displayName : `${species} workshop specimen`;
  if (targetCopy) targetCopy.textContent = mode === "live" ? "Changes remain local until Apply skin is confirmed." : "Nothing in-game can change from this mode.";
  if (targetMetricLabel) targetMetricLabel.textContent = mode === "live" ? "Live target" : "Draft target";
  if (targetMetric) targetMetric.textContent = mode === "live" ? currentSnapshot.liveDino.displayName : "Offline only";
  if (saveLabel?.firstChild) saveLabel.firstChild.textContent = mode === "live" ? "Save as reusable skin " : "Name this workshop draft ";
  const saveButton = creator.querySelector<HTMLButtonElement>("[data-portal-action='skin-save']");
  if (saveButton) saveButton.disabled = mode === "live" && creator.dataset.skinLiveStale === "true";
}

function setSkinWorkshopSpecies(species: string, reinitialize: boolean): void {
  const limits = skinStructureLimitsForSpecies(species);
  const creator = document.querySelector<HTMLElement>("[data-skin-creator]");
  const pattern = creator?.querySelector<HTMLSelectElement>("[data-skin-pattern]");
  const speciesSelect = creator?.querySelector<HTMLSelectElement>("[data-skin-offline-species]");
  const host = creator?.querySelector<HTMLElement>("[data-skin-model-preview]");
  if (!creator || !pattern || !host || !limits) return;
  pattern.dataset.skinSpecies = species;
  pattern.replaceChildren(...Array.from({ length: limits.patternCount }, (_, index) => new Option(`Pattern ${index + 1}`, String(index))));
  pattern.value = "0";
  syncSkinThemeOptions(pattern, 0);
  if (speciesSelect && Array.from(speciesSelect.options).some((option) => option.value === species)) speciesSelect.value = species;
  host.dataset.skinPreviewSpecies = species;
  creator.querySelectorAll<HTMLElement>("[data-skin-workshop-species-label]").forEach((label) => { label.textContent = species; });
  const target = creator.querySelector<HTMLElement>("[data-skin-workshop-target-label]");
  if (target && creator.dataset.skinEditorMode === "offline") target.textContent = `${species} workshop specimen`;
  const applyLabel = creator.querySelector<HTMLElement>(".skin-apply-row strong");
  if (applyLabel) applyLabel.textContent = `Apply to live ${species}`;
  const canvas = host.querySelector<HTMLCanvasElement>("canvas");
  if (canvas) canvas.setAttribute("aria-label", `Interactive ${species} skin preview`);
  if (reinitialize) void initializeSkinModelPreview().then(syncSkinAnimationLab);
}

function selectSkinPreviewGrowth(growth: string): void {
  const button = document.querySelector<HTMLButtonElement>(`button[data-skin-preview-growth="${cssEscape(growth)}"]`);
  if (!button) return;
  document.querySelectorAll<HTMLButtonElement>("button[data-skin-preview-growth]").forEach((entry) => entry.setAttribute("aria-pressed", String(entry === button)));
  const host = document.querySelector<HTMLElement>("[data-skin-model-preview]");
  if (host) host.dataset.skinPreviewGrowth = growth;
  updateSkinModelPreviewGrowth(Number(growth));
  const stage = button.dataset.skinPreviewStage
    ?? ({ "10": "Hatchling", "35": "Juvenile", "70": "Sub-adult", "100": "Adult" } as const)[growth as "10" | "35" | "70" | "100"]
    ?? "Growth preview";
  const text = `${stage} · ${growth}%`;
  const label = document.querySelector<HTMLElement>("[data-skin-preview-growth-label]");
  const metric = document.querySelector<HTMLElement>("[data-skin-preview-growth-metric]");
  if (label) label.textContent = text;
  if (metric) metric.textContent = text;
}

function selectSkinPreviewMotion(motion: SkinPreviewMotion, announce = true): void {
  const host = document.querySelector<HTMLElement>("[data-skin-model-preview]");
  if (host) host.dataset.skinPreviewMotion = motion;
  if (!playSkinModelPreviewMotion(motion)) {
    if (announce) setStatus("That motion is not included in this model's approved animation pack yet.");
    return;
  }
  const label = document.querySelector<HTMLElement>("[data-skin-motion-label]");
  if (label) label.textContent = `${motion === "friendly" ? "F call" : motion} preview`;
  if (announce) setStatus(`${motion === "friendly" ? "Friendly call" : motion} motion playing in the local preview.`);
}

function selectSkinPreviewLighting(mode: SkinPreviewLighting, intensity: number): void {
  document.querySelectorAll<HTMLButtonElement>("button[data-skin-preview-lighting]").forEach((button) => {
    button.setAttribute("aria-pressed", String(button.dataset.skinPreviewLighting === mode));
  });
  const input = document.querySelector<HTMLInputElement>("[data-skin-preview-intensity]");
  const safeIntensity = Number.isFinite(intensity) ? Math.max(35, Math.min(150, intensity)) : 100;
  const host = document.querySelector<HTMLElement>("[data-skin-model-preview]");
  if (host) {
    host.dataset.skinPreviewLighting = mode;
    host.dataset.skinPreviewIntensity = String(safeIntensity);
  }
  if (input) input.value = String(safeIntensity);
  const output = document.querySelector<HTMLOutputElement>("[data-skin-preview-intensity-output]");
  if (output) output.value = `${safeIntensity}%`;
  const label = document.querySelector<HTMLElement>("[data-skin-lighting-label]");
  if (label) label.textContent = mode[0]!.toUpperCase() + mode.slice(1);
  updateSkinModelPreviewLighting(mode, safeIntensity);
}

function restorePortalFocus(locator: PortalFocusLocator | undefined): void {
  if (!locator) return;
  const matches = document.querySelectorAll<HTMLElement>(locator.selector);
  matches.item(locator.index)?.focus({ preventScroll: true });
}

function capturePortalFocus(): PortalFocusLocator | undefined {
  const element = document.activeElement;
  if (!(element instanceof HTMLElement) || element === document.body) return undefined;
  const selector = portalFocusSelector(element);
  if (!selector) return undefined;
  const matches = Array.from(document.querySelectorAll<HTMLElement>(selector));
  const index = matches.indexOf(element);
  return index >= 0 ? { selector, index } : undefined;
}

function portalFocusSelector(element: HTMLElement): string | undefined {
  if (element.id) return `#${cssEscape(element.id)}`;
  if (element.dataset.portalAction) return `[data-portal-action="${cssEscape(element.dataset.portalAction)}"]`;
  if (element instanceof HTMLAnchorElement) return `a[href="${cssEscape(element.getAttribute("href") ?? "")}"]`;
  if (element.dataset.mapLayerToggle) return `[data-map-layer-toggle="${cssEscape(element.dataset.mapLayerToggle)}"]`;
  return undefined;
}

function withHeatmapDemo(response: PortalSnapshotResponse): PortalSnapshotResponse;
function withHeatmapDemo(response: undefined): undefined;
function withHeatmapDemo(response: PortalSnapshotResponse | undefined): PortalSnapshotResponse | undefined;
function withHeatmapDemo(response: PortalSnapshotResponse | undefined): PortalSnapshotResponse | undefined {
  if (!response || searchParams.get("heatmapDemo") !== "1") return response;
  return {
    ...response,
    snapshot: {
      ...response.snapshot,
      heatmap: [
        { latitude: 320000, longitude: -180000, density: "low" },
        { latitude: 80000, longitude: 60000, density: "medium" },
        { latitude: -210000, longitude: 260000, density: "high" }
      ]
    }
  };
}

function preserveDevelopmentQuery(): void {
  if (!searchParams.has("userId") && !searchParams.has("roles") && !searchParams.has("heatmapDemo")) return;
  document.querySelectorAll<HTMLAnchorElement>(".side-nav a[href^='/portal']").forEach((link) => {
    const url = new URL(link.href, window.location.origin);
    if (searchParams.get("userId")) url.searchParams.set("userId", searchParams.get("userId")!);
    if (searchParams.get("roles")) url.searchParams.set("roles", searchParams.get("roles")!);
    if (searchParams.get("heatmapDemo")) url.searchParams.set("heatmapDemo", searchParams.get("heatmapDemo")!);
    link.href = `${url.pathname}${url.search}`;
  });
}

async function loadPortalSnapshot(userIdToLoad: string, roleIdsToResolve: string[]): Promise<PortalSnapshotResponse | undefined> {
  try {
    return await apiClient.loadSnapshot(userIdToLoad, roleIdsToResolve);
  } catch {
    return undefined;
  }
}

function renderPublicLandingPage(): void {
  if (!app) return;
  app.innerHTML = renderPublicLanding({ authNotice: publicLandingAuthNotice(searchParams) });
  initializePublicLanding(app);
}

function isLocalLandingPreview(): boolean {
  return searchParams.get("landingPreview") === "1" && ["127.0.0.1", "localhost", "::1"].includes(window.location.hostname);
}

function renderPortalUnavailable(): void {
  if (!app) return;
  app.innerHTML = `
    <main class="portal-gate">
      <section class="gate-panel" aria-labelledby="portal-gate-title">
        <div class="gate-brand">The Nest Player Portal</div>
        <h1 id="portal-gate-title">Portal temporarily unavailable</h1>
        <p>We could not reach The Nest service. Your account is still signed in and no action was submitted.</p>
        <button class="gate-retry-button" type="button" data-portal-retry>Try again</button>
        <div class="gate-help">
          <strong>Still not loading?</strong>
          <span>Wait a moment and retry. If the problem continues, report it in Discord so staff can check service status.</span>
        </div>
      </section>
    </main>`;
  app.querySelector<HTMLButtonElement>("[data-portal-retry]")?.addEventListener("click", () => window.location.reload());
}

function escapePortalText(value: string): string {
  return value.replace(/[&<>"']/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;"
  })[character] ?? character);
}

function wirePortalActions(): void {
  if (actionsWired) {
    return;
  }

  actionsWired = true;
  app?.addEventListener("toggle", (event) => {
    if (event.target instanceof HTMLDetailsElement) rememberDisclosureState(event.target);
  }, true);
  app?.addEventListener("keydown", (event) => {
    if (!(event instanceof KeyboardEvent)) return;
    if (handlePlayerSearchKeyboard(event)) return;
    if (handleGamblingMenuKeyboard(event)) return;
    if (event.key === "Escape") {
      const panel = document.querySelector<HTMLElement>("[data-global-notification-panel]");
      if (!panel || panel.hidden) return;
      event.preventDefault();
      closePortalNotifications(true);
    }
  });
  app?.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest(".portal-notification-center")) closePortalNotifications();
    const workspaceButton = target?.closest<HTMLButtonElement>("[data-staff-workspace-target]");
    if (workspaceButton) {
      selectStaffWorkspace(workspaceButton);
      return;
    }
    const playerWorkspaceButton = target?.closest<HTMLButtonElement>("[data-staff-player-target]");
    if (playerWorkspaceButton) {
      selectStaffPlayerPanel(playerWorkspaceButton);
      return;
    }
    if (target?.closest("[data-staff-player-back]")) {
      closeStaffPlayerWorkspace();
      return;
    }
    const portalLink = target?.closest<HTMLAnchorElement>("a[href]");
    const mouseEvent = event as MouseEvent;
    if (portalLink && !portalLink.target && !portalLink.hasAttribute("download") && mouseEvent.button === 0
      && !mouseEvent.metaKey && !mouseEvent.ctrlKey && !mouseEvent.shiftKey && !mouseEvent.altKey) {
      const url = new URL(portalLink.href, window.location.origin);
      if (portalLink.closest(".staff-workspace-index") && url.pathname === window.location.pathname && url.hash) {
        const section = document.getElementById(decodeURIComponent(url.hash.slice(1)));
        if (section) {
          event.preventDefault();
          if (section instanceof HTMLDetailsElement) section.open = true;
          window.history.replaceState(window.history.state, "", url);
          section.scrollIntoView({
            behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
            block: "start"
          });
          return;
        }
      }
      if (url.origin === window.location.origin && url.pathname.startsWith("/portal")) {
        event.preventDefault();
        void navigatePortal(url);
        return;
      }
    }
    const confirmButton = target?.closest<HTMLElement>("[data-confirm-action]");
    const actionButton = target?.closest<HTMLElement>("[data-portal-action]");
    const liveActionMode = target?.closest<HTMLButtonElement>("[data-live-action-mode]");
    const systemButton = target?.closest<HTMLElement>("[data-system-action]");
    const bugReportOpen = target?.closest<HTMLElement>("[data-bug-report-open]");
    const bugReportClose = target?.closest<HTMLElement>("[data-bug-report-close]");
    const storeMutationClose = target?.closest<HTMLElement>("[data-store-mutation-close]");
    const profileOpen = target?.closest<HTMLElement>("[data-profile-open]");
    const profileClose = target?.closest<HTMLElement>("[data-profile-close]");
    const profileSuggestion = target?.closest<HTMLButtonElement>("[data-profile-suggestion]");
    const playerPickerSuggestion = target?.closest<HTMLButtonElement>("[data-player-picker-suggestion]");
    const stickTransferSuggestion = target?.closest<HTMLButtonElement>("[data-stick-transfer-suggestion]");
    const stickTransferClose = target?.closest<HTMLButtonElement>("[data-stick-transfer-close]");
    const mapRecenter = target?.closest<HTMLButtonElement>("[data-map-recenter]");
    if (target?.closest("[data-map-weather-focus]")) { focusLiveWeather(); return; }
    const mapCalibrationToggle = target?.closest<HTMLButtonElement>("[data-map-calibration-toggle]");
    const mapCalibrationCopy = target?.closest<HTMLButtonElement>("[data-map-calibration-copy]");
    const mapCalibrationClear = target?.closest<HTMLButtonElement>("[data-map-calibration-clear]");
    const storeCategory = target?.closest<HTMLButtonElement>("[data-store-category]");
    const storeTeleportView = target?.closest<HTMLButtonElement>("[data-store-teleport-view]");
    const catalogEdit = target?.closest<HTMLButtonElement>("[data-catalog-edit]");
    const catalogNew = target?.closest<HTMLButtonElement>("[data-catalog-new]");
    const skinWorkshopMode = target?.closest<HTMLButtonElement>("[data-skin-workshop-mode]");
    const skinPreviewGrowth = target?.closest<HTMLButtonElement>("button[data-skin-preview-growth]");
    const skinPreviewMotion = target?.closest<HTMLButtonElement>("button[data-skin-preview-motion]");
    const skinPreviewLighting = target?.closest<HTMLButtonElement>("button[data-skin-preview-lighting]");
    const skinAnimationRecipe = target?.closest<HTMLButtonElement>("[data-skin-animation-recipe]");
    const skinEffectRecipe = target?.closest<HTMLButtonElement>("[data-skin-effect-recipe]");
    const skinAnimationAddStop = target?.closest<HTMLButtonElement>("[data-skin-animation-add-stop]");
    const skinAnimationRemoveStop = target?.closest<HTMLButtonElement>("[data-skin-animation-remove-stop]");
    const marketplaceOpen = target?.closest<HTMLButtonElement>("[data-marketplace-open]");
    const marketplaceSellOpen = target?.closest<HTMLButtonElement>("[data-marketplace-sell-open]");
    const marketplaceDialogClose = target?.closest<HTMLButtonElement>("[data-marketplace-dialog-close]");
    const marketplaceTab = target?.closest<HTMLButtonElement>("[data-marketplace-tab]");

    if (skinAnimationRecipe?.dataset.skinAnimationRecipe) {
      applySkinAnimationRecipe(skinAnimationRecipe.dataset.skinAnimationRecipe);
      return;
    }

    if (skinEffectRecipe?.dataset.skinEffectRecipe === "hdr_glitch") {
      applyPositiveHdrGlitchRecipe();
      return;
    }

    if (skinAnimationAddStop || skinAnimationRemoveStop) {
      changeSkinAnimationStopCount((skinAnimationAddStop ?? skinAnimationRemoveStop)!, Boolean(skinAnimationAddStop));
      return;
    }

    if (marketplaceTab?.dataset.marketplaceTab) {
      selectMarketplaceTab(marketplaceTab);
      return;
    }

    if (marketplaceDialogClose) {
      marketplaceDialogClose.closest<HTMLDialogElement>("dialog")?.close();
      return;
    }

    if (marketplaceSellOpen) {
      document.querySelector<HTMLDialogElement>("#marketplaceSellDialog")?.showModal();
      return;
    }

    if (marketplaceOpen?.dataset.marketplaceOpen) {
      const dialog = document.getElementById(marketplaceOpen.dataset.marketplaceOpen);
      if (dialog instanceof HTMLDialogElement) {
        dialog.showModal();
        window.requestAnimationFrame(() => initializeMarketplaceSkinPreviews());
      }
      return;
    }

    if (skinWorkshopMode?.dataset.skinWorkshopMode === "live" || skinWorkshopMode?.dataset.skinWorkshopMode === "offline") {
      switchSkinWorkshopMode(skinWorkshopMode.dataset.skinWorkshopMode);
      return;
    }

    if (skinPreviewMotion?.dataset.skinPreviewMotion) {
      selectSkinPreviewMotion(skinPreviewMotion.dataset.skinPreviewMotion as SkinPreviewMotion);
      return;
    }

    if (skinPreviewLighting?.dataset.skinPreviewLighting) {
      const intensity = Number(document.querySelector<HTMLInputElement>("[data-skin-preview-intensity]")?.value ?? "100");
      selectSkinPreviewLighting(skinPreviewLighting.dataset.skinPreviewLighting as SkinPreviewLighting, intensity);
      return;
    }

    if (skinPreviewGrowth?.dataset.skinPreviewGrowth) {
      selectSkinPreviewGrowth(skinPreviewGrowth.dataset.skinPreviewGrowth);
      return;
    }
    if (catalogNew?.dataset.catalogNew) {
      resetStoreProductEditor(catalogNew.dataset.catalogNew as "dino_growth" | "corpse_drop" | "teleport");
      return;
    }

    if (catalogEdit?.dataset.catalogEdit) {
      populateStoreProductEditor(catalogEdit.dataset.catalogEdit);
      return;
    }

    if (storeCategory?.dataset.storeCategory) {
      selectStoreCategory(storeCategory.dataset.storeCategory);
      return;
    }

    if (storeTeleportView?.dataset.storeTeleportView) {
      selectStoreTeleportView(storeTeleportView.dataset.storeTeleportView);
      return;
    }

    if (mapRecenter) {
      recenterLiveMap();
      return;
    }

    if (mapCalibrationToggle) {
      toggleLiveMapCalibration();
      return;
    }

    if (mapCalibrationCopy) {
      void copyLiveMapCalibrationSamples().then((count) => setStatus(count > 0 ? `${count} map calibration samples copied.` : "Capture at least one map calibration sample first."));
      return;
    }

    if (mapCalibrationClear) {
      clearLiveMapCalibrationSamples();
      setStatus("Map calibration samples cleared.");
      return;
    }

    if (playerPickerSuggestion) {
      const picker = playerPickerSuggestion.closest<HTMLElement>(".player-picker");
      const input = picker?.querySelector<HTMLInputElement>("[data-player-picker]");
      if (input) {
        input.value = playerPickerSuggestion.dataset.playerName ?? "";
        input.dataset.selectedUserId = playerPickerSuggestion.dataset.userId ?? "";
        input.dataset.selectedSteam64Id = playerPickerSuggestion.dataset.steam64Id ?? "";
        input.dataset.selectedDiscordUserId = playerPickerSuggestion.dataset.discordUserId ?? "";
      }
      hidePlayerPickerSuggestions();
      return;
    }

    if (stickTransferSuggestion) {
      const input = document.querySelector<HTMLInputElement>("[data-stick-transfer-recipient]");
      if (input) {
        input.value = stickTransferSuggestion.dataset.playerName ?? "";
        input.dataset.selectedUserId = stickTransferSuggestion.dataset.userId ?? "";
        input.closest<HTMLElement>(".stick-transfer-recipient")?.setAttribute("data-selected", "true");
        const status = input.closest<HTMLElement>(".stick-transfer-recipient")?.querySelector<HTMLElement>("[data-stick-transfer-recipient-status]");
        if (status) status.textContent = `Selected: ${stickTransferSuggestion.dataset.playerName ?? "verified player"}`;
      }
      hideStickTransferSuggestions();
      resetStickTransferReview();
      updateStickTransferSubmitState();
      return;
    }

    if (stickTransferClose) {
      closeStickTransferDialog();
      return;
    }

    if (profileSuggestion) {
      const input = document.querySelector<HTMLInputElement>("[data-profile-lookup]");
      if (input) input.value = profileSuggestion.dataset.userId ?? "";
      hideProfileSuggestions();
      void handleProfileLookup();
      return;
    }

    if (!target?.closest(".player-picker")) hidePlayerPickerSuggestions();

    if (profileOpen) {
      showProfileDialog();
      return;
    }

    if (profileClose) {
      closeProfileDialog();
      return;
    }

    if (bugReportOpen) {
      showBugReportDialog();
      return;
    }

    if (bugReportClose) {
      closeBugReportDialog();
      return;
    }

    if (storeMutationClose) {
      closeStoreMutationDialog();
      return;
    }

    if (confirmButton) {
      void handlePortalConfirmation(confirmButton);
      return;
    }

    if (liveActionMode) {
      selectLiveActionMode(liveActionMode);
      return;
    }

    if (actionButton) {
      if (actionButton.dataset.portalAction === "store-buy" && actionButton.dataset.storeItemType === "dino_growth") {
        showStoreMutationDialog(actionButton);
        return;
      }
      if (actionButton.dataset.confirmTitle && actionButton.dataset.confirmed !== "true") {
        closeProfileDialog();
        if (!prepareAuctionConfirmation(actionButton)) return;
        showPortalConfirmation(actionButton);
        return;
      }

      void handlePortalAction(actionButton);
    }

    if (systemButton) {
      if (systemButton.dataset.confirmTitle && systemButton.dataset.confirmed !== "true") {
        showSystemConfirmation(systemButton);
        return;
      }
      void handleSystemAction(systemButton);
    }
  });
  app?.addEventListener("change", (event) => {
    if (event.target instanceof HTMLSelectElement && event.target.matches("[data-marketplace-format]")) {
      const fixed = event.target.value === "fixed";
      document.querySelectorAll<HTMLElement>(".auction-opening-field").forEach(field => { field.hidden = fixed; });
      document.querySelectorAll<HTMLInputElement>("[data-auction-opening]").forEach(input => { input.disabled = fixed; });
      document.querySelectorAll<HTMLInputElement>("[data-auction-buy-now]").forEach(input => { input.placeholder = fixed ? input.min : "No instant purchase"; });
      return;
    }
    if (event.target instanceof HTMLInputElement && event.target.matches("[data-store-mutation-option]")) {
      updateStoreMutationSelection(event.target);
      return;
    }
    if (event.target instanceof HTMLInputElement && event.target.matches("[data-specimen-mutation-option]")) {
      updateSpecimenMutationSelection(event.target);
      return;
    }
    if (event.target instanceof HTMLSelectElement && event.target.matches("[data-specimen-species]")) {
      renderSpecimenMutationOptions();
      return;
    }
    if (event.target instanceof HTMLSelectElement && event.target.matches("[data-mobile-navigation]")) {
      const url = new URL(event.target.value, window.location.origin);
      for (const key of ["userId", "roles", "heatmapDemo"]) {
        const value = searchParams.get(key);
        if (value) url.searchParams.set(key, value);
      }
      void navigatePortal(url);
      return;
    }
    if (event.target instanceof HTMLSelectElement && event.target.matches("[data-skin-offline-species]")) {
      setSkinWorkshopSpecies(event.target.value, true);
      const creator = document.querySelector<HTMLElement>("[data-skin-creator]");
      if (creator?.dataset.skinEditorMode === "offline") skinWorkshopDrafts.offline = captureSkinEditorViewState();
      return;
    }
    if (event.target instanceof HTMLSelectElement && event.target.matches("[data-skin-pattern]")) {
      syncSkinThemeOptions(event.target);
      updateSkinModelPreviewPattern(Number(event.target.value));
      return;
    }
    if (event.target instanceof HTMLSelectElement && event.target.matches("[data-skin-theme]")) {
      const metric = document.querySelector<HTMLElement>("[data-skin-preview-theme-metric]");
      if (metric) metric.textContent = `${Number(event.target.value) + 1} / ${event.target.options.length}`;
      return;
    }

    if (event.target instanceof HTMLInputElement && event.target.matches("[data-skin-animation-toggle]")) {
      syncSkinAnimationLab();
      return;
    }
    if (event.target instanceof HTMLSelectElement && event.target.matches("[data-skin-animation-pattern]")) {
      syncSkinAnimationLab();
      return;
    }
    if (event.target instanceof HTMLSelectElement && event.target.matches("[data-skin-effect-mode]")) {
      const motion = document.querySelector<HTMLSelectElement>("[data-skin-effect-pattern]");
      const pulseModes = ["bioluminescent", "lava", "frost", "blood_moon", "ember", "phantom", "solar_flare", "verdant"];
      if (motion) motion.value = pulseModes.includes(event.target.value) ? "pulse" : "cycle";
      const effects = readSkinEffects();
      syncSkinEffectDesigner(effects.effectMode);
      updateSkinModelPreviewEffects(effects.goreIntensity, effects);
      return;
    }
    if (event.target instanceof HTMLSelectElement && event.target.matches("[data-skin-effect-pattern], [data-skin-environment-mode]")) {
      const effects = readSkinEffects();
      updateSkinModelPreviewEffects(effects.goreIntensity, effects);
      return;
    }
    if (event.target instanceof HTMLSelectElement && event.target.matches("[data-skin-photo-background], [data-skin-photo-lighting]")) {
      syncSkinPhotoStudio();
      return;
    }
    if (event.target instanceof HTMLSelectElement && event.target.matches("[data-ticket-log-type-filter]") && event.target.dataset.bundleId) {
      applyTicketLogTypeFilter(event.target.dataset.bundleId, event.target.value);
      return;
    }
    if (event.target instanceof HTMLSelectElement && event.target.matches("[data-ticket-log-combined-type-filter], [data-ticket-log-combined-participant-filter]") && event.target.dataset.ticketId) {
      applyCombinedTicketLogFilters(event.target.dataset.ticketId);
      return;
    }
    if (event.target instanceof HTMLSelectElement && event.target.matches("[data-player-ticket-type]")) {
      updateSupportTicketForm();
      return;
    }
    if (event.target instanceof HTMLSelectElement && event.target.matches("[data-marketplace-sell-species]")) {
      applyMarketplaceSellFilters();
      return;
    }
    if (event.target instanceof HTMLSelectElement && event.target.matches("[data-marketplace-filter], [data-marketplace-sort]")) {
      applyMarketplaceFilters();
      return;
    }
    if (event.target instanceof HTMLSelectElement && event.target.matches("[data-store-product-type]")) {
      syncStoreProductEditorFields();
      return;
    }
    if (event.target instanceof HTMLSelectElement && event.target.matches("[data-wheel-prize-type]")) {
      const amount = event.target.closest<HTMLElement>("[data-wheel-prize-row]")?.querySelector<HTMLInputElement>("[data-wheel-prize-amount]");
      if (amount) {
        amount.disabled = event.target.value === "no_prize" || event.target.value === "event_choice" || event.target.value === "prime_token";
        if (event.target.value === "no_prize") amount.value = "0";
        if (event.target.value === "event_choice") amount.value = "1";
        if (event.target.value === "prime_token") amount.value = "1";
        if (event.target.value === "dino_voucher" && !["4", "8", "12", "16"].includes(amount.value)) amount.value = "4";
      }
      return;
    }
    if (event.target instanceof HTMLInputElement && event.target.matches("[data-marketplace-filter]")) {
      applyMarketplaceFilters();
      return;
    }
    if (event.target instanceof HTMLInputElement && event.target.matches("[data-player-ticket-files]")) {
      renderPlayerTicketFileSelection(event.target);
      return;
    }
    const reviewConfirmation = event.target instanceof HTMLInputElement
      ? event.target.closest<HTMLInputElement>("[data-case-review-confirm]")
      : null;
    if (reviewConfirmation?.dataset.ticketId) {
      const controls = document.querySelector<HTMLFieldSetElement>(`[data-case-decision-controls][data-ticket-id="${cssEscape(reviewConfirmation.dataset.ticketId)}"]`);
      if (controls) controls.disabled = !reviewConfirmation.checked;
      return;
    }
    const logEvent = event.target instanceof HTMLInputElement
      ? event.target.closest<HTMLInputElement>("[data-ticket-log-event]")
      : null;
    if (logEvent?.dataset.bundleId) {
      updateTicketLogSelection(logEvent.dataset.bundleId);
      return;
    }
    const combinedLogEvent = event.target instanceof HTMLInputElement
      ? event.target.closest<HTMLInputElement>("[data-ticket-log-combined-event]")
      : null;
    if (combinedLogEvent) {
      const panel = combinedLogEvent.closest<HTMLElement>("[data-ticket-log-combined-panel]");
      if (panel?.dataset.ticketId) updateCombinedTicketLogSelection(panel.dataset.ticketId);
      return;
    }
    const caseFilter = event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement
      ? event.target.closest<HTMLElement>("[data-case-filter-source], [data-case-filter-from], [data-case-filter-to]")
      : null;
    if (caseFilter) {
      const timeline = caseFilter.closest<HTMLElement>("[data-case-activity]");
      if (timeline) applyCaseTimelineFilters(timeline);
      return;
    }
    const toggle = event.target instanceof HTMLInputElement
      ? event.target.closest<HTMLInputElement>("[data-map-layer-toggle]")
      : null;
    if (toggle) {
      setMapLayer(toggle.dataset.mapLayerToggle ?? "", toggle.checked, true);
      void refreshMapSnapshot();
      return;
    }
    const monochromeToggle = event.target instanceof HTMLInputElement
      ? event.target.closest<HTMLInputElement>("[data-map-monochrome-toggle]")
      : null;
    if (monochromeToggle) setMapMonochrome(monochromeToggle.checked, true);
  });
  app?.addEventListener("input", (event) => {
    if (event.target instanceof HTMLTextAreaElement && event.target.matches("[data-announcement-message]")) {
      const count = document.querySelector<HTMLElement>("[data-announcement-count]");
      if (count) count.textContent = String(event.target.value.length);
      return;
    }
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.matches("[data-marketplace-sell-search]")) {
      applyMarketplaceSellFilters();
      return;
    }
    if (event.target.matches("[data-skin-preview-growth-range]")) {
      const growth = Number(event.target.value);
      const text = `${skinPreviewGrowthStage(growth)} · ${Math.round(growth)}%`;
      updateSkinModelPreviewGrowth(growth);
      const label = document.querySelector<HTMLElement>("[data-skin-preview-growth-label]");
      const metric = document.querySelector<HTMLElement>("[data-skin-preview-growth-metric]");
      if (label) label.textContent = text;
      if (metric) metric.textContent = text;
      return;
    }
    if (event.target.matches("[data-skin-gore-intensity]")) {
      const effects = readSkinEffects();
      const output = document.querySelector<HTMLElement>("[data-skin-gore-value]");
      if (output) output.textContent = `${effects.goreIntensity}%`;
      updateSkinModelPreviewEffects(effects.goreIntensity, effects);
      return;
    }
    if (event.target.matches("[data-skin-effect-color], [data-skin-effect-speed]")) {
      const effects = readSkinEffects();
      const output = document.querySelector<HTMLElement>("[data-skin-effect-speed-value]");
      if (output) output.textContent = `${effects.effectSpeedSeconds}s`;
      updateSkinModelPreviewEffects(effects.goreIntensity, effects);
      return;
    }
    if (event.target.matches("[data-skin-animation-speed], [data-skin-animation-stop-color], [data-skin-animation-stop-hex]")) {
      syncSkinAnimationInput(event.target);
      return;
    }
    if (event.target.matches("[data-marketplace-preview-growth-range]")) {
      const dialog = event.target.closest<HTMLDialogElement>(".marketplace-detail-dialog");
      const preview = dialog?.querySelector<HTMLElement>(".marketplace-skin-model-detail");
      const growth = Number(event.target.value);
      if (preview) updateMarketplaceSkinPreviewGrowth(preview, growth);
      const label = dialog?.querySelector<HTMLElement>("[data-marketplace-preview-growth-label]");
      if (label) label.textContent = `${skinPreviewGrowthStage(growth)} · ${Math.round(growth)}%`;
      return;
    }
    if (event.target.matches("[data-catalog-search-input]")) {
      filterStoreProducts(event.target.value);
      return;
    }
    if (event.target.matches("[data-wheel-prize-chance]")) {
      updateEventWheelConfigTotal();
      return;
    }
    if (event.target.matches("[data-ecosystem-radius], [data-ecosystem-map-x], [data-ecosystem-map-y]")) {
      previewEcosystemZoneGeometry();
      return;
    }
    if (event.target.matches("[data-skin-preview-intensity]")) {
      const activeLighting = document.querySelector<HTMLButtonElement>("button[data-skin-preview-lighting][aria-pressed='true']")?.dataset.skinPreviewLighting;
      selectSkinPreviewLighting((activeLighting === "daylight" || activeLighting === "night" ? activeLighting : "containment"), Number(event.target.value));
      return;
    }
    if (event.target.matches("[data-skin-color]")) {
      setSkinColourInput(event.target, event.target.value);
      return;
    }
    if (event.target.matches("[data-skin-hex]")) {
      const normalized = normalizeSkinHex(event.target.value);
      event.target.setAttribute("aria-invalid", String(normalized === undefined));
      if (normalized) {
        const field = event.target.dataset.skinHex;
        const colorInput = field
          ? document.querySelector<HTMLInputElement>(`[data-skin-color="${cssEscape(field)}"]`)
          : null;
        if (colorInput) setSkinColourInput(colorInput, normalized);
      }
      return;
    }
    if (event.target.matches("[data-player-picker]")) {
      event.target.dataset.selectedUserId = "";
      event.target.dataset.selectedSteam64Id = "";
      event.target.dataset.selectedDiscordUserId = "";
      const previousTimer = playerPickerTimers.get(event.target);
      if (previousTimer !== undefined) window.clearTimeout(previousTimer);
      const query = event.target.value.trim();
      if (query.length < 2) {
        hidePlayerPickerSuggestions(event.target);
        return;
      }
      const timer = window.setTimeout(() => void updatePlayerPickerSuggestions(event.target as HTMLInputElement, query), 250);
      playerPickerTimers.set(event.target, timer);
      return;
    }
    if (event.target.matches("[data-stick-transfer-recipient]")) {
      event.target.dataset.selectedUserId = "";
      event.target.closest<HTMLElement>(".stick-transfer-recipient")?.removeAttribute("data-selected");
      const status = event.target.closest<HTMLElement>(".stick-transfer-recipient")?.querySelector<HTMLElement>("[data-stick-transfer-recipient-status]");
      if (status) status.textContent = "Choose a verified player from the results.";
      resetStickTransferReview();
      updateStickTransferSubmitState();
      if (stickTransferSearchTimer !== undefined) window.clearTimeout(stickTransferSearchTimer);
      const query = event.target.value.trim();
      if (query.length < 2) {
        hideStickTransferSuggestions();
        return;
      }
      stickTransferSearchTimer = window.setTimeout(() => void updateStickTransferSuggestions(query), 250);
      return;
    }
    if (event.target.matches("[data-stick-transfer-amount]")) {
      resetStickTransferReview();
      updateStickTransferSubmitState();
      return;
    }
    if (event.target.matches("[data-stick-transfer-note]")) {
      resetStickTransferReview();
      updateStickTransferSubmitState();
      return;
    }
    if (!event.target.matches("[data-profile-lookup]")) return;
    if (profileSearchTimer !== undefined) window.clearTimeout(profileSearchTimer);
    const query = event.target.value.trim();
    if (query.length < 2) {
      hideProfileSuggestions();
      return;
    }
    profileSearchTimer = window.setTimeout(() => void updateProfileSuggestions(query), 250);
  });
  app?.addEventListener("submit", (event) => {
    if (event.target instanceof HTMLFormElement && event.target.matches("[data-store-mutation-form]")) {
      event.preventDefault();
      void submitStoreDinoPurchase(event.target);
      return;
    }
    if (event.target instanceof HTMLFormElement && event.target.matches("[data-specimen-claim-form]")) {
      event.preventDefault();
      void submitSpecimenEggClaim(event.target);
      return;
    }
    if (event.target instanceof HTMLFormElement && event.target.matches("[data-bug-report-form]")) {
      event.preventDefault();
      void submitBugReport(event.target);
      return;
    }
    if (event.target instanceof HTMLFormElement && event.target.matches("[data-stick-transfer-form]")) {
      event.preventDefault();
      void submitStickTransfer(event.target);
    }
  });
  app?.addEventListener("cancel", (event) => {
    if (event.target instanceof HTMLDialogElement && ["portalDialog", "storeMutationDialog", "bugReportDialog", "profileDialog", "stickTransferDialog"].includes(event.target.id)) {
      event.preventDefault();
      if (event.target.id === "portalDialog") clearPortalConfirmation();
      else if (event.target.id === "storeMutationDialog") closeStoreMutationDialog();
      else if (event.target.id === "bugReportDialog") closeBugReportDialog();
      else if (event.target.id === "stickTransferDialog") closeStickTransferDialog();
      else closeProfileDialog();
    }
  }, true);
  app?.addEventListener("click", (event) => {
    if (event.target instanceof HTMLDialogElement) {
      if (event.target.id === "portalDialog") clearPortalConfirmation();
      if (event.target.id === "storeMutationDialog") closeStoreMutationDialog();
      if (event.target.id === "bugReportDialog") closeBugReportDialog();
      if (event.target.id === "profileDialog") closeProfileDialog();
      if (event.target.id === "stickTransferDialog") closeStickTransferDialog();
      if (event.target.matches(".marketplace-detail-dialog, .marketplace-sell-dialog")) event.target.close();
    }
  });
}

function initializeDisclosureStates(root: ParentNode): void {
  const occurrences = new globalThis.Map<string, number>();
  root.querySelectorAll<HTMLDetailsElement>("details").forEach((detail) => {
    const summary = detail.querySelector(":scope > summary")?.textContent?.replace(/\s+/g, " ").trim().toLowerCase() ?? "section";
    const base = detail.id ? `id:${detail.id}` : `summary:${summary}`;
    const occurrence = (occurrences.get(base) ?? 0) + 1;
    occurrences.set(base, occurrence);
    const key = `${window.location.pathname}:${base}:${occurrence}`;
    detail.dataset.disclosureKey = key;
    const remembered = disclosureOpenStates.get(key);
    if (remembered !== undefined) detail.open = remembered;
  });
}

function rememberDisclosureState(detail: HTMLDetailsElement): void {
  const key = detail.dataset.disclosureKey;
  if (key) disclosureOpenStates.set(key, detail.open);
}

function hydratePortalIcons(): void {
  createIcons({
    icons: { Activity, Baby, BadgeCheck, BadgeDollarSign, Bell, BriefcaseBusiness, Bug, Camera, Check, ChevronDown, ChevronLeft, ChevronRight, Circle, CircleDotDashed, CircleHelp, Clock3, Coins, Crown, Dices, Dna, Drumstick, Egg, ExternalLink, Flag, FlagTriangleRight, FlaskConical, Focus, Gamepad2, Gavel, HeartPulse, LineChart, Link, LockKeyhole, LogIn, LogOut, Map, MapPinned, Megaphone, MessagesSquare, Palette, Pause, RadioTower, RefreshCw, Rotate3D, Route, Salad, Satellite, ScanSearch, Search, ScrollText, ServerCog, Settings2, ShieldAlert, ShieldCheck, Shuffle, Skull, Store, TentTree, Ticket, TerminalSquare, Trophy, Unlink, UserRound, UsersRound, UserSearch, Warehouse, Waves, X },
    attrs: { "aria-hidden": "true", "stroke-width": 1.8 }
  });
}

function startCooldownTimers(): void {
  updateCooldowns();
  if (cooldownTimerStarted) return;
  cooldownTimerStarted = true;
  window.setInterval(updateCooldowns, 1_000);
}

function updateCooldowns(): void {
  updateCooldownElements("[data-work-cooldown]", "button[data-portal-action='work-reward']", "Available now");
  updateCorpseActionCooldown();
  updateCooldownElements("[data-store-corpse-cooldown]", "button[data-portal-action='store-corpse-buy']", "Available now");
  updateCooldownElements("[data-store-teleport-cooldown]", "button[data-store-item-type='teleport']", "Purchase");
  updateCooldownElements("[data-store-teleport-cooldown-notice]", "[data-no-cooldown-button]", "Ready now · starts only after successful travel");
  updateCooldownElements("[data-blackjack-reset-countdown]", "button[data-portal-action='blackjack-new-hand']", "Deal next hand");
  updateMarketplaceCountdowns();
}

function updateCorpseActionCooldown(): void {
  document.querySelectorAll<HTMLElement>("[data-corpse-cooldown]").forEach((element) => {
    const panel = element.closest<HTMLElement>("[data-live-action-panel]");
    const selector = panel?.querySelector<HTMLButtonElement>("[data-live-action-mode='corpse']");
    const execute = panel?.querySelector<HTMLButtonElement>("[data-live-action-execute]");
    const productSelect = panel?.querySelector<HTMLSelectElement>("#corpseProduct");
    if (!selector) return;

    const endsAt = element.dataset.cooldownEndsAt;
    const remainingSeconds = endsAt
      ? Math.max(0, Math.ceil((new Date(endsAt).getTime() - Date.now()) / 1_000))
      : 0;
    const baseEnabled = selector.dataset.executeBaseEnabled === "true";
    const enabled = baseEnabled && remainingSeconds <= 0;
    selector.dataset.executeEnabled = String(enabled);

    if (remainingSeconds <= 0) {
      element.textContent = "Available now";
      delete element.dataset.cooldownEndsAt;
    } else {
      const hours = Math.floor(remainingSeconds / 3_600);
      const minutes = Math.floor((remainingSeconds % 3_600) / 60);
      const seconds = remainingSeconds % 60;
      element.textContent = hours > 0
        ? `Ready in ${hours}h ${minutes}m`
        : `Ready in ${minutes}m ${seconds}s`;
    }

    if (execute?.dataset.portalAction === "corpse-drop") {
      execute.disabled = !enabled;
    }
    if (productSelect) productSelect.disabled = !enabled;
  });
}

function selectLiveActionMode(selector: HTMLButtonElement): void {
  const panel = selector.closest<HTMLElement>("[data-live-action-panel]");
  const mode = selector.dataset.liveActionMode;
  const execute = panel?.querySelector<HTMLButtonElement>("[data-live-action-execute]");
  if (!panel || !mode || !execute) return;

  panel.querySelectorAll<HTMLButtonElement>("[data-live-action-mode]").forEach((button) => {
    const selected = button === selector;
    button.classList.toggle("selected", selected);
    button.setAttribute("aria-pressed", String(selected));
  });
  panel.querySelectorAll<HTMLElement>("[data-live-action-details]").forEach((details) => {
    details.hidden = details.dataset.liveActionDetails !== mode;
  });

  execute.dataset.portalAction = selector.dataset.executeAction ?? "";
  execute.dataset.confirmTitle = selector.dataset.executeConfirmTitle ?? "";
  execute.dataset.confirmMessage = selector.dataset.executeConfirmMessage ?? "";
  execute.title = selector.dataset.executeTitle ?? "";
  execute.disabled = selector.dataset.executeEnabled !== "true";
  execute.classList.toggle("destructive-command", selector.dataset.executeDestructive === "true");
  execute.querySelector("span")!.textContent = selector.dataset.executeLabel ?? "Execute action";
  delete execute.dataset.confirmed;
}

function updateMarketplaceCountdowns(): void {
  document.querySelectorAll<HTMLElement>("[data-marketplace-countdown]").forEach((element) => {
    const expiry = element.dataset.expiresAt;
    if (!expiry) return;
    const seconds = Math.max(0, Math.ceil((new Date(expiry).getTime() - Date.now()) / 1_000));
    if (seconds === 0) {
      element.textContent = "Ending now";
      return;
    }
    const days = Math.floor(seconds / 86_400);
    const hours = Math.floor(seconds % 86_400 / 3_600);
    const minutes = Math.floor(seconds % 3_600 / 60);
    element.textContent = days > 0 ? `${days}d ${hours}h` : hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m ${seconds % 60}s`;
  });
}

let marketplaceMineOnly = false;

function applyMarketplaceSellFilters(): void {
  const dialog = document.querySelector("#marketplaceSellDialog");
  if (!dialog) return;
  const species = dialog.querySelector<HTMLSelectElement>("[data-marketplace-sell-species]")?.value ?? "";
  const search = dialog.querySelector<HTMLInputElement>("[data-marketplace-sell-search]")?.value.trim().toLowerCase() ?? "";
  const items = Array.from(dialog.querySelectorAll<HTMLElement>("[data-sell-species]"));
  for (const item of items) item.hidden = Boolean(species && species !== item.dataset.sellSpecies)
    || Boolean(search && !`${item.dataset.sellName} ${item.dataset.sellSpecies}`.includes(search));
  const visible = items.filter(item => !item.hidden).length;
  const result = dialog.querySelector("[data-marketplace-sell-results]");
  if (result) result.textContent = `${visible} of ${items.length} dinosaurs shown`;
  const empty = dialog.querySelector<HTMLElement>("[data-marketplace-sell-empty]");
  if (empty) empty.hidden = visible > 0 || items.length === 0;
}

function applyMarketplaceFilters(): void {
  const grids = document.querySelectorAll<HTMLElement>("[data-marketplace-grid]");
  const species = document.querySelector<HTMLSelectElement>("select[data-marketplace-filter='species']")?.value ?? "";
  const primeOnly = document.querySelector<HTMLInputElement>("input[data-marketplace-filter='prime']")?.checked ?? false;
  const sort = document.querySelector<HTMLSelectElement>("[data-marketplace-sort]")?.value ?? "ending";
  let total = 0;
  let shown = 0;
  for (const grid of Array.from(grids)) {
  const cards = Array.from(grid.querySelectorAll<HTMLElement>("[data-marketplace-card]"));
  cards.forEach((card) => {
    card.hidden = Boolean(species && card.dataset.species !== species)
      || Boolean(primeOnly && card.dataset.prime !== "true")
      || Boolean(marketplaceMineOnly && card.dataset.own !== "true");
  });
  const visible = cards.filter(card => !card.hidden).length;
  total += cards.length;
  shown += visible;
  let empty = grid.querySelector<HTMLElement>("[data-marketplace-filter-empty]");
  if (!empty && cards.length) {
    empty = document.createElement("p");
    empty.className = "marketplace-empty panel";
    empty.dataset.marketplaceFilterEmpty = "";
    empty.textContent = "No listings match these filters. Try All species, or switch off Prime only / My listings.";
    grid.append(empty);
  }
  if (empty) empty.hidden = visible > 0 || cards.length === 0;
  cards.sort((left, right) => {
    if (sort === "price-low") return Number(left.dataset.price) - Number(right.dataset.price);
    if (sort === "price-high") return Number(right.dataset.price) - Number(left.dataset.price);
    if (sort === "growth-high") return Number(right.dataset.growth) - Number(left.dataset.growth);
    if (sort === "recent") return new Date(right.dataset.created ?? 0).getTime() - new Date(left.dataset.created ?? 0).getTime();
    return new Date(left.dataset.expires ?? 0).getTime() - new Date(right.dataset.expires ?? 0).getTime();
  }).forEach((card) => grid.append(card));
  }
  const result = document.querySelector("[data-marketplace-filter-status]");
  if (result) result.textContent = `${shown} of ${total} listings shown · Filters apply to auctions and Buy It Now`;
  const emptyMarket = document.querySelector<HTMLElement>("[data-marketplace-no-listings]");
  if (emptyMarket) emptyMarket.hidden = total > 0 || document.querySelector<HTMLElement>("[data-auction-market]")?.dataset.loaded !== "true";
}

function selectMarketplaceTab(tab: HTMLButtonElement): void {
  const dialog = tab.closest<HTMLDialogElement>(".marketplace-detail-dialog");
  if (!dialog || !tab.dataset.marketplaceTab) return;
  dialog.querySelectorAll<HTMLButtonElement>("[data-marketplace-tab]").forEach((button) => {
    const selected = button === tab;
    button.setAttribute("aria-selected", String(selected));
    button.tabIndex = selected ? 0 : -1;
  });
  dialog.querySelectorAll<HTMLElement>("[data-marketplace-tab-panel]").forEach((panel) => {
    panel.hidden = panel.dataset.marketplaceTabPanel !== tab.dataset.marketplaceTab;
  });
}

function updateCooldownElements(elementSelector: string, buttonSelector: string, availableText: string): void {
  document.querySelectorAll<HTMLElement>(elementSelector).forEach((element) => {
    const endsAt = element.dataset.cooldownEndsAt;
    const button = element.closest<HTMLButtonElement>(buttonSelector);
    if (!endsAt) {
      element.textContent = availableText;
      if (button) button.disabled = false;
      return;
    }

    const remainingSeconds = Math.max(0, Math.ceil((new Date(endsAt).getTime() - Date.now()) / 1_000));
    if (remainingSeconds <= 0) {
      element.textContent = availableText;
      delete element.dataset.cooldownEndsAt;
      if (button) button.disabled = false;
      return;
    }

    const hours = Math.floor(remainingSeconds / 3_600);
    const minutes = Math.floor((remainingSeconds % 3_600) / 60);
    const seconds = remainingSeconds % 60;
    element.textContent = hours > 0
      ? `Ready in ${hours}h ${minutes}m`
      : `Ready in ${minutes}m ${seconds}s`;
    if (button) button.disabled = true;
  });
}

function showBugReportDialog(): void {
  const dialog = document.querySelector<HTMLDialogElement>("#bugReportDialog");
  if (!dialog) return;
  if (!dialog.open) dialog.showModal();
  dialog.querySelector<HTMLInputElement>("[name='summary']")?.focus();
}

function closeBugReportDialog(): void {
  const dialog = document.querySelector<HTMLDialogElement>("#bugReportDialog");
  if (dialog?.open) dialog.close();
}

function showStoreMutationDialog(button: HTMLElement): void {
  const catalogItemId = button.dataset.catalogItemId?.trim() ?? "";
  const itemName = button.dataset.storeItemName?.trim() ?? "";
  const species = button.dataset.storeItemSpecies?.trim() ?? "";
  const price = Number(button.dataset.storeItemPrice);
  const dialog = document.querySelector<HTMLDialogElement>("#storeMutationDialog");
  const options = dialog?.querySelector<HTMLElement>("[data-store-mutation-options]");
  if (!dialog || !options || !catalogItemId || !itemName || !species || !Number.isFinite(price)) {
    setStatus("This dinosaur cannot be configured right now. Refresh the store and try again.");
    return;
  }

  pendingStoreDinoSelection = { catalogItemId, itemName, species, price };
  const title = dialog.querySelector<HTMLElement>("[data-store-mutation-title]");
  const speciesLabel = dialog.querySelector<HTMLElement>("[data-store-mutation-species]");
  if (title) title.textContent = `Configure ${itemName}`;
  if (speciesLabel) speciesLabel.textContent = species;
  options.innerHTML = availableStoreMutationsForSpecies(species)
    .map((mutation) => `
      <label class="store-mutation-option">
        <input type="checkbox" value="${escapePortalText(mutation.name)}" data-store-mutation-option />
        <span><strong>${escapePortalText(mutation.name)}</strong><small>${escapePortalText(mutation.description)}</small></span>
      </label>`)
    .join("");
  updateStoreMutationSelection();
  const submit = dialog.querySelector<HTMLButtonElement>("[data-store-mutation-submit]");
  if (submit) submit.textContent = `Purchase for ${formatNumber(price)} ${currentSnapshot.currency.points}`;
  if (!dialog.open) dialog.showModal();
  options.querySelector<HTMLInputElement>("[data-store-mutation-option]")?.focus();
}

function closeStoreMutationDialog(): void {
  pendingStoreDinoSelection = undefined;
  const dialog = document.querySelector<HTMLDialogElement>("#storeMutationDialog");
  if (dialog?.open) dialog.close();
}

function updateStoreMutationSelection(changed?: HTMLInputElement): void {
  const dialog = document.querySelector<HTMLDialogElement>("#storeMutationDialog");
  if (!dialog) return;
  const choices = Array.from(dialog.querySelectorAll<HTMLInputElement>("[data-store-mutation-option]"));
  let selected = choices.filter((choice) => choice.checked);
  if (selected.length > 3 && changed) {
    changed.checked = false;
    selected = choices.filter((choice) => choice.checked);
  }
  choices.forEach((choice) => {
    choice.disabled = selected.length === 3 && !choice.checked;
  });
  const count = dialog.querySelector<HTMLElement>("[data-store-mutation-count]");
  if (count) count.textContent = `${selected.length} / 3 selected`;
  const submit = dialog.querySelector<HTMLButtonElement>("[data-store-mutation-submit]");
  if (submit) submit.disabled = selected.length !== 3;
}

async function submitStoreDinoPurchase(form: HTMLFormElement): Promise<void> {
  const selection = pendingStoreDinoSelection;
  if (!selection) {
    closeStoreMutationDialog();
    return;
  }
  const mutations = Array.from(form.querySelectorAll<HTMLInputElement>("[data-store-mutation-option]:checked"))
    .map((choice) => choice.value);
  if (mutations.length !== 3) {
    updateStoreMutationSelection();
    return;
  }

  const submit = form.querySelector<HTMLButtonElement>("[data-store-mutation-submit]");
  if (submit) {
    submit.disabled = true;
    submit.classList.add("is-busy");
    submit.setAttribute("aria-busy", "true");
  }
  portalActionInFlight = true;
  try {
    setStatus("Purchasing dinosaur with the selected mutations...");
    const fingerprint = `${userId}:${selection.catalogItemId}:${mutations.join("|")}`;
    if (!pendingStorePurchase || pendingStorePurchase.fingerprint !== fingerprint) {
      pendingStorePurchase = { fingerprint, requestId: crypto.randomUUID() };
    }
    const context = { userId, roleIds, serverId: currentSnapshot.server.id };
    const result = await apiClient.buyStoreItem(
      context,
      selection.catalogItemId,
      pendingStorePurchase.requestId,
      mutations
    );
    pendingStorePurchase = undefined;
    const finalStatus = result.operation
      ? await waitForBridgeStatus(result.operation.id, "Store purchase")
      : `${selection.species} purchased with ${mutations.join(", ")}. Check your garage for delivery.`;
    closeStoreMutationDialog();
    await boot();
    setStatus(finalStatus);
  } catch (error) {
    setStatus(`Purchase failed: ${error instanceof Error ? error.message : String(error)}`);
    updateStoreMutationSelection();
  } finally {
    portalActionInFlight = false;
    if (submit) {
      submit.classList.remove("is-busy");
      submit.removeAttribute("aria-busy");
    }
  }
}

function showProfileDialog(): void {
  const dialog = document.querySelector<HTMLDialogElement>("#profileDialog");
  if (dialog && !dialog.open) dialog.showModal();
  void loadPlayerModerationStrikes(true);
}

function closeProfileDialog(): void {
  const dialog = document.querySelector<HTMLDialogElement>("#profileDialog");
  if (dialog?.open) dialog.close();
}

async function submitBugReport(form: HTMLFormElement): Promise<void> {
  if (!form.reportValidity()) return;
  const summary = (form.elements.namedItem("summary") as HTMLInputElement | null)?.value.trim() ?? "";
  const details = (form.elements.namedItem("details") as HTMLTextAreaElement | null)?.value.trim() ?? "";
  const steps = (form.elements.namedItem("steps") as HTMLTextAreaElement | null)?.value.trim() ?? "";
  const submitButton = form.querySelector<HTMLButtonElement>("button[type='submit']");
  if (submitButton) {
    submitButton.disabled = true;
    submitButton.textContent = "Sending...";
  }

  try {
    const result = await apiClient.createBugReport({
      creatorUserId: userId,
      summary,
      details,
      ...(steps ? { steps } : {}),
      page: window.location.pathname
    });
    form.reset();
    closeBugReportDialog();
    setStatus(result.discordDelivery === "delivered"
      ? `Bug report sent. Reference: ${result.ticketId}.`
      : `Bug report saved. Reference: ${result.ticketId}. Discord delivery is pending.`);
  } catch (error) {
    setStatus(`Bug report failed: ${error instanceof Error ? error.message : String(error)}`);
  } finally {
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.textContent = "Send report";
    }
  }
}

function restoreMapLayers(): void {
  document.querySelectorAll<HTMLInputElement>("[data-map-layer-toggle]").forEach((toggle) => {
    const layer = toggle.dataset.mapLayerToggle ?? "";
    let enabled = toggle.checked;
    try {
      const stored = window.localStorage.getItem(`isle-map-layer:${layer}`);
      if (stored !== null && !(layer === "heatmap" && searchParams.get("heatmapDemo") === "1")) enabled = stored === "true";
    } catch {
      // Storage can be unavailable in strict browser privacy modes.
    }
    toggle.checked = enabled;
    setMapLayer(layer, enabled, false);
  });
  const monochromeToggle = document.querySelector<HTMLInputElement>("[data-map-monochrome-toggle]");
  if (monochromeToggle) {
    let enabled = monochromeToggle.checked;
    try {
      const stored = window.localStorage.getItem("isle-map-monochrome");
      if (stored !== null) enabled = stored === "true";
    } catch {
      // Storage can be unavailable in strict browser privacy modes.
    }
    monochromeToggle.checked = enabled;
    setMapMonochrome(enabled, false);
  }
}

function setMapMonochrome(enabled: boolean, persist: boolean): void {
  setLiveMapMonochrome(enabled);
  if (!persist) return;
  try {
    window.localStorage.setItem("isle-map-monochrome", String(enabled));
  } catch {
    // The control still works for the current view without persistence.
  }
}

function setMapLayer(layer: string, enabled: boolean, persist: boolean): void {
  if (!layer) return;
  setLiveMapLayerVisibility(layer, enabled);
  document.querySelectorAll<HTMLElement>("[data-map-layer]").forEach((element) => {
    if (element.dataset.mapLayer === layer) element.hidden = !enabled;
  });
  if (persist) {
    try {
      window.localStorage.setItem(`isle-map-layer:${layer}`, String(enabled));
    } catch {
      // The control still works for the current view without persistence.
    }
  }
}

function updateMapRefreshStatus(): void {
  const state = document.querySelector<HTMLElement>("[data-telemetry-state]");
  const label = document.querySelector<HTMLElement>("[data-telemetry-label]");
  if (!state || !label) return;
  state.classList.remove("stale", "unavailable");
  state.classList.add("live");
  label.textContent = `Map refreshed ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`;
}

async function handlePortalAction(button: HTMLElement): Promise<void> {
  clearPortalConfirmation();
  portalActionInFlight = true;
  const wasDisabled = button instanceof HTMLButtonElement ? button.disabled : false;
  button.classList.add("is-busy");
  button.setAttribute("aria-busy", "true");
  if (button instanceof HTMLButtonElement) button.disabled = true;
  const context = {
    userId,
    roleIds,
    serverId: currentSnapshot.server.id
  };
  const action = button.dataset.portalAction;

  try {
    let finalStatus = "Action accepted.";
    setStatus("Submitting action...");
    if (action === "case-timeline-more") {
      const timeline = button.closest<HTMLElement>("[data-case-activity]");
      if (timeline) {
        button.dataset.caseVisible = String(Number(button.dataset.caseVisible ?? "20") + 20);
        applyCaseTimelineFilters(timeline);
      }
      setStatus("Case timeline updated.");
      return;
    } else if (action === "case-timeline-clear") {
      const timeline = button.closest<HTMLElement>("[data-case-activity]");
      if (timeline) {
        const source = timeline.querySelector<HTMLSelectElement>("[data-case-filter-source]");
        const from = timeline.querySelector<HTMLInputElement>("[data-case-filter-from]");
        const to = timeline.querySelector<HTMLInputElement>("[data-case-filter-to]");
        if (source) source.value = "all";
        if (from) from.value = "";
        if (to) to.value = "";
        const more = timeline.querySelector<HTMLButtonElement>("[data-portal-action=\"case-timeline-more\"]");
        if (more) more.dataset.caseVisible = "20";
        applyCaseTimelineFilters(timeline);
      }
      setStatus("Case timeline filters cleared.");
      return;
    } else if (action === "mod-logs-view" && (button.dataset.logView === "gameplay" || button.dataset.logView === "commands" || button.dataset.logView === "audit")) {
      setModLogView(button.dataset.logView);
      await handleModLogRefresh(false);
      return;
    } else if (action === "mod-logs-refresh") {
      await handleModLogRefresh(false);
      return;
    } else if (action === "mod-logs-more") {
      await handleModLogRefresh(true);
      return;
    } else if (action === "server-health-refresh") {
      await Promise.all([handleServerHealthLoad(false), handleCustomAiMapLoad(false)]);
      return;
    } else if (action === "release-centre-refresh") {
      await handleReleaseCentreLoad();
      return;
    } else if (action === "growth-centre-refresh") {
      await handleGrowthCentreLoad();
      return;
    } else if (action === "growth-centre-range") {
      const requested = Number(button.dataset.growthDays);
      if (requested !== 7 && requested !== 30 && requested !== 90) throw new Error("That Growth Centre range is unavailable.");
      growthCentreDays = requested;
      await handleGrowthCentreLoad();
      return;
    } else if (action === "release-signoff" && button.dataset.releaseCheckKey && button.dataset.releaseResult) {
      await handleReleaseSignoff(button);
      return;
    } else if (action === "custom-ai-map-refresh") {
      await handleCustomAiMapLoad(false);
      return;
    } else if (action === "custom-ai-map-fit") {
      fitCustomAiMap();
      return;
    } else if (action === "marketplace-show-mine") {
      marketplaceMineOnly = !marketplaceMineOnly;
      button.classList.toggle("is-active", marketplaceMineOnly);
      button.textContent = marketplaceMineOnly ? "Show all listings" : "My listings";
      applyMarketplaceFilters();
      setStatus(marketplaceMineOnly ? "Showing your active listings." : "Showing all active listings.");
      return;
    } else if (action === "garage-park") {
      const result = await apiClient.parkLiveDino(context);
      finalStatus = await waitForParkingStatus(result.operation.id);
    } else if (action === "live-dino-observe") {
      const result = await apiClient.observeLiveDino(context, crypto.randomUUID());
      finalStatus = await waitForBridgeStatus(result.operation.id, "Live dinosaur detection");
    } else if (action === "live-dino-slay") {
      const result = await apiClient.slayLiveDino(context, crypto.randomUUID());
      finalStatus = await waitForBridgeStatus(result.operation.id, "Slay");
    } else if (action === "corpse-drop") {
      const catalogItemId = document.querySelector<HTMLSelectElement>("#corpseProduct")?.value || undefined;
      const result = await apiClient.requestCorpseDrop(context, catalogItemId);
      if (!result.eligibility.eligible) {
        finalStatus = `Corpse drop unavailable: ${formatCorpseDenialReasons(result.eligibility.reasons)}.`;
      } else if (result.operation) {
        finalStatus = await waitForBridgeStatus(result.operation.id, "Corpse drop");
      } else {
        finalStatus = "Corpse drop accepted. No bridge operation was returned yet.";
      }
    } else if (action === "work-reward") {
      const reward = await apiClient.claimWorkReward(context);
      finalStatus = `Work reward claimed: +${formatNumber(reward.ledgerEntry.amount)} ${currentSnapshot.currency.points}. New balance: ${formatNumber(reward.wallet.points)}.`;
    } else if (action === "stick-transfer-open") {
      await showStickTransferDialog();
      return;
    } else if (action === "event-wheel-spin") {
      await handleEventWheelSpin(button);
      return;
    } else if (action === "event-wheel-refresh") {
      await handleEventWheelLoad();
      return;
    } else if (action === "event-prime-token-claim" && button.dataset.primeTokenId) {
      await handlePrimeTokenClaim(button, button.dataset.primeTokenId);
      return;
    } else if (action === "gambling-game-select" && button.dataset.gamblingGame) {
      await selectGamblingGame(button.dataset.gamblingGame);
      return;
    } else if (action === "raptor-camera") {
      const stage = document.querySelector<HTMLElement>("[data-raptor-3d-stage]");
      if (stage) { const overview = stage.dataset.camera !== "overview"; stage.dataset.camera = overview ? "overview" : "trackside"; button.setAttribute("aria-pressed", String(overview)); button.textContent = overview ? "Trackside camera" : "Overview camera"; }
      return;
    } else if (action === "raptor-expand") {
      const track = document.querySelector<HTMLElement>(".raptor-track-shell");
      if (document.fullscreenElement) await document.exitFullscreen(); else await track?.requestFullscreen();
      return;
    } else if (action === "blackjack-wager-set" && button.dataset.blackjackWagerValue) {
      setBlackjackWager(Number(button.dataset.blackjackWagerValue), button);
      return;
    } else if (action === "blackjack-deal") {
      await handleBlackjackDeal();
      return;
    } else if (action === "blackjack-hit" || action === "blackjack-stand") {
      await handleBlackjackAction(action === "blackjack-hit" ? "hit" : "stand");
      return;
    } else if (action === "blackjack-new-hand") {
      if (blackjackState) {
        blackjackState = { ...blackjackState, activeHand: undefined };
        renderBlackjackWorkspace("wager");
      }
      return;
    } else if (action === "roulette-bet-select" && button.dataset.rouletteBetType) {
      addRouletteBet(button.dataset.rouletteBetType as RouletteBetType, button.dataset.rouletteBetValue);
      return;
    } else if (action === "roulette-wager-set" && button.dataset.rouletteWagerValue) {
      setRouletteWager(Number(button.dataset.rouletteWagerValue), button);
      return;
    } else if (action === "roulette-repeat") {
      repeatLastRouletteSlip();
      document.querySelector<HTMLElement>("[data-roulette-table]")?.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "center" });
      return;
    } else if (action === "roulette-undo") {
      undoRouletteBet();
      return;
    } else if (action === "roulette-clear") {
      clearRouletteBets();
      return;
    } else if (action === "roulette-bet-remove" && button.dataset.rouletteBetKey) {
      removeRouletteBet(button.dataset.rouletteBetKey);
      return;
    } else if (action === "roulette-spin") {
      await handleRouletteSpin();
      return;
    } else if (action === "raptor-racer-select" && button.dataset.raptorEntrantId) {
      selectRaptorRacer(button.dataset.raptorEntrantId, button.dataset.raptorName ?? "Raptor", button);
      return;
    } else if (action === "raptor-bet") {
      await handleRaptorRaceBet();
      return;
    } else if (action === "skin-save") {
      const mode = document.querySelector<HTMLElement>("[data-skin-creator]")?.dataset.skinEditorMode;
      if (mode !== "offline" && currentSnapshot.skinCapture?.stale) throw new Error("Live dinosaur detection is reconnecting. Your palette is preserved; wait for the live target before saving.");
      await handleSkinSave(context);
      finalStatus = mode === "offline" ? "Workshop skin saved. Open it here or apply it later from Live specimen mode." : "Skin saved. You can reuse its palette on another live dinosaur.";
    } else if (action === "skin-apply-current") {
      if (document.querySelector<HTMLElement>("[data-skin-creator]")?.dataset.skinEditorMode !== "live") throw new Error("Switch to Live specimen mode before applying a skin in-game.");
      if (currentSnapshot.skinCapture?.stale) throw new Error("Live dinosaur detection is reconnecting. Your palette is preserved; wait for the live target before applying.");
      const editorSpecies = document.querySelector<HTMLSelectElement>("[data-skin-pattern]")?.dataset.skinSpecies;
      if (!isLiveSkinEditorSpeciesCurrent(editorSpecies, currentSnapshot.liveDino.species)) {
        throw new Error("Your live dinosaur changed while the skin workshop was open. Refresh the page before applying this skin.");
      }
      const result = await apiClient.applySkinColors(context, currentSnapshot.liveDino.species, currentSnapshot.skinCapture?.targetToken, {
        ...readSkinStructure(),
        ...readSkinEffects(),
        colors: readSkinColors()
      });
      finalStatus = await waitForBridgeStatus(result.operation.id, "Skin application");
    } else if (action === "skin-apply" && button.dataset.presetId) {
      const result = await apiClient.applySkinPreset(context, button.dataset.presetId, currentSnapshot.liveDino.species, currentSnapshot.skinCapture?.targetToken);
      finalStatus = await waitForBridgeStatus(result.operation.id, "Skin application");
    } else if (action === "skin-apply-palette" && button.dataset.presetId) {
      const result = await apiClient.applySkinPalette(context, button.dataset.presetId, currentSnapshot.liveDino.species, currentSnapshot.skinCapture?.targetToken);
      finalStatus = await waitForBridgeStatus(result.operation.id, "Palette application");
    } else if (action === "skin-preview-preset" && button.dataset.presetColors) {
      switchSkinWorkshopMode("offline");
      if (button.dataset.presetSpecies) setSkinWorkshopSpecies(button.dataset.presetSpecies, true);
      previewSavedSkinPalette(button.dataset.presetColors);
      previewSavedSkinStructure(button);
      setStatus("Saved skin opened in the offline workshop. Nothing in-game has changed.");
      return;
    } else if (action === "skin-load-look" && button.dataset.presetColors) {
      previewSavedSkinPalette(button.dataset.presetColors);
      previewSavedSkinStructure(button);
      setStatus("Field preset loaded into the current workshop. Review it before saving or applying.");
      return;
    } else if (action === "skin-preview-diagnostic" && button.dataset.presetColors) {
      previewSavedSkinPalette(button.dataset.presetColors);
      setStatus("Channel check loaded into the preview only. Compare each labelled colour before applying anything in game.");
      return;
    } else if (action === "skin-photo-download") {
      downloadSkinPhotoCard();
      setStatus("Skin photo card downloaded.");
      return;
    } else if (action === "skin-livery-load") {
      await loadPackLivery();
      setStatus("Pack livery loaded into the editor. Review it before applying.");
      return;
    } else if (action === "skin-randomize-all") {
      randomizeSkinColours();
      setStatus("All available colour channels were randomised in the preview. Press Apply skin to use them in game.");
      return;
    } else if (action === "skin-randomize-channel" && button.dataset.skinRandomizeField) {
      randomizeSkinColours(button.dataset.skinRandomizeField);
      setStatus("Colour randomised in the preview. Press Apply skin to use it in game.");
      return;
    } else if (action === "skin-delete-preset" && button.dataset.presetId) {
      await apiClient.deleteSkinPreset(context, button.dataset.presetId);
      finalStatus = "Saved palette deleted.";
    } else if (action === "skin-review-refresh") {
      await handleSkinReviewLoad();
      setStatus("Restricted palette review queue refreshed.");
      return;
    } else if (action === "skin-review-decision" && button.dataset.presetId && button.dataset.skinDecision) {
      await handleSkinReviewDecision(button.dataset.presetId, button.dataset.skinDecision);
      return;
    } else if (action === "discord-disconnect") {
      await apiClient.disconnectDiscord();
      finalStatus = "Discord disconnected. Steam and game data remain linked.";
    } else if (action === "logout") {
      await apiClient.logout();
      window.location.assign("/portal/");
      return;
    } else if (action === "garage-redeem" && button.dataset.garageId) {
      const result = await apiClient.redeemGarageDino(context, button.dataset.garageId);
      finalStatus = await waitForBridgeStatus(result.operation.id, "Retrieval");
    } else if (action === "garage-sell" && button.dataset.garageId) {
      await apiClient.sellGarageDino(context, button.dataset.garageId, "Player sold garage dinosaur from portal.");
      finalStatus = "Garage dinosaur sold. Refreshing snapshot...";
    } else if (action === "garage-discard" && button.dataset.garageId) {
      await apiClient.discardGarageDino(context, button.dataset.garageId, "Player permanently deleted garage dinosaur from portal.");
      finalStatus = "Garage dinosaur deleted.";
    } else if (action === "garage-rename" && button.dataset.garageId) {
      const displayName = button.closest(".garage-item")?.querySelector<HTMLInputElement>("[data-garage-name-input]")?.value.trim() ?? "";
      if (!displayName) throw new Error("Enter a garage name before saving.");
      await apiClient.renameGarageDino(context, button.dataset.garageId, displayName);
      finalStatus = `Garage dinosaur renamed to ${displayName}.`;
    } else if (action?.startsWith("auction-") && button.dataset.auctionId) {
      await handleAuctionAction(button, context.userId);
      return;
    } else if (action === "marketplace-buy" && button.dataset.listingId) {
      await apiClient.buyMarketplaceListing(context, button.dataset.listingId);
      finalStatus = "Marketplace purchase completed. Refreshing snapshot...";
    } else if (action === "community-raffle-enter" && button.dataset.raffleId) {
      const result = await apiClient.enterCommunityRaffle(context.userId, button.dataset.raffleId);
      finalStatus = result.alreadyEntered ? "You already have your one raffle entry." : "You’re entered. This verified account now has its one raffle entry.";
    } else if (action === "marketplace-list-garage" && button.dataset.garageId) {
      await handleMarketplaceListGarage(context, button.dataset.garageId, button.dataset.defaultTitle);
      return;
    } else if (action === "marketplace-cancel" && button.dataset.listingId) {
      await apiClient.cancelMarketplaceListing(context, button.dataset.listingId, "Marketplace listing cancelled from portal.");
      finalStatus = "Marketplace listing cancelled. Refreshing snapshot...";
    } else if (action === "store-buy" && button.dataset.catalogItemId) {
      const fingerprint = `${context.userId}:${button.dataset.catalogItemId}`;
      if (!pendingStorePurchase || pendingStorePurchase.fingerprint !== fingerprint) {
        pendingStorePurchase = { fingerprint, requestId: crypto.randomUUID() };
      }
      const result = await apiClient.buyStoreItem(context, button.dataset.catalogItemId, pendingStorePurchase.requestId);
      pendingStorePurchase = undefined;
      finalStatus = result.operation
        ? await waitForBridgeStatus(result.operation.id, "Store purchase")
        : "Store purchase completed. Check your garage for delivery.";
    } else if (action === "friend-teleport-create") {
      showFriendTeleportResult("Checking your live dinosaur...", "pending");
      const result = await apiClient.createFriendTeleportCode(context.userId);
      showFriendTeleportCode(result.code?.code, result.code?.expiresAt);
      showFriendTeleportResult(result.code ? "Code ready. Your friend can enter it below." : "A code could not be issued yet.", result.code ? "success" : "error");
      setStatus(result.code ? "Rendezvous code ready. Share it privately with one friend." : "Rendezvous code is not available yet.");
      return;
    } else if (action === "friend-teleport-copy") {
      const code = document.querySelector<HTMLElement>("[data-friend-teleport-code-value]")?.textContent?.trim();
      if (!code) throw new Error("Create a rendezvous code first.");
      await navigator.clipboard.writeText(code);
      setStatus("Rendezvous code copied.");
      return;
    } else if (action === "friend-teleport-redeem") {
      const code = document.querySelector<HTMLInputElement>("[data-friend-teleport-input]")?.value.trim();
      if (!code) throw new Error("Enter your friend's rendezvous code.");
      showFriendTeleportResult("Checking both players and dinosaurs...", "pending");
      const result = await apiClient.redeemFriendTeleportCode(context.userId, code);
      finalStatus = await waitForBridgeStatus(result.operation.id, "Friend rendezvous");
      showFriendTeleportResult(finalStatus, /failed|denied|review/i.test(finalStatus) ? "error" : "success");
    } else if (action === "store-corpse-buy" && button.dataset.catalogItemId) {
      const result = await apiClient.requestCorpseDrop(context, button.dataset.catalogItemId);
      finalStatus = result.operation
        ? await waitForBridgeStatus(result.operation.id, "Corpse purchase")
        : "Corpse purchase accepted.";
    } else if (action === "corpse-product-save") {
      await handleCorpseProductSave(context);
      return;
    } else if (action === "store-product-save") {
      await handleStoreProductSave(context);
      return;
    } else if (action === "garage-restore" && button.dataset.targetUserId && button.dataset.garageId) {
      await handleGarageRestore(button.dataset.targetUserId, button.dataset.garageId);
      return;
    } else if (action === "garage-death-restore" && button.dataset.targetUserId && button.dataset.deathEventId) {
      await handleGarageDeathRestore(button.dataset.targetUserId, button.dataset.deathEventId);
      return;
    } else if (action === "garage-migrate" && button.dataset.targetUserId) {
      await handleGarageMigration(button.dataset.targetUserId);
      return;
    } else if (action === "garage-mutation-correct" && button.dataset.targetUserId) {
      await handleMutationCorrection(button, button.dataset.targetUserId);
      return;
    } else if (action === "garage-mutation-inspect" && button.dataset.targetUserId) {
      await handleMutationInspection(button, button.dataset.targetUserId, button.dataset.mutationMode === "live" ? "live" : "stored", button.dataset.garageId);
      return;
    } else if (action === "moderation-case-create" && button.dataset.targetUserId) {
      await handleModerationCaseCreate(button.dataset.targetUserId);
      return;
    } else if (action === "moderation-strike-create" && button.dataset.targetUserId) {
      await handleModerationStrikeCreate(button.dataset.targetUserId);
      return;
    } else if (action === "weather-update") {
      await handleWeatherUpdate();
      return;
    } else if (action === "profile-lookup") {
      await handleProfileLookup();
      return;
    } else if (action === "event-credit-grant") {
      await handleEventCreditGrant();
      return;
    } else if (action === "staff-points-grant" && button.dataset.targetUserId) {
      await handleStaffPointGrant(button.dataset.targetUserId);
      return;
    } else if (action === "corpse-cooldown-reset" && button.dataset.targetUserId) {
      await handleCorpseCooldownReset(button.dataset.targetUserId, button.dataset.serverId ?? currentSnapshot.server.id);
      return;
    } else if (action === "log-samples") {
      await handleLogSamples();
      return;
    } else if (action === "audit-events") {
      await handleAuditEvents();
      return;
    } else if (action === "discord-log-routing-load") {
      await handleDiscordLogRoutingLoad();
      return;
    } else if (action === "discord-log-routing-save") {
      await handleDiscordLogRoutingSave();
      return;
    } else if (action === "discord-log-routing-test") {
      await handleDiscordLogRoutingTest(button.dataset.channelId);
      return;
    } else if (action === "discord-log-routing-delete" && button.dataset.routeId) {
      await handleDiscordLogRoutingDelete(button.dataset.routeId);
      return;
    } else if (action === "discord-log-routing-edit" && button.dataset.channelId) {
      populateDiscordLogRoute(button.dataset.channelId, button.dataset.categories ?? "", button.dataset.enabled === "true");
      return;
    } else if (action === "cross-chat-load") {
      await handleCrossSpeciesChatLoad();
      return;
    } else if (action === "cross-chat-pair-add") {
      addCrossSpeciesChatPair();
      return;
    } else if (action === "cross-chat-pair-remove" && button.dataset.pairIndex) {
      removeCrossSpeciesChatPair(Number(button.dataset.pairIndex));
      return;
    } else if (action === "cross-chat-save") {
      await handleCrossSpeciesChatSave();
      return;
    } else if (action === "ambient-ai-load") {
      await handleAmbientAiLoad();
      return;
    } else if (action === "ambient-ai-save") {
      await handleAmbientAiSave();
      return;
    } else if (action === "corpse-cleanup-save") {
      await handleCorpseCleanupSave();
      return;
    } else if (action === "ecosystem-zones-load") {
      await handleEcosystemZonesLoad();
      return;
    } else if (action === "ecosystem-zone-add-ai") {
      addEcosystemZone("ai");
      return;
    } else if (action === "ecosystem-zone-add-plant") {
      addEcosystemZone("plant");
      return;
    } else if (action === "ecosystem-zone-select" && button.dataset.zoneId) {
      selectEcosystemZone(button.dataset.zoneId);
      return;
    } else if (action === "ecosystem-zone-delete" && button.dataset.zoneId) {
      deleteEcosystemZone(button.dataset.zoneId);
      return;
    } else if (action === "ecosystem-zone-species-add") {
      addEcosystemZoneSpecies();
      return;
    } else if (action === "ecosystem-zone-species-remove" && button.dataset.speciesIndex) {
      removeEcosystemZoneSpecies(Number(button.dataset.speciesIndex));
      return;
    } else if (action === "ecosystem-zones-save") {
      await handleEcosystemZonesSave();
      return;
    } else if (action === "staff-credentials-load") {
      await handleStaffCredentialsLoad();
      return;
    } else if (action === "staff-credentials-add") {
      addStaffCredential();
      return;
    } else if (action === "staff-credentials-add-self" && button.dataset.steamId) {
      addStaffCredential(button.dataset.steamId);
      return;
    } else if (action === "staff-credentials-remove" && button.dataset.steamId) {
      removeStaffCredential(button.dataset.steamId);
      return;
    } else if (action === "staff-credentials-save") {
      await handleStaffCredentialsSave();
      return;
    } else if (action === "moderation-policy-load") {
      await handleModerationPolicyLoad();
      return;
    } else if (action === "moderation-policy-save") {
      await handleModerationPolicySave();
      return;
    } else if (action === "garage-capacity-load") {
      await handleGarageCapacityLoad();
      return;
    } else if (action === "garage-capacity-save") {
      await handleGarageCapacitySave();
      return;
    } else if (action === "work-policy-save") {
      await handleWorkRewardPolicySave();
      return;
    } else if (action === "point-balance-load") {
      await handlePointBalancePolicyLoad();
      return;
    } else if (action === "point-balance-save") {
      await handlePointBalancePolicySave();
      return;
    } else if (action === "patreon-role-load") {
      await handleDiscordPatreonRolePolicyLoad();
      return;
    } else if (action === "patreon-role-save") {
      await handleDiscordPatreonRolePolicySave();
      return;
    } else if (action === "runtime-readiness-load") {
      await handleRuntimeReadinessLoad();
      return;
    } else if (action === "telemetry-health-load") {
      await handleTelemetryHealthLoad();
      return;
    } else if (action === "telemetry-retention-load") {
      await handleTelemetryRetentionLoad();
      return;
    } else if (action === "telemetry-retention-save") {
      await handleTelemetryRetentionSave();
      return;
    } else if (action === "configuration-snapshot-export") {
      await handleConfigurationSnapshotExport(context);
      return;
    } else if (action === "configuration-snapshot-validate") {
      await handleConfigurationSnapshotValidation(context);
      return;
    } else if (action === "access-preview-load") {
      await handleAccessControlPreviewLoad();
      return;
    } else if (action === "portal-access-load") {
      await handlePortalAccessSettingsLoad();
      return;
    } else if (action === "portal-access-save") {
      await handlePortalAccessSettingsSave();
      return;
    } else if (action === "staff-duty-toggle") {
      await handleStaffDutyToggle(button.dataset.onDuty === "true");
      return;
    } else if (action === "staff-performance-load") {
      await handleStaffPerformance();
      return;
    } else if (action === "event-wheel-prize-add") {
      addEventWheelPrizeRow();
      return;
    } else if (action === "event-wheel-prize-remove") {
      button.closest<HTMLElement>("[data-wheel-prize-row]")?.remove();
      updateEventWheelConfigTotal();
      setStatus("Prize removed from the unsaved wheel draft.");
      return;
    } else if (action === "event-wheel-config-save") {
      await handleEventWheelConfigSave();
      return;
    } else if (action === "event-wheel-opening-preset") {
      loadOpeningWeekendWheelPreset();
      return;
    } else if (action === "specimen-hatchery-open") {
      await handleSpecimenHatcheryOpen();
      return;
    } else if (action === "specimen-hatchery-close") {
      const hatchery = document.querySelector<HTMLElement>("#specimenHatchery");
      if (hatchery) hatchery.hidden = true;
      return;
    } else if (action === "specimen-hatchery-tier" && button.dataset.specimenTier) {
      activeSpecimenEggTier = button.dataset.specimenTier as SpecimenEggTier;
      renderCurrentSpecimenHatchery();
      return;
    } else if (action === "specimen-egg-open" && button.dataset.specimenEggId) {
      beginSpecimenIncubation(button.dataset.specimenEggId);
      return;
    } else if (action === "specimen-incubator-cancel") {
      renderCurrentSpecimenHatchery();
      return;
    } else if (action === "specimen-mutations-randomise") {
      randomiseSpecimenMutations();
      return;
    } else if (action === "event-campaign-save") {
      await handleEventCampaignSave();
      return;
    } else if (action === "blackjack-config-save") {
      await handleBlackjackConfigSave();
      return;
    } else if (action === "slots-config-save") {
      await handleSlotsConfigSave();
      return;
    } else if (action === "roulette-config-save") {
      await handleRouletteConfigSave();
      return;
    } else if (action === "raptor-config-save") {
      await handleRaptorRaceConfigSave();
      return;
    } else if (action === "announcement-publish") {
      await handleAnnouncementPublish();
      return;
    } else if (action === "giveaway-enter" && button.dataset.giveawayId) {
      const entered = await giveawayController.enter(button.dataset.giveawayId);
      setStatus(entered ? "You’re entered. Your ticket is saved — good luck!" : "Entry could not be confirmed. Check the giveaway card for details.");
      return;
    } else if (action === "giveaway-publish") {
      await giveawayController.publish();
      setStatus("Giveaway published. Entries are open and the draw is scheduled.");
      return;
    } else if (action === "raffle-preview") {
      await raffleManager.preview();
      return;
    } else if (action === "raffle-launch") {
      await raffleManager.launch();
      setStatus("Community raffle published. Listings and the automatic draw are saved.");
      return;
    } else if (action === "giveaway-archive" && button.dataset.giveawayId) {
      await giveawayController.archive(button.dataset.giveawayId);
      setStatus("Finished giveaway removed from display. Its winner and entry records are retained.");
      return;
    } else if (action === "giveaway-cancel" && button.dataset.giveawayId) {
      await giveawayController.cancel(button.dataset.giveawayId);
      setStatus("Giveaway cancelled. Players can see the reason.");
      return;
    } else if (action === "announcement-end" && button.dataset.announcementId) {
      await handleAnnouncementEnd(button.dataset.announcementId);
      return;
    } else if (action === "game-announcement-send") {
      await handleGameAnnouncementSend();
      return;
    } else if (action === "game-announcement-schedule-create") {
      await handleScheduledGameAnnouncementCreate();
      return;
    } else if (action === "game-announcement-schedule-disable" && button.dataset.scheduleId) {
      await handleScheduledGameAnnouncementDisable(button.dataset.scheduleId);
      return;
    } else if (action === "rulebook-load") {
      await handleRulebookLoad();
      return;
    } else if (action === "rule-create") {
      await handleRuleCreate();
      return;
    } else if (action === "rule-revise-load" && button.dataset.ruleId) {
      loadRuleRevisionForm(button.dataset.ruleId);
      return;
    } else if (action === "rule-form-clear") {
      clearRuleForm();
      return;
    } else if (action === "rule-status" && button.dataset.ruleId && (button.dataset.ruleStatusAction === "publish" || button.dataset.ruleStatusAction === "retire")) {
      await handleRuleStatus(button.dataset.ruleId, button.dataset.ruleStatusAction);
      return;
    } else if (action === "bridge-operations") {
      await handleBridgeOperations();
      return;
    } else if (action === "bridge-resolve" && button.dataset.operationId && isBridgeResolutionStatus(button.dataset.resolutionStatus)) {
      await handleBridgeResolve(button.dataset.operationId, button.dataset.resolutionStatus);
      return;
    } else if (action === "ticket-staff-create") {
      await handleStaffContactTicketCreate(button);
      return;
    } else if (action === "ticket-metrics") {
      await handleTicketMetrics();
      return;
    } else if (action === "notification-toggle") {
      togglePortalNotifications(button);
      return;
    } else if (action === "notification-read-all") {
      await markAllPortalNotificationsRead();
      return;
    } else if (action === "notification-open" && button.dataset.notificationId) {
      await openPortalNotification(button.dataset.notificationId, button.dataset.ticketId, button.dataset.notificationType, button.dataset.destinationPath);
      return;
    } else if (action === "ticket-notification-settings") {
      await showCaseNotificationDialog();
      return;
    } else if (action === "ticket-notification-close") {
      closeCaseNotificationDialog();
      return;
    } else if (action === "ticket-notification-save") {
      await saveCaseNotificationPreferences();
      return;
    } else if (action === "ticket-notification-read-all") {
      await markAllCaseNotificationsRead();
      return;
    } else if (action === "ticket-notification-open" && button.dataset.notificationId && button.dataset.ticketId) {
      await openCaseNotification(button.dataset.notificationId, button.dataset.ticketId);
      return;
    } else if (action === "ticket-bulk-apply") {
      await handleTicketBulkUpdate();
      return;
    } else if (action === "ticket-queue") {
      await handleTicketQueue(false);
      return;
    } else if (action === "ticket-queue-preset" && button.dataset.ticketPreset) {
      await handleTicketQueuePreset(button.dataset.ticketPreset);
      return;
    } else if (action === "ticket-back-queue") {
      const url = new URL(window.location.href);
      url.searchParams.delete("ticket");
      window.history.replaceState({}, "", `${url.pathname}${url.search}`);
      await initializeCaseDesk();
      return;
    } else if (action === "ticket-queue-more") {
      await handleTicketQueue(true);
      return;
    } else if (action === "ticket-detail" && button.dataset.ticketId) {
      await handleTicketDetail(button.dataset.ticketId);
      return;
    } else if (action === "ticket-conversation-toggle") {
      toggleCaseConversationDock();
      return;
    } else if (action === "ticket-conversation-expand") {
      toggleCaseConversationExpansion();
      return;
    } else if (action === "ticket-draft-canned" && button.dataset.ticketId) {
      insertCannedTicketDraft(button.dataset.ticketId);
      return;
    } else if (action === "ticket-draft-rule" && button.dataset.ticketId) {
      insertTicketRuleReference(button.dataset.ticketId);
      return;
    } else if (action === "ticket-reply" && button.dataset.ticketId) {
      await handleTicketReply(button.dataset.ticketId);
      return;
    } else if (action === "ticket-discord-room-open" && button.dataset.ticketId) {
      await handleTicketDiscordRoomOpen(button.dataset.ticketId);
      return;
    } else if (action === "ticket-claim" && button.dataset.ticketId) {
      await handleTicketClaim(button.dataset.ticketId);
      return;
    } else if (action === "ticket-operations-save" && button.dataset.ticketId) {
      await handleTicketOperations(button.dataset.ticketId, "save");
      return;
    } else if (action === "ticket-tags-save" && button.dataset.ticketId) {
      await handleTicketTags(button.dataset.ticketId);
      return;
    } else if (action === "ticket-unassign" && button.dataset.ticketId) {
      await handleTicketOperations(button.dataset.ticketId, "unassign");
      return;
    } else if (action === "ticket-status" && button.dataset.ticketId && button.dataset.ticketStatus) {
      await handleTicketStatus(button.dataset.ticketId, button.dataset.ticketStatus);
      return;
    } else if (action === "ticket-close" && button.dataset.ticketId) {
      await handleTicketClose(button.dataset.ticketId);
      return;
    } else if (action === "ticket-case-strike" && button.dataset.ticketId && button.dataset.targetUserId) {
      await handleTicketCaseStrike(button.dataset.ticketId, button.dataset.targetUserId);
      return;
    } else if (action === "ticket-appeal-strike-resolve" && button.dataset.ticketId && button.dataset.strikeId && button.dataset.strikeStatus) {
      await handleTicketAppealStrikeResolution(button.dataset.ticketId, button.dataset.strikeId, button.dataset.strikeStatus);
      return;
    } else if (action === "ticket-transcript" && button.dataset.ticketId) {
      await handleTicketTranscript(button.dataset.ticketId);
      return;
    } else if (action === "ticket-accused-attach" && button.dataset.ticketId) {
      await handleTicketAccusedAttach(button.dataset.ticketId);
      return;
    } else if (action === "ticket-accused-remove" && button.dataset.ticketId && button.dataset.accusedId) {
      await handleTicketAccusedRemove(button.dataset.ticketId, button.dataset.accusedId);
      return;
    } else if (action === "ticket-log-bundle-create" && button.dataset.ticketId) {
      await handleTicketLogBundleCreate(button.dataset.ticketId);
      return;
    } else if (action === "ticket-log-review" && button.dataset.ticketId && button.dataset.bundleId) {
      await handleTicketLogReview(button.dataset.ticketId, button.dataset.bundleId);
      return;
    } else if (action === "ticket-log-review-all" && button.dataset.ticketId) {
      await handleTicketLogReviewAll(button.dataset.ticketId);
      return;
    } else if (action === "ticket-log-combined-select" && button.dataset.ticketId && button.dataset.selectMode) {
      selectCombinedTicketLogEvents(button.dataset.ticketId, button.dataset.selectMode === "all");
      return;
    } else if (action === "ticket-log-combined-evidence-attach" && button.dataset.ticketId) {
      await handleCombinedTicketLogEvidenceAttach(button.dataset.ticketId);
      return;
    } else if (action === "ticket-evidence-log-review" && button.dataset.ticketId && button.dataset.bundleId && button.dataset.evidenceId) {
      await handleTicketEvidenceLogReview(button.dataset.ticketId, button.dataset.bundleId, button.dataset.evidenceId);
      return;
    } else if (action === "ticket-log-select" && button.dataset.bundleId && button.dataset.selectMode) {
      selectTicketLogEvents(button.dataset.bundleId, button.dataset.selectMode === "all");
      return;
    } else if (action === "ticket-log-evidence-attach" && button.dataset.ticketId && button.dataset.bundleId) {
      await handleTicketLogEvidenceAttach(button.dataset.ticketId, button.dataset.bundleId);
      return;
    } else if (action === "ticket-evidence-add" && button.dataset.ticketId) {
      await handleTicketEvidenceAdd(button.dataset.ticketId, true);
      return;
    } else if (action === "ticket-evidence-writeup" && button.dataset.ticketId) {
      await handleTicketEvidenceWriteup(button.dataset.ticketId);
      return;
    } else if (action === "ticket-evidence-upload" && button.dataset.ticketId) {
      await handleTicketEvidenceUpload(button.dataset.ticketId, true);
      return;
    } else if (action === "ticket-evidence-review" && button.dataset.ticketId && button.dataset.evidenceId && button.dataset.evidenceStatus) {
      await handleTicketEvidenceReview(button.dataset.ticketId, button.dataset.evidenceId, button.dataset.evidenceStatus);
      return;
    } else if (action === "ticket-watcher-add" && button.dataset.ticketId) {
      await handleTicketWatcherAdd(button.dataset.ticketId);
      return;
    } else if (action === "ticket-watcher-remove" && button.dataset.ticketId && button.dataset.watcherUserId) {
      await handleTicketWatcherRemove(button.dataset.ticketId, button.dataset.watcherUserId);
      return;
    } else if (action === "ticket-rule-attach" && button.dataset.ticketId) {
      await handleTicketRuleAttach(button.dataset.ticketId);
      return;
    } else if (action === "ticket-rule-review" && button.dataset.ticketId && button.dataset.ticketRuleId && button.dataset.ruleOutcome) {
      await handleTicketRuleReview(button.dataset.ticketId, button.dataset.ticketRuleId, button.dataset.ruleOutcome);
      return;
    } else if (action === "player-ticket-create") {
      await handlePlayerTicketCreate();
      return;
    } else if (action === "player-ticket-list") {
      await handlePlayerTicketList();
      return;
    } else if (action === "player-ticket-detail" && button.dataset.ticketId) {
      await handlePlayerTicketDetail(button.dataset.ticketId);
      return;
    } else if (action === "player-ticket-reply" && button.dataset.ticketId) {
      await handlePlayerTicketReply(button.dataset.ticketId);
      return;
    } else if (action === "player-ticket-close" && button.dataset.ticketId) {
      await handlePlayerTicketClose(button.dataset.ticketId);
      return;
    } else if (action === "player-ticket-evidence-add" && button.dataset.ticketId) {
      await handleTicketEvidenceAdd(button.dataset.ticketId, false);
      return;
    } else if (action === "player-ticket-evidence-upload" && button.dataset.ticketId) {
      await handleTicketEvidenceUpload(button.dataset.ticketId, false);
      return;
    } else {
      throw new Error("Unsupported portal action.");
    }

    const viewState = capturePortalRefreshViewState();
    await boot();
    restorePortalRefreshViewState(viewState);
    setStatus(finalStatus);
  } catch (error) {
    if (needsRecentSteamAuthentication(error)) {
      persistPortalReauthenticationDraft();
      setStatus("Secure sign-in expired. Opening Steam now; your unsaved entries have been preserved.");
      const returnTo = `${window.location.pathname}${window.location.search}`;
      window.setTimeout(() => {
        window.location.assign(`/api/identity/steam/openid/start?reauth=1&returnTo=${encodeURIComponent(returnTo)}`);
      }, 350);
      return;
    }
    const message = error instanceof Error ? error.message : String(error);
    if (action?.startsWith("friend-teleport-")) showFriendTeleportResult(message, "error");
    setStatus(`Action failed: ${message}`);
  } finally {
    button.classList.remove("is-busy");
    button.removeAttribute("aria-busy");
    if (button instanceof HTMLButtonElement) {
      button.disabled = action === "event-wheel-spin" && eventWheelConfiguration
        ? currentSnapshot.wallet.premium < eventWheelConfiguration.spinCost
        : wasDisabled;
    }
    portalActionInFlight = false;
  }
}

function selectStoreCategory(category: string): void {
  document.querySelectorAll<HTMLButtonElement>("[data-store-category]").forEach((button) => {
    const active = button.dataset.storeCategory === category;
    button.setAttribute("aria-selected", String(active));
    button.tabIndex = active ? 0 : -1;
  });
  document.querySelectorAll<HTMLElement>("[data-store-category-panel]").forEach((panel) => {
    const active = panel.dataset.storeCategoryPanel === category;
    panel.hidden = !active;
    panel.classList.toggle("active", active);
  });
}

function selectStoreTeleportView(view: string): void {
  document.querySelectorAll<HTMLButtonElement>("[data-store-teleport-view]").forEach((button) => {
    const active = button.dataset.storeTeleportView === view;
    button.setAttribute("aria-selected", String(active));
    button.tabIndex = active ? 0 : -1;
  });
  document.querySelectorAll<HTMLElement>("[data-store-teleport-group]").forEach((group) => {
    group.hidden = group.dataset.storeTeleportGroup !== view;
  });
}

async function handleSkinSave(context: PortalActionContext): Promise<void> {
  const mode = document.querySelector<HTMLElement>("[data-skin-creator]")?.dataset.skinEditorMode === "offline" ? "offline" : "live";
  const capture = currentSnapshot.skinCapture;
  if (mode === "live" && (!capture || currentSnapshot.liveDino.status !== "live")) throw new Error("Spawn a live dinosaur before saving from Live specimen mode.");
  const name = document.querySelector<HTMLInputElement>("[data-skin-name]")?.value.trim() ?? "";
  if (name.length < 2) throw new Error("Enter a preset name with at least 2 characters.");
  const colors = readSkinColors();
  const structure = readSkinStructure();
  const species = document.querySelector<HTMLSelectElement>("[data-skin-pattern]")?.dataset.skinSpecies ?? currentSnapshot.liveDino.species;
  await apiClient.createSkinPreset({
    userId: context.userId,
    actorDiscordRoleIds: context.roleIds,
    mode,
    ...(mode === "live" ? {
      expectedSpecies: currentSnapshot.liveDino.species,
      expectedServerId: context.serverId,
      ...(capture?.targetToken ? { expectedSkinTargetToken: capture.targetToken } : {})
    } : {}),
    species,
    name,
    female: structure.female,
    skinVariation: mode === "live" ? capture?.skinVariation ?? 0 : 0,
    patternIndex: structure.patternIndex,
    themeIndex: structure.themeIndex,
    ...readSkinEffects(),
    packLivery: document.querySelector<HTMLInputElement>("[data-skin-pack-livery]")?.checked === true,
    colors,
    restricted: false
  });
}

async function loadPackLivery(): Promise<void> {
  const codeInput = document.querySelector<HTMLInputElement>("[data-skin-livery-code]");
  const code = codeInput?.value.trim().toUpperCase() ?? "";
  if (!/^[A-Z2-9]{7}$/.test(code)) throw new Error("Enter a valid seven-character pack livery code.");
  const { livery } = await apiClient.loadPackLivery(code);
  let changed = 0;
  for (const [field, color] of Object.entries(livery.colors)) {
    const input = document.querySelector<HTMLInputElement>(`[data-skin-color="${cssEscape(field)}"]`);
    if (!input || input.disabled) continue;
    setSkinColourInput(input, linearRgbToHex(color.r, color.g, color.b));
    changed += 1;
  }
  if (!changed) throw new Error("This livery has no colour channels available on your current dinosaur.");
  const virtual = document.createElement("button");
  virtual.dataset.presetFemale = String(livery.female);
  virtual.dataset.presetPattern = String(livery.patternIndex);
  virtual.dataset.presetTheme = String(livery.themeIndex);
  virtual.dataset.presetGore = String(livery.goreIntensity);
  virtual.dataset.presetEffect = livery.effectMode;
  virtual.dataset.presetEffectColor1 = livery.effectColor1;
  virtual.dataset.presetEffectColor2 = livery.effectColor2;
  virtual.dataset.presetEffectColor3 = livery.effectColor3;
  virtual.dataset.presetEffectSpeed = String(livery.effectSpeedSeconds);
  virtual.dataset.presetEffectPattern = livery.effectPattern;
  virtual.dataset.presetEnvironment = livery.environmentMode;
  if (livery.animation) virtual.dataset.presetAnimation = encodeURIComponent(JSON.stringify(livery.animation));
  previewSavedSkinStructure(virtual);
  document.querySelector("[data-skin-creator]")?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function linearRgbToHex(r: number, g: number, b: number): string {
  const channel = (value: number) => {
    const bounded = Math.max(0, Math.min(1, value));
    const srgb = bounded <= 0.0031308 ? bounded * 12.92 : 1.055 * Math.pow(bounded, 1 / 2.4) - 0.055;
    return Math.round(srgb * 255).toString(16).padStart(2, "0");
  };
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

function showFriendTeleportCode(code?: string, expiresAt?: string): void {
  const panel = document.querySelector<HTMLElement>("[data-friend-teleport-code]");
  const value = document.querySelector<HTMLElement>("[data-friend-teleport-code-value]");
  const guidance = document.querySelector<HTMLElement>("[data-friend-teleport-guidance]");
  if (!panel || !value || !code) return;
  panel.hidden = false;
  value.textContent = code;
  if (guidance && expiresAt) guidance.textContent = `Valid until ${new Date(expiresAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}. Single use.`;
}

function selectStaffPlayerPanel(button: HTMLButtonElement): void {
  const workspace = button.closest<HTMLElement>("[data-staff-player-workspace]");
  const targetId = button.dataset.staffPlayerTarget;
  if (!workspace || !targetId) return;
  workspace.querySelectorAll<HTMLButtonElement>("[data-staff-player-target]").forEach((candidate) => candidate.setAttribute("aria-pressed", String(candidate === button)));
  workspace.querySelectorAll<HTMLElement>("[data-staff-player-panel]").forEach((panel) => {
    panel.hidden = panel.dataset.staffPlayerPanel !== targetId;
  });
  workspace.querySelector<HTMLElement>(".staff-player-content")?.scrollIntoView({ behavior: "auto", block: "start" });
}

function closeStaffPlayerWorkspace(): void {
  const result = document.querySelector<HTMLElement>("#profileLookupResult");
  const search = document.querySelector<HTMLElement>("[data-staff-player-search]");
  if (result) result.innerHTML = "";
  if (search) search.hidden = false;
  document.querySelector<HTMLInputElement>("[data-profile-lookup]")?.focus();
}

function showFriendTeleportResult(message: string, tone: "pending" | "success" | "error"): void {
  const result = document.querySelector<HTMLElement>("[data-friend-teleport-result]");
  if (!result) return;
  result.hidden = false;
  result.dataset.tone = tone;
  result.textContent = message;
}

function readSkinStructure(): { female: boolean; patternIndex: number; themeIndex: number } {
  const female = document.querySelector<HTMLSelectElement>("[data-skin-female]")?.value;
  const patternSelect = document.querySelector<HTMLSelectElement>("[data-skin-pattern]");
  const pattern = Number(patternSelect?.value);
  const theme = Number(document.querySelector<HTMLSelectElement>("[data-skin-theme]")?.value);
  const species = patternSelect?.dataset.skinSpecies ?? currentSnapshot.liveDino.species;
  const limits = skinStructureLimitsForSpecies(species);
  const themeCount = skinThemeCountForPattern(species, pattern);
  if (female !== "true" && female !== "false") throw new Error("Choose a valid skin gender.");
  if (!limits || !Number.isSafeInteger(pattern) || pattern < 0 || pattern >= limits.patternCount) {
    throw new Error(`Choose an available ${species} pattern.`);
  }
  if (themeCount === undefined || !Number.isSafeInteger(theme) || theme < 0 || theme >= themeCount) {
    throw new Error("Choose an available theme for this pattern.");
  }
  return { female: female === "true", patternIndex: pattern, themeIndex: theme };
}

function readSkinEffects(): SkinPreviewEffectSettings & { goreIntensity: number; animation?: SkinAnimation } {
  const goreInput = document.querySelector<HTMLInputElement>("[data-skin-gore-intensity]");
  const modeInput = document.querySelector<HTMLSelectElement>("[data-skin-effect-mode]");
  const defaults = { goreIntensity: 0, effectMode: "none", effectColor1: "#ff315c", effectColor2: "#20e3a2", effectColor3: "#4f7cff", effectSpeedSeconds: 30, effectPattern: "cycle", environmentMode: "adaptive" } as const;
  if (!goreInput || !modeInput || goreInput.disabled || modeInput.disabled) return defaults;
  const goreIntensity = Number(goreInput.value);
  const validModes = ["none", "rainbow", "bioluminescent", "lava", "toxic", "frost", "blood_moon", "aurora", "glitch", "ember", "deep_ocean", "phantom", "solar_flare", "verdant", "hdr_glitch", "custom", "environment"] as const;
  const effectMode = validModes.find((value) => value === modeInput.value);
  const effectColor1 = document.querySelector<HTMLInputElement>('[data-skin-effect-color="1"]')?.value ?? defaults.effectColor1;
  const effectColor2 = document.querySelector<HTMLInputElement>('[data-skin-effect-color="2"]')?.value ?? defaults.effectColor2;
  const effectColor3 = document.querySelector<HTMLInputElement>('[data-skin-effect-color="3"]')?.value ?? defaults.effectColor3;
  const effectSpeedSeconds = Number(document.querySelector<HTMLInputElement>("[data-skin-effect-speed]")?.value ?? defaults.effectSpeedSeconds);
  const effectPattern = document.querySelector<HTMLSelectElement>("[data-skin-effect-pattern]")?.value === "pulse" ? "pulse" : "cycle";
  const environmentValue = document.querySelector<HTMLSelectElement>("[data-skin-environment-mode]")?.value;
  const environmentMode = (["day_night", "weather", "biome", "adaptive"] as const).find((value) => value === environmentValue) ?? "adaptive";
  if (!Number.isSafeInteger(goreIntensity) || goreIntensity < 0 || goreIntensity > 100) throw new Error("Choose a gore amount between 0 and 100 percent.");
  if (!effectMode) throw new Error("Choose a valid colour animation.");
  if (![effectColor1, effectColor2, effectColor3].every((value) => /^#[0-9a-f]{6}$/i.test(value))) throw new Error("Choose three valid effect colours.");
  if (!Number.isSafeInteger(effectSpeedSeconds) || effectSpeedSeconds < 8 || effectSpeedSeconds > 120) throw new Error("Choose an effect speed between 8 and 120 seconds.");
  const animation = readSkinAnimation();
  if (effectMode === "hdr_glitch" && animation?.enabled) throw new Error("Prismatic HDR cannot be combined with animated skin regions.");
  return { goreIntensity, effectMode, effectColor1, effectColor2, effectColor3, effectSpeedSeconds, effectPattern, environmentMode, ...(animation ? { animation } : {}) };
}

async function showStickTransferDialog(): Promise<void> {
  const dialog = document.querySelector<HTMLDialogElement>("#stickTransferDialog");
  if (!dialog) return;
  const form = dialog.querySelector<HTMLFormElement>("[data-stick-transfer-form]");
  form?.reset();
  if (form) {
    form.dataset.limitReady = "false";
    form.dataset.submitting = "false";
  }
  const recipient = dialog.querySelector<HTMLInputElement>("[data-stick-transfer-recipient]");
  if (recipient) {
    recipient.dataset.selectedUserId = "";
    recipient.closest<HTMLElement>(".stick-transfer-recipient")?.removeAttribute("data-selected");
  }
  const recipientStatus = dialog.querySelector<HTMLElement>("[data-stick-transfer-recipient-status]");
  if (recipientStatus) recipientStatus.textContent = "Choose a verified player from the results.";
  const result = dialog.querySelector<HTMLElement>("[data-stick-transfer-result]");
  if (result) result.textContent = "";
  const limit = dialog.querySelector<HTMLElement>("[data-stick-transfer-limit]");
  if (limit) limit.innerHTML = "<span>Rolling allowance</span><strong>Checking...</strong><small>No transfer can be submitted until this check completes.</small>";
  const amount = dialog.querySelector<HTMLInputElement>("[data-stick-transfer-amount]");
  if (amount) amount.max = "0";
  hideStickTransferSuggestions();
  resetStickTransferReview(form);
  if (!dialog.open) dialog.showModal();
  updateStickTransferSubmitState();
  try {
    const status = await apiClient.getStickTransferStatus(userId);
    if (limit) limit.innerHTML = `<span>Rolling 24-hour allowance</span><strong>${formatNumber(status.remaining)} / ${formatNumber(status.limit)} remaining</strong><small>${formatNumber(status.sent)} already sent</small>`;
    if (amount) amount.max = String(status.remaining);
    if (form) form.dataset.limitReady = "true";
    if (status.remaining < 1 && result) result.textContent = "You have used your rolling 24-hour sending allowance.";
    updateStickTransferSubmitState();
  } catch (error) {
    if (result) result.textContent = error instanceof Error ? error.message : String(error);
  }
  recipient?.focus();
}

function closeStickTransferDialog(): void {
  const dialog = document.querySelector<HTMLDialogElement>("#stickTransferDialog");
  if (dialog?.open) dialog.close();
  hideStickTransferSuggestions();
}

async function updateStickTransferSuggestions(query: string): Promise<void> {
  const container = document.querySelector<HTMLElement>("[data-stick-transfer-suggestions]");
  if (!container) return;
  try {
    const recipients = await apiClient.searchStickTransferRecipients(userId, query);
    container.innerHTML = recipients.length
      ? recipients.map((recipient) => `<button type="button" data-stick-transfer-suggestion data-user-id="${escapePortalText(recipient.userId)}" data-player-name="${escapePortalText(recipient.name)}"><i data-lucide="UserRound" aria-hidden="true"></i><span><strong>${escapePortalText(recipient.name)}</strong><small>Verified player</small></span></button>`).join("")
      : `<p>No verified players found.</p>`;
    container.hidden = false;
    hydratePortalIcons();
  } catch (error) {
    container.innerHTML = `<p>${escapePortalText(error instanceof Error ? error.message : String(error))}</p>`;
    container.hidden = false;
  }
}

function hideStickTransferSuggestions(): void {
  const container = document.querySelector<HTMLElement>("[data-stick-transfer-suggestions]");
  if (container) container.hidden = true;
}

function resetStickTransferReview(form = document.querySelector<HTMLFormElement>("[data-stick-transfer-form]")): void {
  if (!form) return;
  form.dataset.reviewSignature = "";
  form.dataset.transferRequestId = "";
  const review = form.querySelector<HTMLElement>("[data-stick-transfer-review]");
  if (review) review.hidden = true;
  const submit = form.querySelector<HTMLButtonElement>("[data-stick-transfer-submit]");
  if (submit && form.dataset.submitting !== "true") submit.textContent = "Review transfer";
}

function updateStickTransferSubmitState(): void {
  const dialog = document.querySelector<HTMLDialogElement>("#stickTransferDialog");
  const recipient = dialog?.querySelector<HTMLInputElement>("[data-stick-transfer-recipient]");
  const amount = dialog?.querySelector<HTMLInputElement>("[data-stick-transfer-amount]");
  const submit = dialog?.querySelector<HTMLButtonElement>("[data-stick-transfer-submit]");
  if (!submit) return;
  const value = Number(amount?.value ?? 0);
  const maximum = Number(amount?.max ?? 0);
  const ready = dialog?.querySelector<HTMLFormElement>("[data-stick-transfer-form]")?.dataset.limitReady === "true";
  const submitting = dialog?.querySelector<HTMLFormElement>("[data-stick-transfer-form]")?.dataset.submitting === "true";
  submit.disabled = submitting || !ready || !recipient?.dataset.selectedUserId || !Number.isSafeInteger(value) || value < 1 || value > maximum;
}

async function submitStickTransfer(form: HTMLFormElement): Promise<void> {
  if (!form.reportValidity()) return;
  const recipient = form.querySelector<HTMLInputElement>("[data-stick-transfer-recipient]");
  const amount = Number(form.querySelector<HTMLInputElement>("[data-stick-transfer-amount]")?.value ?? 0);
  const note = form.querySelector<HTMLInputElement>("[data-stick-transfer-note]")?.value.trim() ?? "";
  const targetUserId = recipient?.dataset.selectedUserId ?? "";
  const recipientName = recipient?.value.trim() ?? "";
  const result = form.querySelector<HTMLElement>("[data-stick-transfer-result]");
  const submit = form.querySelector<HTMLButtonElement>("[data-stick-transfer-submit]");
  if (!targetUserId) {
    if (result) result.textContent = "Choose a verified player from the search results.";
    return;
  }
  const maximum = Number(form.querySelector<HTMLInputElement>("[data-stick-transfer-amount]")?.max ?? 0);
  if (!Number.isSafeInteger(amount) || amount < 1 || amount > maximum) {
    if (result) result.textContent = `Enter a whole number between 1 and ${formatNumber(maximum)}.`;
    return;
  }
  const signature = JSON.stringify([targetUserId, recipientName, amount, note]);
  if (form.dataset.reviewSignature !== signature) {
    form.dataset.reviewSignature = signature;
    form.dataset.transferRequestId = crypto.randomUUID();
    const review = form.querySelector<HTMLElement>("[data-stick-transfer-review]");
    const title = form.querySelector<HTMLElement>("[data-stick-transfer-review-title]");
    const copy = form.querySelector<HTMLElement>("[data-stick-transfer-review-copy]");
    if (title) title.textContent = `Send ${formatNumber(amount)} ${currentSnapshot.currency.points} to ${recipientName}?`;
    if (copy) copy.textContent = note ? `Note: ${note}` : "No note will be attached to this transfer.";
    if (review) review.hidden = false;
    if (result) result.textContent = "";
    if (submit) submit.textContent = "Confirm and send";
    review?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    return;
  }
  const requestId = form.dataset.transferRequestId;
  if (!requestId) {
    resetStickTransferReview(form);
    if (result) result.textContent = "Review the transfer again before sending.";
    updateStickTransferSubmitState();
    return;
  }
  form.dataset.submitting = "true";
  if (submit) { submit.disabled = true; submit.setAttribute("aria-busy", "true"); submit.textContent = "Transferring..."; }
  try {
    const transfer = await apiClient.sendSticks({ senderUserId: userId, recipientUserId: targetUserId, requestId, amount, ...(note ? { note } : {}) });
    currentSnapshot.wallet.points = transfer.wallet.points;
    refreshPortalShell(currentSnapshot);
    closeStickTransferDialog();
    setStatus(`${formatNumber(transfer.amount)} ${currentSnapshot.currency.points} sent to ${transfer.recipient.name}. ${formatNumber(transfer.remaining)} remains in your rolling daily allowance.`);
  } catch (error) {
    if (result) result.textContent = error instanceof Error ? error.message : String(error);
  } finally {
    form.dataset.submitting = "false";
    if (submit) { submit.removeAttribute("aria-busy"); submit.textContent = form.dataset.reviewSignature === signature ? "Confirm and send" : "Review transfer"; }
    updateStickTransferSubmitState();
  }
}

function readSkinAnimation(): SkinAnimation | undefined {
  const speed = Number(document.querySelector<HTMLInputElement>("[data-skin-animation-speed]")?.value ?? "30");
  const rawPattern = document.querySelector<HTMLSelectElement>("[data-skin-animation-pattern]")?.value;
  const pattern = rawPattern === "pulse" || rawPattern === "cascade" ? rawPattern : "cycle";
  const tracks: SkinAnimation["tracks"] = {};
  document.querySelectorAll<HTMLElement>("[data-skin-animation-region]").forEach((region) => {
    const field = region.dataset.skinAnimationRegion as keyof SkinAnimation["tracks"] | undefined;
    const enabled = region.querySelector<HTMLInputElement>("[data-skin-animation-toggle]")?.checked === true;
    if (!field || !enabled) return;
    const values = Array.from(region.querySelectorAll<HTMLInputElement>("[data-skin-animation-stop-color]"))
      .map((input) => input.value.toLowerCase())
      .filter((value) => /^#[0-9a-f]{6}$/.test(value));
    if (values.length < 2) return;
    tracks[field] = { stops: values.map((color, index) => ({ position: index / values.length, color })) };
  });
  if (!Object.keys(tracks).length) return undefined;
  return { schemaVersion: 1, enabled: true, cycleSeconds: Math.max(8, Math.min(120, Math.round(speed))), pattern, tracks };
}

function syncSkinAnimationInput(input: HTMLInputElement): void {
  if (input.matches("[data-skin-animation-stop-hex]")) {
    const color = input.closest("[data-skin-animation-stop]")?.querySelector<HTMLInputElement>("[data-skin-animation-stop-color]");
    if (/^#[0-9a-f]{6}$/i.test(input.value) && color) color.value = input.value;
  } else if (input.matches("[data-skin-animation-stop-color]")) {
    const hex = input.closest("[data-skin-animation-stop]")?.querySelector<HTMLInputElement>("[data-skin-animation-stop-hex]");
    if (hex) hex.value = input.value.toUpperCase();
  }
  syncSkinAnimationLab();
}

function syncSkinAnimationLab(): void {
  const animation = readSkinAnimation();
  const active = Object.keys(animation?.tracks ?? {}).length;
  const effect = document.querySelector<HTMLSelectElement>("[data-skin-effect-mode]");
  if (active > 0 && effect?.value === "hdr_glitch") effect.value = "none";
  document.querySelectorAll<HTMLElement>("[data-skin-animation-region]").forEach((region) => {
    region.dataset.active = String(region.querySelector<HTMLInputElement>("[data-skin-animation-toggle]")?.checked === true);
  });
  const count = document.querySelector<HTMLElement>("[data-skin-animation-region-count]");
  const summary = document.querySelector<HTMLElement>("[data-skin-animation-summary]");
  const speed = Number(document.querySelector<HTMLInputElement>("[data-skin-animation-speed]")?.value ?? "30");
  const speedOutput = document.querySelector<HTMLElement>("[data-skin-animation-speed-value]");
  const shades = document.querySelector<HTMLElement>("[data-skin-animation-shades]");
  if (count) count.textContent = `${active} / 10`;
  if (summary) summary.textContent = active ? `${active} selective region ${active === 1 ? "track" : "tracks"} armed` : "Static specimen — select a region below";
  if (speedOutput) speedOutput.textContent = `${Math.round(speed)}s`;
  if (shades) shades.textContent = String(skinAnimationShadeCount(speed));
  updateSkinModelPreviewAnimation(animation);
}

function changeSkinAnimationStopCount(button: HTMLButtonElement, add: boolean): void {
  const region = button.closest<HTMLElement>("[data-skin-animation-region]");
  const stops = region?.querySelector<HTMLElement>("[data-skin-animation-stops]");
  if (!region || !stops) return;
  const entries = Array.from(stops.querySelectorAll<HTMLElement>("[data-skin-animation-stop]"));
  if (add && entries.length < 6) {
    const previous = entries.at(-1)?.querySelector<HTMLInputElement>("[data-skin-animation-stop-color]")?.value ?? "#20e3a2";
    const label = document.createElement("label");
    label.dataset.skinAnimationStop = "";
    label.innerHTML = `<span>${String(entries.length + 1).padStart(2, "0")}</span><input type="color" value="${previous}" data-skin-animation-stop-color /><input type="text" value="${previous.toUpperCase()}" maxlength="7" data-skin-animation-stop-hex />`;
    stops.append(label);
  } else if (!add && entries.length > 2) entries.at(-1)?.remove();
  syncSkinAnimationLab();
}

function applySkinAnimationRecipe(recipe: string): void {
  const recipes: Record<string, { fields: string[]; colors: string[]; pattern: "cycle" | "pulse" | "cascade"; speed: number }> = {
    biolume: { fields: ["MarkingsColor", "Detail1Color", "EyesColor"], colors: ["#042027", "#13e6ca", "#6e65ff"], pattern: "pulse", speed: 42 },
    ember: { fields: ["MarkingsColor", "MouthColor", "Detail1Color"], colors: ["#170704", "#a52d11", "#ffc13b"], pattern: "cascade", speed: 24 },
    aurora: { fields: ["FlankColor", "MarkingsColor", "Detail1Color"], colors: ["#08352f", "#22dfad", "#8752ff"], pattern: "cycle", speed: 58 }
  };
  const selected = recipes[recipe];
  const effect = document.querySelector<HTMLSelectElement>("[data-skin-effect-mode]");
  if (effect) effect.value = "none";
  document.querySelectorAll<HTMLElement>("[data-skin-animation-region]").forEach((region) => {
    const field = region.dataset.skinAnimationRegion ?? "";
    const toggle = region.querySelector<HTMLInputElement>("[data-skin-animation-toggle]");
    if (toggle) toggle.checked = Boolean(selected?.fields.includes(field));
    if (!selected?.fields.includes(field)) return;
    const stops = region.querySelector<HTMLElement>("[data-skin-animation-stops]");
    if (!stops) return;
    stops.innerHTML = selected.colors.map((value, index) => `<label data-skin-animation-stop><span>${String(index + 1).padStart(2, "0")}</span><input type="color" value="${value}" data-skin-animation-stop-color /><input type="text" value="${value.toUpperCase()}" maxlength="7" data-skin-animation-stop-hex /></label>`).join("");
  });
  if (selected) {
    const pattern = document.querySelector<HTMLSelectElement>("[data-skin-animation-pattern]");
    const speed = document.querySelector<HTMLInputElement>("[data-skin-animation-speed]");
    if (pattern) pattern.value = selected.pattern;
    if (speed) speed.value = String(selected.speed);
  }
  syncSkinAnimationLab();
  const settings = readSkinEffects();
  updateSkinModelPreviewEffects(settings.goreIntensity, settings);
}

function applyPositiveHdrGlitchRecipe(): void {
  loadSkinAnimationIntoLab(undefined);
  const effect = document.querySelector<HTMLSelectElement>("[data-skin-effect-mode]");
  if (!effect || effect.disabled) return;
  effect.value = "hdr_glitch";
  syncSkinEffectDesigner("hdr_glitch");
  const settings = readSkinEffects();
  const summary = document.querySelector<HTMLElement>("[data-skin-animation-summary]");
  if (summary) summary.textContent = "Prismatic HDR · all authored regions · fixed 2.0 ceiling";
  updateSkinModelPreviewEffects(settings.goreIntensity, settings);
}

function readSkinColors(): Record<string, { r: number; g: number; b: number; a: number }> {
  const colors: Record<string, { r: number; g: number; b: number; a: number }> = {};
  document.querySelectorAll<HTMLInputElement>("[data-skin-color]").forEach((input) => {
    if (input.disabled) return;
    const field = input.dataset.skinColor;
    if (!field || !/^#[0-9a-f]{6}$/i.test(input.value)) return;
    const originalHex = input.dataset.skinOriginalHex?.toLowerCase();
    if (originalHex === input.value.toLowerCase()) {
      const original = [
        input.dataset.skinOriginalR,
        input.dataset.skinOriginalG,
        input.dataset.skinOriginalB,
        input.dataset.skinOriginalA
      ].map((value) => Number(value));
      if (original.every(Number.isFinite)) {
        colors[field] = { r: original[0]!, g: original[1]!, b: original[2]!, a: original[3]! };
        return;
      }
    }
    colors[field] = {
      r: srgbToLinear(Number.parseInt(input.value.slice(1, 3), 16) / 255),
      g: srgbToLinear(Number.parseInt(input.value.slice(3, 5), 16) / 255),
      b: srgbToLinear(Number.parseInt(input.value.slice(5, 7), 16) / 255),
      a: Number.isFinite(Number(input.dataset.skinOriginalA)) ? Number(input.dataset.skinOriginalA) : 1
    };
  });
  if (Object.keys(colors).length === 0) throw new Error("The latest player snapshot did not contain any safe colour channels.");
  return colors;
}

function previewSavedSkinPalette(encodedColors: string): void {
  let colors: Record<string, string>;
  try {
    colors = JSON.parse(decodeURIComponent(encodedColors)) as Record<string, string>;
  } catch {
    throw new Error("This saved palette could not be loaded into the preview.");
  }
  let changed = 0;
  for (const [field, value] of Object.entries(colors)) {
    const input = document.querySelector<HTMLInputElement>(`[data-skin-color="${cssEscape(field)}"]`);
    if (!input || input.disabled || !/^#[0-9a-f]{6}$/i.test(value)) continue;
    setSkinColourInput(input, value);
    changed += 1;
  }
  if (!changed) throw new Error("This palette has no colour channels available for the current preview.");
  document.querySelector("[data-skin-creator]")?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function previewSavedSkinStructure(button: HTMLElement): void {
  const female = document.querySelector<HTMLSelectElement>("[data-skin-female]");
  const pattern = document.querySelector<HTMLSelectElement>("[data-skin-pattern]");
  if (female && (button.dataset.presetFemale === "true" || button.dataset.presetFemale === "false")) {
    female.value = button.dataset.presetFemale;
  }
  const patternIndex = Number(button.dataset.presetPattern);
  const themeIndex = Number(button.dataset.presetTheme);
  const limits = pattern ? skinStructureLimitsForSpecies(pattern.dataset.skinSpecies ?? "") : undefined;
  if (pattern && limits && Number.isSafeInteger(patternIndex) && patternIndex >= 0 && patternIndex < limits.patternCount) {
    pattern.value = String(patternIndex);
    syncSkinThemeOptions(pattern, themeIndex);
    updateSkinModelPreviewPattern(patternIndex);
  }
  const gore = document.querySelector<HTMLInputElement>("[data-skin-gore-intensity]");
  const effect = document.querySelector<HTMLSelectElement>("[data-skin-effect-mode]");
  const goreIntensity = Number(button.dataset.presetGore ?? "0");
  const effectMode = (["none", "rainbow", "bioluminescent", "lava", "toxic", "frost", "blood_moon", "aurora", "glitch", "ember", "deep_ocean", "phantom", "solar_flare", "verdant", "hdr_glitch", "custom", "environment"] as const)
    .find((value) => value === button.dataset.presetEffect) ?? "none";
  if (gore && !gore.disabled && Number.isSafeInteger(goreIntensity) && goreIntensity >= 0 && goreIntensity <= 100) gore.value = String(goreIntensity);
  if (effect && !effect.disabled) effect.value = effectMode;
  for (const index of [1, 2, 3] as const) {
    const input = document.querySelector<HTMLInputElement>(`[data-skin-effect-color="${index}"]`);
    const value = button.dataset[`presetEffectColor${index}` as keyof DOMStringMap];
    if (input && value && /^#[0-9a-f]{6}$/i.test(value)) input.value = value;
  }
  const speed = document.querySelector<HTMLInputElement>("[data-skin-effect-speed]");
  const patternInput = document.querySelector<HTMLSelectElement>("[data-skin-effect-pattern]");
  const environmentInput = document.querySelector<HTMLSelectElement>("[data-skin-environment-mode]");
  if (speed) speed.value = String(Math.max(8, Math.min(120, Number(button.dataset.presetEffectSpeed ?? "30"))));
  if (patternInput) patternInput.value = button.dataset.presetEffectPattern === "pulse" ? "pulse" : "cycle";
  if (environmentInput) environmentInput.value = (["day_night", "weather", "biome", "adaptive"].includes(button.dataset.presetEnvironment ?? "")) ? button.dataset.presetEnvironment! : "adaptive";
  loadSkinAnimationIntoLab(button.dataset.presetAnimation);
  const output = document.querySelector<HTMLElement>("[data-skin-gore-value]");
  if (output) output.textContent = `${goreIntensity}%`;
  syncSkinEffectDesigner(effectMode);
  const settings = readSkinEffects();
  updateSkinModelPreviewEffects(goreIntensity, settings);
}

function loadSkinAnimationIntoLab(encoded: string | undefined): void {
  let animation: SkinAnimation | undefined;
  if (encoded) {
    try { animation = JSON.parse(decodeURIComponent(encoded)) as SkinAnimation; } catch { animation = undefined; }
  }
  document.querySelectorAll<HTMLElement>("[data-skin-animation-region]").forEach((region) => {
    const field = region.dataset.skinAnimationRegion as keyof SkinAnimation["tracks"] | undefined;
    const track = field ? animation?.tracks[field] : undefined;
    const toggle = region.querySelector<HTMLInputElement>("[data-skin-animation-toggle]");
    if (toggle && !toggle.disabled) toggle.checked = Boolean(track);
    const stops = region.querySelector<HTMLElement>("[data-skin-animation-stops]");
    if (!stops || !track) return;
    stops.innerHTML = track.stops.map((stop, index) => `<label data-skin-animation-stop><span>${String(index + 1).padStart(2, "0")}</span><input type="color" value="${stop.color}" data-skin-animation-stop-color /><input type="text" value="${stop.color.toUpperCase()}" maxlength="7" data-skin-animation-stop-hex /></label>`).join("");
  });
  if (animation) {
    const speed = document.querySelector<HTMLInputElement>("[data-skin-animation-speed]");
    const pattern = document.querySelector<HTMLSelectElement>("[data-skin-animation-pattern]");
    if (speed) speed.value = String(animation.cycleSeconds);
    if (pattern) pattern.value = animation.pattern;
  }
  syncSkinAnimationLab();
}

function syncSkinEffectDesigner(effectMode: SkinPreviewEffectSettings["effectMode"]): void {
  const designer = document.querySelector<HTMLElement>("[data-skin-effect-designer]");
  const environment = document.querySelector<HTMLElement>(".skin-environment-mode");
  if (designer) designer.dataset.active = String(effectMode !== "none");
  document.querySelectorAll<HTMLElement>(".skin-custom-effect-colour").forEach((label) => { label.hidden = effectMode !== "custom"; });
  if (environment) environment.hidden = effectMode !== "environment";
}

function syncSkinPhotoStudio(): void {
  const host = document.querySelector<HTMLElement>("[data-skin-model-preview]");
  if (!host) return;
  host.dataset.photoBackground = document.querySelector<HTMLSelectElement>("[data-skin-photo-background]")?.value ?? "jungle";
  host.dataset.photoLighting = document.querySelector<HTMLSelectElement>("[data-skin-photo-lighting]")?.value ?? "natural";
}

function downloadSkinPhotoCard(): void {
  const source = captureSkinPreviewCanvas();
  if (!source) throw new Error("Wait for the verified model to finish loading before taking a photo.");
  const output = document.createElement("canvas");
  output.width = 1600;
  output.height = 900;
  const context = output.getContext("2d");
  if (!context) throw new Error("This browser cannot create a skin photo.");
  const background = document.querySelector<HTMLSelectElement>("[data-skin-photo-background]")?.value ?? "jungle";
  const lighting = document.querySelector<HTMLSelectElement>("[data-skin-photo-lighting]")?.value ?? "natural";
  const gradients: Record<string, [string, string]> = {
    jungle: ["#163c2a", "#03100a"], night: ["#14264d", "#02050c"], storm: ["#3b4650", "#080c0f"], studio: ["#41544b", "#101713"]
  };
  const [top, bottom] = gradients[background] ?? gradients.jungle!;
  const gradient = context.createLinearGradient(0, 0, 0, output.height);
  gradient.addColorStop(0, top); gradient.addColorStop(1, bottom); context.fillStyle = gradient; context.fillRect(0, 0, output.width, output.height);
  context.globalAlpha = 0.16; context.strokeStyle = "#65e2aa"; context.lineWidth = 1;
  for (let x = 0; x < output.width; x += 80) { context.beginPath(); context.moveTo(x, 0); context.lineTo(x, output.height); context.stroke(); }
  for (let y = 0; y < output.height; y += 80) { context.beginPath(); context.moveTo(0, y); context.lineTo(output.width, y); context.stroke(); }
  context.globalAlpha = 1;
  context.filter = lighting === "dramatic" ? "contrast(125%) saturate(118%) brightness(82%)" : lighting === "bright" ? "brightness(122%) saturate(108%)" : lighting === "silhouette" ? "brightness(32%) contrast(145%) saturate(70%)" : "none";
  context.drawImage(source, 100, 40, 1400, 700); context.filter = "none";
  context.fillStyle = "rgba(2,8,5,.88)"; context.fillRect(0, 730, output.width, 170);
  context.fillStyle = "#f5f5ed"; context.font = "700 52px system-ui"; context.fillText(currentSnapshot.liveDino.displayName || currentSnapshot.liveDino.species, 70, 805);
  context.fillStyle = "#69e0ad"; context.font = "700 24px system-ui"; context.fillText(`${currentSnapshot.liveDino.species.toUpperCase()}  ·  ${Math.round(currentSnapshot.liveDino.growth)}% GROWTH`, 72, 850);
  context.fillStyle = "#d9b94e"; context.textAlign = "right"; context.font = "700 25px system-ui"; context.fillText("THE NEST // SKIN STUDIO", 1530, 840);
  const link = document.createElement("a");
  link.download = `the-nest-${currentSnapshot.liveDino.species.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-skin.png`;
  link.href = output.toDataURL("image/png");
  link.click();
}

function syncSkinThemeOptions(pattern: HTMLSelectElement, preferredTheme?: number): void {
  const theme = document.querySelector<HTMLSelectElement>("[data-skin-theme]");
  const species = pattern.dataset.skinSpecies ?? "";
  const patternIndex = Number(pattern.value);
  const themeCount = skinThemeCountForPattern(species, patternIndex);
  if (!theme || themeCount === undefined) return;
  const current = preferredTheme ?? Number(theme.value);
  theme.replaceChildren(...Array.from({ length: themeCount }, (_, index) =>
    new Option(index === 0 ? "Default" : `Variation ${index + 1}`, String(index))));
  theme.value = String(Number.isSafeInteger(current) && current >= 0 && current < themeCount ? current : 0);
  const metric = document.querySelector<HTMLElement>("[data-skin-preview-theme-metric]");
  if (metric) metric.textContent = `${Number(theme.value) + 1} / ${themeCount}`;
}

function randomizeSkinColours(field?: string): void {
  const selector = field
    ? `[data-skin-color="${cssEscape(field)}"]`
    : "[data-skin-color]";
  const inputs = document.querySelectorAll<HTMLInputElement>(selector);
  let changed = 0;
  for (const input of Array.from(inputs)) {
    if (input.disabled) continue;
    let next = randomSkinHex();
    for (let attempt = 0; attempt < 4 && next.toLowerCase() === input.value.toLowerCase(); attempt += 1) {
      next = randomSkinHex();
    }
    setSkinColourInput(input, next);
    changed += 1;
  }
  if (!changed) throw new Error("No editable colour channels are available to randomise.");
}

function normalizeSkinHex(value: string): string | undefined {
  const normalized = value.trim().toUpperCase();
  if (/^#[0-9A-F]{6}$/.test(normalized)) return normalized;
  if (/^[0-9A-F]{6}$/.test(normalized)) return `#${normalized}`;
  return undefined;
}

function setSkinColourInput(input: HTMLInputElement, value: string): void {
  const normalized = normalizeSkinHex(value);
  if (!normalized) return;
  input.value = normalized;
  const control = input.closest<HTMLElement>(".skin-color-control");
  const swatch = control?.querySelector<HTMLElement>(".skin-swatch");
  if (swatch) swatch.style.background = normalized;
  const field = input.dataset.skinColor;
  if (!field) return;
  const hex = control?.querySelector<HTMLInputElement>(`[data-skin-hex="${cssEscape(field)}"]`);
  if (hex) {
    hex.value = normalized;
    hex.setAttribute("aria-invalid", "false");
  }
  updateSkinModelPreviewChannel(field, normalized);
}

function randomSkinHex(): string {
  const bytes = new Uint8Array(3);
  crypto.getRandomValues(bytes);
  // Avoid nearly black colours so every randomised region remains inspectable.
  return `#${Array.from(bytes, (value) => Math.max(24, value).toString(16).padStart(2, "0")).join("")}`;
}

function srgbToLinear(value: number): number {
  return value <= 0.04045 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
}

async function handleSkinReviewLoad(): Promise<void> {
  const container = document.querySelector<HTMLElement>("#skinReviewQueue");
  if (!container || !currentAccess.permissions.includes("skins.approve")) return;
  container.setAttribute("aria-busy", "true");
  try {
    const [pending, approved] = await Promise.all([
      apiClient.listSkinPresetsForReview("pending_review"),
      apiClient.listSkinPresetsForReview("approved")
    ]);
    if (!document.body.contains(container)) return;
    container.innerHTML = renderSkinReviewQueue(pending, approved, userId);
  } catch (error) {
    container.innerHTML = `<div class="support-empty"><strong>Review queue unavailable</strong><p>${escapeHtmlForStatus(error instanceof Error ? error.message : String(error))}</p></div>`;
  } finally {
    container.removeAttribute("aria-busy");
  }
}

async function handleSkinReviewDecision(presetId: string, rawDecision: string): Promise<void> {
  if (!(["approved", "rejected", "disabled"] as const).includes(rawDecision as "approved" | "rejected" | "disabled")) {
    throw new Error("Unsupported skin review decision.");
  }
  const reason = document.querySelector<HTMLInputElement>(`[data-skin-review-reason][data-preset-id="${CSS.escape(presetId)}"]`)?.value.trim() ?? "";
  if (reason.length < 3) throw new Error("Enter a short review reason before deciding.");
  const decision = rawDecision as "approved" | "rejected" | "disabled";
  await apiClient.reviewSkinPreset(presetId, {
    actorUserId: userId,
    actorDiscordRoleIds: roleIds,
    decision,
    reason
  });
  await handleSkinReviewLoad();
  setStatus(decision === "disabled" ? "Future applications disabled." : `Restricted palette ${decision}.`);
}

function escapeHtmlForStatus(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;").replaceAll("'", "&#039;");
}

function applyCaseTimelineFilters(timeline: HTMLElement): void {
  const source = timeline.querySelector<HTMLSelectElement>("[data-case-filter-source]")?.value ?? "all";
  const from = timeline.querySelector<HTMLInputElement>("[data-case-filter-from]")?.value ?? "";
  const to = timeline.querySelector<HTMLInputElement>("[data-case-filter-to]")?.value ?? "";
  const more = timeline.querySelector<HTMLButtonElement>("[data-portal-action=\"case-timeline-more\"]");
  const visibleLimit = Number(more?.dataset.caseVisible ?? "20");
  const items = Array.from(timeline.querySelectorAll<HTMLElement>("[data-case-source]"));
  const matching = items.filter((item) => {
    const itemSource = item.dataset.caseSource ?? "";
    const itemDate = item.dataset.caseDate ?? "";
    return (source === "all" || itemSource === source)
      && (!from || itemDate >= from)
      && (!to || itemDate <= to);
  });
  const visible = new Set(matching.slice(-visibleLimit));
  items.forEach((item) => { item.hidden = !visible.has(item); });
  const empty = timeline.querySelector<HTMLElement>(".case-activity-empty");
  if (empty) empty.hidden = matching.length > 0;
  if (more) {
    more.hidden = matching.length <= visibleLimit;
    more.textContent = `Show earlier activity (${Math.max(0, matching.length - visibleLimit)} remaining)`;
  }
  const count = timeline.querySelector<HTMLElement>("[data-case-count]");
  if (count) count.textContent = `${Math.min(matching.length, visibleLimit)} of ${matching.length}`;
}

function formatCorpseDenialReasons(reasons: string[]): string {
  const labels: Record<string, string> = {
    steam_not_verified: "Steam is not verified",
    player_snapshot_missing: "live player data has not arrived yet",
    player_offline: "the server currently shows you as offline",
    player_snapshot_stale: "your live dinosaur stats are still syncing",
    growth_unknown: "your growth could not be read",
    growth_too_high: "your dinosaur is already 60% grown or higher",
    hunger_unknown: "your hunger could not be read",
    hunger_too_high: "your hunger must be below 30%",
    cooldown_active: "your corpse-request cooldown is still active"
  };
  return reasons.map((reason) => labels[reason] ?? humanizeReason(reason)).join("; ") || "the server rules did not pass";
}

function humanizeReason(value: string): string {
  return value.replaceAll("_", " ");
}

async function handleCorpseProductSave(context: PortalActionContext): Promise<void> {
  const catalogItemId = document.querySelector<HTMLInputElement>("[data-corpse-product-id]")?.value.trim() ?? "";
  const name = document.querySelector<HTMLInputElement>("[data-corpse-product-name]")?.value.trim() ?? "";
  const species = document.querySelector<HTMLSelectElement>("[data-corpse-product-species]")?.value ?? "";
  const growth = boundedNumber(document.querySelector<HTMLInputElement>("[data-corpse-product-growth]")?.value, 10, 100, 100);
  const price = optionalNumber(document.querySelector<HTMLInputElement>("[data-corpse-product-price]")?.value);
  const stockRaw = document.querySelector<HTMLInputElement>("[data-corpse-product-stock]")?.value.trim() ?? "";
  const stock = stockRaw ? optionalNumber(stockRaw) : null;
  const enabled = document.querySelector<HTMLInputElement>("[data-corpse-product-enabled]")?.checked ?? false;
  const patreonDiscountEligible = document.querySelector<HTMLInputElement>("[data-corpse-product-patreon]")?.checked ?? false;
  const reason = document.querySelector<HTMLInputElement>("[data-corpse-product-reason]")?.value.trim() ?? "";
  if (!/^store_[a-z0-9_]+$/.test(catalogItemId) || name.length < 2 || !species || price === undefined || price < 0 || stock !== null && (stock === undefined || stock < 0) || reason.length < 3) {
    setStatus("Corpse product is incomplete. Check its ID, name, species, price, stock, and audit reason.");
    return;
  }
  await apiClient.saveCorpseProduct(context, catalogItemId, {
    name, species, growth, price, stock, enabled, patreonDiscountEligible, reason
  });
  await boot();
  setStatus(`${name} saved to the corpse catalog.`);
}

async function handleStoreProductSave(context: PortalActionContext): Promise<void> {
  const value = (selector: string) => document.querySelector<HTMLInputElement>(selector)?.value.trim() ?? "";
  const type = document.querySelector<HTMLSelectElement>("[data-store-product-type]")?.value as "dino_growth" | "corpse_drop" | "teleport" | undefined;
  const catalogItemId = value("[data-store-product-id]");
  const name = value("[data-store-product-name]");
  const species = document.querySelector<HTMLSelectElement>("[data-store-product-species]")?.value || undefined;
  const growth = optionalNumber(value("[data-store-product-growth]"));
  const price = optionalNumber(value("[data-store-product-price]"));
  const stockText = value("[data-store-product-stock]");
  const stock = stockText ? optionalNumber(stockText) : null;
  const description = value("[data-store-product-description]") || undefined;
  const destination = value("[data-store-product-destination]") || undefined;
  const x = optionalNumber(value("[data-store-product-x]"));
  const y = optionalNumber(value("[data-store-product-y]"));
  const z = optionalNumber(value("[data-store-product-z]"));
  const yaw = optionalNumber(value("[data-store-product-yaw]"));
  const maxGrowth = optionalNumber(value("[data-store-product-max-growth]"));
  const scatterRadius = optionalNumber(value("[data-store-product-scatter-radius]"));
  const minimumScatterRadius = optionalNumber(value("[data-store-product-minimum-scatter-radius]"));
  const enabled = document.querySelector<HTMLInputElement>("[data-store-product-enabled]")?.checked ?? false;
  const patreonDiscountEligible = document.querySelector<HTMLInputElement>("[data-store-product-patreon]")?.checked ?? false;
  const reason = value("[data-store-product-reason]");
  if (!type || !/^store_[a-z0-9_]+$/.test(catalogItemId) || name.length < 2 || price === undefined || price < 0 || reason.length < 3) {
    setStatus("Store product is incomplete. Check its category, ID, name, price, and audit reason.");
    return;
  }
  await apiClient.saveStoreProduct(context, catalogItemId, {
    type, name, ...(species ? { species } : {}), ...(growth === undefined ? {} : { growth }), price,
    stock: stock === undefined ? null : stock, enabled, patreonDiscountEligible,
    ...(description ? { description } : {}), ...(destination ? { destination } : {}),
    ...(x === undefined ? {} : { x }), ...(y === undefined ? {} : { y }), ...(z === undefined ? {} : { z }),
    ...(yaw === undefined ? {} : { yaw }), ...(maxGrowth === undefined ? {} : { maxGrowth }),
    ...(type === "teleport" ? { resolveGround: true } : {}),
    ...(scatterRadius === undefined ? {} : { scatterRadius }),
    ...(minimumScatterRadius === undefined ? {} : { minimumScatterRadius }), reason
  });
  await boot();
  setStatus("Store product saved.");
}

function populateStoreProductEditor(encoded: string): void {
  try {
    const item = JSON.parse(decodeURIComponent(encoded)) as Record<string, unknown>;
    const set = (selector: string, value: unknown) => { const input = document.querySelector<HTMLInputElement>(selector); if (input) input.value = value === null || value === undefined ? "" : String(value); };
    const type = document.querySelector<HTMLSelectElement>("[data-store-product-type]");
    const species = document.querySelector<HTMLSelectElement>("[data-store-product-species]");
    if (type) type.value = String(item.type ?? "dino_growth");
    if (species) species.value = String(item.species ?? "");
    set("[data-store-product-id]", item.id); set("[data-store-product-name]", item.name); set("[data-store-product-growth]", item.growth);
    set("[data-store-product-price]", item.price); set("[data-store-product-stock]", item.stock); set("[data-store-product-description]", item.description);
    set("[data-store-product-destination]", item.destination); set("[data-store-product-max-growth]", item.maxGrowth);
    set("[data-store-product-x]", item.x); set("[data-store-product-y]", item.y); set("[data-store-product-z]", item.z); set("[data-store-product-yaw]", item.yaw);
    set("[data-store-product-scatter-radius]", item.scatterRadius); set("[data-store-product-minimum-scatter-radius]", item.minimumScatterRadius);
    const enabled = document.querySelector<HTMLInputElement>("[data-store-product-enabled]");
    const patreon = document.querySelector<HTMLInputElement>("[data-store-product-patreon]");
    if (enabled) enabled.checked = Boolean(item.enabled);
    if (patreon) patreon.checked = Boolean(item.patreonDiscountEligible);
    document.querySelectorAll<HTMLElement>("[data-catalog-edit]").forEach((row) => row.classList.toggle("is-selected", row.dataset.catalogId === String(item.id ?? "")));
    const mode = document.querySelector<HTMLElement>("[data-catalog-editor-mode]");
    const title = document.querySelector<HTMLElement>("[data-catalog-editor-title]");
    if (mode) mode.textContent = "Editing product";
    if (title) title.textContent = String(item.name ?? "Store product");
    syncStoreProductEditorFields();
    document.querySelector<HTMLElement>("#catalog-product-editor")?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  } catch {
    setStatus("This catalog item could not be loaded into the editor.");
  }
}

function resetStoreProductEditor(type: "dino_growth" | "corpse_drop" | "teleport"): void {
  const set = (selector: string, value: string | number = "") => {
    const input = document.querySelector<HTMLInputElement>(selector);
    if (input) input.value = String(value);
  };
  const category = document.querySelector<HTMLSelectElement>("[data-store-product-type]");
  const species = document.querySelector<HTMLSelectElement>("[data-store-product-species]");
  if (category) category.value = type;
  if (species) species.value = "";
  const prefixes = { dino_growth: "store_dino_", corpse_drop: "store_corpse_", teleport: "store_teleport_" };
  const titles = { dino_growth: "Create a dinosaur product", corpse_drop: "Create a corpse product", teleport: "Create a teleport product" };
  set("[data-store-product-id]", prefixes[type]);
  set("[data-store-product-name]");
  set("[data-store-product-growth]", type === "teleport" ? "" : type === "dino_growth" ? 75 : 100);
  set("[data-store-product-price]");
  set("[data-store-product-stock]");
  set("[data-store-product-description]");
  set("[data-store-product-destination]");
  set("[data-store-product-max-growth]", 30);
  set("[data-store-product-x]"); set("[data-store-product-y]"); set("[data-store-product-z]"); set("[data-store-product-yaw]");
  set("[data-store-product-scatter-radius]", 16000); set("[data-store-product-minimum-scatter-radius]", 3000);
  set("[data-store-product-reason]", "Created store product from owner portal.");
  const enabled = document.querySelector<HTMLInputElement>("[data-store-product-enabled]");
  const patreon = document.querySelector<HTMLInputElement>("[data-store-product-patreon]");
  if (enabled) enabled.checked = true;
  if (patreon) patreon.checked = false;
  document.querySelectorAll<HTMLElement>("[data-catalog-edit]").forEach((row) => row.classList.remove("is-selected"));
  const mode = document.querySelector<HTMLElement>("[data-catalog-editor-mode]");
  const title = document.querySelector<HTMLElement>("[data-catalog-editor-title]");
  if (mode) mode.textContent = "New product";
  if (title) title.textContent = titles[type];
  syncStoreProductEditorFields();
  document.querySelector<HTMLInputElement>("[data-store-product-name]")?.focus();
}

function syncStoreProductEditorFields(): void {
  const type = document.querySelector<HTMLSelectElement>("[data-store-product-type]")?.value ?? "dino_growth";
  const teleport = type === "teleport";
  const growth = document.querySelector<HTMLInputElement>("[data-store-product-growth]");
  const growthNote = document.querySelector<HTMLElement>("[data-store-product-growth-note]");
  if (growth) {
    growth.disabled = type === "dino_growth";
    if (type === "dino_growth") growth.value = "75";
  }
  if (growthNote) growthNote.hidden = type !== "dino_growth";
  document.querySelectorAll<HTMLElement>("[data-store-product-field='species'], [data-store-product-field='growth']").forEach((field) => { field.hidden = teleport; });
  const destination = document.querySelector<HTMLDetailsElement>(".catalog-teleport-fields");
  if (destination) {
    destination.hidden = !teleport;
    destination.open = teleport;
  }
}

function filterStoreProducts(query: string): void {
  const normalized = query.trim().toLowerCase();
  let visible = 0;
  document.querySelectorAll<HTMLElement>("[data-catalog-group]").forEach((group) => {
    let groupVisible = 0;
    group.querySelectorAll<HTMLElement>("[data-catalog-search]").forEach((row) => {
      const matches = !normalized || (row.dataset.catalogSearch ?? "").includes(normalized);
      row.hidden = !matches;
      if (matches) groupVisible += 1;
    });
    group.hidden = groupVisible === 0 && Boolean(normalized);
    visible += groupVisible;
  });
  const empty = document.querySelector<HTMLElement>("[data-catalog-search-empty]");
  if (empty) empty.hidden = visible > 0 || !normalized;
}

async function handleMarketplaceListGarage(context: PortalActionContext, garageDinoId: string, defaultTitle?: string): Promise<void> {
  const form = readMarketplaceListingForm(defaultTitle);
  if (!form.ok) {
    setStatus(form.error);
    return;
  }

  try {
    setStatus("Creating marketplace listing...");
    const item = currentSnapshot.garage.find(d => d.id === garageDinoId);
    const row = document.querySelector<HTMLElement>(`[data-garage-id="${CSS.escape(garageDinoId)}"][data-portal-action="marketplace-list-garage"]`)?.closest(".marketplace-sell-item");
    const opening = row?.querySelector<HTMLInputElement>("[data-auction-opening]")?.value.trim();
    const buyNow = row?.querySelector<HTMLInputElement>("[data-auction-buy-now]")?.value.trim();
    const auction = document.querySelector<HTMLSelectElement>("[data-marketplace-format]")?.value !== "fixed";
    if (auction) {
      const details = { userId: context.userId, garageDinoId, ...form.payload, ...(opening ? { openingBid: Number(opening) } : {}), ...(buyNow ? { buyNowPrice: Number(buyNow) } : {}) };
      const fingerprint = JSON.stringify(details);
      if (pendingAuctionRequest?.fingerprint !== fingerprint) pendingAuctionRequest = { fingerprint, requestId: crypto.randomUUID() };
      await apiClient.createAuction({ ...details, requestId: pendingAuctionRequest.requestId });
      pendingAuctionRequest = undefined;
    } else await apiClient.createMarketplaceDinoListing(context, garageDinoId, { ...form.payload, buyNowPrice: buyNow ? Number(buyNow) : item?.marketplacePrice ?? undefined });
    await boot();
    setStatus("Listing published. Auctions cannot be withdrawn after their first bid.");
  } catch (error) {
    setStatus(`Marketplace listing failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

function readMarketplaceListingForm(_defaultTitle?: string): { ok: true; payload: {
  durationHours: number;
} } | { ok: false; error: string } {
  const durationHours = boundedNumber(document.querySelector<HTMLSelectElement>("[data-marketplace-duration]")?.value, 1, 168, 24);

  return {
    ok: true,
    payload: { durationHours }
  };
}

async function handlePortalConfirmation(button: HTMLElement): Promise<void> {
  const action = button.dataset.confirmAction;
  if (action === "cancel") {
    clearPortalConfirmation();
    return;
  }

  if (action !== "confirm" || (!pendingPortalAction && !pendingSystemAction)) {
    clearPortalConfirmation();
    return;
  }

  const confirmedAction = pendingPortalAction ?? pendingSystemAction!;
  confirmedAction.dataset.confirmed = "true";
  try {
    if (pendingPortalAction) {
      await handlePortalAction(confirmedAction);
    } else {
      await handleSystemAction(confirmedAction);
    }
  } finally {
    delete confirmedAction.dataset.confirmed;
    clearPortalConfirmation();
  }
}

function showPortalConfirmation(button: HTMLElement): void {
  pendingPortalAction = button;
  pendingSystemAction = undefined;
  showConfirmation(button);
}

function showSystemConfirmation(button: HTMLElement): void {
  pendingPortalAction = undefined;
  pendingSystemAction = button;
  showConfirmation(button);
}

function showConfirmation(button: HTMLElement): void {
  const title = button.dataset.confirmTitle ?? "Confirm action";
  const message = button.dataset.confirmMessage ?? "This will submit a live server action.";
  const dialog = document.querySelector<HTMLDialogElement>("#portalDialog");
  const titleElement = document.querySelector<HTMLElement>("#portalDialogTitle");
  const messageElement = document.querySelector<HTMLElement>("#portalDialogMessage");
  if (!dialog || !titleElement || !messageElement) {
    return;
  }

  titleElement.textContent = title;
  messageElement.textContent = message;
  if (!dialog.open) dialog.showModal();
  dialog.querySelector<HTMLButtonElement>("[data-confirm-action='cancel']")?.focus();
}

function clearPortalConfirmation(): void {
  pendingPortalAction = undefined;
  pendingSystemAction = undefined;
  const dialog = document.querySelector<HTMLDialogElement>("#portalDialog");
  if (dialog?.open) dialog.close();
}

async function waitForParkingStatus(operationId: string): Promise<string> {
  setStatus("Parking accepted. Waiting for the game server to start the safety countdown...");
  const deadline = Date.now() + 100_000;
  let latest: GameBridgeOperationSummary | undefined;
  while (Date.now() < deadline) {
    latest = await findBridgeOperation(operationId);
    if (latest) updateParkingCountdown(latest);
    if (latest && isBridgeOperationTerminal(latest.status)) {
      return formatPlayerBridgeProgress(latest, "Parking").message;
    }
    await sleep(1_000);
  }
  latest = await findBridgeOperation(operationId) ?? latest;
  if (latest) updateParkingCountdown(latest);
  return latest
    ? formatPlayerBridgeProgress(latest, "Parking").message
    : "Parking is still processing. Remain spawned and check again shortly.";
}

function updateParkingCountdown(operation: GameBridgeOperationSummary): void {
  const countdown = document.querySelector<HTMLElement>("[data-parking-countdown]");
  if (!countdown) return;
  countdown.hidden = false;
  const seconds = parkingSecondsRemaining(operation.bridge.sentAt);
  countdown.style.setProperty("--parking-progress", String(parkingCountdownProgress(seconds)));
  const secondsElement = countdown.querySelector<HTMLElement>("[data-parking-seconds]");
  const title = countdown.querySelector<HTMLElement>("[data-parking-countdown-title]");
  if (secondsElement) secondsElement.textContent = String(seconds);
  if (title) {
    title.textContent = operation.bridge.sentAt
      ? seconds > 0 ? `Parking begins in ${seconds} second${seconds === 1 ? "" : "s"}` : "Securing dinosaur..."
      : "Preparing the parking countdown...";
  }
}

async function waitForBridgeStatus(operationId: string, label: string, operationUserId = userId): Promise<string> {
  setStatus(`${label} accepted. Waiting for game-server confirmation...`);
  const operation = await waitForBridgeOperation(operationId, 25_000, 2_500, operationUserId);

  if (!operation) {
    return `${label} is still processing. Keep this page open or check again shortly.`;
  }

  return formatPlayerBridgeProgress(operation, label).message;
}

async function waitForBridgeOperation(
  operationId: string,
  timeoutMs = 25_000,
  pollEveryMs = 2_500,
  operationUserId = userId
): Promise<GameBridgeOperationSummary | undefined> {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    const operation = await findBridgeOperation(operationId, operationUserId);
    if (operation && isBridgeOperationTerminal(operation.status)) {
      return operation;
    }

    await sleep(pollEveryMs);
  }

  return findBridgeOperation(operationId, operationUserId);
}

async function findBridgeOperation(operationId: string, operationUserId = userId): Promise<GameBridgeOperationSummary | undefined> {
  try {
    return await apiClient.getGameBridgeOperation(operationId, operationUserId);
  } catch {
    return undefined;
  }
}

function isBridgeOperationTerminal(status: string): boolean {
  return ["confirmed", "failed", "manual_review", "denied", "refunded"].includes(status);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function formatNumber(value: number): string {
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

async function handleBridgeOperations(): Promise<void> {
  const result = document.querySelector("#bridgeOperationResult");

  try {
    setStatus("Loading bridge operations...");
    const bridgeQueue = await apiClient.listGameBridgeOperations(roleIds, { limit: 25 });
    if (result) {
      result.innerHTML = renderGameBridgeOperations(
        bridgeQueue.operations,
        currentAccess.permissions.includes("server.emergency.control"),
        bridgeQueue.summary
      );
    }
    setStatus("Bridge operations loaded.");
  } catch (error) {
    if (result) {
      result.textContent = "No bridge operations loaded.";
    }
    setStatus(`Bridge operation load failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleBridgeResolve(operationId: string, status: "confirmed" | "failed" | "manual_review"): Promise<void> {
  const reason = document.querySelector<HTMLInputElement>("[data-control-reason]")?.value.trim() || "Manual bridge resolution from portal.";

  try {
    setStatus(`Marking bridge operation ${status}...`);
    await apiClient.resolveGameBridgeOperation(operationId, {
      actorUserId: userId,
      actorDiscordRoleIds: roleIds,
      status,
      reason
    });
    await handleBridgeOperations();
    setStatus(`Bridge operation marked ${status}.`);
  } catch (error) {
    setStatus(`Bridge operation update failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleLogSamples(): Promise<void> {
  const result = document.querySelector("#logSampleResult");

  try {
    setStatus("Loading recent parser samples...");
    const samples = await apiClient.listGameServerLogSamples(roleIds, 25);
    if (result) {
      result.innerHTML = renderGameServerLogSamples(samples);
    }
    setStatus("Parser samples loaded.");
  } catch (error) {
    if (result) {
      result.textContent = "No parser samples loaded.";
    }
    setStatus(`Parser sample load failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleAuditEvents(): Promise<void> {
  const result = document.querySelector("#auditEventResult");
  const type = document.querySelector<HTMLInputElement>("[data-audit-type]")?.value.trim();
  const targetInput = document.querySelector<HTMLInputElement>("[data-audit-target]");
  const targetId = playerPickerValue(targetInput, "lookup");
  try {
    setStatus("Loading audit history...");
    const events = await apiClient.listAuditEvents(roleIds, { ...(type ? { type } : {}), ...(targetId ? { targetId } : {}), limit: 50 });
    if (result) result.innerHTML = renderAuditEvents(events);
    setStatus("Audit history loaded.");
  } catch (error) {
    if (result) result.textContent = "No audit history loaded.";
    setStatus(`Audit history load failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

function startModLogRefresh(): void {
  if (modLogRefreshTimer !== undefined) return;
  modLogRefreshTimer = window.setInterval(() => {
    if (portalPageFromPath(window.location.pathname) !== "staff" || document.hidden || portalActionInFlight || portalInteractionActive()) return;
    void handleModLogRefresh(false, true);
  }, 30_000);
}

function stopModLogRefresh(): void {
  if (modLogRefreshTimer === undefined) return;
  window.clearInterval(modLogRefreshTimer);
  modLogRefreshTimer = undefined;
}

function setModLogView(view: "gameplay" | "commands" | "audit"): void {
  modLogView = view;
  const dashboard = document.querySelector<HTMLElement>("[data-mod-log-dashboard]");
  if (!dashboard) return;
  dashboard.dataset.logView = view;
  dashboard.querySelectorAll<HTMLButtonElement>("[data-portal-action=\"mod-logs-view\"]").forEach((tab) => {
    tab.classList.toggle("is-active", tab.dataset.logView === view);
    tab.setAttribute("aria-pressed", String(tab.dataset.logView === view));
  });
  dashboard.querySelectorAll<HTMLElement>(".mod-log-game-filter").forEach((field) => { field.hidden = view === "audit"; });
  dashboard.querySelectorAll<HTMLElement>(".mod-log-game-only-filter").forEach((field) => { field.hidden = view !== "gameplay"; });
  dashboard.querySelectorAll<HTMLElement>(".mod-log-audit-filter").forEach((field) => { field.hidden = view !== "audit"; });
  const help = dashboard.querySelector<HTMLElement>("[data-mod-log-help]");
  if (help) help.textContent = view === "gameplay"
    ? "Verified means the source identified the event directly. Inferred records are clearly marked and should be corroborated before enforcement."
    : view === "commands"
      ? "Commands observed in the game-server log are shown newest first. The command is recorded even when the game does not identify who issued it."
      : "Website staff actions are permanent audit records. Staff names are shown beside their stored identifiers for accountability.";
}

function readGameplayLogFilters(cursor?: string): GameplayEventFilters {
  const playerInput = document.querySelector<HTMLInputElement>("[data-mod-log-player]");
  const player = playerInput?.dataset.selectedSteam64Id || playerInput?.value.trim() || undefined;
  const category = modLogView === "commands" ? "staff" : document.querySelector<HTMLSelectElement>("[data-mod-log-category]")?.value || undefined;
  const type = modLogView === "commands" ? "server.admin_command" : document.querySelector<HTMLSelectElement>("[data-mod-log-type]")?.value || undefined;
  const attribution = modLogView === "commands" ? "verified" : document.querySelector<HTMLSelectElement>("[data-mod-log-attribution]")?.value as GameplayEventFilters["attribution"];
  const rangeHours = Number(document.querySelector<HTMLSelectElement>("[data-mod-log-range]")?.value ?? 24);
  const to = new Date();
  const from = new Date(to.getTime() - Math.max(1, rangeHours) * 60 * 60 * 1000);
  return {
    ...(player ? { player } : {}),
    ...(category ? { category } : {}),
    ...(type ? { type } : {}),
    ...(attribution ? { attribution } : {}),
    from: from.toISOString(),
    to: to.toISOString(),
    ...(cursor ? { cursor } : {}),
    limit: 50
  };
}

async function handleModLogRefresh(append: boolean, background = false): Promise<void> {
  if (modLogRefreshInFlight || !currentAccess.permissions.includes("audit.view")) return;
  const dashboard = document.querySelector<HTMLElement>("[data-mod-log-dashboard]");
  const result = dashboard?.querySelector<HTMLElement>("[data-mod-log-results]");
  if (!dashboard || !result) return;
  if (append && (modLogView === "audit" || !modGameplayNextCursor)) return;
  modLogRefreshInFlight = true;
  if (!background && !append) result.innerHTML = `<div class="support-empty"><strong>Loading server records</strong><p>Applying your filters now.</p></div>`;
  try {
    if (modLogView !== "audit") {
      const page = await apiClient.listGameplayEvents(currentSnapshot.server.id, roleIds, readGameplayLogFilters(append ? modGameplayNextCursor ?? undefined : undefined));
      modGameplayEvents = append
        ? [...modGameplayEvents, ...page.events.filter((event) => !modGameplayEvents.some((existing) => existing.id === event.id))]
        : page.events;
      modGameplayNextCursor = page.nextCursor;
      result.innerHTML = renderGameplayLogResults(modGameplayEvents, modGameplayNextCursor);
    } else {
      const type = dashboard.querySelector<HTMLInputElement>("[data-mod-audit-type]")?.value.trim();
      const playerInput = dashboard.querySelector<HTMLInputElement>("[data-mod-log-player]");
      const targetId = playerPickerValue(playerInput, "lookup");
      const events = await apiClient.listAuditEvents(roleIds, { ...(type ? { type } : {}), ...(targetId ? { targetId } : {}), limit: 100 });
      result.innerHTML = events.length
        ? renderAuditEvents(events)
        : `<div class="support-empty"><strong>No matching staff actions</strong><p>Clear the player or action type filter and try again.</p></div>`;
    }
    const updated = dashboard.querySelector<HTMLElement>("[data-mod-log-updated]");
    if (updated) updated.textContent = `Updated ${new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
    hydratePortalIcons();
    if (!background) setStatus(append ? "Older log records loaded." : "Log dashboard updated.");
  } catch (error) {
    if (!background) {
      result.innerHTML = `<div class="support-empty"><strong>Logs could not be loaded</strong><p>Nothing was changed. Try again in a moment.</p></div>`;
      setStatus(`Log dashboard failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  } finally {
    modLogRefreshInFlight = false;
  }
}

async function handleDiscordLogRoutingLoad(): Promise<void> {
  const result = document.querySelector<HTMLElement>("#discordLogRoutingResult");
  if (!result) return;
  try {
    setStatus("Loading Discord channels and log routes...");
    discordLogRoutingSettings = await apiClient.getDiscordLogRouting(roleIds);
    result.innerHTML = renderDiscordLogRoutingEditor(discordLogRoutingSettings);
    setStatus("Discord log routing loaded.");
  } catch (error) {
    result.textContent = "Discord log routing could not be loaded.";
    setStatus(`Log routing load failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleDiscordLogRoutingSave(): Promise<void> {
  const channelId = document.querySelector<HTMLSelectElement>("[data-discord-log-channel]")?.value ?? "";
  const categories = selectedDiscordLogCategories();
  const enabled = document.querySelector<HTMLInputElement>("[data-discord-log-enabled]")?.checked ?? true;
  const reason = document.querySelector<HTMLInputElement>("[data-discord-log-reason]")?.value.trim() || "Updated Discord log routing from staff portal.";
  if (!channelId || categories.length === 0) throw new Error("Choose a Discord channel and at least one log category.");
  const existing = discordLogRoutingSettings?.routes.find((route) => route.channelId === channelId);
  await apiClient.saveDiscordLogRoute({ ...(existing ? { id: existing.id } : {}), serverId: currentSnapshot.server.id, channelId, categories, enabled, actorUserId: userId, actorDiscordRoleIds: roleIds, reason });
  setStatus("Discord log route saved.");
  await handleDiscordLogRoutingLoad();
}

async function handleDiscordLogRoutingTest(channelId?: string): Promise<void> {
  const selectedChannel = channelId ?? document.querySelector<HTMLSelectElement>("[data-discord-log-channel]")?.value ?? "";
  const route = discordLogRoutingSettings?.routes.find((item) => item.channelId === selectedChannel);
  const categories = route?.categories ?? selectedDiscordLogCategories();
  if (!selectedChannel || categories.length === 0) throw new Error("Choose a configured channel and categories first.");
  await apiClient.previewDiscordLogRoute({ channelId: selectedChannel, categories, actorDiscordRoleIds: roleIds });
  setStatus("Test embed sent to Discord.");
}

async function handleDiscordLogRoutingDelete(routeId: string): Promise<void> {
  await apiClient.deleteDiscordLogRoute(routeId, { actorUserId: userId, actorDiscordRoleIds: roleIds, reason: "Removed Discord log route from staff portal." });
  setStatus("Discord log route removed.");
  await handleDiscordLogRoutingLoad();
}

function populateDiscordLogRoute(channelId: string, categoriesCsv: string, enabled: boolean): void {
  const select = document.querySelector<HTMLSelectElement>("[data-discord-log-channel]");
  if (select) select.value = channelId;
  const categories = new Set(categoriesCsv.split(",").filter(Boolean));
  document.querySelectorAll<HTMLInputElement>("[data-discord-log-category]").forEach((input) => { input.checked = categories.has(input.value); });
  const enabledInput = document.querySelector<HTMLInputElement>("[data-discord-log-enabled]");
  if (enabledInput) enabledInput.checked = enabled;
  document.querySelector<HTMLElement>("[data-discord-log-channel]")?.focus();
}

function selectedDiscordLogCategories(): string[] {
  return Array.from(document.querySelectorAll<HTMLInputElement>("[data-discord-log-category]:checked")).map((input) => input.value);
}

function renderDiscordLogRoutingEditor(settings: DiscordLogRoutingSettings): string {
  const categoryLabels: Record<string, string> = { players: "Joins and leaves", deaths: "Deaths and kills", garage: "Garage", corpse: "Food requests", prime: "Prime", economy: "Economy", events: "Events and rewards", store: "Store", marketplace: "Marketplace", moderation: "Moderation", tickets: "Player tickets", staff_reports: "Staff reports (owner only)", identity: "Account links", server: "Server operations", admin: "Admin actions" };
  return `<div class="discord-log-editor"><div class="discord-log-form"><label class="tool-select"><span>Discord channel</span><select data-discord-log-channel><option value="">Select a channel</option>${settings.channels.map((channel) => `<option value="${escapeHtml(channel.id)}"># ${escapeHtml(channel.name)}</option>`).join("")}</select></label><label class="control-reason"><span>Change reason</span><input data-discord-log-reason value="Updated Discord log routing from staff portal." /></label></div><div><span class="form-label">Activity to include</span><div class="discord-log-categories">${settings.categories.map((category) => `<label class="discord-log-category"><input type="checkbox" value="${escapeHtml(category)}" data-discord-log-category /> ${escapeHtml(categoryLabels[category] ?? category)}</label>`).join("")}</div></div><label class="control-check"><input type="checkbox" data-discord-log-enabled checked /> Route enabled</label><div class="staff-inline-actions"><button class="tool-button primary-tool" type="button" data-portal-action="discord-log-routing-save">Save route</button><button class="tool-button" type="button" data-portal-action="discord-log-routing-test">Send test embed</button></div><div class="discord-log-route-list">${settings.routes.length ? settings.routes.map((route) => `<div class="discord-log-route"><div><strong># ${escapeHtml(route.channelName)}</strong><small>${route.enabled ? "Active" : "Paused"}</small></div><div><small>${route.categories.map((category) => escapeHtml(categoryLabels[category] ?? category)).join(" • ")}</small></div><div class="staff-inline-actions"><button class="tool-button" type="button" data-portal-action="discord-log-routing-edit" data-channel-id="${escapeHtml(route.channelId)}" data-categories="${escapeHtml(route.categories.join(","))}" data-enabled="${String(route.enabled)}">Edit</button><button class="tool-button" type="button" data-portal-action="discord-log-routing-test" data-channel-id="${escapeHtml(route.channelId)}">Test</button><button class="tool-button danger" type="button" data-portal-action="discord-log-routing-delete" data-route-id="${escapeHtml(route.id)}">Remove</button></div></div>`).join("") : `<p class="form-help">No routes configured yet. Choose a channel and its activity categories above.</p>`}</div></div>`;
}

async function handleRulebookLoad(): Promise<void> {
  const result = document.querySelector<HTMLElement>("#rulebookResult");
  if (!result) return;
  try {
    setStatus("Loading versioned rulebook...");
    rulebookCache = await apiClient.listRuleDefinitions(roleIds);
    result.innerHTML = renderRulebookManager(rulebookCache);
    setStatus("Rulebook loaded.");
  } catch (error) {
    result.textContent = "The rulebook could not be loaded.";
    setStatus(`Rulebook load failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleRuleCreate(): Promise<void> {
  const code = document.querySelector<HTMLInputElement>("[data-rule-code]")?.value.trim() ?? "";
  const section = document.querySelector<HTMLInputElement>("[data-rule-section]")?.value.trim() ?? "";
  const title = document.querySelector<HTMLInputElement>("[data-rule-title]")?.value.trim() ?? "";
  const text = document.querySelector<HTMLTextAreaElement>("[data-rule-text]")?.value.trim() ?? "";
  const keywords = (document.querySelector<HTMLInputElement>("[data-rule-keywords]")?.value ?? "").split(",").map((item) => item.trim()).filter(Boolean);
  const reason = document.querySelector<HTMLInputElement>("[data-rule-reason]")?.value.trim() ?? "";
  const baseRuleId = document.querySelector<HTMLInputElement>("[data-rule-base-id]")?.value ?? "";
  if (code.length < 2 || section.length < 2 || title.length < 3 || text.length < 10 || reason.length < 3) {
    setStatus("Complete the rule code, section, title, full text, and audit reason.");
    return;
  }
  try {
    setStatus("Creating rule draft...");
    if (baseRuleId) {
      await apiClient.reviseRuleDefinition(baseRuleId, { actorUserId: userId, actorDiscordRoleIds: roleIds, reason, section, title, text, keywords });
    } else {
      await apiClient.createRuleDefinition({ actorUserId: userId, actorDiscordRoleIds: roleIds, reason, code, section, title, text, keywords });
    }
    clearRuleForm();
    await handleRulebookLoad();
    setStatus("Rule draft created. Review it before publishing.");
  } catch (error) {
    setStatus(`Rule draft failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

function loadRuleRevisionForm(ruleId: string): void {
  const rule = rulebookCache.find((item) => item.id === ruleId);
  if (!rule) return;
  const set = (selector: string, value: string) => {
    const input = document.querySelector<HTMLInputElement | HTMLTextAreaElement>(selector);
    if (input) input.value = value;
  };
  set("[data-rule-base-id]", rule.id);
  set("[data-rule-code]", rule.code);
  set("[data-rule-section]", rule.section);
  set("[data-rule-title]", rule.title);
  set("[data-rule-keywords]", rule.keywords.join(", "));
  set("[data-rule-text]", rule.text);
  document.querySelector<HTMLInputElement>("[data-rule-code]")?.setAttribute("disabled", "true");
  document.querySelector<HTMLInputElement>("[data-rule-reason]")?.focus();
  setStatus(`Editing a new version of ${rule.code}. The published version remains unchanged.`);
}

function clearRuleForm(): void {
  document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("[data-rule-base-id], [data-rule-code], [data-rule-section], [data-rule-title], [data-rule-text], [data-rule-keywords]").forEach((input) => { input.value = ""; });
  document.querySelector<HTMLInputElement>("[data-rule-code]")?.removeAttribute("disabled");
}

async function handleRuleStatus(ruleId: string, action: "publish" | "retire"): Promise<void> {
  const reason = document.querySelector<HTMLInputElement>("[data-rule-reason]")?.value.trim() || `${action === "publish" ? "Published" : "Retired"} from owner portal.`;
  try {
    setStatus(`${action === "publish" ? "Publishing" : "Retiring"} rule...`);
    await apiClient.changeRuleDefinitionStatus(ruleId, action, { actorUserId: userId, actorDiscordRoleIds: roleIds, reason });
    await handleRulebookLoad();
    setStatus(`Rule ${action === "publish" ? "published for staff cases" : "retired"}.`);
  } catch (error) {
    setStatus(`Rule status change failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleCrossSpeciesChatLoad(): Promise<void> {
  const result = document.querySelector<HTMLElement>("#crossSpeciesChatResult");
  if (!result) return;
  try {
    setStatus("Loading the live cross-species chat configuration...");
    crossSpeciesChatSettings = await apiClient.getCrossSpeciesChatConfig(roleIds);
    result.innerHTML = renderCrossSpeciesChatEditor(crossSpeciesChatSettings);
    setStatus("Cross-species chat configuration loaded from the game server.");
  } catch (error) {
    result.textContent = "Cross-species chat configuration could not be loaded.";
    setStatus(`Cross-species chat load failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleAmbientAiLoad(): Promise<void> {
  const result = document.querySelector<HTMLElement>("#ambientAiResult");
  if (!result) return;
  try {
    setStatus("Loading the verified ambient AI policy...");
    ambientAiSettings = await apiClient.getAmbientAiConfig(roleIds);
    result.innerHTML = renderAmbientAiEditor(ambientAiSettings);
    setStatus("Ambient AI policy loaded from the game server.");
  } catch (error) {
    result.textContent = "Ambient AI configuration could not be loaded.";
    setStatus(`Ambient AI load failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleAmbientAiSave(): Promise<void> {
  if (!ambientAiSettings) throw new Error("Load the ambient AI policy before saving it.");
  const reason = document.querySelector<HTMLInputElement>("[data-ambient-ai-reason]")?.value.trim() ?? "";
  if (reason.length < 3) throw new Error("Enter a short audit reason before saving ambient AI.");
  const mode = document.querySelector<HTMLSelectElement>("[data-ambient-ai-mode]")?.value as AmbientAiSettings["config"]["mode"];
  const fallbackDensity = Number(document.querySelector<HTMLInputElement>("[data-ambient-ai-fallback-density]")?.value);
  const updateIntervalSeconds = Number(document.querySelector<HTMLInputElement>("[data-ambient-ai-update-interval]")?.value);
  const minimumChangeIntervalSeconds = Number(document.querySelector<HTMLInputElement>("[data-ambient-ai-change-interval]")?.value);
  const populationBands = Array.from(document.querySelectorAll<HTMLElement>("[data-ambient-ai-band]")).map((row) => ({
    minPlayers: Number(row.querySelector<HTMLInputElement>("[data-ambient-ai-min-players]")?.value),
    density: Number(row.querySelector<HTMLInputElement>("[data-ambient-ai-density]")?.value)
  }));
  setStatus("Saving and verifying the ambient AI policy...");
  ambientAiSettings = await apiClient.saveAmbientAiConfig({ mode, fallbackDensity, updateIntervalSeconds, minimumChangeIntervalSeconds, populationBands, actorUserId: userId, actorDiscordRoleIds: roleIds, reason });
  const result = document.querySelector<HTMLElement>("#ambientAiResult");
  if (result) result.innerHTML = renderAmbientAiEditor(ambientAiSettings);
  setStatus(mode === "game_ini"
    ? "Ambient AI policy saved. Game.ini now remains authoritative; the worker will not send density changes."
    : "Ambient AI policy saved. The worker will apply the next safe density change automatically.");
}

function renderAmbientAiEditor(settings: AmbientAiSettings): string {
  const bands = settings.config.populationBands.map((band) => `<div class="tool-grid" data-ambient-ai-band><label class="control-reason"><span>Players from</span><input type="number" min="0" max="200" value="${band.minPlayers}" data-ambient-ai-min-players /></label><label class="control-reason"><span>AI density</span><input type="number" min="0" max="10" value="${band.density}" data-ambient-ai-density /></label></div>`).join("");
  return `<div class="ambient-ai-editor"><label class="control-reason"><span>Operating mode</span><select data-ambient-ai-mode><option value="adaptive" ${settings.config.mode === "adaptive" ? "selected" : ""}>Adaptive native ecosystem</option><option value="fallback" ${settings.config.mode === "fallback" ? "selected" : ""}>Fixed conservative fallback</option><option value="game_ini" ${settings.config.mode === "game_ini" ? "selected" : ""}>Game.ini controls density</option><option value="off" ${settings.config.mode === "off" ? "selected" : ""}>Off</option></select><small>Game.ini mode sends no density command. Fallback holds one density. Off actively requests zero native AI.</small></label><div class="tool-grid"><label class="control-reason"><span>Fallback density</span><input type="number" min="0" max="10" value="${settings.config.fallbackDensity}" data-ambient-ai-fallback-density /></label><label class="control-reason"><span>Check every (seconds)</span><input type="number" min="15" max="300" value="${settings.config.updateIntervalSeconds}" data-ambient-ai-update-interval /></label><label class="control-reason"><span>Minimum time between changes</span><input type="number" min="30" max="900" value="${settings.config.minimumChangeIntervalSeconds}" data-ambient-ai-change-interval /></label></div><div class="ambient-ai-bands"><span class="form-label">Adaptive population bands</span><p class="form-help">Density changes only when the online population enters a new band. Game.ini mode leaves your host setting untouched.</p>${bands}</div><div class="runtime-readiness-summary"><strong>Safety boundary</strong><small>This controls EVRIMA's verified native AI density. It does not use the target-near manual spawner and does not destroy cached actors from Lua.</small></div><label class="control-reason"><span>Audit reason</span><input data-ambient-ai-reason value="Updated ambient AI policy from the developer portal." /></label><button class="tool-button primary-tool" type="button" data-portal-action="ambient-ai-save" data-confirm-title="Apply ambient AI policy?" data-confirm-message="Adaptive, fallback, and off send native RCON density commands. Game.ini mode deliberately sends none.">Save policy</button></div>`;
}

function startServerHealthRefresh(): void {
  if (serverHealthRefreshTimer !== undefined) return;
  serverHealthRefreshTimer = window.setInterval(() => {
    const panel = document.querySelector<HTMLElement>("#staff-health");
    if (!panel || panel.hidden || document.hidden || portalActionInFlight || portalInteractionActive()) return;
    void handleServerHealthLoad(true);
    void handleCustomAiMapLoad(true);
  }, 10_000);
}

function stopServerHealthRefresh(): void {
  if (serverHealthRefreshTimer !== undefined) {
    window.clearInterval(serverHealthRefreshTimer);
    serverHealthRefreshTimer = undefined;
  }
  disposeCustomAiMap();
}

async function handleServerHealthLoad(background = false): Promise<void> {
  if (serverHealthRefreshInFlight || !currentAccess.permissions.includes("server.status.view")) return;
  const result = document.querySelector<HTMLElement>("#serverHealthResult");
  if (!result) return;
  serverHealthRefreshInFlight = true;
  if (!background) {
    result.innerHTML = `<div class="support-empty"><strong>Reading server instruments</strong><p>Collecting existing heartbeats without scanning the game world.</p></div>`;
  }
  try {
    const health: ServerHealthSettings = await apiClient.getServerHealth(currentSnapshot.server.id, roleIds);
    result.innerHTML = renderServerHealth(health);
    hydratePortalIcons();
    if (!background) setStatus("Server monitor updated.");
  } catch (error) {
    if (!background) {
      result.innerHTML = `<div class="support-empty"><strong>Server instruments unavailable</strong><p>No controls were changed. Existing gameplay systems continue operating normally.</p></div>`;
      setStatus(`Server monitor failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  } finally {
    serverHealthRefreshInFlight = false;
  }
}

async function handleReleaseCentreLoad(): Promise<void> {
  if (releaseCentreRefreshInFlight || !currentAccess.permissions.includes("server.status.view")) return;
  const result = document.querySelector<HTMLElement>("#releaseCentreResult");
  if (!result) return;
  releaseCentreRefreshInFlight = true;
  result.innerHTML = `<div class="support-empty"><strong>Checking this release</strong><p>Comparing live services, continuous backups, loaded game-server versions, and acceptance tests.</p></div>`;
  try {
    const release = await apiClient.getReleaseCentre();
    result.innerHTML = renderReleaseCentre(release);
    hydratePortalIcons();
    setStatus("Release evidence updated.");
  } catch (error) {
    result.innerHTML = `<div class="support-empty"><strong>Release evidence is unavailable</strong><p>No release state or sign-off was changed. Refresh the evidence to try again.</p></div>`;
    setStatus(`Release Centre failed: ${error instanceof Error ? error.message : String(error)}`);
  } finally {
    releaseCentreRefreshInFlight = false;
  }
}

async function handleGrowthCentreLoad(): Promise<void> {
  if (growthCentreRefreshInFlight || !currentAccess.permissions.includes("growth.view")) return;
  const result = document.querySelector<HTMLElement>("#growthCentreResult");
  if (!result) return;
  growthCentreRefreshInFlight = true;
  result.innerHTML = `<div class="support-empty"><strong>Reading growth instruments</strong><p>Combining existing game-server samples, verified activity totals, and the external listing monitor.</p></div>`;
  try {
    const report: GrowthCentreReport = await apiClient.getGrowthCentre(growthCentreDays);
    result.innerHTML = renderGrowthCentre(report);
    hydratePortalIcons();
    setStatus(`Growth Centre updated for the last ${report.rangeDays} days.`);
  } catch (error) {
    result.innerHTML = `<div class="support-empty"><strong>Growth Centre unavailable</strong><p>No game-server or community settings were changed. Refresh this workspace to try again.</p></div>`;
    setStatus(`Growth Centre failed: ${error instanceof Error ? error.message : String(error)}`);
  } finally {
    growthCentreRefreshInFlight = false;
  }
}

async function handleReleaseSignoff(button: HTMLElement): Promise<void> {
  const checkKey = button.dataset.releaseCheckKey;
  const resultValue = button.dataset.releaseResult;
  const card = button.closest<HTMLElement>("[data-release-check]");
  const notes = card?.querySelector<HTMLInputElement>("[data-release-signoff-note]")?.value.trim() ?? "";
  if (!checkKey || (resultValue !== "passed" && resultValue !== "failed" && resultValue !== "blocked")) throw new Error("This release check could not be identified.");
  if (notes.length < 3) throw new Error("Add a short note describing what you tested.");
  const release: ReleaseCentreSettings = await apiClient.saveReleaseSignoff({ checkKey, result: resultValue, notes });
  const container = document.querySelector<HTMLElement>("#releaseCentreResult");
  if (container) container.innerHTML = renderReleaseCentre(release);
  hydratePortalIcons();
  setStatus(`${card?.querySelector("strong")?.textContent ?? "Release check"} marked ${resultValue}.`);
}

async function handleCorpseCleanupLoad(): Promise<void> {
  const result = document.querySelector<HTMLElement>("#corpseCleanupConfig");
  if (!result) return;
  try {
    corpseCleanupSettings = await apiClient.getCorpseCleanupConfig(roleIds);
    result.innerHTML = renderCorpseCleanupEditor(corpseCleanupSettings);
  } catch (error) {
    result.textContent = "Corpse cleanup schedule could not be loaded.";
    setStatus(`Corpse cleanup load failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleCorpseCleanupSave(): Promise<void> {
  if (!corpseCleanupSettings) throw new Error("Load the corpse cleanup schedule before saving it.");
  const reason = document.querySelector<HTMLInputElement>("[data-corpse-cleanup-reason]")?.value.trim() ?? "";
  const intervalHours = Number(document.querySelector<HTMLInputElement>("[data-corpse-cleanup-interval]")?.value);
  const warningMinutes = Number(document.querySelector<HTMLInputElement>("[data-corpse-cleanup-warning]")?.value);
  if (!Number.isInteger(intervalHours) || intervalHours < 1 || intervalHours > 6) throw new Error("Cleanup interval must be from 1 to 6 hours.");
  if (!Number.isInteger(warningMinutes) || warningMinutes < 1 || warningMinutes > 30) throw new Error("Warning time must be from 1 to 30 minutes.");
  if (reason.length < 3) throw new Error("Enter a short audit reason before saving the cleanup schedule.");
  setStatus("Saving the corpse cleanup schedule...");
  corpseCleanupSettings = await apiClient.saveCorpseCleanupConfig({
    enabled: document.querySelector<HTMLInputElement>("[data-corpse-cleanup-enabled]")?.checked ?? false,
    intervalHours, warningMinutes, actorUserId: userId, actorDiscordRoleIds: roleIds, reason
  });
  const result = document.querySelector<HTMLElement>("#corpseCleanupConfig");
  if (result) result.innerHTML = renderCorpseCleanupEditor(corpseCleanupSettings);
  setStatus("Corpse cleanup schedule saved and audited. No game-server restart is needed.");
}

function renderCorpseCleanupEditor(settings: CorpseCleanupSettings): string {
  return `<div class="event-wheel-config"><div class="event-wheel-config-basics"><label class="control-check"><input type="checkbox" data-corpse-cleanup-enabled${settings.config.enabled ? " checked" : ""} /> Automatic cleanup enabled</label><label><span>Run every</span><select data-corpse-cleanup-interval>${[1, 2, 3, 4, 6].map((hours) => `<option value="${hours}"${hours === settings.config.intervalHours ? " selected" : ""}>${hours} hour${hours === 1 ? "" : "s"} after each restart</option>`).join("")}</select></label><label><span>Warn players</span><select data-corpse-cleanup-warning>${[5, 10, 15].map((minutes) => `<option value="${minutes}"${minutes === settings.config.warningMinutes ? " selected" : ""}>${minutes} minutes before</option>`).join("")}</select></label></div><div class="runtime-readiness-summary"><strong>Restart-anchored UK schedule</strong><small>Restarts remain at 01:00 and 13:00. At the recommended two-hour interval, cleanup runs at 03:00, 05:00, 07:00, 09:00, 11:00 and again from 15:00 through 23:00. This removes every corpse through native RCON; living AI is never cycled.</small></div><label class="control-reason"><span>Audit reason</span><input data-corpse-cleanup-reason maxlength="500" value="Updated the automatic corpse cleanup schedule." /></label><button class="tool-button primary-tool" type="button" data-portal-action="corpse-cleanup-save" data-confirm-title="Update corpse cleanup?" data-confirm-message="The worker will use EVRIMA's global corpse wipe at the selected times. This removes player and AI corpses, but does not remove living AI.">Save cleanup schedule</button></div>`;
}

type EcosystemAiZone = EcosystemZoneSettings["config"]["aiZones"][number];
type EcosystemPlantZone = EcosystemZoneSettings["config"]["plantZones"][number];
type EcosystemZone = EcosystemAiZone | EcosystemPlantZone;

async function handleEcosystemZonesLoad(): Promise<void> {
  const result = document.querySelector<HTMLElement>("#ecosystemZonesResult");
  if (!result) return;
  try {
    setStatus("Loading the Gateway ecosystem plan...");
    ecosystemZoneSettings = await apiClient.getEcosystemZones(roleIds);
    const zones = allEcosystemZones();
    if (!zones.some((zone) => zone.id === selectedEcosystemZoneId)) selectedEcosystemZoneId = zones[0]?.id;
    renderEcosystemZoneWorkspace();
    setStatus("Ecosystem plan loaded. Click Gateway to place the selected zone.");
  } catch (error) {
    result.textContent = "The ecosystem zone planner could not be loaded.";
    setStatus(`Ecosystem zone load failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleEcosystemZonesSave(): Promise<void> {
  if (!ecosystemZoneSettings) throw new Error("Open the ecosystem zone planner before saving.");
  commitEcosystemZoneForm();
  const reason = document.querySelector<HTMLInputElement>("[data-ecosystem-zone-reason]")?.value.trim() ?? "";
  if (reason.length < 3) throw new Error("Enter a short audit reason before saving ecosystem zones.");
  const enabledPlantZones = ecosystemZoneSettings.config.plantZones.filter((zone) => zone.enabled);
  const livePlantTotal = enabledPlantZones.reduce((total, zone) => total + zone.countPerRestart, 0);
  const oversizedZone = enabledPlantZones.find((zone) => zone.countPerRestart < 1 || zone.countPerRestart > 100);
  if (oversizedZone) throw new Error(`${oversizedZone.name} must target between 1 and 100 live plants.`);
  if (livePlantTotal > 200) throw new Error("Enabled plant sanctuaries can target at most 200 live plants in total.");
  if (enabledPlantZones.some((zone) => zone.species.length > 8)) {
    throw new Error("Each plant sanctuary can contain at most eight weighted plant types.");
  }
  setStatus("Saving and verifying the ecosystem zone plan...");
  ecosystemZoneSettings = await apiClient.saveEcosystemZones({
    ...ecosystemZoneSettings.config,
    aiExecution: "off",
    plantExecution: "off",
    actorUserId: userId,
    actorDiscordRoleIds: roleIds,
    reason
  });
  renderEcosystemZoneWorkspace();
  setStatus("Zone plan saved and published. The plant controller will reconcile owned plants without touching native foliage.");
}

function addEcosystemZone(kind: "ai" | "plant"): void {
  if (!ecosystemZoneSettings) return;
  commitEcosystemZoneForm();
  const id = uniqueEcosystemZoneId(kind === "ai" ? "ai-zone" : "herbivore-heaven");
  if (kind === "ai") {
    ecosystemZoneSettings.config.aiZones.push({
      id,
      name: "New AI territory",
      category: "",
      enabled: false,
      center: { mapX: 2048, mapY: 2048 },
      radiusWorldUnits: 15_000,
      spawnRangeWorldUnits: 10_000,
      countPerWave: 1,
      growth: 0.75,
      respawnSeconds: 300,
      despawnSecondsWithoutPlayers: 300,
      maxAlive: 4,
      behavior: "native",
      primeQuestPolicy: "neutral",
      markerColor: "#D86B4F",
      species: [{ species: "Deer", weight: 100 }]
    });
  } else {
    ecosystemZoneSettings.config.plantZones.push({
      id,
      name: ecosystemZoneSettings.config.plantZones.length === 0 ? "Herbivore Heaven" : "New plant sanctuary",
      enabled: false,
      center: { mapX: 2048, mapY: 2048 },
      radiusWorldUnits: 10_000,
      countPerRestart: 20,
      species: [{ species: "fern", weight: 50 }, { species: "cycad", weight: 50 }]
    });
  }
  selectedEcosystemZoneId = id;
  renderEcosystemZoneWorkspace();
  setStatus("Zone created as disabled. Click its intended centre on the map, then review its limits.");
}

function selectEcosystemZone(zoneId: string): void {
  if (!ecosystemZoneSettings) return;
  commitEcosystemZoneForm();
  if (!allEcosystemZones().some((zone) => zone.id === zoneId)) return;
  selectedEcosystemZoneId = zoneId;
  renderEcosystemZoneWorkspace();
}

function deleteEcosystemZone(zoneId: string): void {
  if (!ecosystemZoneSettings) return;
  ecosystemZoneSettings.config.aiZones = ecosystemZoneSettings.config.aiZones.filter((zone) => zone.id !== zoneId);
  ecosystemZoneSettings.config.plantZones = ecosystemZoneSettings.config.plantZones.filter((zone) => zone.id !== zoneId);
  selectedEcosystemZoneId = allEcosystemZones()[0]?.id;
  renderEcosystemZoneWorkspace();
  setStatus("Zone removed from the draft. Save the plan to make the removal authoritative.");
}

function addEcosystemZoneSpecies(): void {
  const zone = selectedEcosystemZone();
  if (!zone || !ecosystemZoneSettings) return;
  commitEcosystemZoneForm();
  const choices = isPlantZone(zone)
    ? ecosystemZoneSettings.plantSpecies.map((plant) => plant.id)
    : ecosystemZoneSettings.aiSpecies;
  const next = choices.find((species) => !zone.species.some((entry) => entry.species === species));
  if (!next) {
    setStatus("Every supported species is already included in this zone.");
    return;
  }
  zone.species.push({ species: next, weight: 25 });
  renderEcosystemZoneWorkspace();
}

function removeEcosystemZoneSpecies(index: number): void {
  const zone = selectedEcosystemZone();
  if (!zone) return;
  commitEcosystemZoneForm();
  if (zone.species.length <= 1) {
    setStatus("A zone must retain at least one species.");
    return;
  }
  zone.species.splice(index, 1);
  renderEcosystemZoneWorkspace();
}

function renderEcosystemZoneWorkspace(): void {
  const result = document.querySelector<HTMLElement>("#ecosystemZonesResult");
  if (!result || !ecosystemZoneSettings) return;
  const zones = allEcosystemZones();
  const selected = selectedEcosystemZone();
  const zoneButtons = zones.length > 0
    ? zones.map((zone) => {
      const kind = isPlantZone(zone) ? "Plants" : "AI";
      return `<button class="ecosystem-zone-item${zone.id === selectedEcosystemZoneId ? " is-selected" : ""}" type="button" data-portal-action="ecosystem-zone-select" data-zone-id="${escapePortalText(zone.id)}"><span><strong>${escapePortalText(zone.name)}</strong><small>${kind} // ${zone.enabled ? "planned on" : "draft off"}</small></span><i data-lucide="ChevronRight" aria-hidden="true"></i></button>`;
    }).join("")
    : `<div class="support-empty"><strong>No zones yet</strong><p>Add an AI territory or Herbivore Heaven, then click the map to position it.</p></div>`;
  result.innerHTML = `
    <div class="ecosystem-zone-editor">
      <div class="ecosystem-zone-status"><span><i data-lucide="ShieldCheck" aria-hidden="true"></i>Bounded live control</span><p>${escapePortalText(ecosystemZoneSettings.rollout.note)}</p></div>
      <div class="ecosystem-zone-layout">
        <aside class="ecosystem-zone-sidebar">
          <div class="staff-inline-actions">
            <button class="tool-button" type="button" data-portal-action="ecosystem-zone-add-ai"><i data-lucide="PawPrint" aria-hidden="true"></i>Add AI</button>
            <button class="tool-button" type="button" data-portal-action="ecosystem-zone-add-plant"><i data-lucide="Sprout" aria-hidden="true"></i>Add plants</button>
          </div>
          <div class="ecosystem-zone-list">${zoneButtons}</div>
        </aside>
        <div class="ecosystem-zone-canvas-wrap">
          <div id="ecosystemZoneMap" class="ecosystem-zone-map" role="region" aria-label="Gateway ecosystem zone placement map"></div>
          <p class="form-help">Select a zone, then click the map to move its centre. Scroll over the point you want to inspect to zoom towards it; drag to pan. The boundary is projected from the exact world-unit radius.</p>
        </div>
      </div>
      ${selected ? renderEcosystemZoneForm(selected) : ""}
      <label class="control-reason"><span>Audit reason</span><input maxlength="500" data-ecosystem-zone-reason value="Updated Gateway ecosystem zone plan." /></label>
      <button class="tool-button primary-tool" type="button" data-portal-action="ecosystem-zones-save" data-confirm-title="Apply ecosystem zone plan?" data-confirm-message="The plan will be verified and audited. Plant changes reconcile live, one controller-owned actor per second; AI settings keep their existing safety boundary."><i data-lucide="Save" aria-hidden="true"></i>Apply zone plan</button>
    </div>`;
  hydratePortalIcons();
  void initializeEcosystemZoneMap(zones, selectedEcosystemZoneId, placeSelectedEcosystemZone);
}

function renderEcosystemZoneForm(zone: EcosystemZone): string {
  const plant = isPlantZone(zone);
  const settings = ecosystemZoneSettings!;
  const available = plant
    ? settings.plantSpecies.map((item) => ({ value: item.id, label: item.name }))
    : settings.aiSpecies.map((species) => ({ value: species, label: species }));
  const speciesRows = zone.species.map((entry, index) => `
    <div class="ecosystem-species-row" data-ecosystem-species-row>
      <label><span>Species</span><select data-ecosystem-species>${available.map((item) => `<option value="${escapePortalText(item.value)}" ${item.value === entry.species ? "selected" : ""}>${escapePortalText(item.label)}</option>`).join("")}</select></label>
      <label><span>Weight</span><input type="number" min="1" max="100" step="1" value="${entry.weight}" data-ecosystem-species-weight /></label>
      <button class="tool-button danger" type="button" data-portal-action="ecosystem-zone-species-remove" data-species-index="${index}" aria-label="Remove ${escapePortalText(entry.species)}"><i data-lucide="X" aria-hidden="true"></i></button>
    </div>`).join("");
  const specific = plant
    ? `<label><span>Target live plants</span><input type="number" min="1" max="100" step="1" value="${zone.countPerRestart}" data-ecosystem-count-per-restart /><small>Up to 100 per zone and 200 total. Saving adds or retires only controller-owned plants, one operation at a time.</small></label>`
    : `<label><span>Category</span><input maxlength="40" placeholder="No category" value="${escapePortalText(zone.category)}" data-ecosystem-category /></label><label><span>Behaviour</span><select data-ecosystem-behavior><option value="native" ${zone.behavior === "native" ? "selected" : ""}>Native behaviour</option><option value="aggressive" ${zone.behavior === "aggressive" ? "selected" : ""}>Aggressive / always hunt</option></select><small>A planning intent until current-build AI behaviour is proven.</small></label><label><span>Prime quest zone</span><select data-ecosystem-prime-policy><option value="neutral" ${zone.primeQuestPolicy === "neutral" ? "selected" : ""}>No special treatment</option><option value="exclude" ${zone.primeQuestPolicy === "exclude" ? "selected" : ""}>Exclude this zone</option></select></label><label><span>Spawn range (world units)</span><input type="number" min="500" max="${zone.radiusWorldUnits}" step="500" value="${zone.spawnRangeWorldUnits}" data-ecosystem-spawn-range /></label><label><span>Count per wave</span><input type="number" min="1" max="5" step="1" value="${zone.countPerWave}" data-ecosystem-count-per-wave /></label><label><span>Maximum alive</span><input type="number" min="${zone.countPerWave}" max="20" step="1" value="${zone.maxAlive}" data-ecosystem-max-alive /></label><label><span>Growth</span><input type="number" min="0.05" max="1" step="0.05" value="${zone.growth}" data-ecosystem-growth /></label><label><span>Respawn delay (seconds)</span><input type="number" min="60" max="3600" step="30" value="${zone.respawnSeconds}" data-ecosystem-respawn /></label><label><span>Despawn if no players nearby (seconds)</span><input type="number" min="30" max="3600" step="30" value="${zone.despawnSecondsWithoutPlayers}" data-ecosystem-despawn /></label><label><span>Map marker colour</span><div class="ecosystem-colour-field"><input type="color" value="${escapePortalText(zone.markerColor)}" data-ecosystem-marker-color /><code>${escapePortalText(zone.markerColor)}</code></div></label>`;
  return `<section class="ecosystem-zone-form" data-ecosystem-zone-form data-zone-id="${escapePortalText(zone.id)}">
    <div class="ecosystem-zone-form-heading"><div><span class="eyebrow">${plant ? "Plant sanctuary" : "AI territory"}</span><h4>${escapePortalText(zone.name)}</h4></div><button class="tool-button danger" type="button" data-portal-action="ecosystem-zone-delete" data-zone-id="${escapePortalText(zone.id)}"><i data-lucide="Trash2" aria-hidden="true"></i>Delete zone</button></div>
    <div class="tool-grid">
      <label class="control-reason"><span>Name</span><input minlength="3" maxlength="60" value="${escapePortalText(zone.name)}" data-ecosystem-name /></label>
      <label class="checkbox-line"><input type="checkbox" ${zone.enabled ? "checked" : ""} data-ecosystem-enabled /><span><strong>${plant ? "Live enabled" : "Planned enabled"}</strong><small>${plant ? "Saving reconciles this sanctuary on the running server." : "AI execution retains its current guarded rollout."}</small></span></label>
      <label class="control-reason"><span>Map X</span><input type="number" min="0" max="4096" step="0.1" value="${zone.center.mapX}" data-ecosystem-map-x /></label>
      <label class="control-reason"><span>Map Y</span><input type="number" min="0" max="4096" step="0.1" value="${zone.center.mapY}" data-ecosystem-map-y /></label>
      <label class="control-reason"><span>Zone radius (world units)</span><input type="number" min="${plant ? 500 : 1000}" max="${plant ? 30000 : 50000}" step="500" value="${zone.radiusWorldUnits}" data-ecosystem-radius /><small>The map boundary updates as this value changes.</small></label>
      ${specific}
    </div>
    <div class="ecosystem-species-editor"><div><span class="form-label">Weighted ${plant ? "plants" : "species"}</span><p class="form-help">Weights are relative. The game picks between entries; they do not need to total 100.</p></div>${speciesRows}<button class="tool-button" type="button" data-portal-action="ecosystem-zone-species-add"><i data-lucide="Plus" aria-hidden="true"></i>Add species</button></div>
  </section>`;
}

function commitEcosystemZoneForm(): void {
  const zone = selectedEcosystemZone();
  const form = document.querySelector<HTMLElement>("[data-ecosystem-zone-form]");
  if (!zone || !form || form.dataset.zoneId !== zone.id) return;
  zone.name = form.querySelector<HTMLInputElement>("[data-ecosystem-name]")?.value.trim() ?? zone.name;
  zone.enabled = form.querySelector<HTMLInputElement>("[data-ecosystem-enabled]")?.checked ?? false;
  zone.center = {
    mapX: Number(form.querySelector<HTMLInputElement>("[data-ecosystem-map-x]")?.value),
    mapY: Number(form.querySelector<HTMLInputElement>("[data-ecosystem-map-y]")?.value)
  };
  zone.radiusWorldUnits = Number(form.querySelector<HTMLInputElement>("[data-ecosystem-radius]")?.value);
  zone.species = Array.from(form.querySelectorAll<HTMLElement>("[data-ecosystem-species-row]")).map((row) => ({
    species: row.querySelector<HTMLSelectElement>("[data-ecosystem-species]")?.value ?? "",
    weight: Number(row.querySelector<HTMLInputElement>("[data-ecosystem-species-weight]")?.value)
  }));
  if (isPlantZone(zone)) {
    zone.countPerRestart = Number(form.querySelector<HTMLInputElement>("[data-ecosystem-count-per-restart]")?.value);
  } else {
    zone.category = form.querySelector<HTMLInputElement>("[data-ecosystem-category]")?.value.trim() ?? "";
    zone.behavior = form.querySelector<HTMLSelectElement>("[data-ecosystem-behavior]")?.value as EcosystemAiZone["behavior"];
    zone.primeQuestPolicy = form.querySelector<HTMLSelectElement>("[data-ecosystem-prime-policy]")?.value as EcosystemAiZone["primeQuestPolicy"];
    zone.spawnRangeWorldUnits = Number(form.querySelector<HTMLInputElement>("[data-ecosystem-spawn-range]")?.value);
    zone.countPerWave = Number(form.querySelector<HTMLInputElement>("[data-ecosystem-count-per-wave]")?.value);
    zone.maxAlive = Number(form.querySelector<HTMLInputElement>("[data-ecosystem-max-alive]")?.value);
    zone.growth = Number(form.querySelector<HTMLInputElement>("[data-ecosystem-growth]")?.value);
    zone.respawnSeconds = Number(form.querySelector<HTMLInputElement>("[data-ecosystem-respawn]")?.value);
    zone.despawnSecondsWithoutPlayers = Number(form.querySelector<HTMLInputElement>("[data-ecosystem-despawn]")?.value);
    zone.markerColor = form.querySelector<HTMLInputElement>("[data-ecosystem-marker-color]")?.value ?? zone.markerColor;
  }
}

function placeSelectedEcosystemZone(mapX: number, mapY: number): void {
  const zone = selectedEcosystemZone();
  if (!zone) {
    setStatus("Add or select a zone before placing it on Gateway.");
    return;
  }
  zone.center = { mapX, mapY };
  const xInput = document.querySelector<HTMLInputElement>("[data-ecosystem-map-x]");
  const yInput = document.querySelector<HTMLInputElement>("[data-ecosystem-map-y]");
  if (xInput) xInput.value = String(mapX);
  if (yInput) yInput.value = String(mapY);
  refreshEcosystemZoneMap(allEcosystemZones(), selectedEcosystemZoneId);
  setStatus(`${zone.name} moved to map ${mapX.toFixed(1)}, ${mapY.toFixed(1)}.`);
}

function previewEcosystemZoneGeometry(): void {
  const zone = selectedEcosystemZone();
  const form = document.querySelector<HTMLElement>("[data-ecosystem-zone-form]");
  if (!zone || !form || form.dataset.zoneId !== zone.id) return;
  const mapX = Number(form.querySelector<HTMLInputElement>("[data-ecosystem-map-x]")?.value);
  const mapY = Number(form.querySelector<HTMLInputElement>("[data-ecosystem-map-y]")?.value);
  const radius = Number(form.querySelector<HTMLInputElement>("[data-ecosystem-radius]")?.value);
  if (Number.isFinite(mapX) && mapX >= 0 && mapX <= GATEWAY_MAP_SIZE
    && Number.isFinite(mapY) && mapY >= 0 && mapY <= GATEWAY_MAP_SIZE) {
    zone.center = { mapX, mapY };
  }
  if (Number.isFinite(radius) && radius > 0) zone.radiusWorldUnits = radius;
  refreshEcosystemZoneMap(allEcosystemZones(), selectedEcosystemZoneId);
}

function allEcosystemZones(): EcosystemZone[] {
  if (!ecosystemZoneSettings) return [];
  return [...ecosystemZoneSettings.config.aiZones, ...ecosystemZoneSettings.config.plantZones];
}

function selectedEcosystemZone(): EcosystemZone | undefined {
  return allEcosystemZones().find((zone) => zone.id === selectedEcosystemZoneId);
}

function isPlantZone(zone: EcosystemZone): zone is EcosystemPlantZone {
  return "countPerRestart" in zone;
}

function uniqueEcosystemZoneId(prefix: string): string {
  const ids = new Set(allEcosystemZones().map((zone) => zone.id));
  const base = `${prefix}-${Date.now().toString(36)}`.slice(0, 40);
  if (!ids.has(base)) return base;
  let suffix = 2;
  while (ids.has(`${base.slice(0, 37)}-${suffix}`)) suffix += 1;
  return `${base.slice(0, 37)}-${suffix}`;
}

async function handleCrossSpeciesChatSave(): Promise<void> {
  if (!crossSpeciesChatSettings) throw new Error("Load the live chat configuration before saving it.");
  const reason = document.querySelector<HTMLInputElement>("[data-cross-chat-reason]")?.value.trim() ?? "";
  if (reason.length < 3) throw new Error("Enter a short audit reason before saving cross-species chat.");
  const maxRange = Number(document.querySelector<HTMLInputElement>("[data-cross-chat-range]")?.value ?? "");
  if (!Number.isInteger(maxRange) || maxRange < 100 || maxRange > 10_000) throw new Error("Hearing range must be a whole number from 100 to 10,000.");
  setStatus("Saving and verifying cross-species chat on the game server...");
  crossSpeciesChatSettings = await apiClient.saveCrossSpeciesChatConfig({
    enabled: document.querySelector<HTMLInputElement>("[data-cross-chat-enabled]")?.checked ?? false,
    maxRange,
    allowAllPairs: document.querySelector<HTMLInputElement>("[data-cross-chat-all-pairs]")?.checked ?? false,
    allowedPairs: crossSpeciesChatSettings.config.allowedPairs,
    actorUserId: userId,
    actorDiscordRoleIds: roleIds,
    reason
  });
  const result = document.querySelector<HTMLElement>("#crossSpeciesChatResult");
  if (result) result.innerHTML = renderCrossSpeciesChatEditor(crossSpeciesChatSettings);
  setStatus("Cross-species chat saved, verified, and audited. No restart is needed.");
}

function addCrossSpeciesChatPair(): void {
  if (!crossSpeciesChatSettings) return;
  const speciesA = document.querySelector<HTMLSelectElement>("[data-cross-chat-species-a]")?.value ?? "";
  const speciesB = document.querySelector<HTMLSelectElement>("[data-cross-chat-species-b]")?.value ?? "";
  if (!speciesA || !speciesB || speciesA === speciesB) {
    setStatus("Choose two different species for the chat pair.");
    return;
  }
  const key = [speciesA, speciesB].sort().join(":");
  const duplicate = crossSpeciesChatSettings.config.allowedPairs.some((pair) => [pair.speciesA, pair.speciesB].sort().join(":") === key);
  if (duplicate) {
    setStatus("That species pair is already in the list.");
    return;
  }
  crossSpeciesChatSettings.config.allowedPairs.push({ speciesA, speciesB });
  refreshCrossSpeciesChatPairs();
  setStatus("Pair added locally. Save changes to apply it to the server.");
}

function removeCrossSpeciesChatPair(index: number): void {
  if (!crossSpeciesChatSettings || !Number.isInteger(index)) return;
  crossSpeciesChatSettings.config.allowedPairs.splice(index, 1);
  refreshCrossSpeciesChatPairs();
  setStatus("Pair removed locally. Save changes to apply it to the server.");
}

function refreshCrossSpeciesChatPairs(): void {
  const container = document.querySelector<HTMLElement>("#crossChatPairList");
  if (container && crossSpeciesChatSettings) {
    container.innerHTML = renderCrossSpeciesChatPairList(crossSpeciesChatSettings.config.allowedPairs);
  }
}

function renderCrossSpeciesChatEditor(settings: CrossSpeciesChatSettings): string {
  const options = settings.availableSpecies.map((species) => `<option value="${escapeHtml(species)}">${escapeHtml(species)}</option>`).join("");
  return `<div class="cross-chat-editor">
    <div class="cross-chat-primary-controls">
      <label class="control-check"><input type="checkbox" data-cross-chat-enabled ${settings.config.enabled ? "checked" : ""} /> Enable cross-species chat</label>
      <label class="control-reason"><span>Maximum hearing range</span><input type="number" min="100" max="10000" step="100" data-cross-chat-range value="${settings.config.maxRange}" /><small>World units; 3,000 is a practical proximity-chat starting point.</small></label>
      <label class="control-check"><input type="checkbox" data-cross-chat-all-pairs ${settings.config.allowAllPairs ? "checked" : ""} /> Allow every species pair</label>
    </div>
    <div class="cross-chat-pairs"><div><span class="form-label">Explicitly allowed pairs</span><p class="form-help">Used only when “Allow every species pair” is off.</p></div>
      <div class="cross-chat-pair-form"><select data-cross-chat-species-a aria-label="First species">${options}</select><span aria-hidden="true">and</span><select data-cross-chat-species-b aria-label="Second species">${options}</select><button class="tool-button" type="button" data-portal-action="cross-chat-pair-add">Add pair</button></div>
      <div id="crossChatPairList" class="cross-chat-pair-list">${renderCrossSpeciesChatPairList(settings.config.allowedPairs)}</div>
    </div>
    <label class="control-reason"><span>Audit reason</span><input data-cross-chat-reason value="Updated cross-species chat from the configuration portal." /></label>
    <button class="tool-button primary-tool" type="button" data-portal-action="cross-chat-save" data-confirm-title="Apply cross-species chat settings?" data-confirm-message="The verified settings will hot-reload on the game server and the change will be audited.">Save and apply</button>
  </div>`;
}

function renderCrossSpeciesChatPairList(pairs: Array<{ speciesA: string; speciesB: string }>): string {
  if (!pairs.length) return `<p class="form-help">No explicit pairs configured.</p>`;
  return pairs.map((pair, index) => `<div class="cross-chat-pair"><span><strong>${escapeHtml(pair.speciesA)}</strong> and <strong>${escapeHtml(pair.speciesB)}</strong></span><button class="tool-button" type="button" data-portal-action="cross-chat-pair-remove" data-pair-index="${index}">Remove</button></div>`).join("");
}

async function handleStaffCredentialsLoad(): Promise<void> {
  const result = document.querySelector<HTMLElement>("#staffCredentialsResult");
  if (!result) return;
  try {
    setStatus("Loading developer credentials from the game server...");
    staffCredentialsSettings = await apiClient.getStaffCredentials(roleIds);
    result.innerHTML = renderStaffCredentialsEditor(staffCredentialsSettings, result.dataset.selfSteamId ?? currentSnapshot.player.steam64Id ?? "");
    setStatus("Developer credentials loaded.");
  } catch (error) {
    result.textContent = "Developer credentials could not be loaded.";
    setStatus(`Developer credential load failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleStaffCredentialsSave(): Promise<void> {
  if (!staffCredentialsSettings) throw new Error("Load developer credentials before saving them.");
  const reason = document.querySelector<HTMLInputElement>("[data-staff-credentials-reason]")?.value.trim() ?? "";
  if (reason.length < 3) throw new Error("Enter a short audit reason before saving developer credentials.");
  setStatus("Saving and verifying developer credentials...");
  staffCredentialsSettings = await apiClient.saveStaffCredentials({
    enabled: document.querySelector<HTMLInputElement>("[data-staff-credentials-enabled]")?.checked ?? false,
    developerSteamIds: staffCredentialsSettings.config.developerSteamIds,
    actorUserId: userId,
    actorDiscordRoleIds: roleIds,
    reason
  });
  const result = document.querySelector<HTMLElement>("#staffCredentialsResult");
  if (result) result.innerHTML = renderStaffCredentialsEditor(staffCredentialsSettings, result.dataset.selfSteamId ?? currentSnapshot.player.steam64Id ?? "");
  setStatus("Developer credentials saved and audited. Online names update within about 15 seconds.");
}

function addStaffCredential(explicitSteamId?: string): void {
  if (!staffCredentialsSettings) return;
  const input = document.querySelector<HTMLInputElement>("[data-staff-credentials-steam]");
  const steamId = explicitSteamId?.trim() || input?.value.trim() || "";
  if (!/^7656119\d{10}$/.test(steamId)) {
    setStatus("Enter a valid 17-digit Steam64 ID.");
    return;
  }
  if (staffCredentialsSettings.config.developerSteamIds.includes(steamId)) {
    setStatus("That Steam account already has a developer assignment in this list.");
    return;
  }
  staffCredentialsSettings.config.developerSteamIds.push(steamId);
  refreshStaffCredentialList();
  if (input && !explicitSteamId) input.value = "";
  setStatus("Developer added locally. Save changes to apply it to the game server.");
}

function removeStaffCredential(steamId: string): void {
  if (!staffCredentialsSettings) return;
  staffCredentialsSettings.config.developerSteamIds = staffCredentialsSettings.config.developerSteamIds.filter((id) => id !== steamId);
  refreshStaffCredentialList();
  setStatus("Developer removed locally. Save changes to apply it to the game server.");
}

function refreshStaffCredentialList(): void {
  const list = document.querySelector<HTMLElement>("#staffCredentialList");
  if (list && staffCredentialsSettings) list.innerHTML = renderStaffCredentialList(staffCredentialsSettings.config.developerSteamIds);
}

function renderStaffCredentialsEditor(settings: StaffCredentialsSettings, selfSteamId: string): string {
  const selfConfigured = Boolean(selfSteamId && settings.config.developerSteamIds.includes(selfSteamId));
  return `<div class="staff-credentials-editor">
    <label class="control-check"><input type="checkbox" data-staff-credentials-enabled ${settings.config.enabled ? "checked" : ""} /> Apply configured developer names</label>
    ${selfSteamId ? `<div class="staff-self-credential"><div><strong>Your linked Steam account</strong><small>${escapeHtml(selfSteamId)}${selfConfigured ? " is already configured." : " can be given the red developer name."}</small></div><button class="tool-button" type="button" data-portal-action="staff-credentials-add-self" data-steam-id="${escapeHtml(selfSteamId)}" ${selfConfigured ? "disabled" : ""}>${selfConfigured ? "Added" : "Add my account"}</button></div>` : ""}
    <div class="staff-credential-add"><label class="control-reason"><span>Another Steam64 ID</span><input data-staff-credentials-steam placeholder="7656119..." /></label><button class="tool-button" type="button" data-portal-action="staff-credentials-add">Add account</button></div>
    <div id="staffCredentialList" class="staff-credential-list">${renderStaffCredentialList(settings.config.developerSteamIds)}</div>
    <label class="control-reason"><span>Audit reason</span><input data-staff-credentials-reason value="Updated in-game developer credential assignments." /></label>
    <button class="tool-button primary-tool" type="button" data-portal-action="staff-credentials-save" data-confirm-title="Apply developer credentials?" data-confirm-message="Configured online players will receive or lose the red developer name. Portal permissions are unchanged.">Save and apply</button>
  </div>`;
}

function renderStaffCredentialList(steamIds: string[]): string {
  if (!steamIds.length) return `<p class="form-help">No developer Steam accounts are configured.</p>`;
  return steamIds.map((steamId) => `<div class="staff-credential-row"><code>${escapeHtml(steamId)}</code><button class="tool-button danger" type="button" data-portal-action="staff-credentials-remove" data-steam-id="${escapeHtml(steamId)}">Remove</button></div>`).join("");
}

async function handleGarageCapacityLoad(): Promise<void> {
  const result = document.querySelector<HTMLElement>("#garageCapacityResult");
  if (!result) return;
  try {
    setStatus("Loading garage capacity policy...");
    garageCapacityPolicySettings = await apiClient.getGarageCapacityPolicy(roleIds);
    result.innerHTML = renderGarageCapacityEditor(garageCapacityPolicySettings);
    setStatus("Garage capacity policy loaded.");
  } catch (error) {
    result.textContent = "Garage capacity could not be loaded.";
    setStatus(`Garage capacity load failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleGarageCapacitySave(): Promise<void> {
  if (!garageCapacityPolicySettings) throw new Error("Load garage capacity before saving it.");
  const numberValue = (selector: string) => Number(document.querySelector<HTMLInputElement>(selector)?.value ?? 0);
  const reason = document.querySelector<HTMLInputElement>("[data-garage-capacity-reason]")?.value.trim() ?? "";
  if (reason.length < 3) throw new Error("Enter a short audit reason before saving garage capacity.");
  const tierText = document.querySelector<HTMLTextAreaElement>("[data-garage-capacity-tiers]")?.value ?? "";
  const tierLimits = tierText.split("\n").map((line) => line.trim()).filter(Boolean).map((line) => {
    const separator = line.lastIndexOf("=");
    if (separator < 1) throw new Error(`Use Tier name = slots for Patreon rule: ${line}`);
    const tierName = line.slice(0, separator).trim();
    const perSpecies = Number(line.slice(separator + 1).trim());
    if (!tierName || !Number.isInteger(perSpecies) || perSpecies < 1 || perSpecies > 100) {
      throw new Error(`Invalid Patreon garage rule: ${line}`);
    }
    return { tierName, perSpecies };
  });
  setStatus("Saving garage capacity policy...");
  garageCapacityPolicySettings = await apiClient.saveGarageCapacityPolicy({
    defaultPerSpecies: numberValue("[data-garage-capacity-default]"),
    patreonPerSpecies: numberValue("[data-garage-capacity-patreon]"),
    staffPerSpecies: numberValue("[data-garage-capacity-staff]"),
    tierLimits,
    actorUserId: userId,
    actorDiscordRoleIds: roleIds,
    reason
  });
  const result = document.querySelector<HTMLElement>("#garageCapacityResult");
  if (result) result.innerHTML = renderGarageCapacityEditor(garageCapacityPolicySettings);
  setStatus("Garage capacity saved and audited.");
}

function renderGarageCapacityEditor(settings: GarageCapacityPolicySettings): string {
  const policy = settings.policy;
  const tiers = policy.tierLimits.map((entry) => `${entry.tierName} = ${entry.perSpecies}`).join("\n");
  return `<div class="moderation-policy-editor">
    <div class="moderation-strike-role-grid">
      <label class="control-reason"><span>Community slots per species</span><input type="number" min="1" max="100" data-garage-capacity-default value="${policy.defaultPerSpecies}" /></label>
      <label class="control-reason"><span>Patreon fallback slots</span><input type="number" min="1" max="100" data-garage-capacity-patreon value="${policy.patreonPerSpecies}" /></label>
      <label class="control-reason"><span>Staff slots per species</span><input type="number" min="1" max="100" data-garage-capacity-staff value="${policy.staffPerSpecies}" /></label>
    </div>
    <label class="control-reason"><span>Patreon tier overrides</span><textarea rows="5" data-garage-capacity-tiers placeholder="Apex = 8\nSupporter = 5">${escapeHtml(tiers)}</textarea><small>One exact Patreon tier name per line. The largest matching benefit wins.</small></label>
    <label class="control-reason"><span>Audit reason</span><input data-garage-capacity-reason maxlength="500" value="Updated garage capacity benefits." /></label>
    <button class="tool-button primary-tool" type="button" data-portal-action="garage-capacity-save" data-confirm-title="Apply garage limits?" data-confirm-message="New parking requests will immediately use these per-species limits. Existing dinosaurs will not be removed.">Save capacity policy</button>
  </div>`;
}

async function handleWorkRewardPolicySave(): Promise<void> {
  const numberValue = (selector: string) => Number(document.querySelector<HTMLInputElement>(selector)?.value ?? 0);
  const minPoints = numberValue("[data-work-policy-min]");
  const maxPoints = numberValue("[data-work-policy-max]");
  const cooldownHours = numberValue("[data-work-policy-cooldown-hours]");
  const reason = document.querySelector<HTMLInputElement>("[data-work-policy-reason]")?.value.trim() ?? "";
  if (!Number.isInteger(minPoints) || minPoints < 1) throw new Error("Minimum Work reward must be a positive whole number.");
  if (!Number.isInteger(maxPoints) || maxPoints < minPoints) throw new Error("Maximum Work reward must be at least the minimum.");
  if (!Number.isInteger(cooldownHours) || cooldownHours < 1 || cooldownHours > 720) throw new Error("Work cooldown must be between 1 and 720 whole hours.");
  if (reason.length < 3) throw new Error("Enter a short audit reason before saving Work rewards.");
  setStatus("Saving Work reward settings...");
  await apiClient.saveWorkRewardPolicy({
    minPoints,
    maxPoints,
    cooldownSeconds: cooldownHours * 3600,
    actorUserId: userId,
    actorDiscordRoleIds: roleIds,
    reason
  });
  setStatus(`Work now grants ${minPoints.toLocaleString()}-${maxPoints.toLocaleString()} Sticks.`);
}

async function handlePointBalancePolicyLoad(): Promise<void> {
  const result = document.querySelector<HTMLElement>("#pointBalancePolicyResult");
  if (!result) return;
  try {
    setStatus("Loading Sticks balance limits...");
    pointBalancePolicySettings = await apiClient.getPointBalancePolicy(roleIds);
    result.innerHTML = renderPointBalancePolicyEditor(pointBalancePolicySettings);
    setStatus("Sticks balance limits loaded.");
  } catch (error) {
    result.textContent = "Balance limits could not be loaded.";
    setStatus(`Balance limit load failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handlePointBalancePolicySave(): Promise<void> {
  if (!pointBalancePolicySettings) throw new Error("Load balance limits before saving them.");
  const numberValue = (selector: string) => Number(document.querySelector<HTMLInputElement>(selector)?.value ?? 0);
  const reason = document.querySelector<HTMLInputElement>("[data-point-cap-reason]")?.value.trim() ?? "";
  const tierText = document.querySelector<HTMLTextAreaElement>("[data-point-cap-tiers]")?.value ?? "";
  const tierLimits = tierText.split("\n").map((line) => line.trim()).filter(Boolean).map((line) => {
    const separator = line.lastIndexOf("=");
    if (separator < 1) throw new Error(`Use Tier name = maximum points for Patreon rule: ${line}`);
    const tierName = line.slice(0, separator).trim();
    const maxPoints = Number(line.slice(separator + 1).trim());
    if (!tierName || !Number.isInteger(maxPoints) || maxPoints < 1 || maxPoints > 1_000_000_000) throw new Error(`Invalid Patreon balance rule: ${line}`);
    return { tierName, maxPoints };
  });
  if (reason.length < 3) throw new Error("Enter a short audit reason before saving balance limits.");
  pointBalancePolicySettings = await apiClient.savePointBalancePolicy({
    defaultMaxPoints: numberValue("[data-point-cap-default]"),
    patreonMaxPoints: numberValue("[data-point-cap-patreon]"),
    staffMaxPoints: numberValue("[data-point-cap-staff]"),
    tierLimits,
    actorUserId: userId,
    actorDiscordRoleIds: roleIds,
    reason
  });
  const result = document.querySelector<HTMLElement>("#pointBalancePolicyResult");
  if (result) result.innerHTML = renderPointBalancePolicyEditor(pointBalancePolicySettings);
  setStatus("Sticks balance limits saved and audited.");
}

function renderPointBalancePolicyEditor(settings: PointBalancePolicySettings): string {
  const policy = settings.policy;
  const tiers = policy.tierLimits.map((entry) => `${entry.tierName} = ${entry.maxPoints}`).join("\n");
  return `<div class="moderation-policy-editor">
    <div class="moderation-strike-role-grid">
      <label class="control-reason"><span>Community maximum</span><input type="number" min="1" max="1000000000" data-point-cap-default value="${policy.defaultMaxPoints}" /></label>
      <label class="control-reason"><span>Patreon fallback maximum</span><input type="number" min="1" max="1000000000" data-point-cap-patreon value="${policy.patreonMaxPoints}" /></label>
      <label class="control-reason"><span>Staff maximum</span><input type="number" min="1" max="1000000000" data-point-cap-staff value="${policy.staffMaxPoints}" /></label>
    </div>
    <label class="control-reason"><span>Patreon tier caps</span><textarea rows="5" data-point-cap-tiers placeholder="Juvenile = 125000\nSub-Adult = 150000\nAdult = 200000\nPrime Elder = 300000">${escapeHtml(tiers)}</textarea><small>Use the exact Patreon tier name. A named tier cap is exact; an eligible staff cap may still be higher.</small></label>
    <label class="control-reason"><span>Audit reason</span><input data-point-cap-reason maxlength="500" value="Updated Sticks balance limits." /></label>
    <button class="tool-button primary-tool" type="button" data-portal-action="point-balance-save" data-confirm-title="Apply Sticks limits?" data-confirm-message="Existing balances are never removed. Future eligible rewards stop at the player's applicable maximum.">Save balance limits</button>
  </div>`;
}

async function handleDiscordPatreonRolePolicyLoad(): Promise<void> {
  const result = document.querySelector<HTMLElement>("#discordPatreonRolePolicyResult");
  if (!result) return;
  setStatus("Loading Discord Patreon roles...");
  discordPatreonRolePolicySettings = await apiClient.getDiscordPatreonRolePolicy(roleIds);
  result.innerHTML = renderDiscordPatreonRolePolicyEditor(discordPatreonRolePolicySettings);
  setStatus("Discord Patreon roles loaded.");
}

async function handleDiscordPatreonRolePolicySave(): Promise<void> {
  if (!discordPatreonRolePolicySettings) throw new Error("Load Patreon role mappings before saving them.");
  const lines = document.querySelector<HTMLTextAreaElement>("[data-patreon-role-mappings]")?.value ?? "";
  const mappings = lines.split("\n").map((line) => line.trim()).filter(Boolean).map((line) => {
    const parts = line.split("=").map((part) => part.trim());
    if (parts.length !== 3 || !/^\d{15,22}$/.test(parts[0] ?? "") || !(parts[1] ?? "")) throw new Error(`Use Role ID = Tier = Priority: ${line}`);
    const priority = Number(parts[2]);
    if (!Number.isInteger(priority) || priority < 0 || priority > 10_000) throw new Error(`Priority must be 0-10000: ${line}`);
    const benefit = theNestPatreonBenefits.find((entry) => entry.tierName.toLowerCase() === parts[1]!.toLowerCase());
    if (!benefit) throw new Error(`Unknown Patreon tier in mapping: ${parts[1]}`);
    return { roleId: parts[0]!, tierName: benefit.tierName, priority };
  });
  const reason = document.querySelector<HTMLInputElement>("[data-patreon-role-reason]")?.value.trim() ?? "";
  if (reason.length < 3) throw new Error("Enter a short audit reason.");
  discordPatreonRolePolicySettings = await apiClient.saveDiscordPatreonRolePolicy({ mappings, actorUserId: userId, actorDiscordRoleIds: roleIds, reason });
  const result = document.querySelector<HTMLElement>("#discordPatreonRolePolicyResult");
  if (result) result.innerHTML = renderDiscordPatreonRolePolicyEditor(discordPatreonRolePolicySettings);
  setStatus("Discord Patreon role mappings saved and audited.");
}

function renderDiscordPatreonRolePolicyEditor(settings: DiscordPatreonRolePolicySettings): string {
  const mappings = settings.policy.mappings.map((entry) => `${entry.roleId} = ${entry.tierName} = ${entry.priority}`).join("\n");
  return `<div class="moderation-policy-editor"><label class="control-reason"><span>Discord role mappings</span><textarea rows="6" data-patreon-role-mappings placeholder="123456789012345678 = Hatchling = 100\n123456789012345679 = Juvenile = 200">${escapeHtml(mappings)}</textarea><small>One role per line. Tier names must be Hatchling, Juvenile, Sub-Adult, Adult, or Prime Elder. Higher priority wins when a member has several mapped roles.</small></label><label class="control-reason"><span>Audit reason</span><input data-patreon-role-reason maxlength="500" value="Updated Discord Patreon role mappings." /></label><button class="tool-button primary-tool" type="button" data-portal-action="patreon-role-save" data-confirm-title="Apply Patreon role mappings?" data-confirm-message="Mapped Discord roles will immediately affect eligible benefits after role synchronization.">Save role mappings</button></div>`;
}

async function handleModerationPolicyLoad(): Promise<void> {
  const result = document.querySelector<HTMLElement>("#moderationPolicyResult");
  if (!result) return;
  try {
    setStatus("Loading moderation policy...");
    moderationPolicySettings = await apiClient.getModerationPolicy(roleIds);
    result.innerHTML = renderModerationPolicyEditor(moderationPolicySettings);
    setStatus("Moderation policy loaded.");
  } catch (error) {
    result.textContent = "Moderation policy could not be loaded.";
    setStatus(`Moderation policy load failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleModerationPolicySave(): Promise<void> {
  if (!moderationPolicySettings) throw new Error("Load moderation policy before saving it.");
  const reason = document.querySelector<HTMLInputElement>("[data-moderation-policy-reason]")?.value.trim() ?? "";
  if (reason.length < 3) throw new Error("Enter a short audit reason before saving moderation policy.");
  const roleValue = (selector: string) => document.querySelector<HTMLSelectElement>(selector)?.value || null;
  const escalationRoleIds = Array.from(document.querySelectorAll<HTMLInputElement>("[data-moderation-escalation-role]:checked"))
    .map((input) => input.value);
  setStatus("Saving moderation policy...");
  moderationPolicySettings = await apiClient.saveModerationPolicy({
    strikeOneRoleId: roleValue("[data-moderation-strike-role='1']"),
    strikeTwoRoleId: roleValue("[data-moderation-strike-role='2']"),
    strikeThreeRoleId: roleValue("[data-moderation-strike-role='3']"),
    escalationRoleIds,
    playtimeRewardBlockAtWeight: (() => {
      const value = Number(document.querySelector<HTMLSelectElement>("[data-moderation-playtime-block]")?.value ?? 0);
      return value >= 1 && value <= 3 ? value as 1 | 2 | 3 : null;
    })(),
    ladder: readModerationLadder(),
    actorUserId: userId,
    actorDiscordRoleIds: roleIds,
    reason
  });
  const result = document.querySelector<HTMLElement>("#moderationPolicyResult");
  if (result) result.innerHTML = renderModerationPolicyEditor(moderationPolicySettings);
  setStatus("Moderation policy saved and audited.");
}

function renderModerationPolicyEditor(settings: ModerationPolicySettings): string {
  const roleOptions = (selected?: string) => {
    const missingSelection = selected && !settings.roles.some((role) => role.id === selected)
      ? `<option value="${escapeHtml(selected)}" selected>Current role (${escapeHtml(selected)})</option>`
      : "";
    return `<option value="">No Discord role</option>${missingSelection}${settings.roles.map((role) => `<option value="${escapeHtml(role.id)}"${role.id === selected ? " selected" : ""}>${escapeHtml(role.name)}</option>`).join("")}`;
  };
  const roleName = (roleId: string) => settings.roles.find((role) => role.id === roleId)?.name ?? roleId;
  const escalationRoles = settings.roles.length
    ? settings.roles.map((role) => `<label class="control-check"><input type="checkbox" data-moderation-escalation-role value="${escapeHtml(role.id)}"${settings.policy.escalationRoleIds.includes(role.id) ? " checked" : ""} /> ${escapeHtml(role.name)}</label>`).join("")
    : settings.policy.escalationRoleIds.map((roleId) => `<label class="control-check"><input type="checkbox" data-moderation-escalation-role value="${escapeHtml(roleId)}" checked /> ${escapeHtml(roleName(roleId))}</label>`).join("");
  const ladderRows = (["warning", "strike_1", "strike_2", "strike_3", "strike_4"] as const).map((level) => {
    const step = settings.policy.ladder[level];
    const label = level === "warning" ? "Warning" : `Strike ${level.slice(-1)}`;
    return `<div class="moderation-ladder-row"><strong>${label}</strong><label><span>Profile days</span><input type="number" min="1" max="3650" data-moderation-ladder-profile="${level}" value="${step.profileRetentionDays ?? ""}" placeholder="Permanent" /></label><label><span>Game ban hours</span><input type="number" min="1" max="87600" data-moderation-ladder-ban="${level}" value="${step.gameBanHours ?? ""}" placeholder="None" ${step.permanentGameBan ? "disabled" : ""} /></label><label class="control-check"><input type="checkbox" data-moderation-ladder-permanent="${level}"${step.permanentGameBan ? " checked" : ""}${level !== "strike_4" ? " disabled" : ""} /> Permanent game ban</label></div>`;
  }).join("");
  return `<div class="moderation-policy-editor">
    ${settings.roleDiscoveryError ? `<p class="staff-service-note"><strong>Discord roles unavailable</strong><span>${escapeHtml(settings.roleDiscoveryError)}</span></p>` : ""}
    <div class="moderation-strike-role-grid">
      <label class="tool-select"><span>Warning / Strike 1 role</span><select data-moderation-strike-role="1">${roleOptions(settings.policy.strikeRoles.strikeOneRoleId)}</select></label>
      <label class="tool-select"><span>Strike 2 role</span><select data-moderation-strike-role="2">${roleOptions(settings.policy.strikeRoles.strikeTwoRoleId)}</select></label>
      <label class="tool-select"><span>Strike 3 role</span><select data-moderation-strike-role="3">${roleOptions(settings.policy.strikeRoles.strikeThreeRoleId)}</select></label>
    </div>
    <fieldset class="moderation-escalation-roles"><legend>Warning and strike ladder</legend><p>Profile expiry controls the active player-facing consequence. Historical audit records are retained. Game-ban durations are policy values; enforcement remains visibly pending until the game-server ban worker confirms delivery.</p><div class="moderation-ladder-grid">${ladderRows}</div></fieldset>
    <fieldset class="moderation-escalation-roles"><legend>Automatic earning consequence</legend><p>Optionally pause only the automatic Sticks earned each minute while a player has this much active strike weight. Work, purchases, refunds, staff grants, and garage actions are unchanged.</p><label class="tool-select"><span>Pause playtime earnings at</span><select data-moderation-playtime-block><option value=""${settings.policy.playtimeRewardBlockAtWeight ? "" : " selected"}>Disabled</option>${[1, 2, 3].map((weight) => `<option value="${weight}"${settings.policy.playtimeRewardBlockAtWeight === weight ? " selected" : ""}>Strike weight ${weight} or higher</option>`).join("")}</select></label></fieldset>
    <fieldset class="moderation-escalation-roles"><legend>Senior case oversight roles</legend><p>Selected roles can review every assigned or unassigned case and handle escalations. Use this for Senior Mod, Community Lead, or equivalent roles. Owners and admins always retain oversight.</p><div>${escalationRoles || `<span class="form-help">No additional senior oversight roles are configured.</span>`}</div></fieldset>
    <label class="control-reason"><span>Audit reason</span><input data-moderation-policy-reason maxlength="500" value="Updated strike roles or senior case oversight." /></label>
    <button class="tool-button primary-tool" type="button" data-portal-action="moderation-policy-save" data-confirm-title="Apply moderation policy?" data-confirm-message="Strike consequences and senior case visibility will change immediately. The update will be audited.">Save policy</button>
  </div>`;
}

function readModerationLadder(): ModerationPolicySettings["policy"]["ladder"] {
  const levels = ["warning", "strike_1", "strike_2", "strike_3", "strike_4"] as const;
  return Object.fromEntries(levels.map((level) => {
    const profile = Number(document.querySelector<HTMLInputElement>(`[data-moderation-ladder-profile="${level}"]`)?.value ?? 0);
    const ban = Number(document.querySelector<HTMLInputElement>(`[data-moderation-ladder-ban="${level}"]`)?.value ?? 0);
    const permanent = document.querySelector<HTMLInputElement>(`[data-moderation-ladder-permanent="${level}"]`)?.checked ?? false;
    return [level, {
      level,
      ...(profile > 0 ? { profileRetentionDays: profile } : {}),
      ...(ban > 0 && !permanent ? { gameBanHours: ban } : {}),
      permanentGameBan: permanent
    }];
  })) as ModerationPolicySettings["policy"]["ladder"];
}

async function handleRuntimeReadinessLoad(): Promise<void> {
  const result = document.querySelector<HTMLElement>("#runtimeReadinessResult");
  if (!result) return;
  try {
    setStatus("Running production readiness checks...");
    const readiness = await apiClient.getRuntimeReadiness();
    result.innerHTML = renderRuntimeReadiness(readiness);
    setStatus(readiness.launchReady ? "Launch safeguards passed." : readiness.ok ? "Core runtime passed; launch setup still needs attention." : "Runtime readiness needs attention.");
  } catch (error) {
    result.textContent = "Readiness checks could not be loaded.";
    setStatus(`Readiness check failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

function renderRuntimeReadiness(readiness: RuntimeReadinessSettings): string {
  const status = (label: string, ready: boolean, detail: string, required = true) => `<div class="runtime-readiness-row ${ready ? "is-ready" : required ? "needs-attention" : "is-optional"}"><span aria-hidden="true"></span><div><strong>${escapeHtml(label)}</strong><small>${escapeHtml(detail)}</small></div><b>${ready ? "Ready" : required ? "Needs setup" : "Optional"}</b></div>`;
  const rows = [
    status("Database", readiness.database.ok, readiness.database.ok ? "Player, economy, garage, and case records are available." : readiness.database.error ?? "Database health check failed."),
    status("Public portal", readiness.publicBaseUrl.configured && !readiness.publicBaseUrl.local, "Public HTTPS address used for identity callbacks."),
    status("Discord integration", readiness.integrations.discord, "Bot identity, guild, and Discord account linking."),
    status("Discord staff access", readiness.discordPolicy.staffAccessRoles, "At least one configured owner, admin, moderator, or support role."),
    status("Verified player role", readiness.discordPolicy.verifiedRole, "Role applied after a player links and verifies Steam."),
    status("Strike enforcement roles", readiness.discordPolicy.strikeRoles, "Discord roles for strike levels one, two, and three."),
    status("Emergency control roles", readiness.discordPolicy.emergencyControlRoles, "Exact senior roles allowed to use incident controls."),
    status("Moderation log channels", readiness.discordPolicy.moderationLogChannels, "Private destinations for moderation and audit records."),
    status("Bug report channel", readiness.discordPolicy.bugReportChannel, "Private destination for portal bug reports."),
    status("Ticket notification channels", readiness.discordPolicy.ticketNotificationChannels, "Private destinations for support, reports, appeals, and owner-only staff reports."),
    status("Discord ticket channels", readiness.discordPolicy.ticketCategories, readiness.discordPolicy.ticketCategories ? "Optional active-ticket and archive categories are configured." : "Portal-first cases are active; private Discord ticket channels are not configured.", false),
    status("Steam sign-in", readiness.integrations.steamOpenId, "Steam OpenID realm and callback configuration."),
    status("Portal security", readiness.security.portalSessions && readiness.security.serviceAuthentication, "Signed sessions and trusted service-to-service requests."),
    status("Deployment", Boolean(readiness.deployment.commitSha && readiness.deployment.region), readiness.deployment.commitSha && readiness.deployment.region ? `${readiness.deployment.branch ?? "deployed branch"} at ${readiness.deployment.commitSha} in ${readiness.deployment.region}.` : "Build revision or Railway replica region is unavailable."),
    status("Private case uploads", readiness.integrations.privateCaseEvidenceConfigured, readiness.integrations.privateCaseEvidenceConfigured ? "Private evidence storage and scanning are configured." : "Players can use secure HTTPS links, but direct private uploads are unavailable."),
    status("Continuous database backups", readiness.integrations.continuousDatabaseBackups, readiness.integrations.continuousDatabaseBackups ? "Railway PostgreSQL archiving is current and has no newer failed archive." : "A successful PostgreSQL archive from the last hour could not be verified."),
    status("Case retention", readiness.caseHistory.databaseBacked && readiness.caseHistory.retentionEnabled, readiness.caseHistory.retentionEnabled ? `Closed tickets: ${readiness.caseHistory.closedTicketDays} days. Evidence: ${readiness.caseHistory.closedEvidenceDays} days.` : "Automatic case retention is disabled.")
  ];
  const heading = readiness.launchReady ? "Launch safeguards ready" : readiness.ok ? "Core service ready; launch setup remains" : "Core service needs attention";
  return `<div class="runtime-readiness"><div class="runtime-readiness-summary"><strong>${heading}</strong><small>Checked ${escapeHtml(new Date(readiness.time).toLocaleString())}. Worker-side game bridge health remains in Staff tools.</small></div><div class="runtime-readiness-list">${rows.join("")}</div></div>`;
}

let telemetryRetentionSettings: TelemetryRetentionSettings | undefined;

async function handleTelemetryHealthLoad(): Promise<void> {
  const result = document.querySelector<HTMLElement>("#telemetryHealthResult");
  if (!result) return;
  try {
    setStatus("Checking telemetry pipeline...");
    const health = await apiClient.getTelemetryHealth(currentSnapshot.server.id, roleIds);
    result.innerHTML = renderTelemetryHealth(health);
    setStatus(health.latest?.status === "healthy" ? "Telemetry pipeline is healthy." : "Telemetry pipeline needs attention.");
  } catch (error) {
    result.innerHTML = `<div class="support-empty"><strong>Telemetry health is unavailable</strong><p>${escapeHtml(error instanceof Error ? error.message : String(error))}</p></div>`;
    setStatus("Telemetry health check failed.");
  }
}

function renderTelemetryHealth(health: TelemetryHealthSettings): string {
  if (!health.latest) return `<div class="support-empty"><strong>No telemetry samples yet</strong><p>The worker has not recorded a collection cycle for this server.</p></div>`;
  const latest = health.latest;
  const freshness = Math.max(0, Date.now() - Date.parse(latest.ingestedAt));
  const detail = latest.error ? `<p class="staff-service-note warning"><strong>Latest error</strong><span>${escapeHtml(latest.error)}</span></p>` : "";
  return `<div class="runtime-readiness"><div class="runtime-readiness-summary"><strong>${latest.status === "healthy" ? "Pipeline healthy" : latest.status === "degraded" ? "Pipeline degraded" : "Pipeline failed"}</strong><small>${escapeHtml(latest.source)} sampled ${escapeHtml(formatAge(freshness))} ago. ${latest.recordCount} records persisted.</small></div><div class="telemetry-metric-grid"><div><span>Collector duration</span><strong>${formatMilliseconds(latest.collectorDurationMs)}</strong></div><div><span>Capture delay</span><strong>${formatMilliseconds(latest.captureToIngestMs)}</strong></div><div><span>Database delay</span><strong>${formatMilliseconds(latest.ingestToPersistMs)}</strong></div><div><span>Invalid rows</span><strong>${health.summary.invalidLines}</strong></div><div><span>Duplicates</span><strong>${health.summary.duplicates}</strong></div><div><span>Sequence gaps</span><strong>${health.summary.missingSequences}</strong></div><div><span>P95 collector</span><strong>${formatMilliseconds(health.summary.p95CollectorDurationMs ?? undefined)}</strong></div><div><span>P95 capture delay</span><strong>${formatMilliseconds(health.summary.p95CaptureToIngestMs ?? undefined)}</strong></div></div>${detail}</div>`;
}

async function handleTelemetryRetentionLoad(): Promise<void> {
  const result = document.querySelector<HTMLElement>("#telemetryRetentionResult");
  if (!result) return;
  try {
    setStatus("Loading telemetry retention policy...");
    telemetryRetentionSettings = await apiClient.getTelemetryRetention(currentSnapshot.server.id, roleIds);
    result.innerHTML = renderTelemetryRetention(telemetryRetentionSettings);
    setStatus("Telemetry retention policy loaded.");
  } catch (error) {
    result.innerHTML = `<div class="support-empty"><strong>Retention policy is unavailable</strong><p>${escapeHtml(error instanceof Error ? error.message : String(error))}</p></div>`;
    setStatus("Telemetry retention policy could not be loaded.");
  }
}

async function handleTelemetryRetentionSave(): Promise<void> {
  if (!telemetryRetentionSettings) throw new Error("Load the retention policy before saving it.");
  const numberValue = (selector: string) => Number(document.querySelector<HTMLInputElement>(selector)?.value ?? 0);
  const reason = document.querySelector<HTMLInputElement>("[data-telemetry-retention-reason]")?.value.trim() ?? "";
  if (reason.length < 3) throw new Error("Enter a short audit reason.");
  setStatus("Saving telemetry retention policy...");
  telemetryRetentionSettings = await apiClient.saveTelemetryRetention({
    ...telemetryRetentionSettings,
    preciseLocationDays: numberValue("[data-telemetry-retention-location]"),
    chatEventDays: numberValue("[data-telemetry-retention-chat]"),
    gameplayEventDays: numberValue("[data-telemetry-retention-gameplay]"),
    telemetryHealthDays: numberValue("[data-telemetry-retention-health]"),
    actorUserId: userId,
    actorDiscordRoleIds: roleIds,
    reason
  });
  const result = document.querySelector<HTMLElement>("#telemetryRetentionResult");
  if (result) result.innerHTML = renderTelemetryRetention(telemetryRetentionSettings);
  setStatus("Telemetry retention policy saved and audited.");
}

function renderTelemetryRetention(settings: TelemetryRetentionSettings): string {
  return `<div class="moderation-policy-editor"><div class="tool-grid"><label class="control-reason"><span>Precise locations</span><input type="number" min="1" max="30" value="${settings.preciseLocationDays}" data-telemetry-retention-location /><small>Coordinates are redacted after this many days.</small></label><label class="control-reason"><span>Chat events</span><input type="number" min="7" max="365" value="${settings.chatEventDays}" data-telemetry-retention-chat /></label><label class="control-reason"><span>Gameplay events</span><input type="number" min="30" max="730" value="${settings.gameplayEventDays}" data-telemetry-retention-gameplay /></label><label class="control-reason"><span>Health samples</span><input type="number" min="7" max="180" value="${settings.telemetryHealthDays}" data-telemetry-retention-health /></label></div><label class="control-reason"><span>Audit reason</span><input maxlength="500" value="Reviewed telemetry privacy and evidence retention." data-telemetry-retention-reason /></label><button class="tool-button primary-tool" type="button" data-portal-action="telemetry-retention-save" data-confirm-title="Apply retention policy?" data-confirm-message="Expired telemetry will be removed by the worker. Precise locations are redacted before other gameplay evidence expires.">Save retention policy</button><p class="form-help">Last updated ${escapeHtml(new Date(settings.updatedAt).toLocaleString())}.</p></div>`;
}

function formatMilliseconds(value: number | undefined): string {
  if (value === undefined) return "Not reported";
  return value >= 1_000 ? `${(value / 1_000).toFixed(1)}s` : `${Math.round(value)}ms`;
}

function formatAge(value: number): string {
  if (value < 60_000) return `${Math.max(1, Math.round(value / 1_000))}s`;
  if (value < 3_600_000) return `${Math.round(value / 60_000)}m`;
  return `${Math.round(value / 3_600_000)}h`;
}

async function handleConfigurationSnapshotExport(context: PortalActionContext): Promise<void> {
  const result = document.querySelector<HTMLElement>("#configurationSnapshotResult");
  if (!result) return;
  try {
    setStatus("Preparing a secret-free configuration snapshot...");
    const snapshot = await apiClient.exportConfigurationSnapshot(context);
    const fileName = `the-nest-config-${snapshot.exportedAt.slice(0, 10)}.json`;
    const url = URL.createObjectURL(new Blob([JSON.stringify(snapshot, null, 2)], { type: "application/json" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    result.innerHTML = `<div class="runtime-readiness-summary"><strong>Recovery snapshot downloaded</strong><small>${escapeHtml(fileName)} contains ${snapshot.summary.discordLogRoutes} log routes, ${snapshot.summary.storeCatalogItems} catalog items, ${snapshot.summary.rules} rule versions, and ${snapshot.summary.systemControlOverrides} runtime overrides. Keep it somewhere private and off-server.</small></div>`;
    setStatus("Configuration recovery snapshot downloaded.");
  } catch (error) {
    result.textContent = "The configuration snapshot could not be exported.";
    setStatus(`Configuration export failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleConfigurationSnapshotValidation(context: PortalActionContext): Promise<void> {
  const result = document.querySelector<HTMLElement>("#configurationSnapshotResult");
  const input = document.querySelector<HTMLInputElement>("[data-configuration-snapshot-file]");
  const file = input?.files?.[0];
  if (!result || !file) {
    setStatus("Choose a configuration snapshot JSON file for The Nest first.");
    return;
  }
  if (file.size > 2_000_000) {
    setStatus("Configuration snapshot validation failed: file is larger than 2 MB.");
    return;
  }
  try {
    setStatus("Validating configuration snapshot...");
    const parsed = JSON.parse(await file.text()) as import("./types.js").ConfigurationSnapshotSettings;
    const validation = await apiClient.validateConfigurationSnapshot(context, parsed);
    const messages = [
      ...validation.issues.map((issue) => `<li class="needs-attention">${escapeHtml(issue)}</li>`),
      ...validation.warnings.map((warning) => `<li>${escapeHtml(warning)}</li>`)
    ].join("");
    result.innerHTML = `<div class="runtime-readiness-summary"><strong>${validation.compatible ? "Snapshot is compatible" : "Snapshot cannot be restored"}</strong><small>${validation.compatible ? "Schema, server identity, section counts, and secret exclusions passed." : "No settings were changed. Correct the issues before considering a restore."}</small></div>${messages ? `<ul class="configuration-validation-messages">${messages}</ul>` : ""}`;
    setStatus(validation.compatible ? "Configuration snapshot validation passed." : "Configuration snapshot validation found issues.");
  } catch (error) {
    result.textContent = "The selected file is not a valid The Nest configuration snapshot.";
    setStatus(`Configuration validation failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleAccessControlPreviewLoad(): Promise<void> {
  const result = document.querySelector<HTMLElement>("#accessControlPreviewResult");
  if (!result) return;
  try {
    setStatus("Loading Discord role access...");
    const preview = await apiClient.getAccessControlPreview(roleIds);
    result.innerHTML = renderAccessControlPreview(preview);
    setStatus("Discord role access loaded.");
  } catch (error) {
    result.textContent = "Discord role access could not be loaded.";
    setStatus(`Access preview failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handlePortalAccessSettingsLoad(): Promise<void> {
  const result = document.querySelector<HTMLElement>("#portalAccessSettingsResult");
  if (!result) return;
  result.textContent = "Loading website access settings...";
  try {
    const settings = await apiClient.getPortalAccessSettings(roleIds);
    result.innerHTML = renderPortalAccessSettingsEditor(settings);
  } catch (error) {
    result.innerHTML = `<p class="form-help is-error">${escapePortalText(error instanceof Error ? error.message : String(error))}</p>`;
  }
}

async function handlePortalAccessSettingsSave(): Promise<void> {
  const enabled = document.querySelector<HTMLInputElement>("[data-portal-access-enabled]")?.checked === true;
  const reason = document.querySelector<HTMLInputElement>("[data-portal-access-reason]")?.value.trim() || "Updated website password gate.";
  setStatus("Saving website access settings...");
  const settings = await apiClient.savePortalAccessSettings({
    enabled,
    actorUserId: userId,
    actorDiscordRoleIds: roleIds,
    reason
  });
  const result = document.querySelector<HTMLElement>("#portalAccessSettingsResult");
  if (result) result.innerHTML = renderPortalAccessSettingsEditor(settings);
  setStatus(`Website password gate ${settings.enabled ? "enabled" : "disabled"}.`);
}

function renderPortalAccessSettingsEditor(settings: import("./types.js").PortalAccessSettings): string {
  const availability = settings.passwordConfigured
    ? "A password is configured securely in Railway."
    : "No password is configured in Railway, so the gate cannot be enabled.";
  return `<div class="moderation-policy-editor"><label class="control-check"><input type="checkbox" data-portal-access-enabled${settings.enabled ? " checked" : ""}${settings.passwordConfigured ? "" : " disabled"} /> Require the website access password</label><p class="form-help">${escapePortalText(availability)}</p><label class="control-reason"><span>Audit reason</span><input data-portal-access-reason maxlength="500" value="Updated website password gate from the owner panel." /></label><button class="tool-button primary-tool" type="button" data-portal-action="portal-access-save" data-confirm-title="${settings.enabled ? "Disable website password?" : "Enable website password?"}" data-confirm-message="${settings.enabled ? "Visitors will be able to reach the normal Steam sign-in without the shared password." : "New visitors will need the configured password before reaching the portal."}"${settings.passwordConfigured ? "" : " disabled"}>Save access setting</button></div>`;
}

function renderAccessControlPreview(preview: AccessControlPreviewSettings): string {
  if (!preview.roles.length) {
    return `<div class="support-empty"><strong>No access roles configured</strong><p>Add Discord role IDs to the Railway access variables before inviting staff.</p></div>`;
  }
  return `<div class="access-preview-list">
    ${preview.roleDiscoveryError ? `<p class="staff-service-note"><strong>Discord names unavailable</strong><span>${escapeHtml(preview.roleDiscoveryError)}</span></p>` : ""}
    ${preview.roles.map((role) => `<details class="access-preview-role"${role.groups.includes("owner") ? " open" : ""}><summary><span><strong>${escapeHtml(role.name ?? `Unknown role ${role.id}`)}</strong><small>${escapeHtml(role.configuredAs.join(" - "))}</small></span><span class="panel-count">${role.permissions.length}</span></summary><div><dl><div><dt>Discord role ID</dt><dd><code>${escapeHtml(role.id)}</code></dd></div><div><dt>Access groups</dt><dd>${escapeHtml(role.groups.join(", ") || "Direct permissions only")}</dd></div></dl><p><strong>Capabilities</strong></p><ul>${role.permissions.map((permission) => `<li>${escapeHtml(humanizePermission(permission))}</li>`).join("")}</ul></div></details>`).join("")}
    ${preview.missingRoleIds.length ? `<p class="staff-service-note warning"><strong>Missing Discord roles</strong><span>${preview.missingRoleIds.length} configured role ${preview.missingRoleIds.length === 1 ? "ID does" : "IDs do"} not exist in the connected guild.</span></p>` : ""}
  </div>`;
}

function humanizePermission(permission: string): string {
  return permission.split(".").map((part) => part.replaceAll("_", " ")).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" / ");
}

async function handleStaffDutyLoad(): Promise<void> {
  const result = document.querySelector<HTMLElement>("#staffDutyResult");
  if (!result) return;
  try {
    const duty = await apiClient.getStaffDuty(userId, roleIds);
    result.innerHTML = renderStaffDutyStatus(duty);
  } catch (error) {
    result.innerHTML = `<div class="staff-duty-state is-unavailable"><strong>Duty status unavailable</strong><small>${escapeHtml(error instanceof Error ? error.message : String(error))}</small></div>`;
  }
}

async function handleStaffDutyToggle(currentlyOnDuty: boolean): Promise<void> {
  const reason = document.querySelector<HTMLInputElement>("[data-staff-duty-reason]")?.value.trim()
    || (currentlyOnDuty ? "Ended staff duty from the portal." : "Started staff duty from the portal.");
  const result = document.querySelector<HTMLElement>("#staffDutyResult");
  try {
    setStatus(currentlyOnDuty ? "Ending staff duty..." : "Starting staff duty...");
    const duty = await apiClient.setStaffDuty({
      actorUserId: userId,
      actorDiscordRoleIds: roleIds,
      onDuty: !currentlyOnDuty,
      reason
    });
    if (result) result.innerHTML = renderStaffDutyStatus(duty);
    setStatus(duty.onDuty ? "You are on duty. Game admin tools will apply shortly." : "You are off duty. Game admin tools will be removed shortly.");
  } catch (error) {
    setStatus(`Staff duty update failed: ${error instanceof Error ? error.message : String(error)}`);
    await handleStaffDutyLoad();
  }
}

async function handleStaffContactTicketCreate(button: HTMLElement): Promise<void> {
  const playerInput = document.querySelector<HTMLInputElement>("[data-staff-contact-player]");
  const targetUserId = playerPickerValue(playerInput, "user");
  const subjectInput = document.querySelector<HTMLInputElement>("[data-staff-contact-subject]");
  const bodyInput = document.querySelector<HTMLTextAreaElement>("[data-staff-contact-body]");
  const subject = subjectInput?.value.trim() ?? "";
  const body = bodyInput?.value.trim() ?? "";
  if (!targetUserId) throw new Error("Choose a player from the search suggestions.");
  if (targetUserId === userId) throw new Error("Choose another player for this case.");
  if (subject.length < 3) throw new Error("Enter a clear case subject.");
  if (body.length < 10) throw new Error("Write an opening message of at least 10 characters.");

  const submissionKey = button.dataset.submissionKey || `staff-contact:${crypto.randomUUID()}`;
  button.dataset.submissionKey = submissionKey;
  setStatus("Opening a private player case...");
  const ticket = await apiClient.createStaffContactTicket({
    actorUserId: userId,
    actorDiscordRoleIds: roleIds,
    targetUserId,
    subject,
    body,
    submissionKey
  });
  delete button.dataset.submissionKey;
  if (playerInput) {
    playerInput.value = "";
    delete playerInput.dataset.selectedUserId;
    delete playerInput.dataset.selectedSteam64Id;
    delete playerInput.dataset.selectedDiscordUserId;
  }
  if (subjectInput) subjectInput.value = "";
  if (bodyInput) bodyInput.value = "";
  setStatus(`${ticket.reference} opened. The player can reply from My Tickets.`);
  await handleTicketDetail(ticket.id);
}

function scheduleTicketQueueEventRefresh(delayMs = 250): void {
  if (ticketQueueEventRefreshTimer !== undefined) window.clearTimeout(ticketQueueEventRefreshTimer);
  ticketQueueEventRefreshTimer = window.setTimeout(() => {
    ticketQueueEventRefreshTimer = undefined;
    if (portalPageFromPath(window.location.pathname) !== "cases" || currentStaffTicketId) return;
    if (portalActionInFlight || portalInteractionActive() || ticketQueueRefreshCount > 0) {
      scheduleTicketQueueEventRefresh(750);
      return;
    }
    void handleTicketQueue(false, true);
  }, delayMs);
}

async function handleTicketQueue(append = false, background = false): Promise<void> {
  if (append && !ticketQueueCursor) return;
  const sequence = ++ticketQueueLoadSequence;
  ticketQueueRefreshCount += 1;
  const result = document.querySelector("#ticketQueueResult");

  try {
    if (!append && !background) {
      currentStaffTicketId = undefined;
      setCaseDeskDetailMode(false);
    }
    if (!background) setStatus(append ? "Loading more support tickets..." : "Loading support tickets...");
    const query = document.querySelector<HTMLInputElement>("[data-ticket-filter-query]")?.value.trim();
    const type = document.querySelector<HTMLSelectElement>("[data-ticket-filter-type]")?.value;
    const status = document.querySelector<HTMLSelectElement>("[data-ticket-filter-status]")?.value;
    const claimedByUserId = playerPickerValue(document.querySelector<HTMLInputElement>("[data-ticket-filter-assignee]"), "user");
    const creatorUserId = playerPickerValue(document.querySelector<HTMLInputElement>("[data-ticket-filter-reporter]"), "user");
    const accused = playerPickerValue(document.querySelector<HTMLInputElement>("[data-ticket-filter-accused]"), "lookup");
    const ruleCode = document.querySelector<HTMLInputElement>("[data-ticket-filter-rule]")?.value.trim();
    const tag = document.querySelector<HTMLInputElement>("[data-ticket-filter-tag]")?.value.trim().toLowerCase();
    const resolutionType = document.querySelector<HTMLSelectElement>("[data-ticket-filter-resolution]")?.value;
    const createdFrom = document.querySelector<HTMLInputElement>("[data-ticket-filter-created-from]")?.value;
    const createdTo = document.querySelector<HTMLInputElement>("[data-ticket-filter-created-to]")?.value;
    const unassigned = document.querySelector<HTMLInputElement>("[data-ticket-filter-unassigned]")?.checked ?? false;
    const page = await apiClient.listSupportTickets(roleIds, {
      actorUserId: userId,
      ...(query && query.length >= 2 ? { query } : {}),
      ...(type ? { type } : {}),
      ...(status ? { status } : {}),
      lifecycle: status === "closed" ? "closed" : status ? "active" : ticketQueueLifecycle,
      ...(claimedByUserId ? { claimedByUserId } : {}),
      ...(creatorUserId ? { creatorUserId } : {}),
      ...(accused && accused.length >= 2 ? { accused } : {}),
      ...(ruleCode ? { ruleCode } : {}),
      ...(tag ? { tag } : {}),
      ...(resolutionType ? { resolutionType } : {}),
      ...(createdFrom ? { createdFrom: `${createdFrom}T00:00:00.000Z` } : {}),
      ...(createdTo ? { createdTo: `${createdTo}T23:59:59.999Z` } : {}),
      ...(unassigned ? { unassigned: true } : {}),
      ...(ticketQueuePreset === "followup" ? { needsFollowup: true } : {}),
      ...(append && ticketQueueCursor ? { cursor: ticketQueueCursor } : {}),
      limit: 50
    });
    if (sequence !== ticketQueueLoadSequence) return;
    const currentItems = append ? ticketQueueItems : [];
    const known = new Set(currentItems.map((ticket) => ticket.id));
    ticketQueueItems = [...currentItems, ...page.tickets.filter((ticket) => !known.has(ticket.id))];
    ticketQueueCursor = page.nextCursor;
    if (result) {
      result.innerHTML = renderSupportTicketList(ticketQueueItems, ticketQueueCursor);
    }
    if (!background) setStatus(append ? "More tickets loaded." : "Ticket queue loaded.");
  } catch (error) {
    if (sequence !== ticketQueueLoadSequence) return;
    if (!background && result && ticketQueueItems.length === 0) {
      result.textContent = "No tickets loaded.";
    }
    if (!background) setStatus(`Ticket queue load failed: ${error instanceof Error ? error.message : String(error)}`);
  } finally {
    ticketQueueRefreshCount = Math.max(0, ticketQueueRefreshCount - 1);
  }
}

async function initializeCaseDesk(): Promise<void> {
  void refreshCaseNotificationBadge();
  const ticketId = new URLSearchParams(window.location.search).get("ticket");
  if (ticketId) {
    await handleTicketDetail(ticketId);
    return;
  }
  await handleTicketMetrics();
  await handleTicketQueuePreset("unassigned");
}

function startNotificationRefresh(): void {
  if (notificationRefreshStarted) return;
  notificationRefreshStarted = true;
  window.setInterval(() => {
    if (!document.hidden) void refreshPortalNotifications();
    if (!document.hidden && portalPageFromPath(window.location.pathname) === "support" && currentPlayerTicketId) {
      void refreshPlayerTicketConversation(currentPlayerTicketId);
    }
    if (!document.hidden && portalPageFromPath(window.location.pathname) === "cases" && currentStaffTicketId && !portalActionInFlight && !portalInteractionActive()) {
      void refreshStaffTicketConversation(currentStaffTicketId);
    }
  }, 60_000);
  window.setInterval(() => {
    if (!document.hidden && portalPageFromPath(window.location.pathname) === "cases") void handleTicketMetrics(true);
  }, 60_000);
}

function startAnnouncementRefresh(): void {
  if (announcementRefreshStarted) return;
  announcementRefreshStarted = true;
  window.setInterval(() => {
    if (!document.hidden) void refreshPortalAnnouncements();
  }, 60_000);
}

async function refreshPortalAnnouncements(): Promise<void> {
  if (announcementRefreshInFlight || !userId) return;
  announcementRefreshInFlight = true;
  try {
    activeAnnouncements = await apiClient.listPortalAnnouncements();
    if (document.querySelector("[data-game-announcement-schedule-list]")) {
      scheduledGameAnnouncements = await apiClient.listScheduledGameAnnouncements();
    }
    updateAnnouncementSurfaces();
  } catch {
    // Retain the last successful announcement state through a temporary outage.
  } finally {
    announcementRefreshInFlight = false;
  }
}

function updateAnnouncementSurfaces(): void {
  giveawayController.render();
  const bar = document.querySelector<HTMLElement>("[data-portal-announcement-bar]");
  if (bar) {
    bar.innerHTML = renderPortalAnnouncementBar(activeAnnouncements) + renderCommunityRaffleAnnouncement(currentSnapshot);
    bar.hidden = !bar.innerHTML;
  }
  const manager = document.querySelector<HTMLElement>("[data-announcement-manager-list]");
  if (manager) manager.innerHTML = renderAnnouncementManager(activeAnnouncements);
  const scheduleManager = document.querySelector<HTMLElement>("[data-game-announcement-schedule-list]");
  if (scheduleManager) scheduleManager.innerHTML = renderScheduledGameAnnouncementManager(scheduledGameAnnouncements);
  hydratePortalIcons();
}

async function handleAnnouncementPublish(): Promise<void> {
  const input = document.querySelector<HTMLTextAreaElement>("[data-announcement-message]");
  const duration = document.querySelector<HTMLSelectElement>("[data-announcement-duration]");
  const message = input?.value.trim() ?? "";
  if (message.length < 3) throw new Error("Enter an announcement of at least 3 characters.");
  await apiClient.publishPortalAnnouncement({
    message,
    expiresInMinutes: duration?.value ? Number(duration.value) : null,
    actorUserId: userId,
    actorDiscordRoleIds: roleIds
  });
  if (input) input.value = "";
  const count = document.querySelector<HTMLElement>("[data-announcement-count]");
  if (count) count.textContent = "0";
  await refreshPortalAnnouncements();
  setStatus("Announcement published to the player portal.");
}

async function handleAnnouncementEnd(announcementId: string): Promise<void> {
  await apiClient.endPortalAnnouncement(announcementId, { actorUserId: userId, actorDiscordRoleIds: roleIds });
  await refreshPortalAnnouncements();
  setStatus("Announcement ended.");
}

async function handleGameAnnouncementSend(): Promise<void> {
  const input = document.querySelector<HTMLTextAreaElement>("[data-game-announcement-immediate-message]");
  const message = input?.value.trim() ?? "";
  if (message.length < 3) throw new Error("Enter an in-game announcement of at least 3 characters.");
  await apiClient.sendGameAnnouncement({ message, actorUserId: userId, actorDiscordRoleIds: roleIds });
  if (input) input.value = "";
  setStatus("In-game announcement queued. It should appear within 30 seconds.");
}

async function handleScheduledGameAnnouncementCreate(): Promise<void> {
  const messageInput = document.querySelector<HTMLTextAreaElement>("[data-game-announcement-message]");
  const intervalInput = document.querySelector<HTMLInputElement>("[data-game-announcement-interval]");
  const startsInput = document.querySelector<HTMLInputElement>("[data-game-announcement-starts]");
  const message = messageInput?.value.trim() ?? "";
  const intervalMinutes = Number(intervalInput?.value ?? 0);
  const startsInMinutes = Number(startsInput?.value ?? 0);
  if (message.length < 3) throw new Error("Enter an in-game announcement of at least 3 characters.");
  if (!Number.isInteger(intervalMinutes) || intervalMinutes < 5) throw new Error("Repeat interval must be at least 5 minutes.");
  if (!Number.isInteger(startsInMinutes) || startsInMinutes < 1) throw new Error("First delivery must be at least 1 minute from now.");
  await apiClient.createScheduledGameAnnouncement({ message, intervalMinutes, startsInMinutes, actorUserId: userId, actorDiscordRoleIds: roleIds });
  if (messageInput) messageInput.value = "";
  await refreshPortalAnnouncements();
  setStatus("Recurring in-game announcement scheduled.");
}

async function handleScheduledGameAnnouncementDisable(scheduleId: string): Promise<void> {
  await apiClient.disableScheduledGameAnnouncement(scheduleId, { actorUserId: userId, actorDiscordRoleIds: roleIds });
  await refreshPortalAnnouncements();
  setStatus("Recurring in-game announcement disabled.");
}

async function refreshPlayerTicketConversation(ticketId: string): Promise<void> {
  if (playerTicketRefreshInFlight) return;
  playerTicketRefreshInFlight = true;
  try {
    const detail = await apiClient.getSupportTicket(ticketId, userId, []);
    if (currentPlayerTicketId !== ticketId || portalPageFromPath(window.location.pathname) !== "support") return;
    const root = document.querySelector<HTMLElement>(`[data-player-ticket-detail][data-ticket-id="${cssEscape(ticketId)}"]`);
    const messages = root?.querySelector<HTMLElement>("[data-player-ticket-messages]");
    if (!root || !messages) return;

    if (root.dataset.ticketStatus !== detail.ticket.status) {
      await handlePlayerTicketDetail(ticketId, true);
      return;
    }

    const nextSignature = playerTicketMessageSignature(detail.messages);
    if (messages.dataset.messageSignature === nextSignature) return;
    const conversation = root.querySelector<HTMLElement>(".player-ticket-conversation");
    const wasNearBottom = conversation
      ? conversation.scrollHeight - conversation.scrollTop - conversation.clientHeight < 80
      : false;
    messages.innerHTML = renderPlayerTicketMessages(detail.ticket, detail.messages);
    messages.dataset.messageSignature = nextSignature;
    const updated = root.querySelector<HTMLElement>("[data-player-ticket-updated]");
    if (updated) updated.textContent = `Updated ${new Date(detail.ticket.updatedAt).toLocaleString()}`;
    if (conversation && wasNearBottom) conversation.scrollTop = conversation.scrollHeight;
  } catch {
    // Keep the current conversation visible during a temporary network interruption.
  } finally {
    playerTicketRefreshInFlight = false;
  }
}

function appendPlayerTicketConversationMessage(
  ticket: SupportTicketSummary,
  message: SupportTicketMessageSummary
): void {
  const root = document.querySelector<HTMLElement>(`[data-player-ticket-detail][data-ticket-id="${cssEscape(ticket.id)}"]`);
  const messages = root?.querySelector<HTMLElement>("[data-player-ticket-messages]");
  if (!root || !messages || messages.querySelector(`[data-ticket-message-id="${cssEscape(message.id)}"]`)) return;

  if (messages.textContent?.trim() === "No updates yet.") messages.innerHTML = "";
  messages.insertAdjacentHTML("beforeend", renderPlayerTicketMessages(ticket, [message]));
  const signature = messages.dataset.messageSignature ?? "";
  messages.dataset.messageSignature = `${signature}${signature ? "|" : ""}${message.id}:${message.createdAt}`;
  const updated = root.querySelector<HTMLElement>("[data-player-ticket-updated]");
  if (updated) updated.textContent = `Updated ${new Date(ticket.updatedAt).toLocaleString()}`;
  const conversation = root.querySelector<HTMLElement>(".player-ticket-conversation");
  if (conversation) conversation.scrollTop = conversation.scrollHeight;
}

async function refreshPortalNotifications(): Promise<void> {
  if (
    notificationRefreshInFlight
    || notificationRefreshSuspended
    || !userId
    || Date.now() < notificationRefreshRetryAt
  ) return;
  notificationRefreshInFlight = true;
  try {
    await refreshPortalRoleSessionIfNeeded();
    const [ticketInbox, rewardInbox] = await Promise.all([
      apiClient.listStaffTicketNotifications(userId, roleIds, 20),
      apiClient.listEventRewardNotifications(userId, currentSnapshot.server.id, 20)
    ]);
    const notifications = [...ticketInbox.notifications, ...rewardInbox.notifications]
      .sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt))
      .slice(0, 20);
    const unreadCount = ticketInbox.unreadCount + rewardInbox.unreadCount;
    const badge = document.querySelector<HTMLElement>("[data-global-notification-count]");
    if (badge) {
      badge.textContent = unreadCount > 99 ? "99+" : String(unreadCount);
      badge.hidden = unreadCount <= 0;
    }
    const list = document.querySelector<HTMLElement>("[data-global-notification-list]");
    if (list) list.innerHTML = renderPortalNotifications(notifications);
    notificationRefreshFailures = 0;
    notificationRefreshRetryAt = 0;
  } catch (error) {
    if (isPortalAuthenticationError(error)) {
      notificationRefreshSuspended = true;
      if (!notificationAuthenticationNoticeShown) {
        notificationAuthenticationNoticeShown = true;
        setStatus("Your portal session has expired. Sign in again when convenient; unsent ticket details remain on this page.");
      }
    } else {
      notificationRefreshFailures += 1;
      notificationRefreshRetryAt = Date.now() + Math.min(
        NOTIFICATION_RETRY_MAX_MS,
        5_000 * (2 ** Math.min(notificationRefreshFailures - 1, 4))
      );
    }
  } finally {
    notificationRefreshInFlight = false;
  }
}

async function handleCustomAiMapLoad(background = false): Promise<void> {
  if (customAiMapRefreshInFlight || !currentAccess.permissions.includes("server.status.view")) return;
  if (!document.querySelector("#customAiMap")) return;
  customAiMapRefreshInFlight = true;
  try {
    const settings = await apiClient.getCustomAiMap(currentSnapshot.server.id, roleIds);
    await updateCustomAiMap(settings);
    if (!background) setStatus(`Custom AI tracker updated: ${settings.actors.length} exact-owned AI.`);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    markCustomAiMapUnavailable(message);
    if (!background) setStatus(`Custom AI tracker failed: ${message}`);
  } finally {
    customAiMapRefreshInFlight = false;
  }
}

async function refreshPortalRoleSessionIfNeeded(): Promise<void> {
  if (Date.now() - portalRoleSessionRefreshedAt < PORTAL_ROLE_REFRESH_INTERVAL_MS) return;
  if (!portalRoleSessionRefreshInFlight) {
    portalRoleSessionRefreshInFlight = apiClient.refreshPortalSession()
      .then(() => {
        portalRoleSessionRefreshedAt = Date.now();
      })
      .finally(() => {
        portalRoleSessionRefreshInFlight = undefined;
      });
  }
  await portalRoleSessionRefreshInFlight;
}

function togglePortalNotifications(button: HTMLElement): void {
  const panel = document.querySelector<HTMLElement>("[data-global-notification-panel]");
  if (!panel) return;
  panel.hidden = !panel.hidden;
  button.setAttribute("aria-expanded", String(!panel.hidden));
  if (!panel.hidden) void refreshPortalNotifications();
}

function closePortalNotifications(restoreFocus = false): void {
  const panel = document.querySelector<HTMLElement>("[data-global-notification-panel]");
  const button = document.querySelector<HTMLButtonElement>("[data-portal-action='notification-toggle']");
  if (panel) panel.hidden = true;
  button?.setAttribute("aria-expanded", "false");
  if (restoreFocus) button?.focus({ preventScroll: true });
}

function focusPortalMain(): void {
  document.querySelector<HTMLElement>("#portalMainContent")?.focus({ preventScroll: true });
}

async function markAllPortalNotificationsRead(): Promise<void> {
  try {
    await Promise.all([
      apiClient.markAllStaffTicketNotificationsRead(userId, roleIds),
      apiClient.markAllEventRewardNotificationsRead(userId, currentSnapshot.server.id)
    ]);
    await refreshPortalNotifications();
  } catch (error) {
    setStatus(`Notifications could not be updated: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function openPortalNotification(notificationId: string, ticketId?: string, type?: string, destinationPath?: string): Promise<void> {
  try {
    if (type === "event_reward") await apiClient.markEventRewardNotificationRead(notificationId, userId);
    else await apiClient.markStaffTicketNotificationRead(notificationId, userId, roleIds);
  } catch {
    // Opening the destination remains useful when another session marked it read first.
  }
  closePortalNotifications();
  if (type === "event_reward") {
    await navigatePortal(new URL(!destinationPath || destinationPath === "/portal/gambling" ? "/portal/events" : destinationPath, window.location.origin));
    return;
  }
  if (!ticketId) return;
  const canUseCaseDesk = currentAccess.permissions.includes("tickets.view") || currentAccess.permissions.includes("tickets.staff_report.view");
  const path = type === "moderation"
    ? "/portal/support"
    : canUseCaseDesk ? `/portal/cases?ticket=${encodeURIComponent(ticketId)}` : `/portal/support?ticket=${encodeURIComponent(ticketId)}`;
  await navigatePortal(new URL(path, window.location.origin));
}

async function showCaseNotificationDialog(): Promise<void> {
  const dialog = document.querySelector<HTMLDialogElement>("#caseNotificationDialog");
  if (!dialog) return;
  if (!dialog.open) dialog.showModal();
  await loadCaseNotifications();
}

function closeCaseNotificationDialog(): void {
  const dialog = document.querySelector<HTMLDialogElement>("#caseNotificationDialog");
  if (dialog?.open) dialog.close();
}

async function loadCaseNotifications(): Promise<void> {
  const result = document.querySelector<HTMLElement>("#caseNotificationList");
  try {
    const [preference, inbox] = await Promise.all([
      apiClient.getStaffTicketNotificationPreferences(userId, roleIds),
      apiClient.listStaffTicketNotifications(userId, roleIds)
    ]);
    setChecked("[data-ticket-notification-in-app]", preference.inAppEnabled);
    setChecked("[data-ticket-notification-discord]", preference.discordDmEnabled);
    setChecked("[data-ticket-notification-assignment]", preference.notifyAssignment);
    setChecked("[data-ticket-notification-watched]", preference.notifyWatchedCase);
    setChecked("[data-ticket-notification-reporter-reply]", preference.notifyReporterReply);
    if (result) result.innerHTML = renderStaffTicketNotifications(inbox.notifications);
    updateCaseNotificationBadge(inbox.unreadCount);
  } catch (error) {
    if (result) result.innerHTML = `<div class="support-empty"><strong>Notifications unavailable</strong><p>${escapeHtml(error instanceof Error ? error.message : String(error))}</p></div>`;
    setStatus(`Case notification load failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function refreshCaseNotificationBadge(): Promise<void> {
  try {
    const inbox = await apiClient.listStaffTicketNotifications(userId, roleIds);
    updateCaseNotificationBadge(inbox.unreadCount);
  } catch {
    updateCaseNotificationBadge(0);
  }
}

function updateCaseNotificationBadge(unreadCount: number): void {
  const badge = document.querySelector<HTMLElement>("[data-ticket-notification-count]");
  if (!badge) return;
  badge.textContent = String(unreadCount);
  badge.hidden = unreadCount <= 0;
}

async function saveCaseNotificationPreferences(): Promise<void> {
  try {
    setStatus("Saving case notification preferences...");
    await apiClient.updateStaffTicketNotificationPreferences({
      actorUserId: userId,
      actorDiscordRoleIds: roleIds,
      inAppEnabled: isChecked("[data-ticket-notification-in-app]"),
      discordDmEnabled: isChecked("[data-ticket-notification-discord]"),
      notifyAssignment: isChecked("[data-ticket-notification-assignment]"),
      notifyWatchedCase: isChecked("[data-ticket-notification-watched]"),
      notifyReporterReply: isChecked("[data-ticket-notification-reporter-reply]")
    });
    setStatus("Case notification preferences saved.");
  } catch (error) {
    setStatus(`Case notification update failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function markAllCaseNotificationsRead(): Promise<void> {
  try {
    await apiClient.markAllStaffTicketNotificationsRead(userId, roleIds);
    await loadCaseNotifications();
    setStatus("Case notifications marked as read.");
  } catch (error) {
    setStatus(`Notification update failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function openCaseNotification(notificationId: string, ticketId: string): Promise<void> {
  try {
    await apiClient.markStaffTicketNotificationRead(notificationId, userId, roleIds);
  } catch {
    // The case can still be opened if another session already marked the alert as read.
  }
  closeCaseNotificationDialog();
  await handleTicketDetail(ticketId);
  void refreshCaseNotificationBadge();
}

function setChecked(selector: string, checked: boolean): void {
  const input = document.querySelector<HTMLInputElement>(selector);
  if (input) input.checked = checked;
}

function isChecked(selector: string): boolean {
  return document.querySelector<HTMLInputElement>(selector)?.checked === true;
}

async function handleTicketQueuePreset(preset: string): Promise<void> {
  ticketQueuePreset = preset;
  ticketQueueLifecycle = preset === "closed" ? "closed" : "active";
  document.querySelectorAll<HTMLElement>("[data-ticket-preset]").forEach((item) => {
    const active = item.dataset.ticketPreset === preset;
    item.classList.toggle("is-active", active);
    item.setAttribute("aria-pressed", String(active));
  });
  setTicketFilterValue("[data-ticket-filter-query]", "");
  setTicketFilterValue("[data-ticket-filter-type]", "");
  setTicketFilterValue("[data-ticket-filter-status]", "");
  setTicketFilterValue("[data-ticket-filter-assignee]", "");
  setTicketFilterValue("[data-ticket-filter-reporter]", "");
  setTicketFilterValue("[data-ticket-filter-accused]", "");
  setTicketFilterValue("[data-ticket-filter-rule]", "");
  setTicketFilterValue("[data-ticket-filter-tag]", "");
  setTicketFilterValue("[data-ticket-filter-resolution]", "");
  setTicketFilterValue("[data-ticket-filter-created-from]", "");
  setTicketFilterValue("[data-ticket-filter-created-to]", "");
  setTicketFilterChecked("[data-ticket-filter-unassigned]", false);

  if (preset === "mine") {
    setTicketFilterValue("[data-ticket-filter-assignee]", currentSnapshot?.player.discordName ?? userId);
    const assignee = document.querySelector<HTMLInputElement>("[data-ticket-filter-assignee]");
    if (assignee) assignee.dataset.selectedUserId = userId;
  }
  if (preset === "unassigned") setTicketFilterChecked("[data-ticket-filter-unassigned]", true);
  if (preset === "awaiting") setTicketFilterValue("[data-ticket-filter-status]", "awaiting_reporter");
  if (preset === "escalated") setTicketFilterValue("[data-ticket-filter-status]", "escalated");
  if (preset === "owner") setTicketFilterValue("[data-ticket-filter-status]", "owner_review");
  if (preset === "closed") {
    setTicketFilterValue("[data-ticket-filter-status]", "closed");
  }
  await handleTicketQueue(false);
}

function setTicketFilterValue(selector: string, value: string): void {
  const field = document.querySelector<HTMLInputElement | HTMLSelectElement>(selector);
  if (field) {
    field.value = value;
    if (field instanceof HTMLInputElement && field.matches("[data-player-picker]")) {
      field.dataset.selectedUserId = "";
      field.dataset.selectedSteam64Id = "";
    }
  }
}

function setTicketFilterChecked(selector: string, checked: boolean): void {
  const field = document.querySelector<HTMLInputElement>(selector);
  if (field) field.checked = checked;
}

async function handleTicketMetrics(background = false): Promise<void> {
  if (ticketMetricsRefreshInFlight) return;
  ticketMetricsRefreshInFlight = true;
  const result = document.querySelector("#ticketMetricsResult");
  const periodDays = Number(document.querySelector<HTMLSelectElement>("[data-ticket-metrics-period]")?.value ?? "30");
  try {
    if (!background) setStatus("Loading ticket overview...");
    const metrics = await apiClient.getSupportTicketMetrics(roleIds, userId, periodDays);
    if (result) result.innerHTML = renderSupportTicketMetrics(metrics);
    updateTicketPresetCounts(metrics);
    if (!background) setStatus("Ticket overview updated.");
  } catch (error) {
    if (!background) {
      if (result) result.textContent = "Ticket overview is unavailable.";
      setStatus(`Ticket overview failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  } finally {
    ticketMetricsRefreshInFlight = false;
  }
}

function updateTicketPresetCounts(metrics: { canViewTeamCases: boolean; canViewOwnerCases: boolean; open: number; unassigned: number; assignedToActor: number; closed: number; needsFollowup: number; escalated: number; ownerReview: number }): void {
  const teamPreset = document.querySelector<HTMLButtonElement>('[data-ticket-preset="team"]');
  if (teamPreset) teamPreset.hidden = !metrics.canViewTeamCases;
  const escalatedPreset = document.querySelector<HTMLButtonElement>('[data-ticket-preset="escalated"]');
  if (escalatedPreset) escalatedPreset.hidden = !metrics.canViewTeamCases;
  const ownerPreset = document.querySelector<HTMLButtonElement>('[data-ticket-preset="owner"]');
  if (ownerPreset) ownerPreset.hidden = !metrics.canViewOwnerCases;
  const counts = { unassigned: metrics.unassigned, mine: metrics.assignedToActor, followup: metrics.needsFollowup, escalated: metrics.escalated, owner: metrics.ownerReview, team: metrics.open, closed: metrics.closed };
  for (const [preset, count] of Object.entries(counts)) {
    const target = document.querySelector<HTMLElement>(`[data-ticket-preset-count="${preset}"]`);
    if (target) target.textContent = String(count);
  }
}

async function handleStaffPerformance(): Promise<void> {
  const result = document.querySelector<HTMLElement>("#staffPerformanceResult");
  const periodDays = Number(document.querySelector<HTMLSelectElement>("[data-staff-performance-period]")?.value ?? "7");
  const from = document.querySelector<HTMLInputElement>("[data-staff-performance-from]")?.value;
  const to = document.querySelector<HTMLInputElement>("[data-staff-performance-to]")?.value;
  const staffUserId = playerPickerValue(document.querySelector<HTMLInputElement>("[data-staff-performance-player]"), "user");
  try {
    const report = await apiClient.getStaffPerformance(userId, roleIds, {
      periodDays,
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      ...(staffUserId ? { staffUserId } : {})
    });
    if (result) result.innerHTML = renderStaffPerformance(report);
    setStatus("Staff activity updated.");
  } catch (error) {
    if (result) result.innerHTML = `<div class="support-empty"><strong>Team activity is unavailable</strong><p>${escapePortalText(error instanceof Error ? error.message : String(error))}</p></div>`;
    setStatus("Staff activity could not be loaded.");
  }
}

async function selectGamblingGame(game: string): Promise<void> {
  if (rouletteSpinInFlight) { setStatus("Let the ball settle before changing tables."); return; }
  if (game !== "slots" && game !== "blackjack" && game !== "roulette" && game !== "raptor-racing") return;
  document.querySelectorAll<HTMLButtonElement>("[data-gambling-game]").forEach((button) => {
    const selected = button.dataset.gamblingGame === game;
    button.setAttribute("aria-selected", String(selected));
    button.tabIndex = selected ? 0 : -1;
    const state = button.querySelector<HTMLElement>("b");
    if (state) state.textContent = selected ? gamblingGameStatus(game) : "SELECT";
  });
  document.querySelectorAll<HTMLElement>("[data-gambling-panel]").forEach((panel) => {
    panel.hidden = panel.dataset.gamblingPanel !== game;
  });
  if (game === "slots" && !slotsLoaded) await handleSlotsLoad();
  if (game === "blackjack" && !blackjackState) await handleBlackjackLoad();
  if (game === "roulette" && !rouletteState) await handleRouletteLoad();
  if (game === "raptor-racing") await handleRaptorRaceLoad();
}

function handleGamblingMenuKeyboard(event: KeyboardEvent): boolean {
  const current = event.target instanceof HTMLButtonElement
    ? event.target.closest<HTMLButtonElement>("[data-gambling-game]")
    : null;
  if (!current || !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return false;
  const tabs = Array.from(document.querySelectorAll<HTMLButtonElement>("[data-gambling-game]"));
  const index = tabs.indexOf(current);
  if (index < 0 || tabs.length === 0) return false;
  event.preventDefault();
  const nextIndex = event.key === "Home"
    ? 0
    : event.key === "End"
      ? tabs.length - 1
      : (index + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
  const next = tabs[nextIndex];
  if (!next?.dataset.gamblingGame) return true;
  next.focus();
  void selectGamblingGame(next.dataset.gamblingGame);
  return true;
}

async function handleBlackjackLoad(): Promise<void> {
  const workspace = document.querySelector<HTMLElement>("#blackjackWorkspace");
  if (!workspace) return;
  try {
    blackjackState = await apiClient.getBlackjack(userId, currentSnapshot.server.id);
    blackjackConfiguration = blackjackState.configuration;
    currentSnapshot.wallet.points = blackjackState.balance;
    workspace.innerHTML = renderBlackjack(blackjackState, currentSnapshot.currency);
    updateGamblingGameBadge("blackjack");
    updateCooldowns();
    refreshPortalShell(currentSnapshot);
    hydratePortalIcons();
  } catch (error) {
    workspace.innerHTML = `<section class="panel blackjack-closed"><span class="eyebrow">Table connection lost</span><h3>The Keeper could not verify the table</h3><p>${escapePortalText(error instanceof Error ? error.message : String(error))}</p><button class="tool-button" type="button" data-portal-action="gambling-game-select" data-gambling-game="blackjack">Try again</button></section>`;
    updateGamblingGameBadge("blackjack", "OFFLINE");
  }
}

function setBlackjackWager(value: number, selectedButton?: HTMLElement): void {
  const input = document.querySelector<HTMLInputElement>("[data-blackjack-wager]");
  if (!input || !Number.isFinite(value)) return;
  input.value = String(value);
  document.querySelectorAll<HTMLButtonElement>("[data-blackjack-wager-value]").forEach((button) => {
    button.setAttribute("aria-pressed", String(button === selectedButton || Number(button.dataset.blackjackWagerValue) === value));
  });
}

async function handleBlackjackDeal(): Promise<void> {
  const input = document.querySelector<HTMLInputElement>("[data-blackjack-wager]");
  const wager = Number(input?.value ?? 0);
  const configuration = blackjackState?.configuration ?? blackjackConfiguration;
  if (!configuration) throw new Error("The table limits have not loaded.");
  if (!Number.isInteger(wager) || wager < configuration.minimumWager || wager > configuration.maximumWager) {
    throw new Error(`Choose a wager between ${formatNumber(configuration.minimumWager)} and ${formatNumber(configuration.maximumWager)} ${currentSnapshot.currency.points}.`);
  }
  if (wager % configuration.wagerStep !== 0) throw new Error(`Wagers use increments of ${formatNumber(configuration.wagerStep)} ${currentSnapshot.currency.points}.`);
  if (wager > (blackjackState?.balance ?? 0)) throw new Error(`You have ${formatNumber(blackjackState?.balance ?? 0)} ${currentSnapshot.currency.points} available.`);
  const fingerprint = `${currentSnapshot.server.id}:${wager}`;
  if (!pendingBlackjackDeal || pendingBlackjackDeal.fingerprint !== fingerprint) {
    pendingBlackjackDeal = { fingerprint, requestId: crypto.randomUUID() };
  }
  const table = document.querySelector<HTMLElement>("[data-blackjack-table]");
  table?.classList.add("is-dealing");
  try {
    const result = await apiClient.dealBlackjack({
      userId,
      serverId: currentSnapshot.server.id,
      requestId: pendingBlackjackDeal.requestId,
      wager
    });
    pendingBlackjackDeal = undefined;
    if (!blackjackState) await handleBlackjackLoad();
    if (!blackjackState) return;
    blackjackState = {
      ...blackjackState,
      balance: result.balance,
      activeHand: result.hand,
      recentHands: result.hand.status === "settled"
        ? [{ id: result.hand.id, wager: result.hand.wager, outcome: result.hand.outcome, payout: result.hand.payout, playerScore: result.hand.playerScore, dealerScore: result.hand.dealerScore, createdAt: result.hand.createdAt }, ...blackjackState.recentHands.filter((hand) => hand.id !== result.hand.id)].slice(0, 8)
        : blackjackState.recentHands
    };
    renderBlackjackWorkspace(result.hand.status === "player_turn" ? "hit" : "new-hand");
    setStatus(result.hand.status === "settled" ? `Blackjack settled: ${blackjackStatusText(result.hand.outcome)}.` : "The Keeper dealt a new hand.");
  } finally {
    table?.classList.remove("is-dealing");
  }
}

async function handleBlackjackAction(action: "hit" | "stand"): Promise<void> {
  const hand = blackjackState?.activeHand;
  if (!hand || hand.status !== "player_turn") throw new Error("There is no active blackjack hand.");
  const fingerprint = `${hand.id}:${hand.version}:${action}`;
  if (!pendingBlackjackAction || pendingBlackjackAction.fingerprint !== fingerprint) {
    pendingBlackjackAction = { fingerprint, requestId: crypto.randomUUID() };
  }
  const table = document.querySelector<HTMLElement>("[data-blackjack-table]");
  table?.classList.add("is-resolving");
  try {
    const result = await apiClient.playBlackjack({
      handId: hand.id,
      userId,
      serverId: currentSnapshot.server.id,
      requestId: pendingBlackjackAction.requestId,
      expectedVersion: hand.version,
      action
    });
    pendingBlackjackAction = undefined;
    if (!blackjackState) return;
    blackjackState = {
      ...blackjackState,
      balance: result.balance,
      activeHand: result.hand,
      recentHands: result.hand.status === "settled"
        ? [{ id: result.hand.id, wager: result.hand.wager, outcome: result.hand.outcome, payout: result.hand.payout, playerScore: result.hand.playerScore, dealerScore: result.hand.dealerScore, createdAt: result.hand.createdAt }, ...blackjackState.recentHands.filter((candidate) => candidate.id !== result.hand.id)].slice(0, 8)
        : blackjackState.recentHands
    };
    renderBlackjackWorkspace(result.hand.status === "player_turn" ? action : "new-hand");
    setStatus(result.hand.status === "settled" ? `Blackjack settled: ${blackjackStatusText(result.hand.outcome)}.` : `Card drawn. Hand total: ${result.hand.playerScore}.`);
  } finally {
    table?.classList.remove("is-resolving");
  }
}

function renderBlackjackWorkspace(focusTarget?: "wager" | "hit" | "stand" | "new-hand"): void {
  if (!blackjackState) return;
  const workspace = document.querySelector<HTMLElement>("#blackjackWorkspace");
  if (!workspace) return;
  currentSnapshot.wallet.points = blackjackState.balance;
  workspace.innerHTML = renderBlackjack(blackjackState, currentSnapshot.currency);
  document.querySelectorAll<HTMLElement>("[data-blackjack-balance]").forEach((element) => {
    element.textContent = formatNumber(blackjackState?.balance ?? 0);
  });
  updateGamblingGameBadge("blackjack");
  updateCooldowns();
  refreshPortalShell(currentSnapshot);
  hydratePortalIcons();
  if (focusTarget) {
    const selector = ({
      wager: "[data-blackjack-wager]",
      hit: "[data-portal-action='blackjack-hit']",
      stand: "[data-portal-action='blackjack-stand']",
      "new-hand": "[data-portal-action='blackjack-new-hand']"
    } as const)[focusTarget];
    window.requestAnimationFrame(() => document.querySelector<HTMLElement>(selector)?.focus({ preventScroll: true }));
  }
}

function blackjackStatusText(outcome?: BlackjackOutcome): string {
  return ({ blackjack: "blackjack", win: "win", push: "push", loss: "dealer win", bust: "bust", dealer_blackjack: "dealer blackjack" } as const)[outcome ?? "loss"];
}

async function handleRouletteLoad(): Promise<void> {
  if (rouletteSpinInFlight) return;
  const workspace = document.querySelector<HTMLElement>("#rouletteWorkspace");
  if (!workspace) return;
  try {
    rouletteState = await apiClient.getRoulette(userId, currentSnapshot.server.id);
    rouletteConfiguration = rouletteState.configuration;
    selectedRouletteBets = [];
    rouletteBetActions = [];
    selectedRouletteChip = rouletteState.configuration.wagerStep;
    currentSnapshot.wallet.points = rouletteState.balance;
    workspace.innerHTML = renderRoulette(rouletteState, currentSnapshot.currency);
    updateGamblingGameBadge("roulette");
    refreshPortalShell(currentSnapshot);
    hydratePortalIcons();
  } catch (error) {
    workspace.innerHTML = `<section class="panel blackjack-closed"><span class="eyebrow">Wheel connection lost</span><h3>The Bonewheel could not be verified</h3><p>${escapePortalText(error instanceof Error ? error.message : String(error))}</p><button class="tool-button" type="button" data-portal-action="gambling-game-select" data-gambling-game="roulette">Try again</button></section>`;
    updateGamblingGameBadge("roulette", "OFFLINE");
  }
}

function addRouletteBet(type: RouletteBetType, value?: string): void {
  if (!rouletteState || rouletteSpinInFlight) return;
  const chip = selectedRouletteChip || rouletteState.configuration.wagerStep;
  const total = rouletteTotalWager();
  const limit = Math.min(rouletteState.configuration.maximumWager, rouletteState.balance);
  if (total + chip > limit) {
    setStatus(`That chip would take the slip above your ${formatNumber(limit)} ${currentSnapshot.currency.points} table limit.`);
    return;
  }
  const key = rouletteBetKey(type, value);
  const existing = selectedRouletteBets.find((bet) => rouletteBetKey(bet.type, bet.value) === key);
  if (existing) existing.wager += chip;
  else {
    if (selectedRouletteBets.length >= 24) { setStatus("A Bonewheel slip can contain no more than 24 positions."); return; }
    selectedRouletteBets.push({ type, ...(value ? { value } : {}), wager: chip });
  }
  rouletteBetActions.push({ key, wager: chip });
  pendingRouletteSpin = undefined;
  updateRouletteBetTicket();
}

function setRouletteWager(value: number, selectedButton?: HTMLElement): void {
  if (!Number.isFinite(value) || value <= 0) return;
  selectedRouletteChip = value;
  document.querySelectorAll<HTMLButtonElement>("[data-roulette-wager-value]").forEach((button) => button.setAttribute("aria-pressed", String(button === selectedButton || Number(button.dataset.rouletteWagerValue) === value)));
}

function updateRouletteBetTicket(): void {
  const total = rouletteTotalWager();
  const count = document.querySelector<HTMLElement>("[data-roulette-position-count]");
  const totalElement = document.querySelector<HTMLElement>("[data-roulette-total]");
  const returns = document.querySelector<HTMLElement>("[data-roulette-return]");
  if (count) count.textContent = String(selectedRouletteBets.length);
  if (totalElement) totalElement.textContent = formatNumber(total);
  if (returns) returns.textContent = formatNumber(rouletteMaximumReturn());
  const canSpin = Boolean(rouletteState && total >= rouletteState.configuration.minimumWager && total <= rouletteState.configuration.maximumWager && total <= rouletteState.balance);
  const spin = document.querySelector<HTMLButtonElement>("[data-portal-action='roulette-spin']");
  if (spin) spin.disabled = !canSpin;
  const note = document.querySelector<HTMLElement>("[data-roulette-spin-note]");
  if (note) note.textContent = total === 0 ? "Place your first chip" : canSpin ? `${selectedRouletteBets.length} position${selectedRouletteBets.length === 1 ? "" : "s"} · ${formatNumber(total)} ${currentSnapshot.currency.points}` : `Minimum total stake ${formatNumber(rouletteState?.configuration.minimumWager ?? 0)}`;
  document.querySelectorAll<HTMLButtonElement>("[data-portal-action='roulette-undo'], [data-portal-action='roulette-clear']").forEach((button) => { button.disabled = total === 0; });
  const badge = document.querySelector<HTMLElement>("[data-roulette-slip-badge]");
  if (badge) badge.textContent = total ? `${selectedRouletteBets.length} LIVE` : "EMPTY";
  const lines = document.querySelector<HTMLElement>("[data-roulette-slip-lines]");
  if (lines) lines.innerHTML = selectedRouletteBets.length ? selectedRouletteBets.map((bet) => `<div class="roulette-slip-line"><span><strong>${escapePortalText(rouletteBetDisplay(bet.type, bet.value))}</strong><small>${rouletteOdds(bet.type) - 1}:1 · return ${formatNumber(bet.wager * rouletteOdds(bet.type))}</small></span><b>${formatNumber(bet.wager)}</b><button type="button" data-portal-action="roulette-bet-remove" data-roulette-bet-key="${escapePortalText(rouletteBetKey(bet.type, bet.value))}" aria-label="Remove ${escapePortalText(rouletteBetDisplay(bet.type, bet.value))}">×</button></div>`).join("") : `<p>Choose a chip, then click numbers or outside fields. Small edge targets place splits, streets, corners and six-lines.</p>`;
  document.querySelectorAll<HTMLElement>("[data-roulette-position-chip]").forEach((chip) => {
    const bet = selectedRouletteBets.find((candidate) => rouletteBetKey(candidate.type, candidate.value) === chip.dataset.roulettePositionChip);
    chip.textContent = bet ? (bet.wager >= 1_000 ? `${(bet.wager / 1_000).toFixed(bet.wager % 1_000 === 0 ? 0 : 1)}k` : String(bet.wager)) : "";
    chip.closest("button")?.classList.toggle("has-chip", Boolean(bet));
  });
}

async function handleRouletteSpin(): Promise<void> {
  if (rouletteSpinInFlight) return;
  const configuration = rouletteState?.configuration ?? rouletteConfiguration;
  if (!rouletteState || !configuration) throw new Error("The Bonewheel limits have not loaded.");
  if (!selectedRouletteBets.length) throw new Error("Place at least one chip first.");
  const wager = rouletteTotalWager();
  if (!Number.isInteger(wager) || wager < configuration.minimumWager || wager > configuration.maximumWager || wager % configuration.wagerStep !== 0) throw new Error(`Choose a valid wager in ${formatNumber(configuration.wagerStep)} ${currentSnapshot.currency.points} increments.`);
  if (wager > rouletteState.balance) throw new Error(`You have ${formatNumber(rouletteState.balance)} ${currentSnapshot.currency.points} available.`);
  const canonicalBets = [...selectedRouletteBets].sort((a, b) => rouletteBetKey(a.type, a.value).localeCompare(rouletteBetKey(b.type, b.value)));
  const fingerprint = `${currentSnapshot.server.id}:${canonicalBets.map((bet) => `${rouletteBetKey(bet.type, bet.value)}:${bet.wager}`).join("|")}`;
  if (!pendingRouletteSpin || pendingRouletteSpin.fingerprint !== fingerprint) pendingRouletteSpin = { fingerprint, requestId: crypto.randomUUID() };
  const workspace = document.querySelector<HTMLElement>("#rouletteWorkspace");
  const controls = Array.from(workspace?.querySelectorAll<HTMLButtonElement | HTMLInputElement>("button, input") ?? []).map((element) => ({ element, disabled: element.disabled }));
  rouletteSpinInFlight = true;
  controls.forEach(({ element }) => { element.disabled = true; });
  workspace?.querySelector<HTMLElement>("[data-roulette-wheel]")?.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth", block: "center" });
  const wheelStatus = workspace?.querySelector<HTMLElement>("[data-wheel-status]");
  if (wheelStatus) wheelStatus.textContent = "Releasing the ball…";
  try {
    const result = await apiClient.spinRoulette({ userId, serverId: currentSnapshot.server.id, requestId: pendingRouletteSpin.requestId, bets: canonicalBets });
    pendingRouletteSpin = undefined;
    const wheel = workspace?.querySelector<HTMLElement>("[data-roulette-wheel]");
    if (wheel) {
      if (wheelStatus) wheelStatus.textContent = "No more bets · ball in play";
      const resultLabel = workspace?.querySelector<HTMLElement>("[data-roulette-result]");
      if (resultLabel) resultLabel.textContent = "In motion";
      await animateRouletteWheel(wheel, result.spin.winningNumber);
    }
    rouletteState = { ...rouletteState, balance: result.balance, recentSpins: [result.spin, ...rouletteState.recentSpins.filter((spin) => spin.id !== result.spin.id)].slice(0, 12) };
    currentSnapshot.wallet.points = result.balance;
    selectedRouletteBets = [];
    rouletteBetActions = [];
    if (workspace?.isConnected) workspace.innerHTML = renderRoulette(rouletteState, currentSnapshot.currency);
    document.querySelectorAll<HTMLElement>("[data-roulette-balance], [data-blackjack-balance]").forEach((element) => { element.textContent = formatNumber(result.balance); });
    updateGamblingGameBadge("roulette");
    refreshPortalShell(currentSnapshot);
    hydratePortalIcons();
    setStatus(`The Bonewheel settled on ${result.spin.winningNumber} ${result.spin.winningColor}. ${result.spin.payout > 0 ? `${formatNumber(result.spin.payout)} ${currentSnapshot.currency.points} returned.` : "The wager was lost."}`);
    workspace?.querySelector<HTMLButtonElement>("[data-portal-action='roulette-repeat']")?.focus({ preventScroll: true });
  } catch (error) {
    controls.forEach(({ element, disabled }) => { element.disabled = disabled; });
    if (wheelStatus) wheelStatus.textContent = "Release interrupted · retry your slip";
    throw error;
  } finally {
    rouletteSpinInFlight = false;
  }
}

function rouletteBetDisplay(type: RouletteBetType, value?: string): string {
  if (type === "straight") return `Number ${value}`;
  if (type === "dozen" || type === "column") return ({ first: `1st ${type}`, second: `2nd ${type}`, third: `3rd ${type}` } as Record<string, string>)[value ?? ""] ?? type;
  if (["split", "street", "corner", "six_line"].includes(type)) return `${type === "six_line" ? "Six-line" : type[0]!.toUpperCase() + type.slice(1)} ${value}`;
  return ({ red: "Red", black: "Black", odd: "Odd", even: "Even", low: "1–18", high: "19–36" } as Record<string, string>)[type] ?? type;
}

function rouletteBetKey(type: RouletteBetType, value?: string): string { return `${type}:${value ?? ""}`; }
function rouletteTotalWager(): number { return selectedRouletteBets.reduce((sum, bet) => sum + bet.wager, 0); }
function rouletteOdds(type: RouletteBetType): number { return ({ straight: 36, split: 18, street: 12, corner: 9, six_line: 6, dozen: 3, column: 3 } as Partial<Record<RouletteBetType, number>>)[type] ?? 2; }
function rouletteBetWins(type: RouletteBetType, value: string | undefined, number: number): boolean {
  if (["straight", "split", "street", "corner", "six_line"].includes(type)) return (value ?? "").split(",").map(Number).includes(number);
  if (type === "red" || type === "black") return number !== 0 && rouletteColorForNumber(number) === type;
  if (type === "odd" || type === "even") return number !== 0 && (number % 2 === 1) === (type === "odd");
  if (type === "low" || type === "high") return type === "low" ? number >= 1 && number <= 18 : number >= 19 && number <= 36;
  if (type === "dozen") return value === "first" ? number >= 1 && number <= 12 : value === "second" ? number >= 13 && number <= 24 : number >= 25 && number <= 36;
  if (type === "column") return number !== 0 && number % 3 === ({ first: 1, second: 2, third: 0 } as Record<string, number>)[value ?? ""];
  return false;
}
function rouletteColorForNumber(number: number): "red" | "black" | "green" { return number === 0 ? "green" : new Set([1,3,5,7,9,12,14,16,18,19,21,23,25,27,30,32,34,36]).has(number) ? "red" : "black"; }
function rouletteMaximumReturn(): number {
  const returns = Array.from({ length: 37 }, (_, number) => selectedRouletteBets.reduce(
    (sum, bet) => sum + (rouletteBetWins(bet.type, bet.value, number) ? bet.wager * rouletteOdds(bet.type) : 0), 0
  ));
  return Math.max(0, ...returns);
}
function clearRouletteBets(): void { selectedRouletteBets = []; rouletteBetActions = []; pendingRouletteSpin = undefined; updateRouletteBetTicket(); }
function removeRouletteBet(key: string): void { selectedRouletteBets = selectedRouletteBets.filter((bet) => rouletteBetKey(bet.type, bet.value) !== key); rouletteBetActions = rouletteBetActions.filter((action) => action.key !== key); pendingRouletteSpin = undefined; updateRouletteBetTicket(); }
function undoRouletteBet(): void {
  const action = rouletteBetActions.pop(); if (!action) return;
  const bet = selectedRouletteBets.find((candidate) => rouletteBetKey(candidate.type, candidate.value) === action.key);
  if (bet) { bet.wager -= action.wager; if (bet.wager <= 0) selectedRouletteBets = selectedRouletteBets.filter((candidate) => candidate !== bet); }
  pendingRouletteSpin = undefined; updateRouletteBetTicket();
}
function repeatLastRouletteSlip(): void {
  const last = rouletteState?.recentSpins[0]; if (!last) return;
  if (last.wager > Math.min(rouletteState!.balance, rouletteState!.configuration.maximumWager)) { setStatus("Your last slip is above the current balance or table limit."); return; }
  const bets = last.bets ?? [{ type: last.betType, ...(last.betValue ? { value: last.betValue } : {}), wager: last.wager }];
  selectedRouletteBets = bets.map(({ type, value, wager }) => ({ type, ...(value ? { value } : {}), wager }));
  rouletteBetActions = selectedRouletteBets.map((bet) => ({ key: rouletteBetKey(bet.type, bet.value), wager: bet.wager }));
  pendingRouletteSpin = undefined; updateRouletteBetTicket();
}

async function handleRaptorRaceLoad(): Promise<void> {
  const workspace = document.querySelector<HTMLElement>("#raptorRaceWorkspace");
  if (!workspace || raptorRaceLoading || raptorRaceBetting) return;
  raptorRaceLoading = true;
  const version = raptorRaceWriteVersion;
  try {
    const next = await apiClient.getRaptorRace(userId, currentSnapshot.server.id);
    if (workspace.isConnected && !raptorRaceBetting && version === raptorRaceWriteVersion) paintRaptorRace(workspace, next);
  } catch (error) {
    if (!workspace.querySelector("[data-raptor-race]")) workspace.innerHTML = `<section class="panel blackjack-closed"><span class="eyebrow">Feed interrupted</span><h3>The containment cameras went dark</h3><p>${escapePortalText(error instanceof Error ? error.message : String(error))}</p><button class="tool-button" type="button" data-portal-action="gambling-game-select" data-gambling-game="raptor-racing">Reconnect feed</button></section>`;
    if (workspace.querySelector("[data-raptor-race]") && !workspace.querySelector("[data-raptor-feed-error]")) workspace.querySelector(".raptor-camera-toolbar")?.insertAdjacentHTML("afterend", '<p data-raptor-feed-error role="alert" class="raptor-watch-note">Connection interrupted. Retrying the shared race feed…</p>');
    updateGamblingGameBadge("raptor-racing", "OFFLINE");
  } finally {
    raptorRaceLoading = false;
    if (raptorRaceRefreshTimer) window.clearTimeout(raptorRaceRefreshTimer);
    raptorRaceRefreshTimer = window.setTimeout(() => {
      const panel = document.querySelector<HTMLElement>("[data-gambling-panel='raptor-racing']");
      if (panel && !panel.hidden) void handleRaptorRaceLoad();
    }, 2_000);
  }
}

function paintRaptorRace(workspace: HTMLElement, next: RaptorRaceState): void {
  const sameHeat = raptorRaceState?.race.raceId === next.race.raceId;
  const wagerInput = workspace.querySelector<HTMLInputElement>("[data-raptor-wager]");
  const wager = wagerInput?.value;
  const focusedWager = document.activeElement === wagerInput;
  selectedRaptorEntrantId = next.userBet?.entrantId ?? (sameHeat ? selectedRaptorEntrantId : undefined);
  workspace.querySelector("[data-raptor-feed-error]")?.remove();
  raptorRaceState = next;
  raptorRaceConfiguration = next.configuration;
  currentSnapshot.wallet.points = next.balance;
  document.querySelectorAll<HTMLElement>("[data-blackjack-balance], [data-roulette-balance]").forEach(element => { element.textContent = formatNumber(next.balance); });
  // Clock-only polls must not recreate the canvas, focused form or fullscreen element.
  const key = JSON.stringify({ ...next, serverNow: undefined });
  if (key !== renderedRaptorRaceKey || !workspace.querySelector("[data-raptor-race]")) {
    const oldRoot = workspace.querySelector<HTMLElement>("[data-raptor-race]");
    const oldShell = workspace.querySelector<HTMLElement>(".raptor-track-shell");
    const retainedStage = sameHeat ? workspace.querySelector<HTMLElement>("[data-raptor-3d-stage]") : null;
    const template = document.createElement("template");
    template.innerHTML = renderRaptorRace(next, currentSnapshot.currency);
    const newRoot = template.content.querySelector<HTMLElement>("[data-raptor-race]");
    const newShell = template.content.querySelector<HTMLElement>(".raptor-track-shell");
    if (!retainedStage || !newRoot) disposeRaptorRace3d();
    if (retainedStage) template.content.querySelector("[data-raptor-3d-stage]")?.replaceWith(retainedStage);
    if (oldRoot && oldShell && newRoot && newShell) {
      Array.from(oldRoot.attributes).forEach(attribute => oldRoot.removeAttribute(attribute.name));
      Array.from(newRoot.attributes).forEach(attribute => oldRoot.setAttribute(attribute.name, attribute.value));
      oldShell.replaceChildren(...Array.from(newShell.childNodes));
      const sidecar = newRoot.querySelector(".raptor-race-sidecar");
      if (sidecar) oldRoot.querySelector(".raptor-race-sidecar")?.replaceWith(sidecar);
    } else workspace.replaceChildren(template.content);
    renderedRaptorRaceKey = key;
    const input = workspace.querySelector<HTMLInputElement>("[data-raptor-wager]");
    if (input && sameHeat && wager) { input.value = wager; if (focusedWager) input.focus({ preventScroll: true }); }
    if (selectedRaptorEntrantId && next.race.phase === "betting" && !next.userBet) {
      const button = workspace.querySelector<HTMLElement>(`[data-raptor-entrant-id="${CSS.escape(selectedRaptorEntrantId)}"]`);
      if (button) selectRaptorRacer(selectedRaptorEntrantId, button.dataset.raptorName ?? "", button);
    }
    const cameraButton = workspace.querySelector<HTMLElement>("[data-portal-action='raptor-camera']");
    if (cameraButton && retainedStage?.dataset.camera === "overview") { cameraButton.textContent = "Trackside camera"; cameraButton.setAttribute("aria-pressed", "true"); }
    const expandButton = workspace.querySelector<HTMLButtonElement>("[data-portal-action='raptor-expand']");
    if (expandButton) { expandButton.hidden = !document.fullscreenEnabled; expandButton.textContent = document.fullscreenElement ? "Exit fullscreen ↙" : "Expand race ↗"; }
    hydratePortalIcons();
  }
  const root = workspace.querySelector<HTMLElement>("[data-raptor-race]");
  if (root) root.dataset.serverNow = next.serverNow;
  startRaptorRaceAnimation();
  const stage = workspace.querySelector<HTMLElement>("[data-raptor-3d-stage]");
  if (stage) void mountRaptorRace3d(stage, next);
  updateGamblingGameBadge("raptor-racing");
  refreshPortalShell(currentSnapshot);
}

function selectRaptorRacer(entrantId: string, name: string, selectedButton: HTMLElement): void {
  if (raptorRaceBetting || raptorRaceState?.race.phase !== "betting" || raptorRaceState.userBet) return;
  selectedRaptorEntrantId = entrantId;
  document.querySelectorAll<HTMLButtonElement>("[data-raptor-entrant-id]").forEach((button) => { button.classList.toggle("is-backed", button === selectedButton); button.setAttribute("aria-pressed", String(button === selectedButton)); });
  const selection = document.querySelector<HTMLElement>("[data-raptor-selection]");
  if (selection) selection.textContent = name;
  const betButton = document.querySelector<HTMLButtonElement>("[data-portal-action='raptor-bet']");
  if (betButton) betButton.disabled = false;
}

async function handleRaptorRaceBet(): Promise<void> {
  if (raptorRaceBetting) return;
  const state = raptorRaceState;
  if (!state || state.race.phase !== "betting") throw new Error("Betting is no longer open for this heat.");
  if (!selectedRaptorEntrantId) throw new Error("Choose a runner first.");
  const wager = Number(document.querySelector<HTMLInputElement>("[data-raptor-wager]")?.value ?? 0);
  const config = state.configuration;
  if (!Number.isInteger(wager) || wager < config.minimumWager || wager > config.maximumWager || wager % config.wagerStep !== 0) throw new Error(`Choose a valid wager in ${formatNumber(config.wagerStep)} ${currentSnapshot.currency.points} increments.`);
  if (wager > state.balance) throw new Error(`You have ${formatNumber(state.balance)} ${currentSnapshot.currency.points} available.`);
  const fingerprint = `${state.race.raceId}:${selectedRaptorEntrantId}:${wager}`;
  if (!pendingRaptorRaceBet || pendingRaptorRaceBet.fingerprint !== fingerprint) pendingRaptorRaceBet = { fingerprint, requestId: crypto.randomUUID() };
  raptorRaceWriteVersion++;
  raptorRaceBetting = true;
  const button = document.querySelector<HTMLButtonElement>("[data-portal-action='raptor-bet']");
  if (button) button.disabled = true;
  try {
    const next = await apiClient.placeRaptorRaceBet({ userId, serverId: currentSnapshot.server.id, requestId: pendingRaptorRaceBet.requestId, raceId: state.race.raceId, entrantId: selectedRaptorEntrantId, wager });
    pendingRaptorRaceBet = undefined;
    const workspace = document.querySelector<HTMLElement>("#raptorRaceWorkspace");
    if (workspace) paintRaptorRace(workspace, next);
    setStatus(`${next.userBet?.entrantName ?? "Runner"} backed for ${formatNumber(wager)} ${currentSnapshot.currency.points}.`);
  } finally {
    raptorRaceBetting = false;
    if (button?.isConnected) button.disabled = raptorRaceState?.race.phase !== "betting" || Boolean(raptorRaceState?.userBet);
    void handleRaptorRaceLoad();
  }
}

function startRaptorRaceAnimation(): void {
  if (raptorRaceAnimationFrame) window.cancelAnimationFrame(raptorRaceAnimationFrame);
  const root = document.querySelector<HTMLElement>("[data-raptor-race]");
  if (!root) return;
  const offset = Date.parse(root.dataset.serverNow ?? "") - Date.now();
  const startsAt = Date.parse(root.dataset.raceStartsAt ?? "");
  const finishesAt = Date.parse(root.dataset.raceFinishesAt ?? "");
  const phase = root.dataset.racePhase;
  const state = raptorRaceState;
  if (!state) return;
  const order = raceFinishOrder(state);
  const lanes = Array.from(root.querySelectorAll<HTMLElement>("[data-raptor-entrant-id]"));
  let last = 0;
  const tick = () => {
    if (!root.isConnected) return;
    raptorRaceAnimationFrame = window.requestAnimationFrame(tick);
    if (Date.now() - last < 100) return;
    last = Date.now();
    const serverNow = Date.now() + offset;
    root.querySelectorAll<HTMLElement>("[data-raptor-countdown]").forEach((clock) => {
      const remaining = Math.max(0, Date.parse(clock.dataset.targetTime ?? "") - serverNow);
      clock.textContent = `${String(Math.floor(remaining / 60_000)).padStart(2, "0")}:${String(Math.floor((remaining % 60_000) / 1_000)).padStart(2, "0")}`;
    });
    if (phase === "betting" && serverNow >= Date.parse(state.race.bettingClosesAt)) root.querySelectorAll<HTMLButtonElement>("[data-raptor-entrant-id], [data-portal-action='raptor-bet']").forEach(button => { button.disabled = true; });
    const progress = phase === "finished" ? 1 : phase === "racing" ? Math.max(0, Math.min(1, (serverNow - startsAt) / (finishesAt - startsAt))) : 0;
    const positions = lanes.map((lane, index) => ({ lane, position: raceRunnerProgress(progress, index, order.indexOf(lane.dataset.raptorEntrantId ?? "")) })).sort((a, b) => b.position - a.position);
    positions.forEach(({ lane, position }, rank) => {
      const label = lane.querySelector<HTMLElement>("[data-raptor-position]");
      if (label) label.textContent = progress > 0 ? `${rank + 1}${rank === 0 ? "st" : rank === 1 ? "nd" : rank === 2 ? "rd" : "th"}` : "5×";
      lane.classList.toggle("is-leading", progress > 0 && rank === 0);
      lane.style.setProperty("--runner-progress", `${position * 100}%`);
    });
  };
  tick();
}

async function handleEventWheelLoad(): Promise<void> {
  const workspace = document.querySelector<HTMLElement>("#eventWheelWorkspace");
  if (!workspace) return;
  try {
    const state = await apiClient.getEventWheel(userId, currentSnapshot.server.id);
    eventWheelConfiguration = state.configuration;
    eventWheelLoaded = true;
    eventWheelSpinInFlight = false;
    currentSnapshot.wallet.premium = state.balance;
    workspace.innerHTML = renderEventWheel(state, currentSnapshot.currency);
    refreshPortalShell(currentSnapshot);
    hydratePortalIcons();
  } catch (error) {
    workspace.innerHTML = `<section class="panel event-wheel-closed"><span class="eyebrow">Telemetry unavailable</span><h3>Survival Allocation could not be loaded</h3><p>${escapePortalText(error instanceof Error ? error.message : String(error))}</p><button class="tool-button" type="button" data-portal-action="event-wheel-refresh">Retry connection</button></section>`;
  }
}

async function handleEventWheelSpin(button: HTMLElement): Promise<void> {
  if (eventWheelSpinInFlight) return;
  if (!eventWheelConfiguration) {
    await handleEventWheelLoad();
    return;
  }
  const disc = document.querySelector<HTMLElement>("[data-event-wheel-disc]");
  const resultView = document.querySelector<HTMLElement>("[data-event-wheel-result]");
  if (!disc || !resultView) return;
  eventWheelSpinInFlight = true;
  if (button instanceof HTMLButtonElement) button.disabled = true;
  resultView.innerHTML = `<strong>Egg accepted</strong><span>The chamber is selecting and recording one verified allocation...</span>`;
  try {
    const fingerprint = `${userId}:${currentSnapshot.server.id}:${eventWheelConfiguration.updatedAt}`;
    if (!pendingEventWheelSpin || pendingEventWheelSpin.fingerprint !== fingerprint) {
      pendingEventWheelSpin = { fingerprint, requestId: crypto.randomUUID() };
    }
    const result = await apiClient.spinEventWheel({ userId, serverId: currentSnapshot.server.id, requestId: pendingEventWheelSpin.requestId });
    pendingEventWheelSpin = undefined;
    const visualPrizes = arrangeEventWheelPrizes(eventWheelConfiguration.prizes);
    const selectedIndex = Math.max(0, visualPrizes.findIndex((prize) => prize.id === result.spin.prizeId));
    const selectedCenter = eventWheelPrizeCenter(visualPrizes, selectedIndex);
    currentSnapshot.wallet = result.wallet;
    await animateEventWheelRelease(disc, 360 * 6 + (360 - selectedCenter));
    resultView.innerHTML = `<strong>${escapePortalText(result.spin.prizeLabel)}</strong><span>${escapePortalText(eventWheelResultDescription(result.spin.prizeType, result.spin.prizeAmount))}</span>`;
    document.querySelectorAll<HTMLElement>("[data-event-wheel-balance]").forEach((element) => { element.textContent = formatNumber(result.wallet.premium); });
    refreshPortalShell(currentSnapshot);
    setStatus(`Event Wheel result: ${result.spin.prizeLabel}.`);
  } catch (error) {
    resultView.innerHTML = `<strong>Spin not completed</strong><span>${escapePortalText(error instanceof Error ? error.message : String(error))}</span>`;
    throw error;
  } finally {
    eventWheelSpinInFlight = false;
    if (button instanceof HTMLButtonElement) button.disabled = currentSnapshot.wallet.premium < eventWheelConfiguration.spinCost;
  }
}

async function animateEventWheelRelease(disc: HTMLElement, targetRotation: number): Promise<void> {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  disc.classList.add("is-resetting");
  disc.classList.remove("is-spinning");
  disc.style.removeProperty("--wheel-result-rotation");
  void disc.offsetWidth;
  disc.style.setProperty("--wheel-result-rotation", `${targetRotation}deg`);
  disc.classList.remove("is-resetting");
  if (reducedMotion) {
    disc.classList.add("is-spinning");
    await new Promise((resolve) => window.setTimeout(resolve, 280));
    return;
  }
  await new Promise<void>((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
    const fallback = window.setTimeout(resolve, 4_650);
    disc.addEventListener("transitionend", (event) => {
      if (event.propertyName !== "transform") return;
      window.clearTimeout(fallback);
      resolve();
    }, { once: true });
    disc.classList.add("is-spinning");
  })));
}

async function handlePrimeTokenClaim(button: HTMLElement, spinId: string): Promise<void> {
  if (button instanceof HTMLButtonElement) button.disabled = true;
  try {
    const result = await apiClient.claimPrimeToken(spinId, {
      userId,
      serverId: currentSnapshot.server.id,
      requestId: crypto.randomUUID()
    });
    if (result.applied) {
      setStatus("This Prime token was already applied and confirmed.");
    } else if (result.operation) {
      setStatus(await waitForBridgeStatus(result.operation.id, "Prime token"));
    } else {
      setStatus("Prime token is still awaiting game-server confirmation.");
    }
    await handleEventWheelLoad();
  } catch (error) {
    if (button instanceof HTMLButtonElement) button.disabled = false;
    throw error;
  }
}

async function handleSpecimenHatcheryOpen(): Promise<void> {
  const hatchery = document.querySelector<HTMLElement>("#specimenHatchery");
  if (!hatchery) return;
  hatchery.hidden = false;
  hatchery.innerHTML = `<div class="hatchery-loading"><span class="specimen-egg-shell tier-recovered" aria-hidden="true"><i></i></span><strong>Opening secure incubation records</strong><p>Verifying sealed rewards against your account...</p></div>`;
  hatchery.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
  try {
    specimenEggInventory = await apiClient.getSpecimenEggs(userId, currentSnapshot.server.id);
    const firstAvailable = specimenEggInventory.eggs.find((egg) => !egg.claimedAt);
    if (firstAvailable) activeSpecimenEggTier = firstAvailable.tier;
    renderCurrentSpecimenHatchery();
  } catch (error) {
    hatchery.innerHTML = `<div class="hatchery-empty"><strong>Incubation records unavailable</strong><p>${escapePortalText(error instanceof Error ? error.message : String(error))}</p><button type="button" data-portal-action="specimen-hatchery-open">Retry</button></div>`;
  }
}

function renderCurrentSpecimenHatchery(): void {
  const hatchery = document.querySelector<HTMLElement>("#specimenHatchery");
  if (!hatchery || !specimenEggInventory) return;
  hatchery.innerHTML = renderSpecimenHatchery(specimenEggInventory, activeSpecimenEggTier);
  hydratePortalIcons();
}

function beginSpecimenIncubation(eggId: string): void {
  const egg = specimenEggInventory?.eggs.find((candidate) => candidate.id === eggId && !candidate.claimedAt);
  const hatchery = document.querySelector<HTMLElement>("#specimenHatchery");
  if (!egg || !hatchery) return;
  hatchery.innerHTML = renderSpecimenIncubator(egg);
  renderSpecimenMutationOptions();
  const sequence = hatchery.querySelector<HTMLElement>("[data-incubator-sequence]");
  const status = hatchery.querySelector<HTMLElement>("[data-incubator-status]");
  window.requestAnimationFrame(() => sequence?.classList.add("is-running"));
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  window.setTimeout(() => {
    if (status) status.textContent = "Shell integrity compromised. Genome controls released.";
    sequence?.classList.add("is-hatched");
    hatchery.querySelector<HTMLElement>("[data-specimen-species]")?.focus({ preventScroll: true });
  }, reduced ? 50 : 2200);
}

function renderSpecimenMutationOptions(): void {
  const form = document.querySelector<HTMLFormElement>("[data-specimen-claim-form]");
  const container = form?.querySelector<HTMLElement>("[data-specimen-mutation-options]");
  const species = form?.querySelector<HTMLSelectElement>("[data-specimen-species]")?.value ?? "";
  if (!form || !container || !species) return;
  container.innerHTML = availableStoreMutationsForSpecies(species).map((mutation) => `
    <label class="incubator-mutation-option">
      <input type="checkbox" value="${escapePortalText(mutation.name)}" data-specimen-mutation-option />
      <span><strong>${escapePortalText(mutation.name)}</strong><small>${escapePortalText(mutation.description)}</small></span>
    </label>`).join("");
  updateSpecimenMutationSelection();
}

function updateSpecimenMutationSelection(changed?: HTMLInputElement): void {
  const form = document.querySelector<HTMLFormElement>("[data-specimen-claim-form]");
  if (!form) return;
  const required = Number(form.dataset.requiredMutations ?? 0);
  const choices = Array.from(form.querySelectorAll<HTMLInputElement>("[data-specimen-mutation-option]"));
  let selected = choices.filter((choice) => choice.checked);
  if (selected.length > required && changed) {
    changed.checked = false;
    selected = choices.filter((choice) => choice.checked);
  }
  choices.forEach((choice) => { choice.disabled = selected.length >= required && !choice.checked; });
  const count = form.querySelector<HTMLElement>("[data-specimen-mutation-count]");
  if (count) count.textContent = `${selected.length} / ${required}`;
  const submit = form.querySelector<HTMLButtonElement>("[data-specimen-claim-submit]");
  if (submit) submit.disabled = selected.length !== required;
}

function randomiseSpecimenMutations(): void {
  const form = document.querySelector<HTMLFormElement>("[data-specimen-claim-form]");
  if (!form) return;
  const required = Number(form.dataset.requiredMutations ?? 0);
  const choices = Array.from(form.querySelectorAll<HTMLInputElement>("[data-specimen-mutation-option]"));
  const selected = choices.filter((choice) => choice.checked);
  const remaining = choices.filter((choice) => !choice.checked)
    .map((choice) => ({ choice, order: crypto.getRandomValues(new Uint32Array(1))[0]! }))
    .sort((left, right) => left.order - right.order);
  for (const entry of remaining.slice(0, Math.max(0, required - selected.length))) entry.choice.checked = true;
  updateSpecimenMutationSelection();
}

async function submitSpecimenEggClaim(form: HTMLFormElement): Promise<void> {
  const eggId = form.dataset.specimenEggId ?? "";
  const egg = specimenEggInventory?.eggs.find((candidate) => candidate.id === eggId && !candidate.claimedAt);
  const species = form.querySelector<HTMLSelectElement>("[data-specimen-species]")?.value ?? "";
  const mutations = Array.from(form.querySelectorAll<HTMLInputElement>("[data-specimen-mutation-option]:checked"), (choice) => choice.value);
  if (!egg || !species || mutations.length !== egg.mutationCount) throw new Error(`Choose exactly ${egg?.mutationCount ?? "the required"} mutations.`);
  const submit = form.querySelector<HTMLButtonElement>("[data-specimen-claim-submit]");
  if (submit) submit.disabled = true;
  const requestId = form.dataset.requestId || crypto.randomUUID();
  form.dataset.requestId = requestId;
  const result = await apiClient.claimSpecimenEgg(egg.id, {
    userId,
    serverId: currentSnapshot.server.id,
    requestId,
    species,
    mutations
  }).catch((error) => {
    if (submit) submit.disabled = false;
    throw error;
  });
  specimenEggInventory = await apiClient.getSpecimenEggs(userId, currentSnapshot.server.id);
  const sequence = document.querySelector<HTMLElement>("[data-incubator-sequence]");
  if (sequence) {
    sequence.classList.add("is-transferred");
    sequence.innerHTML = `<div class="incubator-transfer-complete"><span>TRANSFER COMPLETE</span><strong>${escapePortalText(species)} secured in Garage</strong><p>${mutations.length} mutations recorded · 75% growth · Prime</p><a class="tool-button primary-tool" href="/portal/garage">Open Garage</a><button type="button" data-portal-action="specimen-incubator-cancel">Return to Hatchery</button></div>`;
  }
  setStatus(`${egg.label} incubated successfully. ${species} is now in your garage${result.replayed ? " (verified existing transfer)" : ""}.`);
}

function gamblingGameStatus(game: "slots" | "blackjack" | "roulette" | "raptor-racing"): string {
  if (game === "slots") return "SELECTED";
  if (game === "blackjack") {
    if (!blackjackState) return "CONNECT";
    if (blackjackState.activeHand?.status === "player_turn") return "IN PLAY";
    return blackjackState.configuration.enabled ? "OPEN" : "SEALED";
  }
  if (game === "roulette") {
    if (!rouletteState) return "CONNECT";
    return rouletteState.configuration.enabled ? "OPEN" : "SEALED";
  }
  if (!raptorRaceState) return "CONNECT";
  if (!raptorRaceState.configuration.enabled) return "SEALED";
  return raptorRaceState.race.phase === "racing" ? "LIVE" : raptorRaceState.race.phase === "betting" ? "BET NOW" : "LOCKED";
}

function updateGamblingGameBadge(game: "slots" | "blackjack" | "roulette" | "raptor-racing", override?: string): void {
  const button = document.querySelector<HTMLButtonElement>(`[data-gambling-game="${game}"]`);
  if (!button || button.getAttribute("aria-selected") !== "true") return;
  const badge = button.querySelector<HTMLElement>("b");
  if (badge) badge.textContent = override ?? gamblingGameStatus(game);
}

function eventWheelPrizeCenter(prizes: EventWheelPrizeSummary[], index: number): number {
  const before = prizes.slice(0, index).reduce((sum, prize) => sum + prize.weight, 0);
  return ((before + (prizes[index]?.weight ?? 0) / 2) / 10_000) * 360;
}

function eventWheelResultDescription(type: WheelPrizeType, amount: number): string {
  if (type === "sunny_points") return `${formatNumber(amount)} ${currentSnapshot.currency.points} added to your wallet.`;
  if (type === "event_credits") return `${formatNumber(amount)} ${currentSnapshot.currency.premium} returned to your wallet.`;
  if (type === "dino_voucher") return `${specimenEggLabelForAmount(amount)} secured in The Hatchery. Open the Specimen Incubator when ready.`;
  if (type === "prime_token") return "Prime token secured in your reward vault. Spawn as the dinosaur you want to upgrade, then apply it once.";
  if (type === "event_choice") return "Your right to choose the next community event is recorded in Reward Logs.";
  return "No wallet reward this time.";
}

function specimenEggLabelForAmount(amount: number): string {
  return ({ 4: "Recovered Egg", 8: "Viable Egg", 12: "Enhanced Egg", 16: "Apex Egg" } as Record<number, string>)[amount] ?? "Specimen Egg";
}

async function handleEventCreditGrant(): Promise<void> {
  const player = document.querySelector<HTMLInputElement>("[data-event-credit-player]");
  const amount = Number(document.querySelector<HTMLInputElement>("[data-event-credit-amount]")?.value ?? "0");
  const reason = document.querySelector<HTMLInputElement>("[data-event-credit-reason]")?.value.trim() ?? "";
  const targetUserId = player?.dataset.selectedUserId ?? "";
  if (!targetUserId) throw new Error("Choose a player from the name suggestions first.");
  if (!Number.isInteger(amount) || amount < 1 || amount > 100) throw new Error("Award between 1 and 100 Eggs.");
  if (reason.length < 5) throw new Error("Add the event name or a clear award reason.");
  const fingerprint = JSON.stringify([userId, targetUserId, currentSnapshot.server.id, amount, reason]);
  if (!pendingEventCreditGrant || pendingEventCreditGrant.fingerprint !== fingerprint) {
    pendingEventCreditGrant = { fingerprint, requestId: crypto.randomUUID() };
  }
  const result = await apiClient.grantEventCredits({
    actorUserId: userId,
    targetUserId,
    serverId: currentSnapshot.server.id,
    requestId: pendingEventCreditGrant.requestId,
    amount,
    reason
  });
  const output = document.querySelector<HTMLElement>("[data-event-credit-grant-result]");
  if (output) output.innerHTML = `<div class="staff-service-note is-ready"><strong>Award recorded</strong><span>${formatNumber(amount)} ${escapePortalText(currentSnapshot.currency.premium)} awarded to ${escapePortalText(result.playerName)}. New balance: ${formatNumber(result.balance)}.</span></div>`;
  pendingEventCreditGrant = undefined;
  if (player) { player.value = ""; player.dataset.selectedUserId = ""; }
  setStatus(`${formatNumber(amount)} ${currentSnapshot.currency.premium} awarded to ${result.playerName}.`);
}

async function handleStaffPointGrant(targetUserId: string): Promise<void> {
  const amount = Number(document.querySelector<HTMLInputElement>("[data-staff-points-amount]")?.value ?? "0");
  const reason = document.querySelector<HTMLInputElement>("[data-staff-points-reason]")?.value.trim() ?? "";
  if (!Number.isSafeInteger(amount) || amount < 1 || amount > 1_000_000) {
    throw new Error(`Grant between 1 and 1,000,000 ${currentSnapshot.currency.points}.`);
  }
  if (reason.length < 5) throw new Error("Add a ticket reference or clear audit reason.");

  const fingerprint = JSON.stringify([userId, targetUserId, amount, reason]);
  if (!pendingStaffPointGrant || pendingStaffPointGrant.fingerprint !== fingerprint) {
    pendingStaffPointGrant = { fingerprint, requestId: crypto.randomUUID() };
  }
  const result = await apiClient.grantStaffPoints({
    actorUserId: userId,
    actorDiscordRoleIds: roleIds,
    targetUserId,
    requestId: pendingStaffPointGrant.requestId,
    amount,
    reason
  });
  pendingStaffPointGrant = undefined;

  const output = document.querySelector<HTMLElement>("[data-staff-points-result]");
  if (output) {
    output.innerHTML = `<div class="staff-service-note is-ready"><strong>Grant recorded</strong><span>${formatNumber(result.awardedAmount)} ${escapePortalText(currentSnapshot.currency.points)} credited to ${escapePortalText(result.playerName)}. New balance: ${formatNumber(result.wallet.points)}.</span></div>`;
  }
  setStatus(`${formatNumber(result.awardedAmount)} ${currentSnapshot.currency.points} credited to ${result.playerName}; the staff audit log was updated.`);
}

async function handleEventWheelConfigLoad(): Promise<void> {
  const container = document.querySelector<HTMLElement>("#eventWheelConfig");
  if (!container) return;
  try {
    eventWheelConfiguration = await apiClient.getEventWheelConfig(currentSnapshot.server.id);
    container.innerHTML = renderEventWheelConfiguration(eventWheelConfiguration, currentSnapshot.currency);
  } catch (error) {
    container.innerHTML = `<div class="support-empty"><strong>Wheel configuration is unavailable</strong><p>${escapePortalText(error instanceof Error ? error.message : String(error))}</p></div>`;
  }
}

function addEventWheelPrizeRow(): void {
  const rows = document.querySelector<HTMLElement>("[data-wheel-config-rows]");
  if (!rows) return;
  if (rows.children.length >= 12) throw new Error("The wheel supports up to 12 prize segments.");
  const id = `prize_${crypto.randomUUID().replaceAll("-", "").slice(0, 12)}`;
  rows.insertAdjacentHTML("beforeend", renderEventWheelPrizeRow({ id, label: "New prize", type: "sunny_points", amount: 100, weight: 100 }));
  updateEventWheelConfigTotal();
}

function loadOpeningWeekendWheelPreset(): void {
  const rows = document.querySelector<HTMLElement>("[data-wheel-config-rows]");
  if (!rows) return;
  const prizes: EventWheelPrizeSummary[] = [
    { id: "opening_respin", label: "Egg returned — respin", type: "event_credits", amount: 1, weight: 2000 },
    { id: "opening_sticks_2000", label: "2,000 Sticks", type: "sunny_points", amount: 2000, weight: 2400 },
    { id: "opening_sticks_5000", label: "5,000 Sticks", type: "sunny_points", amount: 5000, weight: 1900 },
    { id: "opening_sticks_10000", label: "10,000 Sticks", type: "sunny_points", amount: 10000, weight: 1200 },
    { id: "opening_sticks_15000", label: "15,000 Sticks", type: "sunny_points", amount: 15000, weight: 800 },
    { id: "opening_prime_4", label: "Recovered Egg", type: "dino_voucher", amount: 4, weight: 600 },
    { id: "opening_prime_8", label: "Viable Egg", type: "dino_voucher", amount: 8, weight: 400 },
    { id: "opening_prime_12", label: "Enhanced Egg", type: "dino_voucher", amount: 12, weight: 200 },
    { id: "opening_prime_16", label: "Apex Egg", type: "dino_voucher", amount: 16, weight: 100 },
    { id: "opening_prime_token", label: "Prime Token", type: "prime_token", amount: 1, weight: 200 },
    { id: "opening_choose_event", label: "Choose the next event", type: "event_choice", amount: 1, weight: 200 }
  ];
  rows.innerHTML = prizes.map((prize) => renderEventWheelPrizeRow(prize)).join("");
  updateEventWheelConfigTotal();
  setStatus("Opening Weekend reward draft loaded. Review it, add a reason, then save when ready.");
}

function updateEventWheelConfigTotal(): void {
  const total = Array.from(document.querySelectorAll<HTMLInputElement>("[data-wheel-prize-chance]"), (input) => Number(input.value || 0)).reduce((sum, chance) => sum + chance, 0);
  const target = document.querySelector<HTMLElement>("[data-wheel-config-total]");
  if (!target) return;
  target.textContent = `${Number(total.toFixed(2))}%`;
  target.classList.toggle("is-invalid", Math.abs(total - 100) > 0.001);
}

function readEventWheelPrizes(): EventWheelPrizeSummary[] {
  return Array.from(document.querySelectorAll<HTMLElement>("[data-wheel-prize-row]"), (row) => {
    const type = row.querySelector<HTMLSelectElement>("[data-wheel-prize-type]")?.value as WheelPrizeType;
    return {
      id: row.dataset.prizeId ?? `prize_${crypto.randomUUID().slice(0, 8)}`,
      label: row.querySelector<HTMLInputElement>("[data-wheel-prize-label]")?.value.trim() ?? "",
      type,
      amount: type === "no_prize" ? 0 : Number(row.querySelector<HTMLInputElement>("[data-wheel-prize-amount]")?.value ?? "0"),
      weight: Math.round(Number(row.querySelector<HTMLInputElement>("[data-wheel-prize-chance]")?.value ?? "0") * 100)
    };
  });
}

async function handleEventWheelConfigSave(): Promise<void> {
  const enabled = document.querySelector<HTMLInputElement>("[data-wheel-config-enabled]")?.checked ?? false;
  const spinCost = Number(document.querySelector<HTMLInputElement>("[data-wheel-config-cost]")?.value ?? "1");
  const reason = document.querySelector<HTMLInputElement>("[data-wheel-config-reason]")?.value.trim() ?? "";
  const prizes = readEventWheelPrizes();
  const totalWeight = prizes.reduce((sum, prize) => sum + prize.weight, 0);
  if (prizes.length < 2 || prizes.length > 12) throw new Error("Configure between 2 and 12 prizes.");
  if (totalWeight !== 10_000) throw new Error("Prize chances must total exactly 100%.");
  if (reason.length < 5) throw new Error("Add a clear reason for changing the wheel.");
  eventWheelConfiguration = await apiClient.saveEventWheelConfig({ actorUserId: userId, serverId: currentSnapshot.server.id, enabled, spinCost, prizes, reason });
  const container = document.querySelector<HTMLElement>("#eventWheelConfig");
  if (container) container.innerHTML = renderEventWheelConfiguration(eventWheelConfiguration, currentSnapshot.currency);
  setStatus(`Event Wheel ${enabled ? "opened" : "saved as closed"} with ${prizes.length} prizes.`);
}

async function handleEventCampaignLoad(): Promise<void> {
  const config = document.querySelector<HTMLElement>("#eventCampaignConfig");
  const logs = document.querySelector<HTMLElement>("#eventRewardLogs");
  if (!config || !logs) return;
  try {
    const [campaignState, rewardLogs] = await Promise.all([
      apiClient.getEventJoinCampaign(currentSnapshot.server.id),
      apiClient.getEventRewardLogs(currentSnapshot.server.id)
    ]);
    config.innerHTML = renderEventJoinCampaign(campaignState.campaign, campaignState.active, Boolean(campaignState.canManageBoosts), campaignState.stagedGrowthMultiplier);
    logs.innerHTML = renderEventRewardLogs(rewardLogs);
    hydratePortalIcons();
  } catch (error) {
    const message = escapePortalText(error instanceof Error ? error.message : String(error));
    config.innerHTML = `<div class="support-empty"><strong>Event campaign is unavailable</strong><p>${message}</p></div>`;
    logs.innerHTML = `<div class="support-empty"><strong>Reward Logs are unavailable</strong><p>${message}</p></div>`;
  }
}

async function handleEventCampaignSave(): Promise<void> {
  const campaignKey = document.querySelector<HTMLInputElement>("[data-event-campaign-key]")?.value.trim() ?? "";
  const name = document.querySelector<HTMLInputElement>("[data-event-campaign-name]")?.value.trim() ?? "";
  const enabled = document.querySelector<HTMLInputElement>("[data-event-campaign-enabled]")?.checked ?? false;
  const rewardEggs = Number(document.querySelector<HTMLInputElement>("[data-event-campaign-eggs]")?.value ?? "0");
  const pointsMultiplier = Number(document.querySelector<HTMLSelectElement>("[data-event-campaign-points-multiplier]")?.value ?? "1");
  const growthMultiplier = Number(document.querySelector<HTMLSelectElement>("[data-event-campaign-growth-multiplier]")?.value ?? "1.25");
  const startValue = document.querySelector<HTMLInputElement>("[data-event-campaign-start]")?.value ?? "";
  const endValue = document.querySelector<HTMLInputElement>("[data-event-campaign-end]")?.value ?? "";
  const reason = document.querySelector<HTMLInputElement>("[data-event-campaign-reason]")?.value.trim() ?? "";
  if (!startValue || !endValue) throw new Error("Choose both the campaign start and end.");
  if (reason.length < 5) throw new Error("Add a clear audit reason.");
  const saved = await apiClient.saveEventJoinCampaign({
    actorUserId: userId,
    serverId: currentSnapshot.server.id,
    campaignKey,
    name,
    enabled,
    startsAt: new Date(startValue).toISOString(),
    endsAt: new Date(endValue).toISOString(),
    rewardEggs,
    pointsMultiplier,
    growthMultiplier,
    reason
  });
  await handleEventCampaignLoad();
  setStatus(saved.growthStageError ?? `${name} ${enabled ? "enabled" : "saved in standby"}; ${pointsMultiplier}x Sticks follows the schedule and ${growthMultiplier}x growth is staged for a restart.`);
}

async function handleBlackjackConfigLoad(): Promise<void> {
  const container = document.querySelector<HTMLElement>("#blackjackConfig");
  if (!container) return;
  try {
    blackjackConfiguration = await apiClient.getBlackjackConfig(currentSnapshot.server.id);
    container.innerHTML = renderBlackjackConfiguration(blackjackConfiguration, currentSnapshot.currency.points);
  } catch (error) {
    container.innerHTML = `<div class="support-empty"><strong>Blackjack configuration is unavailable</strong><p>${escapePortalText(error instanceof Error ? error.message : String(error))}</p></div>`;
  }
}

async function handleBlackjackConfigSave(): Promise<void> {
  const enabled = document.querySelector<HTMLInputElement>("[data-blackjack-config-enabled]")?.checked ?? false;
  const dealerHitsSoft17 = document.querySelector<HTMLInputElement>("[data-blackjack-config-soft17]")?.checked ?? false;
  const minimumWager = Number(document.querySelector<HTMLInputElement>("[data-blackjack-config-min]")?.value ?? 0);
  const maximumWager = Number(document.querySelector<HTMLInputElement>("[data-blackjack-config-max]")?.value ?? 0);
  const wagerStep = Number(document.querySelector<HTMLInputElement>("[data-blackjack-config-step]")?.value ?? 0);
  const cooldownSeconds = Number(document.querySelector<HTMLInputElement>("[data-blackjack-config-cooldown]")?.value ?? 0);
  const dailyWagerLimit = Number(document.querySelector<HTMLInputElement>("[data-blackjack-config-daily]")?.value ?? 0);
  const reason = document.querySelector<HTMLInputElement>("[data-blackjack-config-reason]")?.value.trim() ?? "";
  if (![minimumWager, maximumWager, wagerStep, cooldownSeconds, dailyWagerLimit].every(Number.isInteger)) throw new Error("Blackjack limits must be whole numbers.");
  if (wagerStep < 2 || wagerStep % 2 !== 0) throw new Error("Wager increments must be even so 3:2 blackjack payouts remain exact.");
  if (minimumWager < 1 || maximumWager < minimumWager) throw new Error("Maximum wager must be at least the minimum wager.");
  if (dailyWagerLimit < maximumWager) throw new Error("Daily wager limit must be at least the maximum single wager.");
  if (reason.length < 5) throw new Error("Add a clear reason for changing the blackjack table.");
  blackjackConfiguration = await apiClient.saveBlackjackConfig({
    actorUserId: userId,
    serverId: currentSnapshot.server.id,
    enabled,
    minimumWager,
    maximumWager,
    wagerStep,
    cooldownSeconds,
    dailyWagerLimit,
    dealerHitsSoft17,
    reason
  });
  const container = document.querySelector<HTMLElement>("#blackjackConfig");
  if (container) container.innerHTML = renderBlackjackConfiguration(blackjackConfiguration, currentSnapshot.currency.points);
  setStatus(`Blackjack ${enabled ? "opened" : "saved as closed"} with a ${formatNumber(maximumWager)} ${currentSnapshot.currency.points} maximum wager.`);
}

async function handleRouletteConfigLoad(): Promise<void> {
  const container = document.querySelector<HTMLElement>("#rouletteConfig");
  if (!container) return;
  try {
    rouletteConfiguration = await apiClient.getRouletteConfig(currentSnapshot.server.id);
    container.innerHTML = renderRouletteConfiguration(rouletteConfiguration, currentSnapshot.currency.points);
    hydratePortalIcons();
  } catch (error) {
    container.innerHTML = `<div class="support-empty"><strong>Roulette configuration is unavailable</strong><p>${escapePortalText(error instanceof Error ? error.message : String(error))}</p></div>`;
  }
}

async function handleRouletteConfigSave(): Promise<void> {
  const enabled = document.querySelector<HTMLInputElement>("[data-roulette-config-enabled]")?.checked ?? false;
  const minimumWager = Number(document.querySelector<HTMLInputElement>("[data-roulette-config-min]")?.value ?? 0);
  const maximumWager = Number(document.querySelector<HTMLInputElement>("[data-roulette-config-max]")?.value ?? 0);
  const wagerStep = Number(document.querySelector<HTMLInputElement>("[data-roulette-config-step]")?.value ?? 0);
  const cooldownSeconds = Number(document.querySelector<HTMLInputElement>("[data-roulette-config-cooldown]")?.value ?? 0);
  const dailyWagerLimit = Number(document.querySelector<HTMLInputElement>("[data-roulette-config-daily]")?.value ?? 0);
  const reason = document.querySelector<HTMLInputElement>("[data-roulette-config-reason]")?.value.trim() ?? "";
  if (![minimumWager, maximumWager, wagerStep, cooldownSeconds, dailyWagerLimit].every(Number.isInteger)) throw new Error("Roulette limits must be whole numbers.");
  if (wagerStep < 1 || minimumWager % wagerStep !== 0) throw new Error("Wager increments must divide the minimum wager exactly.");
  if (minimumWager < 1 || maximumWager < minimumWager) throw new Error("Maximum wager must be at least the minimum wager.");
  if (dailyWagerLimit < maximumWager) throw new Error("Daily wager limit must be at least the maximum single wager.");
  if (reason.length < 5) throw new Error("Add a clear reason for changing the Bonewheel.");
  rouletteConfiguration = await apiClient.saveRouletteConfig({
    actorUserId: userId, serverId: currentSnapshot.server.id, enabled, minimumWager, maximumWager,
    wagerStep, cooldownSeconds, dailyWagerLimit, reason
  });
  const container = document.querySelector<HTMLElement>("#rouletteConfig");
  if (container) container.innerHTML = renderRouletteConfiguration(rouletteConfiguration, currentSnapshot.currency.points);
  hydratePortalIcons();
  setStatus(`The Bonewheel ${enabled ? "opened" : "saved as closed"} with a ${formatNumber(maximumWager)} ${currentSnapshot.currency.points} maximum wager.`);
}

async function handleRaptorRaceConfigLoad(): Promise<void> {
  const container = document.querySelector<HTMLElement>("#raptorRaceConfig");
  if (!container) return;
  try {
    raptorRaceConfiguration = await apiClient.getRaptorRaceConfig(currentSnapshot.server.id);
    container.innerHTML = renderRaptorRaceConfiguration(raptorRaceConfiguration, currentSnapshot.currency.points);
    hydratePortalIcons();
  } catch (error) {
    container.innerHTML = `<div class="support-empty"><strong>Raptor race configuration is unavailable</strong><p>${escapePortalText(error instanceof Error ? error.message : String(error))}</p></div>`;
  }
}

async function handleRaptorRaceConfigSave(): Promise<void> {
  const enabled = document.querySelector<HTMLInputElement>("[data-raptor-config-enabled]")?.checked ?? false;
  const minimumWager = Number(document.querySelector<HTMLInputElement>("[data-raptor-config-min]")?.value ?? 0);
  const maximumWager = Number(document.querySelector<HTMLInputElement>("[data-raptor-config-max]")?.value ?? 0);
  const wagerStep = Number(document.querySelector<HTMLInputElement>("[data-raptor-config-step]")?.value ?? 0);
  const dailyWagerLimit = Number(document.querySelector<HTMLInputElement>("[data-raptor-config-daily]")?.value ?? 0);
  const reason = document.querySelector<HTMLInputElement>("[data-raptor-config-reason]")?.value.trim() ?? "";
  if (![minimumWager, maximumWager, wagerStep, dailyWagerLimit].every(Number.isInteger)) throw new Error("Race limits must be whole numbers.");
  if (wagerStep < 1 || minimumWager % wagerStep !== 0) throw new Error("Wager increments must divide the minimum wager exactly.");
  if (minimumWager < 1 || maximumWager < minimumWager || dailyWagerLimit < maximumWager) throw new Error("Review the minimum, maximum and daily race limits.");
  if (reason.length < 5) throw new Error("Add a clear reason for changing the track.");
  raptorRaceConfiguration = await apiClient.saveRaptorRaceConfig({ actorUserId: userId, serverId: currentSnapshot.server.id, enabled, minimumWager, maximumWager, wagerStep, dailyWagerLimit, reason });
  const container = document.querySelector<HTMLElement>("#raptorRaceConfig");
  if (container) container.innerHTML = renderRaptorRaceConfiguration(raptorRaceConfiguration, currentSnapshot.currency.points);
  hydratePortalIcons();
  setStatus(`The Scrapyard Sprint ${enabled ? "opened" : "saved as closed"}.`);
}

async function handleTicketBulkUpdate(): Promise<void> {
  const ticketIds = Array.from(
    document.querySelectorAll<HTMLInputElement>("[data-ticket-bulk-select]:checked"),
    (input) => input.value,
  );
  const action = document.querySelector<HTMLSelectElement>("[data-ticket-bulk-action]")?.value;
  const value = document.querySelector<HTMLInputElement>("[data-ticket-bulk-value]")?.value.trim() ?? "";
  const reason = document.querySelector<HTMLInputElement>("[data-ticket-bulk-reason]")?.value.trim() ?? "";
  if (!ticketIds.length || ticketIds.length > 25) {
    setStatus("Select between 1 and 25 cases.");
    return;
  }
  if (!reason) {
    setStatus("Add an audit reason for the bulk change.");
    return;
  }
  if (action !== "assign" && action !== "add_tags") return;
  const tags = action === "add_tags"
    ? value.split(",").map((tag) => tag.trim().toLowerCase()).filter(Boolean)
    : [];
  if (action === "assign" && !/^usr_[A-Za-z0-9_-]+$/.test(value)) {
    setStatus("Enter the linked usr_ ID of the staff assignee.");
    return;
  }
  if (action === "add_tags" && (!tags.length || tags.length > 10 || new Set(tags).size !== tags.length || tags.some((tag) => !/^[a-z0-9][a-z0-9-]{0,23}$/.test(tag)))) {
    setStatus("Enter up to 10 unique lowercase tags separated by commas.");
    return;
  }
  try {
    setStatus("Applying safe bulk case update...");
    const result = await apiClient.bulkUpdateSupportTickets({
      actorUserId: userId,
      actorDiscordRoleIds: roleIds,
      reason,
      ticketIds,
      action,
      ...(action === "assign" ? { assigneeUserId: value } : { tags })
    });
    await handleTicketQueue();
    setStatus(`Bulk update completed: ${result.updated} changed, ${result.unchangedOrUnavailable} unchanged or unavailable.`);
  } catch (error) {
    setStatus(`Bulk case update failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleTicketTags(ticketId: string): Promise<void> {
  const input = document.querySelector<HTMLInputElement>(`[data-ticket-tags][data-ticket-id="${cssEscape(ticketId)}"]`);
  const reason = document.querySelector<HTMLInputElement>(`[data-ticket-tags-reason][data-ticket-id="${cssEscape(ticketId)}"]`)?.value.trim() ?? "";
  const tags = (input?.value ?? "")
    .split(",")
    .map((tag) => tag.trim().toLowerCase())
    .filter(Boolean);
  if (tags.length > 10 || new Set(tags).size !== tags.length || tags.some((tag) => !/^[a-z0-9][a-z0-9-]{0,23}$/.test(tag))) {
    setStatus("Use up to 10 unique lowercase tags containing letters, numbers, or hyphens.");
    return;
  }
  if (!reason) {
    setStatus("Add an audit reason before changing case tags.");
    return;
  }
  try {
    setStatus("Saving case tags...");
    await apiClient.updateSupportTicketTags(ticketId, { actorUserId: userId, actorDiscordRoleIds: roleIds, reason, tags });
    await handleTicketDetail(ticketId);
    setStatus("Case tags saved.");
  } catch (error) {
    setStatus(`Case tag update failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleTicketDetail(ticketId: string): Promise<void> {
  const result = document.querySelector("#ticketQueueResult");
  const mainScroll = document.querySelector<HTMLElement>(".case-workspace-main")?.scrollTop ?? 0;
  const sidebarScroll = document.querySelector<HTMLElement>(".case-workspace-sidebar")?.scrollTop ?? 0;
  const conversationScroll = document.querySelector<HTMLElement>(".case-conversation-list")?.scrollTop ?? Number.POSITIVE_INFINITY;
  const pageScroll = window.scrollY;
  const draft = captureCaseWorkspaceDraft();
  const focus = capturePortalFocus();
  document.querySelectorAll<HTMLDetailsElement>(".case-workspace details").forEach(rememberDisclosureState);

  try {
    setStatus("Loading ticket detail...");
    const [detail, ruleCatalog] = await Promise.all([
      apiClient.getSupportTicket(ticketId, userId, roleIds),
      loadCaseRuleCatalog()
    ]);
    if (result) {
      result.innerHTML = renderSupportTicketDetail(
        detail.ticket,
        detail.messages,
        detail.accusedPlayers,
        detail.reporterProfile,
        detail.accusedProfiles ?? [],
        detail.evidence ?? [],
        detail.watchers ?? [],
        detail.rules ?? [],
        ruleCatalog,
        detail.evidenceUploadsEnabled,
        detail.logBundles ?? [],
        detail.claimedByDisplayName,
        currentAccess.permissions.includes("moderation.create"),
        currentAccess.permissions.includes("tickets.manage"),
        detail.appealedStrike,
        currentAccess.permissions.includes("moderation.manage"),
        currentAccess.permissions.includes("economy.grant"),
        detail.garageRestoreCandidates,
        currentAccess.permissions.includes("garage.override") || currentAccess.permissions.includes("tickets.outcomes.manage"),
        detail.discordRoom,
        detail.canSendToOwner
      );
      initializeDisclosureStates(result);
    }
    setCaseDeskDetailMode(true);
    currentStaffTicketId = ticketId;
    requestAnimationFrame(() => {
      restoreCaseWorkspaceDraft(draft);
      const main = document.querySelector<HTMLElement>(".case-workspace-main");
      const sidebar = document.querySelector<HTMLElement>(".case-workspace-sidebar");
      const conversation = document.querySelector<HTMLElement>(".case-conversation-list");
      if (main) main.scrollTop = mainScroll;
      if (sidebar) sidebar.scrollTop = sidebarScroll;
      if (conversation) conversation.scrollTop = Number.isFinite(conversationScroll) ? conversationScroll : conversation.scrollHeight;
      window.scrollTo({ top: pageScroll, behavior: "auto" });
      restorePortalFocus(focus);
    });
    if (portalPageFromPath(window.location.pathname) === "cases") {
      const url = new URL(window.location.href);
      url.searchParams.set("ticket", ticketId);
      window.history.replaceState({}, "", `${url.pathname}${url.search}`);
    }
    setStatus("Ticket detail loaded.");
  } catch (error) {
    setStatus(`Ticket detail load failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function refreshStaffTicketConversation(ticketId: string): Promise<void> {
  if (staffTicketRefreshInFlight || currentStaffTicketId !== ticketId) return;
  const list = document.querySelector<HTMLElement>(".case-conversation-list");
  if (!list) return;
  staffTicketRefreshInFlight = true;
  try {
    const detail = await apiClient.getSupportTicket(ticketId, userId, roleIds);
    if (currentStaffTicketId !== ticketId || portalPageFromPath(window.location.pathname) !== "cases") return;
    const messages = detail.messages.filter((message) => !message.body.startsWith("[Evidence write-up]\n"));
    const signature = messages.map((message) => `${message.id}:${message.createdAt}`).join("|");
    if (signature === list.dataset.messageSignature) return;
    const wasNearBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 80;
    const previousMessageIds = new Set(Array.from(list.querySelectorAll<HTMLElement>("[data-message-id]")).map((message) => message.dataset.messageId));
    const newExternalMessages = messages.filter((message) => message.authorUserId !== userId && !previousMessageIds.has(message.id)).length;
    list.innerHTML = renderCaseConversationMessages(detail.ticket, messages);
    list.dataset.messageSignature = signature;
    const count = document.querySelector<HTMLElement>("[data-case-conversation-count]");
    if (count) count.textContent = String(messages.length);
    const dock = document.querySelector<HTMLElement>("[data-case-conversation-dock]");
    const mobile = window.matchMedia("(max-width: 780px)").matches;
    const conversationVisible = !dock || !mobile || dock.classList.contains("is-open");
    if (wasNearBottom && conversationVisible) {
      list.scrollTop = list.scrollHeight;
      setCaseConversationUnread(0);
    } else if (newExternalMessages > 0) {
      setCaseConversationUnread(caseConversationUnread() + newExternalMessages);
    }
  } catch {
    // Keep the current case visible through temporary network failures.
  } finally {
    staffTicketRefreshInFlight = false;
  }
}

function caseConversationUnread(): number {
  const badge = document.querySelector<HTMLElement>("[data-case-conversation-unread]");
  return Number.parseInt(badge?.textContent ?? "0", 10) || 0;
}

function setCaseConversationUnread(count: number): void {
  document.querySelectorAll<HTMLElement>("[data-case-conversation-unread]").forEach((badge) => {
    badge.textContent = String(Math.max(0, count));
    badge.hidden = count <= 0;
  });
}

function toggleCaseConversationDock(force?: boolean): void {
  const dock = document.querySelector<HTMLElement>("[data-case-conversation-dock]");
  if (!dock) return;
  const open = force ?? !dock.classList.contains("is-open");
  dock.classList.toggle("is-open", open);
  document.querySelectorAll<HTMLButtonElement>("[data-portal-action=\"ticket-conversation-toggle\"]").forEach((button) => button.setAttribute("aria-expanded", String(open)));
  if (open) {
    setCaseConversationUnread(0);
    requestAnimationFrame(() => {
      const list = dock.querySelector<HTMLElement>(".case-conversation-list");
      if (list) list.scrollTop = list.scrollHeight;
      dock.querySelector<HTMLTextAreaElement>("[data-ticket-reply-body]")?.focus();
    });
  }
}

function toggleCaseConversationExpansion(): void {
  const dock = document.querySelector<HTMLElement>("[data-case-conversation-dock]");
  const button = document.querySelector<HTMLButtonElement>("[data-portal-action=\"ticket-conversation-expand\"]");
  if (!dock || !button) return;
  const expanded = !dock.classList.contains("is-expanded");
  dock.classList.toggle("is-expanded", expanded);
  button.setAttribute("aria-expanded", String(expanded));
  button.textContent = expanded ? "Compact" : "Expand";
}

interface CaseDraftValue {
  value: string;
  checked?: boolean;
  selectedUserId?: string;
}

function captureCaseWorkspaceDraft(): Map<string, CaseDraftValue> {
  const draft = new globalThis.Map<string, CaseDraftValue>();
  const controls = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(".case-workspace input, .case-workspace textarea, .case-workspace select");
  controls.forEach((control, index) => {
    if (control instanceof HTMLInputElement && (control.type === "file" || control.type === "hidden")) return;
    const key = caseDraftControlKey(control, index);
    draft.set(key, {
      value: control.value,
      ...(control instanceof HTMLInputElement && (control.type === "checkbox" || control.type === "radio") ? { checked: control.checked } : {}),
      ...(control.dataset.selectedUserId ? { selectedUserId: control.dataset.selectedUserId } : {})
    });
  });
  return draft;
}

function restoreCaseWorkspaceDraft(draft: Map<string, CaseDraftValue>): void {
  const controls = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(".case-workspace input, .case-workspace textarea, .case-workspace select");
  controls.forEach((control, index) => {
    const value = draft.get(caseDraftControlKey(control, index));
    if (!value) return;
    control.value = value.value;
    if (control instanceof HTMLInputElement && value.checked !== undefined) control.checked = value.checked;
    if (value.selectedUserId) control.dataset.selectedUserId = value.selectedUserId;
  });
  syncCaseDecisionControls();
}

function syncCaseDecisionControls(): void {
  document.querySelectorAll<HTMLInputElement>("[data-case-review-confirm]").forEach((confirmation) => {
    const ticketId = confirmation.dataset.ticketId;
    if (!ticketId) return;
    const controls = document.querySelector<HTMLFieldSetElement>(`[data-case-decision-controls][data-ticket-id="${cssEscape(ticketId)}"]`);
    if (controls) controls.disabled = !confirmation.checked;
  });
}

function caseDraftControlKey(control: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement, index: number): string {
  const identifiers = Array.from(control.attributes)
    .filter((attribute) => attribute.name.startsWith("data-") && attribute.name !== "data-selected-user-id")
    .map((attribute) => `${attribute.name}=${attribute.value}`)
    .sort()
    .join("|");
  return `${control.tagName}:${control.getAttribute("type") ?? ""}:${identifiers || control.name || index}`;
}

async function loadCaseRuleCatalog(): Promise<RuleDefinitionSummary[]> {
  if (caseRuleCatalogCache.length && Date.now() - caseRuleCatalogLoadedAt < 60_000) return caseRuleCatalogCache;
  caseRuleCatalogCache = await apiClient.listRuleDefinitions(roleIds, { status: "published" });
  caseRuleCatalogLoadedAt = Date.now();
  return caseRuleCatalogCache;
}

function setCaseDeskDetailMode(active: boolean): void {
  document.body.classList.toggle("case-detail-active", active);
  document.querySelector<HTMLElement>(".case-desk")?.classList.toggle("is-case-open", active);
  document.querySelector<HTMLElement>(".content")?.classList.toggle("case-detail-mode", active);
}

const cannedTicketDrafts: Record<string, string> = {
  acknowledge: "Thanks for contacting the staff team. Your case has been received and is now being reviewed. We will update you here if we need anything else.",
  "more-information": "We need a little more information before we can continue. Please add the relevant date and time, what happened, and any screenshots or video links you have.",
  "progress-update": "Your case is still under review. We have not reached a final decision yet, but we will post the outcome here when the investigation is complete.",
  resolved: "The staff review is complete. Please read the outcome below, and reply here if you need clarification about this decision."
};

function insertCannedTicketDraft(ticketId: string): void {
  const select = document.querySelector<HTMLSelectElement>(`[data-ticket-canned-response][data-ticket-id="${cssEscape(ticketId)}"]`);
  const draft = select?.value ? cannedTicketDrafts[select.value] : undefined;
  if (!draft) {
    setStatus("Choose a response starter first.");
    return;
  }
  appendTicketDraft(ticketId, draft);
  setStatus("Draft inserted. Review and edit it before sending.");
}

function insertTicketRuleReference(ticketId: string): void {
  const select = document.querySelector<HTMLSelectElement>(`[data-ticket-rule-reference][data-ticket-id="${cssEscape(ticketId)}"]`);
  const option = select?.selectedOptions[0];
  if (!select?.value || !option) {
    setStatus("Choose a published rule first.");
    return;
  }
  appendTicketDraft(ticketId, `Rule ${select.value} - ${option.dataset.ruleTitle ?? "Server rule"}: `);
  setStatus("Rule reference inserted. Add the relevant explanation before sending.");
}

function appendTicketDraft(ticketId: string, text: string): void {
  const textarea = document.querySelector<HTMLTextAreaElement>(`[data-ticket-reply-body][data-ticket-id="${cssEscape(ticketId)}"]`);
  if (!textarea) return;
  textarea.value = textarea.value.trim() ? `${textarea.value.trim()}\n\n${text}` : text;
  textarea.focus();
  textarea.setSelectionRange(textarea.value.length, textarea.value.length);
}

async function handleTicketReply(ticketId: string): Promise<void> {
  const body = document.querySelector<HTMLTextAreaElement>(`[data-ticket-reply-body][data-ticket-id="${cssEscape(ticketId)}"]`)?.value.trim();
  const internal = document.querySelector<HTMLInputElement>(`[data-ticket-reply-internal][data-ticket-id="${cssEscape(ticketId)}"]`)?.checked ?? false;

  if (!body) {
    setStatus("Write a ticket reply before sending.");
    return;
  }

  try {
    setStatus("Sending ticket reply...");
    const reply = await apiClient.replyToSupportTicket(ticketId, {
      authorUserId: userId,
      actorDiscordRoleIds: roleIds,
      body,
      internal
    });
    appendCaseConversationMessage(reply.message, reply.ticket.creatorUserId);
    const textarea = document.querySelector<HTMLTextAreaElement>(`[data-ticket-reply-body][data-ticket-id="${cssEscape(ticketId)}"]`);
    if (textarea) textarea.value = "";
    setStatus("Ticket reply sent.");
  } catch (error) {
    setStatus(`Ticket reply failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

const EVIDENCE_WRITEUP_PREFIX = "[Evidence write-up]\n";

async function handleTicketEvidenceWriteup(ticketId: string): Promise<void> {
  const textarea = document.querySelector<HTMLTextAreaElement>(`[data-ticket-evidence-writeup][data-ticket-id="${cssEscape(ticketId)}"]`);
  const body = textarea?.value.trim() ?? "";
  if (body.length < 10) {
    setStatus("Add a short evidence explanation before saving the write-up.");
    textarea?.focus();
    return;
  }

  try {
    setStatus("Saving internal evidence write-up...");
    await apiClient.replyToSupportTicket(ticketId, {
      authorUserId: userId,
      actorDiscordRoleIds: roleIds,
      body: `${EVIDENCE_WRITEUP_PREFIX}${body}`,
      internal: true
    });
    if (textarea) textarea.value = "";
    await handleTicketDetail(ticketId);
    setStatus("Evidence write-up saved to the permanent case record.");
  } catch (error) {
    setStatus(`Evidence write-up failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

function appendCaseConversationMessage(message: SupportTicketMessageSummary, creatorUserId: string): void {
  const list = document.querySelector<HTMLElement>(".case-conversation-list");
  if (!list) return;
  list.querySelector(".support-empty")?.remove();
  const item = document.createElement("article");
  item.className = `case-message ${message.internal ? "is-internal" : "is-public"}`;
  item.dataset.messageId = message.id;
  item.dataset.messageAuthorId = message.authorUserId;
  const displayName = message.authorDisplayName?.trim() || message.authorUserId;
  const author = message.internal ? `${displayName} (internal note)` : displayName;
  item.innerHTML = `<header><strong>${escapePortalText(author)}</strong><time datetime="${escapePortalText(message.createdAt)}">Just now</time></header><p>${escapePortalText(message.body)}</p>`;
  list.append(item);
  const count = list.closest(".case-conversation-panel")?.querySelector<HTMLElement>("[data-case-conversation-count]");
  if (count) count.textContent = String(list.querySelectorAll(".case-message").length);
  item.scrollIntoView({ block: "nearest", behavior: "smooth" });
}

async function handleTicketClaim(ticketId: string): Promise<void> {
  try {
    setStatus("Claiming ticket...");
    await apiClient.claimSupportTicket(ticketId, staffTicketActionPayload("Claimed from portal."));
    await handleTicketDetail(ticketId);
    setStatus("Ticket claimed.");
  } catch (error) {
    setStatus(`Ticket claim failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleTicketOperations(ticketId: string, mode: "save" | "unassign"): Promise<void> {
  const assignee = playerPickerValue(document.querySelector<HTMLInputElement>(`[data-ticket-assignee][data-ticket-id="${cssEscape(ticketId)}"]`), "user");
  const reason = document.querySelector<HTMLInputElement>(`[data-ticket-operations-reason][data-ticket-id="${cssEscape(ticketId)}"]`)?.value.trim() ?? "";
  if (!reason) {
    setStatus("Add an audit reason before changing case operations.");
    return;
  }
  if (mode === "save" && assignee && !assignee.startsWith("usr_")) {
    setStatus("Assigned staff user IDs must start with usr_.");
    return;
  }

  try {
    setStatus("Updating case operations...");
    await apiClient.updateSupportTicketOperations(ticketId, {
      ...staffTicketActionPayload(reason),
      ...(mode === "unassign" ? { claimedByUserId: null } : mode === "save" && assignee ? { claimedByUserId: assignee } : {})
    });
    if (mode === "unassign") {
      await handleTicketQueuePreset("unassigned");
      setStatus("Case returned to the unassigned queue.");
    } else if (assignee && assignee !== userId) {
      const returnPreset = ticketQueuePreset === "mine" ? "unassigned" : ticketQueuePreset;
      await handleTicketQueuePreset(returnPreset);
      setStatus("Case assigned and removed from your active workspace.");
    } else {
      await handleTicketDetail(ticketId);
      setStatus("Case assignment updated.");
    }
  } catch (error) {
    setStatus(`Case operation update failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleTicketStatus(ticketId: string, status: string): Promise<void> {
  try {
    const escalationReason = status === "escalated" || status === "owner_review"
      ? (document.querySelector(`${status === "owner_review" ? "[data-ticket-owner-review-reason]" : "[data-ticket-escalation-reason]"}[data-ticket-id="${CSS.escape(ticketId)}"]`) as HTMLInputElement | HTMLTextAreaElement | null)?.value.trim() ?? ""
      : "";
    if ((status === "escalated" || status === "owner_review") && escalationReason.length < 5) {
      setStatus(status === "owner_review"
        ? "Explain the exact owner decision needed before sending this case."
        : "Enter a meaningful escalation reason before escalating this case.");
      return;
    }
    setStatus("Updating ticket status...");
    await apiClient.updateSupportTicketStatus(ticketId, {
      ...staffTicketActionPayload(escalationReason || `Moved to ${status} from portal.`),
      status,
      ...(escalationReason ? { escalationReason } : {})
    });
    if (status === "escalated") {
      await handleTicketQueuePreset(currentAccess.featureFlags.showSeniorTools ? "escalated" : "unassigned");
      setStatus("Case handed to the senior queue.");
      return;
    }
    if (status === "owner_review") {
      await handleTicketQueuePreset(currentAccess.permissions.includes("tickets.staff_report.view") ? "owner" : "escalated");
      setStatus("Case handed to the owner queue.");
      return;
    }
    await handleTicketDetail(ticketId);
    setStatus("Ticket status updated.");
  } catch (error) {
    setStatus(`Ticket status update failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleTicketClose(ticketId: string): Promise<void> {
  try {
    const reviewConfirmed = document.querySelector<HTMLInputElement>(`[data-case-review-confirm][data-ticket-id="${cssEscape(ticketId)}"]`)?.checked ?? false;
    if (!reviewConfirmed) {
      setStatus("Confirm that you reviewed the case before recording an outcome.");
      return;
    }
    const resolutionType = (document.querySelector(`[data-ticket-resolution][data-ticket-id="${CSS.escape(ticketId)}"]`) as HTMLSelectElement | null)?.value.trim() ?? "";
    const closureNote = (document.querySelector(`[data-ticket-closure-note][data-ticket-id="${CSS.escape(ticketId)}"]`) as HTMLTextAreaElement | null)?.value.trim() ?? "";
    const reporterClosureMessage = (document.querySelector(`[data-ticket-reporter-message][data-ticket-id="${CSS.escape(ticketId)}"]`) as HTMLTextAreaElement | null)?.value.trim() ?? "";
    if (!resolutionType || closureNote.length < 5) {
      setStatus("Choose a resolution and enter a meaningful staff closure note.");
      return;
    }
    const moderationToggle = document.querySelector<HTMLInputElement>(`[data-ticket-close-moderation][data-ticket-id="${cssEscape(ticketId)}"]`);
    const moderationEnabled = moderationToggle?.checked ?? false;
    const moderationReason = document.querySelector<HTMLTextAreaElement>(`[data-ticket-case-strike-reason][data-ticket-id="${cssEscape(ticketId)}"]`)?.value.trim() ?? "";
    const moderationLevel = document.querySelector<HTMLSelectElement>(`[data-ticket-case-strike-level][data-ticket-id="${cssEscape(ticketId)}"]`)?.value as "warning" | "strike_1" | "strike_2" | "strike_3" | "strike_4" | undefined;
    const targetUserId = moderationToggle?.dataset.targetUserId;
    let moderation: {
      targetUserId: string;
      level: "warning" | "strike_1" | "strike_2" | "strike_3" | "strike_4";
      reason: string;
      requestId: string;
    } | undefined;
    if (moderationEnabled) {
      if (!targetUserId || !moderationLevel || moderationReason.length < 5) {
        setStatus("Choose a moderation level and add an evidence-based reason before closing.");
        return;
      }
      const fingerprint = JSON.stringify([userId, targetUserId, ticketId, moderationReason, moderationLevel, closureNote]);
      if (!pendingModerationStrike || pendingModerationStrike.fingerprint !== fingerprint) {
        pendingModerationStrike = { fingerprint, requestId: crypto.randomUUID() };
      }
      moderation = {
        targetUserId,
        level: moderationLevel,
        reason: moderationReason,
        requestId: pendingModerationStrike.requestId
      };
    }
    const compensationEnabled = document.querySelector<HTMLInputElement>(`[data-ticket-close-compensation][data-ticket-id="${cssEscape(ticketId)}"]`)?.checked ?? false;
    const compensationTargetUserId = document.querySelector<HTMLSelectElement>(`[data-ticket-compensation-target][data-ticket-id="${cssEscape(ticketId)}"]`)?.value.trim() ?? "";
    const compensationAmount = Number(document.querySelector<HTMLInputElement>(`[data-ticket-compensation-amount][data-ticket-id="${cssEscape(ticketId)}"]`)?.value ?? "");
    const compensationReason = document.querySelector<HTMLTextAreaElement>(`[data-ticket-compensation-reason][data-ticket-id="${cssEscape(ticketId)}"]`)?.value.trim() ?? "";
    let compensation: { targetUserId: string; amount: number; reason: string; requestId: string } | undefined;
    if (compensationEnabled) {
      if (!compensationTargetUserId || !Number.isSafeInteger(compensationAmount) || compensationAmount <= 0 || compensationAmount > 1_000_000 || compensationReason.length < 5) {
        setStatus("Choose a compensation recipient, enter 1 to 1,000,000 whole Sticks, and add a clear reason before closing.");
        return;
      }
      const fingerprint = JSON.stringify([userId, ticketId, compensationTargetUserId, compensationAmount, compensationReason, closureNote]);
      if (!pendingTicketCompensation || pendingTicketCompensation.fingerprint !== fingerprint) {
        pendingTicketCompensation = { fingerprint, requestId: crypto.randomUUID() };
      }
      compensation = {
        targetUserId: compensationTargetUserId,
        amount: compensationAmount,
        reason: compensationReason,
        requestId: pendingTicketCompensation.requestId
      };
    }
    const garageRestoreEnabled = document.querySelector<HTMLInputElement>(`[data-ticket-close-garage-restore][data-ticket-id="${cssEscape(ticketId)}"]`)?.checked ?? false;
    const garageRestoreSelection = document.querySelector<HTMLSelectElement>(`[data-ticket-garage-restore-target][data-ticket-id="${cssEscape(ticketId)}"]`)?.value.trim() ?? "";
    const garageRestoreReason = document.querySelector<HTMLTextAreaElement>(`[data-ticket-garage-restore-reason][data-ticket-id="${cssEscape(ticketId)}"]`)?.value.trim() ?? "";
    let garageRestored = false;
    if (garageRestoreEnabled) {
      const [restoreTargetUserId = "", restoreSource = "", restoreSourceId = ""] = garageRestoreSelection.split("|", 3);
      const validSource = restoreSource === "garage_history"
        ? restoreSourceId.startsWith("garage_")
        : restoreSource === "death_snapshot" && restoreSourceId.length > 0;
      if (!restoreTargetUserId.startsWith("usr_") || !validSource || garageRestoreReason.length < 5) {
        setStatus("Choose an eligible historical dinosaur and add a clear restoration reason before closing.");
        return;
      }
      const fingerprint = JSON.stringify([userId, ticketId, restoreTargetUserId, restoreSource, restoreSourceId, garageRestoreReason]);
      if (pendingTicketGarageRestore?.completed && pendingTicketGarageRestore.fingerprint !== fingerprint) {
        setStatus("A dinosaur was already restored for this closure attempt. Retry the case outcome without changing the restoration selection.");
        return;
      }
      if (!pendingTicketGarageRestore || pendingTicketGarageRestore.fingerprint !== fingerprint) {
        pendingTicketGarageRestore = { fingerprint, requestId: crypto.randomUUID(), completed: false };
      }
      setStatus("Restoring the selected dinosaur before closing the case...");
      if (restoreSource === "death_snapshot") {
        await apiClient.restoreDeathSnapshot(restoreSourceId, {
          targetUserId: restoreTargetUserId,
          actorUserId: userId,
          actorDiscordRoleIds: roleIds,
          reason: `${garageRestoreReason} Case ${ticketId}.`,
          ticketId
        });
      } else {
        await apiClient.restoreGarageDino({
          targetUserId: restoreTargetUserId,
          garageDinoId: restoreSourceId,
          actorUserId: userId,
          actorDiscordRoleIds: roleIds,
          reason: `${garageRestoreReason} Case ${ticketId}.`,
          requestId: pendingTicketGarageRestore.requestId,
          ticketId
        });
      }
      pendingTicketGarageRestore.completed = true;
      garageRestored = true;
    }
    setStatus("Closing ticket...");
    const result = await apiClient.closeSupportTicket(ticketId, {
      ...staffTicketActionPayload(closureNote),
      resolutionType,
      closureNote,
      ...(reporterClosureMessage ? { reporterClosureMessage } : {}),
      ...(moderation ? { moderation } : {}),
      ...(compensation ? { compensation } : {})
    });
    pendingModerationStrike = undefined;
    pendingTicketCompensation = undefined;
    pendingTicketGarageRestore = undefined;
    await handleTicketQueuePreset(ticketQueuePreset === "closed" ? "closed" : ticketQueuePreset);
    if (!result.moderation) {
      const compensationMessage = result.compensation
        ? ` ${result.compensation.awardedAmount.toLocaleString("en-GB")} Sticks were credited.`
        : "";
      const garageMessage = garageRestored ? " The selected dinosaur was restored." : "";
      setStatus(`Ticket closed and moved to the closed queue.${garageMessage}${compensationMessage}`);
      return;
    }
    const roleErrors = result.moderation.discordRoleSync.errors.length;
    const delivery = result.moderation.discordNotice.delivered
      ? "The player was notified on the portal and Discord."
      : "The portal notice is available, but the Discord DM could not be delivered.";
    const compensationMessage = result.compensation
      ? ` ${result.compensation.awardedAmount.toLocaleString("en-GB")} Sticks were also credited.`
      : "";
    const garageMessage = garageRestored ? " The selected dinosaur was restored." : "";
    setStatus(`Ticket closed and moderation recorded. ${delivery}${garageMessage}${compensationMessage}${roleErrors ? " Discord role sync needs owner review." : ""}`);
  } catch (error) {
    setStatus(`Case outcome failed; the case remains open and your entries are retained: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleTicketCaseStrike(ticketId: string, targetUserId: string): Promise<void> {
  const reviewConfirmed = document.querySelector<HTMLInputElement>(`[data-case-review-confirm][data-ticket-id="${cssEscape(ticketId)}"]`)?.checked ?? false;
  const reason = document.querySelector<HTMLTextAreaElement>(`[data-ticket-case-strike-reason][data-ticket-id="${cssEscape(ticketId)}"]`)?.value.trim() ?? "";
  const level = document.querySelector<HTMLSelectElement>(`[data-ticket-case-strike-level][data-ticket-id="${cssEscape(ticketId)}"]`)?.value as "warning" | "strike_1" | "strike_2" | "strike_3" | "strike_4" | undefined;
  if (!reviewConfirmed) {
    setStatus("Confirm that you reviewed the case before issuing a strike.");
    return;
  }
  if (reason.length < 5 || !level) {
    setStatus("Add an evidence-based reason and choose a valid moderation level.");
    return;
  }
  try {
    setStatus("Recording strike from reviewed case...");
    const evidenceUrl = new URL(window.location.href);
    evidenceUrl.searchParams.set("ticket", ticketId);
    const fingerprint = JSON.stringify([userId, targetUserId, ticketId, reason, level]);
    if (!pendingModerationStrike || pendingModerationStrike.fingerprint !== fingerprint) {
      pendingModerationStrike = { fingerprint, requestId: crypto.randomUUID() };
    }
    const result = await apiClient.createModerationStrike({
      targetUserId,
      actorUserId: userId,
      actorDiscordRoleIds: roleIds,
      reason,
      evidenceUrl: evidenceUrl.toString(),
      supportTicketId: ticketId,
      level,
      weight: level === "warning" ? 0 : Number(level.slice(-1)),
      surface: "website",
      requestId: pendingModerationStrike.requestId
    });
    pendingModerationStrike = undefined;
    const strikeButton = document.querySelector<HTMLButtonElement>(`[data-portal-action="ticket-case-strike"][data-ticket-id="${cssEscape(ticketId)}"]`);
    if (strikeButton) {
      strikeButton.disabled = true;
      strikeButton.textContent = "Strike recorded";
    }
    const panel = strikeButton?.closest<HTMLElement>(".case-strike-panel");
    panel?.querySelector("[data-strike-result]")?.remove();
    const notice = document.createElement("p");
    notice.dataset.strikeResult = "true";
    notice.className = "case-action-result";
    const roleErrors = result.discordRoleSync?.errors.length ?? 0;
    notice.textContent = result.discordNotice?.delivered
      ? `Strike recorded. The player was notified on the portal and Discord${roleErrors ? "; Discord role sync needs owner review" : "."}`
      : `Strike recorded. The portal notice is live${roleErrors ? "; Discord role sync needs owner review" : ""}. Discord DM could not be delivered.`;
    panel?.append(notice);
    setStatus(notice.textContent);
  } catch (error) {
    setStatus(`Strike could not be recorded: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleTicketAppealStrikeResolution(ticketId: string, strikeId: string, rawStatus: string): Promise<void> {
  const status = rawStatus === "voided" ? "voided" : rawStatus === "forgiven" ? "forgiven" : undefined;
  const reason = document.querySelector<HTMLTextAreaElement>(`[data-ticket-appeal-resolution-reason][data-ticket-id="${cssEscape(ticketId)}"]`)?.value.trim() ?? "";
  if (!status || reason.length < 5) {
    setStatus("Add a clear appeal outcome reason before lifting the strike.");
    return;
  }
  try {
    setStatus(status === "forgiven" ? "Forgiving the appealed strike..." : "Voiding the appealed strike...");
    const result = await apiClient.resolveModerationStrike(strikeId, {
      actorUserId: userId,
      actorDiscordRoleIds: roleIds,
      status,
      reason,
      surface: "website"
    });
    await handleTicketDetail(ticketId);
    setStatus(result.discordNotice?.delivered
      ? `Strike ${status}; the player was notified on the portal and Discord.`
      : `Strike ${status}; the portal record is updated. Discord DM could not be delivered.`);
  } catch (error) {
    setStatus(`Strike resolution failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleTicketTranscript(ticketId: string): Promise<void> {
  const result = document.querySelector("#ticketQueueResult");

  try {
    setStatus("Loading ticket transcript...");
    const detail = await apiClient.getSupportTicketTranscript(ticketId, userId, roleIds);
    if (result) {
      result.innerHTML = renderSupportTicketTranscript(detail.ticket, detail.transcript);
    }
    setStatus("Ticket transcript loaded.");
  } catch (error) {
    setStatus(`Ticket transcript load failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleTicketAccusedAttach(ticketId: string): Promise<void> {
  const lookup = playerPickerValue(document.querySelector<HTMLInputElement>(`[data-ticket-accused-lookup][data-ticket-id="${cssEscape(ticketId)}"]`), "lookup");
  const reason = document.querySelector<HTMLInputElement>(`[data-ticket-accused-reason][data-ticket-id="${cssEscape(ticketId)}"]`)?.value.trim() ?? "";
  const confirmReporterOverride = document.querySelector<HTMLInputElement>(`[data-ticket-reporter-override][data-ticket-id="${cssEscape(ticketId)}"]`)?.checked ?? false;
  if (!lookup) {
    setStatus("Enter the accused player's name or identifier.");
    return;
  }
  if (reason.length < 3) {
    setStatus("Enter a clear reason for attaching this player.");
    return;
  }

  try {
    setStatus("Resolving and attaching accused player...");
    const profile = await apiClient.getProfileByLookup(lookup);
    await apiClient.attachSupportTicketAccused(ticketId, {
      actorUserId: userId,
      actorDiscordRoleIds: roleIds,
      targetUserId: profile.userId,
      reason,
      confirmReporterOverride
    });
    await handleTicketDetail(ticketId);
    setStatus(`${profile.playerData?.name || profile.discord.name} attached to the staff investigation. They were not notified.`);
  } catch (error) {
    setStatus(`Accused player attachment failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleTicketAccusedRemove(ticketId: string, accusedId: string): Promise<void> {
  try {
    setStatus("Removing accused player from investigation...");
    await apiClient.removeSupportTicketAccused(ticketId, accusedId, staffTicketActionPayload("Removed accused player from the staff case workspace."));
    await handleTicketDetail(ticketId);
    setStatus("Accused player removed. They were not notified.");
  } catch (error) {
    setStatus(`Accused player removal failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleTicketLogBundleCreate(ticketId: string): Promise<void> {
  const participantSelect = document.querySelector<HTMLSelectElement>(`[data-ticket-log-participant][data-ticket-id="${cssEscape(ticketId)}"]`);
  const participant = participantSelect?.value ?? "";
  const hours = Number(document.querySelector<HTMLSelectElement>(`[data-ticket-log-window][data-ticket-id="${cssEscape(ticketId)}"]`)?.value ?? "24");
  const reason = document.querySelector<HTMLInputElement>(`[data-ticket-log-reason][data-ticket-id="${cssEscape(ticketId)}"]`)?.value.trim() ?? "";
  const participants = participant === "all"
    ? Array.from(participantSelect?.options ?? [], (option) => option.value).filter((value) => value === "reporter" || value.startsWith("accused:"))
    : [participant];

  if (!participants.length || participants.some((value) => value !== "reporter" && !value.startsWith("accused:"))) {
    setStatus("Choose the reporter or an attached accused player.");
    return;
  }
  if (![1, 6, 24, 72, 168].includes(hours)) {
    setStatus("Choose a valid log time window.");
    return;
  }
  if (reason.length < 3) {
    setStatus("Add a clear reason for capturing these player logs.");
    return;
  }

  let completed = 0;
  try {
    const windowEnd = new Date();
    const windowStart = new Date(windowEnd.getTime() - hours * 60 * 60 * 1000);
    for (const selectedParticipant of participants) {
      const accusedId = selectedParticipant.startsWith("accused:") ? selectedParticipant.slice("accused:".length) : undefined;
      const participantRole = accusedId ? "accused" : "reporter";
      setStatus(`Capturing immutable player log snapshot ${completed + 1} of ${participants.length}...`);
      await apiClient.createSupportTicketLogBundle(ticketId, {
        ...staffTicketActionPayload(reason),
        participantRole,
        ...(accusedId ? { accusedId } : {}),
        windowStart: windowStart.toISOString(),
        windowEnd: windowEnd.toISOString()
      });
      completed += 1;
    }
    await handleTicketDetail(ticketId);
    setStatus(`${completed} player log snapshot${completed === 1 ? "" : "s"} captured and attached to the case.`);
  } catch (error) {
    if (completed > 0) await handleTicketDetail(ticketId);
    const failure = error instanceof Error ? error.message : String(error);
    setStatus(completed > 0
      ? `${completed} of ${participants.length} player snapshots were captured. The remaining capture failed: ${failure}`
      : `Player logs could not be captured: ${failure}`);
  }
}

async function handleTicketLogReview(ticketId: string, bundleId: string): Promise<void> {
  const panel = document.querySelector<HTMLElement>(`[data-ticket-log-review-panel][data-ticket-id="${cssEscape(ticketId)}"][data-bundle-id="${cssEscape(bundleId)}"]`);
  if (!panel) return;
  if (!panel.hidden && panel.dataset.loaded === "true") {
    panel.hidden = true;
    return;
  }
  panel.hidden = false;
  panel.innerHTML = `<div class="support-empty"><strong>Loading verified logs</strong><p>Reading the immutable snapshot...</p></div>`;
  try {
    const count = await loadTicketLogReview(ticketId, bundleId, panel);
    setStatus(`${count} verified log event${count === 1 ? "" : "s"} ready for review.`);
  } catch (error) {
    panel.innerHTML = `<div class="support-empty"><strong>Logs could not be opened</strong><p>${escapePortalText(error instanceof Error ? error.message : String(error))}</p></div>`;
    setStatus(`Log review failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleTicketEvidenceLogReview(ticketId: string, bundleId: string, evidenceId: string): Promise<void> {
  const panel = document.querySelector<HTMLElement>(`[data-ticket-evidence-log-panel][data-ticket-id="${cssEscape(ticketId)}"][data-bundle-id="${cssEscape(bundleId)}"][data-evidence-id="${cssEscape(evidenceId)}"]`);
  if (!panel) return;
  if (!panel.hidden && panel.dataset.loaded === "true") {
    panel.hidden = true;
    return;
  }
  panel.hidden = false;
  panel.innerHTML = `<div class="support-empty"><strong>Opening attached logs</strong><p>Verifying the preserved evidence snapshot...</p></div>`;
  try {
    const detail = await apiClient.getSupportTicketLogBundle(ticketId, bundleId);
    panel.innerHTML = renderSupportTicketEvidenceLogReview(detail.logBundle, detail.events);
    panel.dataset.loaded = "true";
    setStatus(`${detail.events.length} attached log event${detail.events.length === 1 ? "" : "s"} ready to review.`);
  } catch (error) {
    panel.innerHTML = `<div class="support-empty"><strong>Attached logs could not be opened</strong><p>${escapePortalText(error instanceof Error ? error.message : String(error))}</p></div>`;
  }
}

async function handleTicketLogReviewAll(ticketId: string): Promise<void> {
  const panel = document.querySelector<HTMLElement>(`[data-ticket-log-combined-panel][data-ticket-id="${cssEscape(ticketId)}"]`);
  const bundleIds = Array.from(document.querySelectorAll<HTMLElement>(`[data-ticket-log-review-panel][data-ticket-id="${cssEscape(ticketId)}"]`), (item) => item.dataset.bundleId).filter((value): value is string => Boolean(value));
  if (!panel || !bundleIds.length) {
    setStatus("Capture player logs before opening a combined review.");
    return;
  }
  if (!panel.hidden && panel.dataset.loaded === "true") {
    panel.hidden = true;
    return;
  }
  panel.hidden = false;
  panel.innerHTML = `<div class="support-empty"><strong>Building combined timeline</strong><p>Reading the preserved participant snapshots...</p></div>`;
  setStatus(`Opening ${bundleIds.length} participant log snapshot${bundleIds.length === 1 ? "" : "s"}...`);
  try {
    const details = await Promise.all(bundleIds.map((bundleId) => apiClient.getSupportTicketLogBundle(ticketId, bundleId)));
    panel.innerHTML = renderSupportTicketCombinedLogReview(ticketId, details, currentAccess.permissions.includes("tickets.manage"));
    panel.dataset.loaded = "true";
    const total = details.reduce((sum, detail) => sum + detail.events.length, 0);
    setStatus(`${total} verified events opened in one chronological timeline.`);
  } catch (error) {
    panel.innerHTML = `<div class="support-empty"><strong>Combined timeline could not be opened</strong><p>${escapePortalText(error instanceof Error ? error.message : String(error))}</p></div>`;
    setStatus(`Combined log review failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

function applyCombinedTicketLogFilters(ticketId: string): void {
  const panel = document.querySelector<HTMLElement>(`[data-ticket-log-combined-panel][data-ticket-id="${cssEscape(ticketId)}"]`);
  if (!panel) return;
  const eventType = panel.querySelector<HTMLSelectElement>("[data-ticket-log-combined-type-filter]")?.value ?? "all";
  const participant = panel.querySelector<HTMLSelectElement>("[data-ticket-log-combined-participant-filter]")?.value ?? "all";
  let visible = 0;
  panel.querySelectorAll<HTMLElement>("[data-ticket-log-combined-event-row]").forEach((row) => {
    const show = (eventType === "all" || row.dataset.eventType === eventType)
      && (participant === "all" || row.dataset.participantBundleId === participant);
    row.hidden = !show;
    if (show) visible += 1;
    else {
      const input = row.querySelector<HTMLInputElement>("[data-ticket-log-combined-event]");
      if (input) input.checked = false;
    }
  });
  const count = panel.querySelector<HTMLElement>("[data-ticket-log-combined-visible-count]");
  if (count) count.textContent = String(visible);
  updateCombinedTicketLogSelection(ticketId);
}

function selectCombinedTicketLogEvents(ticketId: string, selected: boolean): void {
  const panel = document.querySelector<HTMLElement>(`[data-ticket-log-combined-panel][data-ticket-id="${cssEscape(ticketId)}"]`);
  panel?.querySelectorAll<HTMLInputElement>("[data-ticket-log-combined-event]").forEach((input) => {
    const row = input.closest<HTMLElement>("[data-ticket-log-combined-event-row]");
    input.checked = selected && !row?.hidden;
  });
  updateCombinedTicketLogSelection(ticketId);
}

function updateCombinedTicketLogSelection(ticketId: string): void {
  const panel = document.querySelector<HTMLElement>(`[data-ticket-log-combined-panel][data-ticket-id="${cssEscape(ticketId)}"]`);
  if (!panel) return;
  const selected = panel.querySelectorAll<HTMLInputElement>("[data-ticket-log-combined-event]:checked").length;
  const count = panel.querySelector<HTMLElement>("[data-ticket-log-combined-selected-count]");
  const button = panel.querySelector<HTMLButtonElement>('[data-portal-action="ticket-log-combined-evidence-attach"]');
  if (count) count.textContent = String(selected);
  if (button) button.disabled = selected === 0;
}

async function handleCombinedTicketLogEvidenceAttach(ticketId: string): Promise<void> {
  const panel = document.querySelector<HTMLElement>(`[data-ticket-log-combined-panel][data-ticket-id="${cssEscape(ticketId)}"]`);
  if (!panel) return;
  const selected = Array.from(panel.querySelectorAll<HTMLInputElement>("[data-ticket-log-combined-event]:checked"));
  const label = panel.querySelector<HTMLInputElement>("[data-ticket-log-combined-evidence-label]")?.value.trim() ?? "";
  const reason = panel.querySelector<HTMLInputElement>("[data-ticket-log-combined-evidence-reason]")?.value.trim() ?? "";
  if (!selected.length) {
    setStatus("Select at least one log entry to attach as evidence.");
    return;
  }
  if (label.length < 2 || reason.length < 3) {
    setStatus("Add an evidence label and a clear audit reason.");
    return;
  }
  const byBundle = new globalThis.Map<string, string[]>();
  for (const input of selected) {
    if (!input.dataset.bundleId) continue;
    byBundle.set(input.dataset.bundleId, [...(byBundle.get(input.dataset.bundleId) ?? []), input.value]);
  }
  let completed = 0;
  try {
    for (const [bundleId, eventIds] of byBundle) {
      await apiClient.attachSupportTicketLogEvidence(ticketId, bundleId, {
        ...staffTicketActionPayload(reason),
        label: byBundle.size === 1 ? label : `${label} (${completed + 1}/${byBundle.size})`,
        eventIds
      });
      completed += 1;
    }
    await handleTicketDetail(ticketId);
    setStatus(`${selected.length} selected log event${selected.length === 1 ? "" : "s"} preserved as immutable case evidence.`);
  } catch (error) {
    if (completed) await handleTicketDetail(ticketId);
    setStatus(`${completed} of ${byBundle.size} evidence group${byBundle.size === 1 ? "" : "s"} attached. ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function loadTicketLogReview(ticketId: string, bundleId: string, panel: HTMLElement): Promise<number> {
  try {
    const detail = await apiClient.getSupportTicketLogBundle(ticketId, bundleId);
    panel.innerHTML = renderSupportTicketLogReview(
      ticketId,
      detail.logBundle,
      detail.events,
      currentAccess.permissions.includes("tickets.manage")
    );
    panel.dataset.loaded = "true";
    return detail.events.length;
  } catch (error) {
    panel.innerHTML = `<div class="support-empty"><strong>Logs could not be opened</strong><p>${escapePortalText(error instanceof Error ? error.message : String(error))}</p></div>`;
    throw error;
  }
}

function selectTicketLogEvents(bundleId: string, selected: boolean): void {
  document.querySelectorAll<HTMLInputElement>(`[data-ticket-log-event][data-bundle-id="${cssEscape(bundleId)}"]`).forEach((input) => {
    const row = input.closest<HTMLElement>("[data-ticket-log-event-row]");
    input.checked = selected && !row?.hidden;
  });
  updateTicketLogSelection(bundleId);
}

function applyTicketLogTypeFilter(bundleId: string, eventType: string): void {
  let visible = 0;
  document.querySelectorAll<HTMLElement>(`[data-ticket-log-review-panel][data-bundle-id="${cssEscape(bundleId)}"] [data-ticket-log-event-row]`).forEach((row) => {
    const show = eventType === "all" || row.dataset.eventType === eventType;
    row.hidden = !show;
    if (show) visible += 1;
    else {
      const input = row.querySelector<HTMLInputElement>("[data-ticket-log-event]");
      if (input) input.checked = false;
    }
  });
  const count = document.querySelector<HTMLElement>(`[data-ticket-log-visible-count][data-bundle-id="${cssEscape(bundleId)}"]`);
  if (count) count.textContent = String(visible);
  updateTicketLogSelection(bundleId);
}

function updateTicketLogSelection(bundleId: string): void {
  const selected = document.querySelectorAll<HTMLInputElement>(`[data-ticket-log-event][data-bundle-id="${cssEscape(bundleId)}"]:checked`).length;
  const count = document.querySelector<HTMLElement>(`[data-ticket-log-selected-count][data-bundle-id="${cssEscape(bundleId)}"]`);
  const button = document.querySelector<HTMLButtonElement>(`[data-portal-action="ticket-log-evidence-attach"][data-bundle-id="${cssEscape(bundleId)}"]`);
  if (count) count.textContent = String(selected);
  if (button) button.disabled = selected === 0;
}

async function handleTicketLogEvidenceAttach(ticketId: string, bundleId: string): Promise<void> {
  const eventIds = Array.from(
    document.querySelectorAll<HTMLInputElement>(`[data-ticket-log-event][data-bundle-id="${cssEscape(bundleId)}"]:checked`),
    (input) => input.value
  );
  const label = document.querySelector<HTMLInputElement>(`[data-ticket-log-evidence-label][data-bundle-id="${cssEscape(bundleId)}"]`)?.value.trim() ?? "";
  const reason = document.querySelector<HTMLInputElement>(`[data-ticket-log-evidence-reason][data-bundle-id="${cssEscape(bundleId)}"]`)?.value.trim() ?? "";
  if (!eventIds.length) {
    setStatus("Select at least one log entry to attach as evidence.");
    return;
  }
  if (label.length < 2 || reason.length < 3) {
    setStatus("Add an evidence label and a clear audit reason.");
    return;
  }
  try {
    setStatus(`Attaching ${eventIds.length} selected log event${eventIds.length === 1 ? "" : "s"} as evidence...`);
    await apiClient.attachSupportTicketLogEvidence(ticketId, bundleId, {
      ...staffTicketActionPayload(reason),
      label,
      eventIds
    });
    await handleTicketDetail(ticketId);
    setStatus("Selected logs were preserved as immutable case evidence.");
  } catch (error) {
    setStatus(`Selected logs could not be attached: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleTicketEvidenceAdd(ticketId: string, staff: boolean): Promise<void> {
  const prefix = staff ? "ticket-evidence" : "player-ticket-evidence";
  const label = document.querySelector<HTMLInputElement>(`[data-${prefix}-label][data-ticket-id="${cssEscape(ticketId)}"]`)?.value.trim() ?? "";
  const url = document.querySelector<HTMLInputElement>(`[data-${prefix}-url][data-ticket-id="${cssEscape(ticketId)}"]`)?.value.trim() ?? "";
  if (label.length < 2) {
    setStatus("Add a short label for the evidence.");
    return;
  }
  if (!url.startsWith("https://")) {
    setStatus("Evidence links must be complete HTTPS URLs.");
    return;
  }
  try {
    setStatus("Adding evidence link...");
    await apiClient.addSupportTicketEvidence(ticketId, {
      authorUserId: userId,
      actorDiscordRoleIds: staff ? roleIds : [],
      label,
      url
    });
    if (staff) await handleTicketDetail(ticketId);
    else await handlePlayerTicketDetail(ticketId);
    setStatus("Evidence link added and recorded.");
  } catch (error) {
    setStatus(`Evidence could not be added: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleTicketEvidenceUpload(ticketId: string, staff: boolean): Promise<void> {
  const prefix = staff ? "ticket-evidence" : "player-ticket-evidence";
  const label = document.querySelector<HTMLInputElement>(`[data-${prefix}-upload-label][data-ticket-id="${cssEscape(ticketId)}"]`)?.value.trim() ?? "";
  const input = document.querySelector<HTMLInputElement>(`[data-${prefix}-file][data-ticket-id="${cssEscape(ticketId)}"]`);
  const files = Array.from(input?.files ?? []);
  if (label.length < 2) { setStatus("Add a short label for the evidence file."); return; }
  if (!files.length) { setStatus("Choose an evidence file to upload."); return; }
  if (files.length > 5) { setStatus("Upload no more than 5 evidence files at a time."); return; }
  if (!staff) {
    const unsupported = files.find((file) => !fileExtensionAllowed(file.name, playerTicketCapabilities.evidenceUploads.extensions));
    if (unsupported) {
      setStatus(`${unsupported.name} is not a supported photo or video. Use PNG, JPG, WEBP, MP4, MOV, or WEBM.`);
      return;
    }
  }
  try {
    setStatus(`Scanning and storing ${files.length} evidence file${files.length === 1 ? "" : "s"} securely...`);
    for (const file of files) {
      const fileLabel = files.length === 1 ? label : `${label} - ${file.name}`.slice(0, 120);
      await apiClient.uploadSupportTicketEvidence(ticketId, fileLabel, file);
    }
    if (staff) await handleTicketDetail(ticketId); else await handlePlayerTicketDetail(ticketId);
    setStatus(`${files.length} evidence file${files.length === 1 ? "" : "s"} passed security scanning and ${files.length === 1 ? "was" : "were"} stored privately.`);
  } catch (error) {
    setStatus(`Evidence could not be uploaded: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleTicketEvidenceReview(ticketId: string, evidenceId: string, status: string): Promise<void> {
  if (!(["reviewed", "relevant", "inconclusive", "rejected"] as string[]).includes(status)) {
    setStatus("Choose a valid evidence review state.");
    return;
  }
  const reason = document.querySelector<HTMLInputElement>(`[data-ticket-evidence-reason][data-ticket-id="${cssEscape(ticketId)}"][data-evidence-id="${cssEscape(evidenceId)}"]`)?.value.trim() ?? "";
  if ((status === "inconclusive" || status === "rejected") && reason.length < 3) {
    setStatus("Inconclusive or rejected evidence requires a short review reason.");
    return;
  }
  try {
    setStatus("Recording evidence review...");
    await apiClient.reviewSupportTicketEvidence(ticketId, evidenceId, {
      actorUserId: userId,
      actorDiscordRoleIds: roleIds,
      status: status as "reviewed" | "relevant" | "inconclusive" | "rejected",
      ...(reason ? { reason } : {})
    });
    await handleTicketDetail(ticketId);
    setStatus(`Evidence marked ${status}.`);
  } catch (error) {
    setStatus(`Evidence review failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleTicketWatcherAdd(ticketId: string): Promise<void> {
  const watcherUserId = playerPickerValue(document.querySelector<HTMLInputElement>(`[data-ticket-watcher-user][data-ticket-id="${cssEscape(ticketId)}"]`), "user");
  const reason = document.querySelector<HTMLInputElement>(`[data-ticket-watcher-reason][data-ticket-id="${cssEscape(ticketId)}"]`)?.value.trim() ?? "";
  if (!watcherUserId.startsWith("usr_") || !reason) {
    setStatus("Add a valid staff user ID and audit reason.");
    return;
  }
  try {
    setStatus("Adding case watcher...");
    await apiClient.addSupportTicketWatcher(ticketId, { ...staffTicketActionPayload(reason), watcherUserId });
    await handleTicketDetail(ticketId);
    setStatus("Case watcher added.");
  } catch (error) {
    setStatus(`Watcher add failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleTicketWatcherRemove(ticketId: string, watcherUserId: string): Promise<void> {
  try {
    setStatus("Removing case watcher...");
    await apiClient.removeSupportTicketWatcher(ticketId, {
      ...staffTicketActionPayload("Removed case watcher from portal."),
      watcherUserId
    });
    await handleTicketDetail(ticketId);
    setStatus("Case watcher removed.");
  } catch (error) {
    setStatus(`Watcher removal failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleTicketRuleAttach(ticketId: string): Promise<void> {
  const ruleId = document.querySelector<HTMLSelectElement>(`[data-ticket-rule-definition][data-ticket-id="${cssEscape(ticketId)}"]`)?.value ?? "";
  const reason = document.querySelector<HTMLInputElement>(`[data-ticket-rule-attach-reason][data-ticket-id="${cssEscape(ticketId)}"]`)?.value.trim() ?? "";
  if (!ruleId || reason.length < 3) {
    setStatus("Choose a published rule and record why it may apply.");
    return;
  }
  try {
    setStatus("Attaching rule snapshot...");
    await apiClient.attachSupportTicketRule(ticketId, { ...staffTicketActionPayload(reason), ruleId });
    await handleTicketDetail(ticketId);
    setStatus("Rule attached to the case history.");
  } catch (error) {
    setStatus(`Rule could not be attached: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleTicketRuleReview(ticketId: string, ticketRuleId: string, outcome: string): Promise<void> {
  if (!(["upheld", "not_upheld", "inconclusive"] as string[]).includes(outcome)) return;
  const reviewReason = document.querySelector<HTMLInputElement>(`[data-ticket-rule-reason][data-ticket-id="${cssEscape(ticketId)}"][data-ticket-rule-id="${cssEscape(ticketRuleId)}"]`)?.value.trim() ?? "";
  if ((outcome === "not_upheld" || outcome === "inconclusive") && reviewReason.length < 3) {
    setStatus("Not upheld and inconclusive outcomes require a short reason.");
    return;
  }
  try {
    setStatus("Recording rule outcome...");
    await apiClient.reviewSupportTicketRule(ticketId, ticketRuleId, {
      ...staffTicketActionPayload(`Rule outcome recorded as ${outcome}.`),
      outcome: outcome as "upheld" | "not_upheld" | "inconclusive",
      ...(reviewReason ? { reviewReason } : {})
    });
    await handleTicketDetail(ticketId);
    setStatus(`Rule marked ${outcome.replaceAll("_", " ")}.`);
  } catch (error) {
    setStatus(`Rule outcome failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

let pendingTicketSubmission: { fingerprint: string; key: string } | undefined;

async function handlePlayerTicketCapabilities(): Promise<void> {
  try {
    playerTicketCapabilities = await apiClient.getSupportTicketCapabilities();
  } catch {
    playerTicketCapabilities = {
      evidenceUploads: { enabled: false, maxBytes: 0, maxFilesPerSubmission: 5, extensions: [] }
    };
  }
  updatePlayerTicketUploadControls();
}

function updatePlayerTicketUploadControls(): void {
  const input = document.querySelector<HTMLInputElement>("[data-player-ticket-files]");
  const dropzone = document.querySelector<HTMLElement>("[data-player-upload-dropzone]");
  const title = document.querySelector<HTMLElement>("[data-player-upload-title]");
  const state = document.querySelector<HTMLElement>("[data-player-upload-state]");
  const capability = playerTicketCapabilities.evidenceUploads;
  if (input) input.disabled = !capability.enabled;
  dropzone?.classList.remove("is-checking");
  dropzone?.classList.toggle("is-disabled", !capability.enabled);
  const videosEnabled = capability.extensions.some((extension) => ["mp4", "mov", "webm"].includes(extension));
  if (input) input.accept = videosEnabled
    ? "image/png,image/jpeg,image/webp,video/mp4,video/quicktime,video/webm"
    : "image/png,image/jpeg,image/webp";
  if (title) title.textContent = capability.enabled
    ? (videosEnabled ? "Choose photos or video clips" : "Choose photos")
    : "Direct uploads are currently unavailable";
  if (state) {
    state.textContent = capability.enabled
      ? `Up to ${capability.maxFilesPerSubmission} files, ${formatFileSize(capability.maxBytes)} each. ${videosEnabled ? "Files are type-checked and malware-scanned" : "Photos are decoded, stripped of metadata, and safely re-encoded"} before private storage.`
      : "You can still add an HTTPS link now, or attach files after staff enable secure storage.";
  }
}

async function loadPlayerModerationStrikes(force = false): Promise<void> {
  const profileOutput = document.querySelector<HTMLElement>("[data-player-moderation-history]");
  const appealSelect = document.querySelector<HTMLSelectElement>("[data-player-appeal-strike]");
  if (!force && playerModerationStrikes) {
    renderPlayerModerationSurfaces(playerModerationStrikes, profileOutput, appealSelect);
    return;
  }
  try {
    const strikes = await apiClient.listMyModerationStrikes(userId);
    playerModerationStrikes = strikes;
    renderPlayerModerationSurfaces(strikes, profileOutput, appealSelect);
  } catch (error) {
    if (profileOutput) profileOutput.innerHTML = `<p class="form-help is-error">Moderation history could not be loaded. Please try again.</p>`;
    if (appealSelect) {
      appealSelect.replaceChildren(new Option("Moderation history unavailable", ""));
      appealSelect.disabled = true;
    }
    if (force) setStatus(`Moderation history failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

function renderPlayerModerationSurfaces(
  strikes: PlayerModerationStrikeSummary[],
  profileOutput = document.querySelector<HTMLElement>("[data-player-moderation-history]"),
  appealSelect = document.querySelector<HTMLSelectElement>("[data-player-appeal-strike]")
): void {
  if (profileOutput) profileOutput.innerHTML = renderPlayerModerationHistory(strikes);
  if (!appealSelect) return;
  const appealable = strikes.filter((strike) => strike.status === "active" || strike.status === "expired");
  appealSelect.replaceChildren(new Option(appealable.length ? "Punishment is not listed" : "No linked warning or strike found", ""));
  for (const strike of appealable) {
    const when = new Date(strike.createdAt).toLocaleDateString("en-GB");
    appealSelect.add(new Option(`${strike.status === "active" ? "Active" : "Expired"} - ${strike.reason} (${when})`, strike.id));
  }
  appealSelect.disabled = appealable.length === 0;
  const requestedStrike = searchParams.get("strike");
  if (requestedStrike && appealable.some((strike) => strike.id === requestedStrike)) appealSelect.value = requestedStrike;
}

function updateSupportTicketForm(): void {
  const type = document.querySelector<HTMLSelectElement>("[data-player-ticket-type]")?.value;
  const config = playerTicketFormConfig(type);
  const target = document.querySelector<HTMLElement>("[data-player-report-target]");
  const input = target?.querySelector<HTMLInputElement>("[data-player-ticket-target]");
  const label = target?.querySelector<HTMLElement>("[data-player-report-target-label]");
  const help = target?.querySelector<HTMLElement>("[data-player-report-target-help]");
  const subjectLabel = document.querySelector<HTMLElement>("[data-player-ticket-subject-label]");
  const subjectInput = document.querySelector<HTMLInputElement>("[data-player-ticket-subject]");
  const bodyLabel = document.querySelector<HTMLElement>("[data-player-ticket-body-label]");
  const bodyInput = document.querySelector<HTMLTextAreaElement>("[data-player-ticket-body]");
  const bodyHelp = document.querySelector<HTMLElement>("[data-player-ticket-body-help]");
  const guidance = document.querySelector<HTMLElement>("[data-player-ticket-guidance]");
  const evidenceHeading = document.querySelector<HTMLElement>("[data-player-ticket-evidence-heading]");
  const evidenceSection = document.querySelector<HTMLElement>("[data-player-ticket-evidence-section]");
  const submitStep = document.querySelector<HTMLElement>("[data-player-ticket-submit-step]");
  const submitButton = document.querySelector<HTMLButtonElement>('[data-portal-action="player-ticket-create"]');
  const appealSection = document.querySelector<HTMLElement>("[data-player-appeal-strike-section]");
  const appealSelect = document.querySelector<HTMLSelectElement>("[data-player-appeal-strike]");
  if (guidance) guidance.textContent = config.guidance;
  if (subjectLabel) subjectLabel.textContent = config.subjectLabel;
  if (subjectInput) subjectInput.placeholder = config.subjectPlaceholder;
  if (bodyLabel) bodyLabel.textContent = config.bodyLabel;
  if (bodyInput) bodyInput.placeholder = config.bodyPlaceholder;
  if (bodyHelp) bodyHelp.textContent = config.bodyHelp;
  if (target) target.hidden = !config.showTarget;
  if (input) input.required = config.targetRequired;
  if (label) label.textContent = type === "player_report" ? "Reported player (optional)" : "Staff member (optional)";
  if (help) {
    help.textContent = type === "player_report"
      ? "If you do not know their name, leave this blank. Staff can identify and attach the player during the investigation."
      : "Optional. If the staff member is not listed, identify them in the description. Only authorised owners can see staff reports.";
  }
  if (evidenceHeading) evidenceHeading.hidden = !config.showEvidence;
  if (evidenceSection) evidenceSection.hidden = !config.showEvidence;
  if (submitStep) submitStep.textContent = config.showEvidence ? "3" : "2";
  if (submitButton) submitButton.dataset.confirmMessage = config.confirmation;
  if (appealSection) appealSection.hidden = type !== "ban_appeal";
  if (appealSelect) appealSelect.required = false;
  if (type === "ban_appeal") void loadPlayerModerationStrikes();
  if (!config.showTarget) {
    if (input) {
      input.value = "";
      input.dataset.selectedUserId = "";
      input.dataset.selectedSteam64Id = "";
      input.dataset.selectedDiscordUserId = "";
    }
  }
  if (!config.showEvidence) {
    const evidenceUrl = document.querySelector<HTMLInputElement>("[data-player-ticket-evidence]");
    const fileInput = document.querySelector<HTMLInputElement>("[data-player-ticket-files]");
    const fileList = document.querySelector<HTMLElement>("[data-player-ticket-file-list]");
    if (evidenceUrl) evidenceUrl.value = "";
    if (fileInput) fileInput.value = "";
    if (fileList) { fileList.replaceChildren(); fileList.hidden = true; }
  }
}

function renderPlayerTicketFileSelection(input: HTMLInputElement): void {
  const output = document.querySelector<HTMLElement>("[data-player-ticket-file-list]");
  if (!output) return;
  const files = Array.from(input.files ?? []);
  output.replaceChildren();
  output.hidden = files.length === 0;
  const capability = playerTicketCapabilities.evidenceUploads;
  for (const file of files) {
    const row = document.createElement("span");
    row.textContent = `${file.name} (${formatFileSize(file.size)})`;
    if (capability.maxBytes > 0 && file.size > capability.maxBytes) row.className = "is-invalid";
    output.append(row);
  }
  if (files.length > capability.maxFilesPerSubmission) {
    const warning = document.createElement("strong");
    warning.textContent = `Choose no more than ${capability.maxFilesPerSubmission} files.`;
    warning.className = "is-invalid";
    output.append(warning);
  }
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} bytes`;
}

function setPlayerSupportDetailMode(active: boolean): void {
  document.querySelector<HTMLElement>(".support-workspace")?.classList.toggle("is-ticket-open", active);
}

async function handlePlayerTicketCreate(): Promise<void> {
  const type = document.querySelector<HTMLSelectElement>("[data-player-ticket-type]")?.value as PlayerTicketType | undefined;
  const subject = document.querySelector<HTMLInputElement>("[data-player-ticket-subject]")?.value.trim() ?? "";
  const body = document.querySelector<HTMLTextAreaElement>("[data-player-ticket-body]")?.value.trim() ?? "";
  const targetSteam64Id = playerPickerValue(document.querySelector<HTMLInputElement>("[data-player-ticket-target]"), "steam64");
  const appealedStrikeId = document.querySelector<HTMLSelectElement>("[data-player-appeal-strike]")?.value ?? "";
  const evidenceUrl = document.querySelector<HTMLInputElement>("[data-player-ticket-evidence]")?.value.trim() ?? "";
  const fileInput = document.querySelector<HTMLInputElement>("[data-player-ticket-files]");
  const files = Array.from(fileInput?.files ?? []);
  const config = playerTicketFormConfig(type);
  if (!type || subject.length < 3 || body.length < config.bodyMinLength) {
    setStatus(`Complete "${config.subjectLabel}" and "${config.bodyLabel}" before submitting.`);
    return;
  }
  if (evidenceUrl) {
    try {
      const parsed = new URL(evidenceUrl);
      if (parsed.protocol !== "https:") throw new Error("Evidence links must use HTTPS.");
    } catch {
      setStatus("Enter a complete evidence URL, including https://.");
      return;
    }
  }
  const capability = playerTicketCapabilities.evidenceUploads;
  if (files.length && !capability.enabled) {
    setStatus("Direct evidence uploads are currently unavailable. Add an HTTPS link or submit the ticket without a file.");
    return;
  }
  if (files.length > capability.maxFilesPerSubmission) {
    setStatus(`Choose no more than ${capability.maxFilesPerSubmission} evidence files.`);
    return;
  }
  const oversized = files.find((file) => capability.maxBytes > 0 && file.size > capability.maxBytes);
  if (oversized) {
    setStatus(`${oversized.name} is larger than the ${formatFileSize(capability.maxBytes)} evidence limit.`);
    return;
  }
  const unsupported = files.find((file) => !fileExtensionAllowed(file.name, capability.extensions));
  if (unsupported) {
    setStatus(`${unsupported.name} is not a supported photo or video. Use PNG, JPG, WEBP, MP4, MOV, or WEBM.`);
    return;
  }

  try {
    setStatus("Submitting support ticket...");
    const fingerprint = JSON.stringify({ type, subject, body, targetSteam64Id, appealedStrikeId, evidenceUrl, files: files.map((file) => [file.name, file.size, file.lastModified]) });
    if (!pendingTicketSubmission || pendingTicketSubmission.fingerprint !== fingerprint) {
      pendingTicketSubmission = { fingerprint, key: `portal:${crypto.randomUUID()}` };
    }
    const ticket = await apiClient.createSupportTicket({
      type,
      creatorUserId: userId,
      subject,
      body,
      submissionKey: pendingTicketSubmission.key,
      ...((type === "player_report" || type === "staff_report") && /^7656\d{13}$/.test(targetSteam64Id) ? { targetSteam64Id } : {}),
      ...(type === "ban_appeal" && appealedStrikeId ? { appealedStrikeId } : {}),
      ...(evidenceUrl ? { evidenceUrl } : {})
    });
    pendingTicketSubmission = undefined;
    let uploaded = 0;
    let uploadError: string | undefined;
    for (const file of files) {
      try {
        setStatus(`Ticket created. Scanning evidence ${uploaded + 1} of ${files.length}...`);
        await apiClient.uploadSupportTicketEvidence(ticket.id, `Initial evidence - ${file.name}`.slice(0, 120), file);
        uploaded += 1;
      } catch (error) {
        uploadError = error instanceof Error ? error.message : String(error);
        break;
      }
    }
    document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("[data-player-ticket-subject], [data-player-ticket-body], [data-player-ticket-target], [data-player-ticket-evidence]").forEach((input) => { input.value = ""; });
    if (fileInput) fileInput.value = "";
    const fileList = document.querySelector<HTMLElement>("[data-player-ticket-file-list]");
    if (fileList) { fileList.replaceChildren(); fileList.hidden = true; }
    await handlePlayerTicketDetail(ticket.id);
    if (uploadError) {
      setStatus(`Ticket submitted, but evidence upload stopped after ${uploaded} of ${files.length} files: ${uploadError}. You can retry from the ticket.`);
    } else {
      setStatus(`Ticket submitted${uploaded ? ` with ${uploaded} private evidence file${uploaded === 1 ? "" : "s"}` : ""}. Staff can now review it.`);
    }
  } catch (error) {
    setStatus(`Ticket submission failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

function fileExtensionAllowed(fileName: string, extensions: string[]): boolean {
  const extension = fileName.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1];
  return Boolean(extension && extensions.includes(extension));
}

async function handlePlayerTicketList(): Promise<void> {
  const result = document.querySelector("#playerTicketResult");
  if (!result) return;
  setPlayerSupportDetailMode(false);
  currentPlayerTicketId = undefined;
  if (portalPageFromPath(window.location.pathname) === "support") {
    const url = new URL(window.location.href);
    url.searchParams.delete("ticket");
    window.history.replaceState({}, "", `${url.pathname}${url.search}`);
  }
  try {
    const tickets = await apiClient.listMySupportTickets(userId, 25);
    result.innerHTML = renderPlayerTicketList(tickets);
  } catch (error) {
    result.textContent = "Your tickets could not be loaded right now.";
    setStatus(`Ticket list failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handlePlayerTicketDetail(ticketId: string, background = false): Promise<void> {
  const result = document.querySelector("#playerTicketResult");
  if (!result) return;
  try {
    const detail = await apiClient.getSupportTicket(ticketId, userId, []);
    const scrollTop = window.scrollY;
    result.innerHTML = renderPlayerTicketDetail(detail.ticket, detail.messages, detail.evidence ?? [], detail.evidenceUploadsEnabled, detail.appealedStrike);
    setPlayerSupportDetailMode(true);
    currentPlayerTicketId = ticketId;
    const url = new URL(window.location.href);
    url.searchParams.set("ticket", ticketId);
    window.history.replaceState({}, "", `${url.pathname}${url.search}`);
    if (background) window.scrollTo({ top: scrollTop, behavior: "auto" });
  } catch (error) {
    if (!background) setStatus(`Ticket detail failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handlePlayerTicketReply(ticketId: string): Promise<void> {
  const body = document.querySelector<HTMLTextAreaElement>(`[data-player-ticket-reply][data-ticket-id="${cssEscape(ticketId)}"]`)?.value.trim() ?? "";
  if (body.length < 2) {
    setStatus("Write a reply before sending it.");
    return;
  }
  try {
    const reply = await apiClient.replyToSupportTicket(ticketId, { authorUserId: userId, actorDiscordRoleIds: [], body, internal: false });
    appendPlayerTicketConversationMessage(reply.ticket, reply.message);
    const textarea = document.querySelector<HTMLTextAreaElement>(`[data-player-ticket-reply][data-ticket-id="${cssEscape(ticketId)}"]`);
    if (textarea) textarea.value = "";
    setStatus("Reply sent to staff.");
  } catch (error) {
    setStatus(`Ticket reply failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handlePlayerTicketClose(ticketId: string): Promise<void> {
  try {
    await apiClient.closeSupportTicket(ticketId, { actorUserId: userId, actorDiscordRoleIds: [], reason: "Closed by reporter from the player portal." });
    await handlePlayerTicketDetail(ticketId);
    setStatus("Ticket closed and archived.");
  } catch (error) {
    setStatus(`Ticket close failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleWeatherUpdate(): Promise<void> {
  const form = readWeatherForm();
  if (!form.reason) {
    setStatus("Enter an audit reason before updating weather.");
    return;
  }

  try {
    setStatus("Updating weather panel...");
    await apiClient.updateWeather(currentSnapshot.server.id, {
      condition: form.condition,
      intensity: form.intensity,
      ...(typeof form.temperatureC === "number" ? { temperatureC: form.temperatureC } : {}),
      ...(typeof form.windKph === "number" ? { windKph: form.windKph } : {}),
      ...(form.forecastNote ? { forecastNote: form.forecastNote } : {}),
      actorUserId: userId,
      actorDiscordRoleIds: roleIds,
      reason: form.reason
    });
    await boot();
    setStatus("Weather panel updated and audited.");
  } catch (error) {
    setStatus(`Weather update failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

function readWeatherForm(): {
  condition: string;
  intensity: number;
  temperatureC?: number;
  windKph?: number;
  forecastNote?: string;
  reason: string;
} {
  const condition = document.querySelector<HTMLSelectElement>("[data-weather-condition]")?.value ?? "unknown";
  const intensity = boundedNumber(document.querySelector<HTMLInputElement>("[data-weather-intensity]")?.value, 0, 100, 0);
  const temperatureC = optionalNumber(document.querySelector<HTMLInputElement>("[data-weather-temperature]")?.value);
  const windKph = optionalNumber(document.querySelector<HTMLInputElement>("[data-weather-wind]")?.value);
  const forecastNote = document.querySelector<HTMLInputElement>("[data-weather-note]")?.value.trim() ?? "";
  const reason = document.querySelector<HTMLInputElement>("[data-weather-reason]")?.value.trim() ?? "";

  return {
    condition,
    intensity,
    ...(typeof temperatureC === "number" ? { temperatureC } : {}),
    ...(typeof windKph === "number" ? { windKph } : {}),
    ...(forecastNote ? { forecastNote } : {}),
    reason
  };
}

function optionalNumber(value: string | undefined): number | undefined {
  if (!value?.trim()) {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function boundedNumber(value: string | undefined, min: number, max: number, fallback: number): number {
  const parsed = optionalNumber(value);
  if (typeof parsed !== "number") {
    return fallback;
  }

  return Math.min(max, Math.max(min, parsed));
}

async function handleProfileLookup(): Promise<void> {
  const input = document.querySelector<HTMLInputElement>("[data-profile-lookup], [data-profile-discord-id]");
  const result = document.querySelector("#profileLookupResult");
  const lookup = input?.value.trim();

  if (!lookup) {
    setStatus("Enter a player name, Discord name, Steam64, or Discord ID to look up.");
    return;
  }

  try {
    setStatus("Looking up player profile...");
    const profile = await apiClient.getProfileByLookup(lookup);
    const [cases, strikeHistory] = await Promise.all([
      apiClient.listModerationCases(profile.userId, roleIds),
      apiClient.listModerationStrikes(profile.userId, roleIds)
    ]);
    const ledger = currentAccess.permissions.includes("economy.view")
      ? await apiClient.listLedgerEntries(profile.userId, roleIds, 25)
      : undefined;
    const garageHistory = currentAccess.permissions.includes("garage.manage") || currentAccess.permissions.includes("garage.override")
      ? await apiClient.getGarageHistory(profile.userId)
      : undefined;
    const canCorrectMutations = currentAccess.permissions.includes("garage.mutation.correct");
    if (result) {
      result.innerHTML = renderStaffPlayerWorkspace({
        profile,
        cases,
        strikes: strikeHistory.strikes,
        roleActions: strikeHistory.roleActions,
        ...(ledger ? { ledger } : {}),
        ...(garageHistory ? { garageHistory } : {}),
        currency: currentSnapshot.currency,
        canCreateModeration: currentAccess.permissions.includes("moderation.create"),
        canCorrectMutations,
        canGrantPoints: currentAccess.permissions.includes("economy.grant"),
        canResetCooldown: currentAccess.permissions.includes("moderation.create"),
        canRestoreGarage: currentAccess.permissions.includes("garage.override")
      });
      const search = document.querySelector<HTMLElement>("[data-staff-player-search]");
      if (search) search.hidden = true;
      hydratePortalIcons();
    }
    setStatus("Player profile loaded.");
  } catch (error) {
    if (result) {
      result.textContent = "No profile loaded.";
    }
    setStatus(`Profile lookup failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function updateProfileSuggestions(query: string): Promise<void> {
  const container = document.querySelector<HTMLElement>("#profileSearchSuggestions");
  const input = document.querySelector<HTMLInputElement>("[data-profile-lookup]");
  if (!container || !input || input.value.trim() !== query) return;
  try {
    const matches = await apiClient.searchProfiles(query);
    if (input.value.trim() !== query) return;
    if (!matches.length) {
      container.innerHTML = `<p>No matching players</p>`;
    } else {
      container.innerHTML = matches.map((match) => `
        <button type="button" role="option" data-profile-suggestion data-user-id="${escapePortalText(match.userId)}">
          <strong>${escapePortalText(match.name)}</strong>
          <small>${escapePortalText([
            match.discordName ? `Discord: ${match.discordName}` : "Discord not linked",
            match.steam64Id ? `Steam: ${match.steam64Id}` : "Steam not linked"
          ].join(" · "))}</small>
        </button>`).join("");
    }
    container.hidden = false;
    input.setAttribute("aria-expanded", "true");
  } catch {
    hideProfileSuggestions();
  }
}

function hideProfileSuggestions(): void {
  const container = document.querySelector<HTMLElement>("#profileSearchSuggestions");
  if (container) container.hidden = true;
  document.querySelector<HTMLInputElement>("[data-profile-lookup]")?.setAttribute("aria-expanded", "false");
}

async function updatePlayerPickerSuggestions(input: HTMLInputElement, query: string): Promise<void> {
  const container = input.closest<HTMLElement>(".player-picker")?.querySelector<HTMLElement>("[data-player-picker-results]");
  if (!container || input.value.trim() !== query) return;
  connectPlayerPickerList(input, container);
  try {
    const matches = await apiClient.searchProfiles(query);
    if (input.value.trim() !== query) return;
    container.innerHTML = matches.length
      ? matches.map((match) => `<button type="button" role="option" data-player-picker-suggestion data-user-id="${escapePortalText(match.userId)}" data-steam64-id="${escapePortalText(match.steam64Id ?? "")}" data-discord-user-id="${escapePortalText(match.discordUserId ?? "")}" data-player-name="${escapePortalText(match.name)}"><strong>${escapePortalText(match.name)}</strong><small>${escapePortalText([
          match.discordName ? `Discord: ${match.discordName}` : "Discord not linked",
          match.steam64Id ? `Steam: ${match.steam64Id}` : "Steam not linked"
        ].join(" · "))}</small></button>`).join("")
      : `<p>No matching players</p>`;
    container.hidden = false;
    input.setAttribute("aria-expanded", "true");
  } catch {
    hidePlayerPickerSuggestions(input);
  }
}

function connectPlayerPickerList(input: HTMLInputElement, container: HTMLElement): void {
  if (!container.id) container.id = `playerPickerList${++playerPickerListSequence}`;
  input.setAttribute("aria-autocomplete", "list");
  input.setAttribute("aria-controls", container.id);
  input.setAttribute("aria-expanded", String(!container.hidden));
}

function hidePlayerPickerSuggestions(input?: HTMLInputElement): void {
  if (input) {
    const container = input.closest<HTMLElement>(".player-picker")?.querySelector<HTMLElement>("[data-player-picker-results]");
    if (container) container.hidden = true;
    input.setAttribute("aria-expanded", "false");
    return;
  }
  document.querySelectorAll<HTMLElement>("[data-player-picker-results]").forEach((container) => {
    container.hidden = true;
    container.closest<HTMLElement>(".player-picker")?.querySelector<HTMLInputElement>("[data-player-picker]")?.setAttribute("aria-expanded", "false");
  });
}

function handlePlayerSearchKeyboard(event: KeyboardEvent): boolean {
  const target = event.target instanceof HTMLElement ? event.target : undefined;
  if (!target) return false;
  const input = target.closest<HTMLInputElement>("[data-profile-lookup], [data-player-picker]");
  if (input) {
    if (event.key === "Escape") {
      input.matches("[data-profile-lookup]") ? hideProfileSuggestions() : hidePlayerPickerSuggestions(input);
      return true;
    }
    if (event.key !== "ArrowDown") return false;
    const list = searchSuggestionList(input);
    const first = list && !list.hidden ? list.querySelector<HTMLButtonElement>("[role='option']") : null;
    if (!first) return false;
    event.preventDefault();
    first.focus();
    return true;
  }

  const option = target.closest<HTMLButtonElement>("[data-profile-suggestion], [data-player-picker-suggestion]");
  if (!option) return false;
  const list = option.closest<HTMLElement>("[role='listbox']");
  const options = list ? Array.from(list.querySelectorAll<HTMLButtonElement>("[role='option']")) : [];
  const index = options.indexOf(option);
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    option.click();
    return true;
  }
  if (event.key === "Escape") {
    event.preventDefault();
    const ownerInput = list?.closest<HTMLElement>(".player-picker")?.querySelector<HTMLInputElement>("[data-player-picker]")
      ?? document.querySelector<HTMLInputElement>("[data-profile-lookup]");
    ownerInput?.focus();
    ownerInput?.matches("[data-profile-lookup]") ? hideProfileSuggestions() : hidePlayerPickerSuggestions(ownerInput ?? undefined);
    return true;
  }
  if (!list || !["ArrowDown", "ArrowUp"].includes(event.key) || index < 0) return false;
  event.preventDefault();
  const nextIndex = event.key === "ArrowDown" ? Math.min(options.length - 1, index + 1) : Math.max(0, index - 1);
  options[nextIndex]?.focus();
  return true;
}

function searchSuggestionList(input: HTMLInputElement): HTMLElement | null {
  return input.matches("[data-profile-lookup]")
    ? document.querySelector<HTMLElement>("#profileSearchSuggestions")
    : input.closest<HTMLElement>(".player-picker")?.querySelector<HTMLElement>("[data-player-picker-results]") ?? null;
}

function playerPickerValue(input: HTMLInputElement | null, mode: "user" | "steam64" | "lookup"): string {
  if (!input) return "";
  const raw = input.value.trim();
  if (mode === "user") return input.dataset.selectedUserId || (raw.startsWith("usr_") ? raw : "");
  if (mode === "steam64") return input.dataset.selectedSteam64Id || (/^7656\d{13}$/.test(raw) ? raw : "");
  return input.dataset.selectedUserId || input.dataset.selectedSteam64Id || raw;
}

async function handleCorpseCooldownReset(targetUserId: string, serverId: string): Promise<void> {
  const reason = document.querySelector<HTMLInputElement>("[data-corpse-reset-reason]")?.value.trim() ?? "";
  if (reason.length < 3) {
    setStatus("Enter a reason or ticket reference before resetting the food cooldown.");
    return;
  }
  try {
    setStatus("Resetting food-request cooldown...");
    await apiClient.resetCorpseCooldown({ targetUserId, actorUserId: userId, actorDiscordRoleIds: roleIds, serverId, reason });
    await handleProfileLookup();
    setStatus("Food-request cooldown reset and recorded in the audit log.");
  } catch (error) {
    setStatus(`Cooldown reset failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleModerationCaseCreate(targetUserId: string): Promise<void> {
  const form = readModerationActionForm();
  if (!form.reason) {
    setStatus("Enter a moderation reason before creating a case.");
    return;
  }

  try {
    setStatus("Creating moderation case...");
    await apiClient.createModerationCase({
      type: form.caseType,
      targetUserId,
      actorUserId: userId,
      actorDiscordRoleIds: roleIds,
      reason: form.reason,
      ...(form.evidenceUrl ? { evidenceUrl: form.evidenceUrl } : {}),
      ...(form.duration ? { duration: form.duration } : {}),
      surface: "website"
    });
    await handleProfileLookup();
    setStatus("Moderation case created and audited.");
  } catch (error) {
    setStatus(`Moderation case failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleModerationStrikeCreate(targetUserId: string): Promise<void> {
  const form = readModerationActionForm();
  if (!form.reason) {
    setStatus("Enter a moderation reason before creating a strike.");
    return;
  }
  if (!form.evidenceUrl) {
    setStatus("Add a ticket transcript, Discord message, clip, or other evidence URL before creating a strike.");
    return;
  }

  try {
    setStatus("Creating moderation strike...");
    const fingerprint = JSON.stringify([userId, targetUserId, form.reason, form.evidenceUrl, form.duration ?? "", form.strikeWeight]);
    if (!pendingModerationStrike || pendingModerationStrike.fingerprint !== fingerprint) {
      pendingModerationStrike = { fingerprint, requestId: crypto.randomUUID() };
    }
    await apiClient.createModerationStrike({
      targetUserId,
      actorUserId: userId,
      actorDiscordRoleIds: roleIds,
      reason: form.reason,
      evidenceUrl: form.evidenceUrl,
      ...(form.duration ? { duration: form.duration } : {}),
      weight: form.strikeWeight,
      surface: "website",
      requestId: pendingModerationStrike.requestId
    });
    pendingModerationStrike = undefined;
    await handleProfileLookup();
    setStatus("Moderation strike created, audited, and role actions recalculated.");
  } catch (error) {
    setStatus(`Moderation strike failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

function readModerationActionForm(): {
  reason: string;
  evidenceUrl?: string;
  duration?: string;
  caseType: string;
  strikeWeight: number;
} {
  const reason = document.querySelector<HTMLInputElement>("[data-moderation-reason]")?.value.trim() ?? "";
  const evidenceUrl = document.querySelector<HTMLInputElement>("[data-moderation-evidence]")?.value.trim() ?? "";
  const duration = document.querySelector<HTMLInputElement>("[data-moderation-duration]")?.value.trim() ?? "";
  const caseType = document.querySelector<HTMLSelectElement>("[data-moderation-case-type]")?.value ?? "warning";
  const rawStrikeWeight = Number(document.querySelector<HTMLSelectElement>("[data-moderation-strike-weight]")?.value ?? "1");

  return {
    reason,
    ...(evidenceUrl ? { evidenceUrl } : {}),
    ...(duration ? { duration } : {}),
    caseType,
    strikeWeight: Number.isInteger(rawStrikeWeight) && rawStrikeWeight >= 1 && rawStrikeWeight <= 3 ? rawStrikeWeight : 1
  };
}

async function handleGarageRestore(targetUserId: string, garageDinoId: string): Promise<void> {
  const requestKey = `${targetUserId}:${garageDinoId}`;
  const requestId = pendingGarageRestores.get(requestKey) ?? crypto.randomUUID();
  pendingGarageRestores.set(requestKey, requestId);
  try {
    setStatus("Restoring garage record...");
    await apiClient.restoreGarageDino({
      targetUserId,
      garageDinoId,
      actorUserId: userId,
      actorDiscordRoleIds: roleIds,
      reason: "Restored previous garage record from portal.",
      requestId
    });
    pendingGarageRestores.delete(requestKey);
    await handleProfileLookup();
    setStatus("Garage record restored and audited.");
  } catch (error) {
    setStatus(`Garage restore failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleGarageMigration(targetUserId: string): Promise<void> {
  const readNumber = (selector: string, fallback: number): number => {
    const raw = document.querySelector<HTMLInputElement>(selector)?.value.trim();
    return raw ? Number(raw) : fallback;
  };
  const species = document.querySelector<HTMLSelectElement>("[data-garage-migration-species]")?.value ?? "";
  const displayName = document.querySelector<HTMLInputElement>("[data-garage-migration-name]")?.value.trim() ?? "";
  const sourceReference = document.querySelector<HTMLInputElement>("[data-garage-migration-reference]")?.value.trim() ?? "";
  const reason = document.querySelector<HTMLInputElement>("[data-garage-migration-reason]")?.value.trim() ?? "";
  if (!species || !displayName || sourceReference.length < 3 || reason.length < 3) {
    setStatus("Choose a species and enter a name, migration reference, and audit reason.");
    return;
  }
  const mutationText = document.querySelector<HTMLTextAreaElement>("[data-garage-migration-mutations]")?.value ?? "";
  const mutations = [...new Set(mutationText.split(/[\n,]/).map((value) => value.trim()).filter(Boolean))];
  if (mutations.length > 16) {
    setStatus("A migrated dinosaur can have at most 16 mutations.");
    return;
  }
  const x = document.querySelector<HTMLInputElement>("[data-garage-migration-x]")?.value.trim() ?? "";
  const y = document.querySelector<HTMLInputElement>("[data-garage-migration-y]")?.value.trim() ?? "";
  const z = document.querySelector<HTMLInputElement>("[data-garage-migration-z]")?.value.trim() ?? "";
  if ((x && !y) || (!x && y)) {
    setStatus("Enter both X and Y when adding a parked location.");
    return;
  }
  const growth = readNumber("[data-garage-migration-growth]", 75);
  const health = readNumber("[data-garage-migration-health]", 100);
  const stamina = readNumber("[data-garage-migration-stamina]", 100);
  const hunger = readNumber("[data-garage-migration-hunger]", 100);
  const thirst = readNumber("[data-garage-migration-thirst]", 100);
  const primeActive = document.querySelector<HTMLInputElement>("[data-garage-migration-prime]")?.checked ?? false;
  const percentages = [growth, health, stamina, hunger, thirst];
  const coordinates = [x, y, z].filter(Boolean).map(Number);
  if (percentages.some((value) => !Number.isFinite(value) || value < 0 || value > 100)) {
    setStatus("Growth and vitals must be numbers between 0 and 100.");
    return;
  }
  if (coordinates.some((value) => !Number.isFinite(value))) {
    setStatus("Parked location coordinates must be valid numbers.");
    return;
  }
  try {
    setStatus("Adding verified legacy dinosaur to storage...");
    const fingerprint = JSON.stringify([
      targetUserId,
      species,
      displayName,
      growth,
      health,
      stamina,
      hunger,
      thirst,
      primeActive,
      mutations,
      x,
      y,
      z,
      sourceReference,
      reason
    ]);
    if (!pendingGarageMigration || pendingGarageMigration.fingerprint !== fingerprint) {
      pendingGarageMigration = { fingerprint, requestId: crypto.randomUUID() };
    }
    const result = await apiClient.migrateGarageDino({
      targetUserId,
      actorUserId: userId,
      actorDiscordRoleIds: roleIds,
      requestId: pendingGarageMigration.requestId,
      species,
      displayName,
      growth,
      health,
      stamina,
      hunger,
      thirst,
      primeActive,
      mutations,
      ...(x && y ? { position: { latitude: Number(x), longitude: Number(y), ...(z ? { altitude: Number(z) } : {}) } } : {}),
      sourceReference,
      reason
    });
    pendingGarageMigration = undefined;
    await handleProfileLookup();
    setStatus(result.duplicate ? "This migration was already saved; no duplicate was created." : `${displayName} was added to the player's garage and audited.`);
  } catch (error) {
    setStatus(`Garage migration failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleTicketDiscordRoomOpen(ticketId: string): Promise<void> {
  try {
    setStatus("Opening the private Discord evidence room...");
    const result = await apiClient.openSupportTicketDiscordRoom(ticketId, {
      actorUserId: userId,
      actorDiscordRoleIds: roleIds,
      reason: "Opened a private Discord room for case evidence exchange."
    });
    await handleTicketDetail(ticketId);
    setStatus(result.replayed ? "Discord evidence room already active." : "Discord evidence room opened.");
    if (result.url) window.open(result.url, "_blank", "noopener,noreferrer");
  } catch (error) {
    setStatus(`Discord room failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleGarageDeathRestore(targetUserId: string, deathEventId: string): Promise<void> {
  try {
    setStatus("Recovering verified death snapshot...");
    await apiClient.restoreDeathSnapshot(deathEventId, {
      targetUserId,
      actorUserId: userId,
      actorDiscordRoleIds: roleIds,
      reason: "Recovered verified PlayerStats death snapshot from staff profile."
    });
    await handleProfileLookup();
    setStatus("Death snapshot recovered to the player's garage and audited.");
  } catch (error) {
    setStatus(`Death recovery failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleMutationCorrection(button: HTMLElement, targetUserId: string): Promise<void> {
  const tool = button.closest<HTMLElement>("[data-mutation-correction-tool]");
  const mode = button.dataset.mutationMode;
  const mutation = tool?.querySelector<HTMLSelectElement>("[data-mutation-correction-choice]")?.value.trim() ?? "";
  const slotSelect = tool?.querySelector<HTMLSelectElement>("[data-mutation-correction-slot]");
  const targetSlot = slotSelect?.value ?? "";
  const expectedMutation = slotSelect?.selectedOptions[0]?.dataset.currentMutation ?? "";
  const reason = tool?.querySelector<HTMLInputElement>("[data-mutation-correction-reason]")?.value.trim() ?? "";
  if ((mode !== "live" && mode !== "offline" && mode !== "stored") || !mutation || !targetSlot || reason.length < 3) {
    setStatus("Choose an exact slot and mutation, then enter a ticket reference or audit reason.");
    return;
  }

  try {
    setStatus(`Submitting ${mode} mutation correction...`);
    const result = await apiClient.correctDinoMutation({
      targetUserId,
      actorUserId: userId,
      actorDiscordRoleIds: roleIds,
      mode,
      ...(button.dataset.garageId ? { garageDinoId: button.dataset.garageId } : {}),
      mutation,
      targetSlot,
      expectedMutation,
      reason
    });
    if (result.queuedUntilOnline) {
      await handleProfileLookup();
      setStatus("Correction queued safely. It will apply only when this exact safelogged dinosaur next loads and passes identity and slot verification.");
      return;
    }
    const finalStatus = await waitForBridgeStatus(result.operation.id, "Mutation correction", targetUserId);
    await handleProfileLookup();
    setStatus(finalStatus);
  } catch (error) {
    setStatus(`Mutation correction failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function handleMutationInspection(button: HTMLElement, targetUserId: string, mode: "live" | "stored", garageDinoId?: string): Promise<void> {
  const tool = button.closest<HTMLElement>("[data-mutation-correction-tool]");
  const reason = tool?.querySelector<HTMLInputElement>("[data-mutation-inspection-reason]")?.value.trim() ?? "";
  if (reason.length < 3) {
    setStatus(`Enter a ticket reference or audit reason before loading the ${mode} mutation slots.`);
    return;
  }
  try {
    setStatus(mode === "live" ? "Requesting a fresh 16-slot read from the current live dinosaur..." : "Loading the exact 16 mutation slots from DinoStorage...");
    const result = await apiClient.inspectDinoMutations({
      targetUserId,
      actorUserId: userId,
      actorDiscordRoleIds: roleIds,
      mode,
      ...(garageDinoId ? { garageDinoId } : {}),
      reason
    });
    const finalStatus = await waitForBridgeStatus(result.operation.id, "Mutation slot inspection", targetUserId);
    if (mode === "live") await waitForExactMutationSlots(targetUserId);
    await handleProfileLookup();
    setStatus(finalStatus);
  } catch (error) {
    setStatus(`Mutation slot inspection failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function waitForExactMutationSlots(targetUserId: string, timeoutMs = 20_000): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const profile = await apiClient.getProfileByLookup(targetUserId);
    if (profile.playerData?.online && profile.playerData.mutationSlotsExact) return true;
    await new Promise((resolve) => setTimeout(resolve, 1_500));
  }
  return false;
}

async function handleSystemAction(button: HTMLElement): Promise<void> {
  const reason = document.querySelector<HTMLInputElement>("[data-control-reason]")?.value.trim() || "Portal staff control request";
  const action = button.dataset.systemAction;

  if (!action) {
    return;
  }

  try {
    setStatus("Submitting staff control...");
    await apiClient.requestSystemControl({
      userId,
      roleIds,
      serverId: currentSnapshot.server.id
    }, action as SystemControlAction, reason);
    setStatus("Staff control accepted and audited.");
    await boot();
  } catch (error) {
    setStatus(`Staff control failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

function setStatus(message: string): void {
  const status = document.querySelector<HTMLElement>("#portalStatus");
  if (status) {
    if (statusTimer !== undefined) window.clearTimeout(statusTimer);
    status.textContent = message;
    status.classList.toggle("error", /failed|denied|could not|conflict|unavailable/i.test(message));
    status.classList.toggle("progress", /submitting|waiting|requested|creating/i.test(message));
    status.hidden = false;
    statusTimer = window.setTimeout(() => {
      status.hidden = true;
    }, /waiting|requested|submitting/i.test(message) ? 30_000 : 8_000);
  }
}

function staffTicketActionPayload(reason: string): { actorUserId: string; actorDiscordRoleIds: string[]; reason: string } {
  return {
    actorUserId: userId,
    actorDiscordRoleIds: roleIds,
    reason
  };
}

function cssEscape(value: string): string {
  return typeof CSS !== "undefined" && CSS.escape ? CSS.escape(value) : value.replaceAll('"', '\\"');
}

function escapeHtml(value: unknown): string {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function isBridgeResolutionStatus(value: string | undefined): value is "confirmed" | "failed" | "manual_review" {
  return value === "confirmed" || value === "failed" || value === "manual_review";
}

async function handleSlotsConfigLoad(): Promise<void> {
  const container = document.querySelector<HTMLElement>("#slotsConfig");
  if (!container) return;
  try {
    slotsConfiguration = await apiClient.getSlotsConfig(currentSnapshot.server.id);
    container.innerHTML = renderSlotsConfiguration(slotsConfiguration, currentSnapshot.currency.points);
    hydratePortalIcons();
  } catch (error) {
    container.innerHTML = `<div class="support-empty"><strong>Slots configuration is unavailable</strong><p>${escapePortalText(error instanceof Error ? error.message : String(error))}</p></div>`;
  }
}

async function handleSlotsConfigSave(): Promise<void> {
  const enabled = document.querySelector<HTMLInputElement>("[data-slots-config-enabled]")?.checked ?? false;
  const minimumWager = Number(document.querySelector<HTMLInputElement>("[data-slots-config-min]")?.value ?? 0);
  const maximumWager = Number(document.querySelector<HTMLInputElement>("[data-slots-config-max]")?.value ?? 0);
  const wagerStep = Number(document.querySelector<HTMLInputElement>("[data-slots-config-step]")?.value ?? 0);
  const cooldownSeconds = Number(document.querySelector<HTMLInputElement>("[data-slots-config-cooldown]")?.value ?? 0);
  const dailyWagerLimit = Number(document.querySelector<HTMLInputElement>("[data-slots-config-daily]")?.value ?? 0);
  const reason = document.querySelector<HTMLInputElement>("[data-slots-config-reason]")?.value.trim() ?? "";
  if (![minimumWager, maximumWager, wagerStep, cooldownSeconds, dailyWagerLimit].every(Number.isInteger)) throw new Error("Slots limits must be whole numbers.");
  if (wagerStep < 1 || minimumWager % wagerStep !== 0) throw new Error("Wager increments must divide the minimum wager exactly.");
  if (minimumWager < 1 || maximumWager < minimumWager) throw new Error("Maximum wager must be at least the minimum wager.");
  if (dailyWagerLimit < maximumWager) throw new Error("Daily wager limit must be at least the maximum single wager.");
  if (reason.length < 5) throw new Error("Add a clear reason for changing the Fossil Fortune.");
  slotsConfiguration = await apiClient.saveSlotsConfig({
    actorUserId: userId, serverId: currentSnapshot.server.id, enabled, minimumWager, maximumWager,
    wagerStep, cooldownSeconds, dailyWagerLimit, reason
  });
  const container = document.querySelector<HTMLElement>("#slotsConfig");
  if (container) container.innerHTML = renderSlotsConfiguration(slotsConfiguration, currentSnapshot.currency.points);
  hydratePortalIcons();
  setStatus(`Fossil Fortune ${enabled ? "opened" : "saved as closed"} with a ${formatNumber(maximumWager)} ${currentSnapshot.currency.points} maximum wager.`);
}


async function handleSlotsLoad(): Promise<void> {
  const workspace = document.querySelector<HTMLElement>("#slotsWorkspace");
  if (!workspace || slotsLoaded) return;
  slotsLoaded = true;
  await mountSlots(workspace, apiClient, userId, currentSnapshot.server.id, balance => { currentSnapshot.wallet.points = balance; refreshPortalShell(currentSnapshot); document.querySelectorAll<HTMLElement>("[data-blackjack-balance], [data-roulette-balance]").forEach(node => node.textContent = formatNumber(balance)); });
}

async function refreshAuctions(force = false): Promise<void> {
  const host = document.querySelector<HTMLElement>("[data-auction-market]");
  if (!host || auctionRefreshInFlight || (!force && portalInteractionActive())) return;
  auctionRefreshInFlight = true;
  try {
    const state = await apiClient.getAuctions(userId);
    if (!host.isConnected) return;
    const unchanged = auctionState && JSON.stringify({ ...state, serverTime: "" }) === JSON.stringify({ ...auctionState, serverTime: "" });
    auctionState = state;
    const total = document.querySelector<HTMLElement>(".marketplace-summary strong");
    if (total) total.textContent = String(currentSnapshot.marketplace.length + state.auctions.filter(a => a.status === "open").length);
    if (unchanged && !force && host.dataset.loaded === "true") return;
    const grid = document.querySelector<HTMLElement>("[data-marketplace-grid]");
    if (!grid) return;
    const oldCards = Array.from(grid.querySelectorAll<HTMLElement>("[data-auction-id][data-marketplace-card]"));
    const models = new globalThis.Map(oldCards.map(card => [card.dataset.auctionId, card.querySelector("[data-marketplace-skin-preview]")]));
    host.innerHTML = renderAuctions(state);
    oldCards.forEach(card => card.remove());
    grid.insertAdjacentHTML("beforeend", renderAuctionListings(state));
    host.dataset.loaded = "true";
    for (const card of Array.from(grid.querySelectorAll<HTMLElement>("[data-auction-id][data-marketplace-card]"))) {
      const previous = models.get(card.dataset.auctionId); const next = card.querySelector("[data-marketplace-skin-preview]");
      if (previous && next) next.replaceWith(previous);
    }
    const species = document.querySelector<HTMLSelectElement>("[data-marketplace-filter='species']");
    for (const name of new Set(state.auctions.filter(a => a.status === "open").map(a => a.species))) {
      if (species && !Array.from(species.options).some(o => o.value === name.toLowerCase())) species.add(new Option(name, name.toLowerCase()));
    }
    if (species) Array.from(species.options).filter(option => option.value).sort((a, b) => a.text.localeCompare(b.text)).forEach(option => species.append(option));
    hydratePortalIcons(); applyMarketplaceFilters(); updateMarketplaceCountdowns(); initializeMarketplaceSkinPreviews();
  } catch (error) {
    if (host.isConnected && host.dataset.loaded !== "true") host.innerHTML = `<p class="form-help is-error">Auctions could not load. ${escapePortalText(error instanceof Error ? error.message : String(error))} Refresh to try again.</p>`;
  } finally { auctionRefreshInFlight = false; }
}

function prepareAuctionConfirmation(button: HTMLElement): boolean {
  try {
    if (button.dataset.portalAction === "auction-bid") {
      const input = button.closest(".auction-card")?.querySelector<HTMLInputElement>("[data-auction-bid]");
      if (!input || !input.reportValidity() || !Number.isSafeInteger(Number(input.value)) || Number(input.value) <= 0) return false;
      button.dataset.confirmedAmount = input.value;
      button.dataset.confirmMessage = `Place a bid of ${Number(input.value).toLocaleString("en-GB")} Sticks? Your bid is binding and will be reserved until you are outbid or the auction ends. If you win, collect your dinosaur from Marketplace.`;
    }
    if (button.dataset.portalAction === "marketplace-list-garage") {
      const item = currentSnapshot.garage.find(d => d.id === button.dataset.garageId);
      if (!item || item.marketplacePrice === null) return false;
      const row = button.closest(".marketplace-sell-item");
      const opening = row?.querySelector<HTMLInputElement>("[data-auction-opening]");
      const buyNow = row?.querySelector<HTMLInputElement>("[data-auction-buy-now]");
      const auction = document.querySelector<HTMLSelectElement>("[data-marketplace-format]")?.value !== "fixed";
      if (buyNow?.value && !buyNow.reportValidity() || auction && opening?.value && !opening.reportValidity()) return false;
      const amount = opening?.value ? Number(opening.value) : item.marketplacePrice ?? 0;
      const instant = buyNow?.value ? Number(buyNow.value) : undefined;
      if (auction && instant !== undefined && instant < amount) { setStatus("Buy It Now must be at least the opening bid."); return false; }
      const duration = document.querySelector<HTMLSelectElement>("[data-marketplace-duration]")?.selectedOptions[0]?.textContent ?? "24 hours";
      button.dataset.confirmMessage = auction
        ? `Auction ${item.displayName} for ${duration}, opening at ${amount.toLocaleString("en-GB")} Sticks${instant ? `, with Buy It Now at ${instant.toLocaleString("en-GB")} Sticks` : ", without Buy It Now"}. The highest bid wins, even below the calculated value. You cannot cancel after the first bid.`
        : `List ${item.displayName} for ${(instant ?? item.marketplacePrice ?? 0).toLocaleString("en-GB")} Sticks for ${duration}. Your dinosaur will be locked until it sells, expires or you withdraw it.`;
    }
    return true;
  } catch (error) { setStatus(error instanceof Error ? error.message : String(error)); return false; }
}

async function handleAuctionAction(button: HTMLElement, actorUserId: string): Promise<void> {
  const id = button.dataset.auctionId!, action = button.dataset.portalAction;
  let message = "Auction updated.";
  if (action === "auction-bid" || action === "auction-buy") {
    const amount = Number(action === "auction-buy" ? button.dataset.amount : button.dataset.confirmedAmount ?? button.closest(".auction-card")?.querySelector<HTMLInputElement>("[data-auction-bid]")?.value);
    const details = { userId: actorUserId, amount, buyNow: action === "auction-buy" };
    const fingerprint = JSON.stringify({ id, ...details });
    if (pendingAuctionRequest?.fingerprint !== fingerprint) pendingAuctionRequest = { fingerprint, requestId: crypto.randomUUID() };
    await apiClient.bidAuction(id, { ...details, requestId: pendingAuctionRequest.requestId });
    pendingAuctionRequest = undefined;
    message = action === "auction-buy" ? "Purchased. Your dinosaur is ready to collect below." : "Bid accepted. Your Sticks are reserved while you lead.";
  } else if (action === "auction-cancel") {
    await apiClient.auctionAction(id, "cancel", actorUserId); message = "Auction withdrawn. Your dinosaur is back in your garage.";
  } else if (action === "auction-collect") {
    await apiClient.auctionAction(id, "collect", actorUserId); message = "Your dinosaur is now in your garage.";
  } else if (action === "auction-proceeds") {
    const result = await apiClient.auctionAction(id, "proceeds", actorUserId);
    message = result.claimed ? `${result.claimed.toLocaleString("en-GB")} Sticks collected. Any remainder stays safely reserved until your wallet has room.` : "Your wallet is full. Spend some Sticks, then collect your proceeds.";
  }
  await boot(); setStatus(message);
}
